import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { useObjectRef as useObjectRef$1, mergeProps as mergeProps$1, useId as useId$1 } from '@react-aria/utils';
import { InfoState, Divider, Field, Modal, FieldLabel } from '@gemini/ui';
import { useCallback, useMemo, useState, createContext, useContext, useRef, useEffect, forwardRef, useId } from 'react';
import { useLocale, useTranslation, useObjectRef, Box, useTokens, getRootFontSizeValue, useHoverable, useFocusable, mergeProps, usePopover, FocusableProvider, Overlay, usePressable, useBreakpointValue, useResizeObserver, useLayoutEffect } from '@gemini/core';
import { VisuallyHidden } from '@react-aria/visually-hidden';
import { TextFieldBase } from '@gemini/ui/unsafe';
import { Item } from '@react-stately/collections';
import { useComboBoxState } from '@react-stately/combobox';
import { AsYouType, parseDigits, isValidPhoneNumber, validatePhoneNumberLength } from 'libphonenumber-js/core';
import { useControlledState } from '@react-stately/utils';
import { useTextField } from '@react-aria/textfield';
import { useComboBox } from '@react-aria/combobox';
import { CaretUpFilledIcon, CaretDownFilledIcon } from '@gemini/icons';
import { DismissButton } from '@react-aria/overlays';
import { useOption, useListBox } from '@react-aria/listbox';
import { focusSafely } from '@react-aria/focus';

const cache = new Map();
/**
 * Provides localized string collation for the current locale. Automatically updates when the locale changes,
 * and handles caching of the collator for performance.
 * @param options - Collator options.
 */ function useCollator(options) {
    const locale = useLocale();
    const cacheKey = locale + (options ? Object.entries(options).sort((a, b)=>a[0] < b[0] ? -1 : 1).join() : '');
    if (cache.has(cacheKey)) {
        return cache.get(cacheKey);
    }
    const formatter = new Intl.Collator(locale, options);
    cache.set(cacheKey, formatter);
    return formatter;
}

/**
 * Provides localized string search functionality that is useful for filtering or matching items
 * in a list. Options can be provided to adjust the sensitivity to case, diacritics, and other parameters.
 */ function useFilter(options) {
    const collator = useCollator({
        usage: 'search',
        ...options
    });
    const contains = useCallback((string, substring)=>{
        if (substring.length === 0) {
            return true;
        }
        string = string.normalize('NFC');
        substring = substring.normalize('NFC');
        let scan = 0;
        const sliceLen = substring.length;
        for(; scan + sliceLen <= string.length; scan++){
            const slice = string.slice(scan, scan + sliceLen);
            if (collator.compare(substring, slice) === 0) {
                return true;
            }
        }
        return false;
    }, [
        collator
    ]);
    return useMemo(()=>({
            contains
        }), [
        contains
    ]);
}

const usePhoneNumberTranslation = ()=>useTranslation({
        'en-GB': {
            'phonenumber::countryName::GD': 'Grenada',
            'phonenumber::countryName::CH': 'Switzerland',
            'phonenumber::countryName::SL': 'Sierra Leone',
            'phonenumber::countryName::HU': 'Hungary',
            'phonenumber::countryName::TW': 'Taiwan',
            'phonenumber::countryName::WF': 'Wallis and Futuna',
            'phonenumber::countryName::BB': 'Barbados',
            'phonenumber::countryName::CI': 'Ivory Coast',
            'phonenumber::countryName::TN': 'Tunisia',
            'phonenumber::countryName::IT': 'Italy',
            'phonenumber::countryName::BJ': 'Benin',
            'phonenumber::countryName::ID': 'Indonesia',
            'phonenumber::countryName::CV': 'Cape Verde',
            'phonenumber::countryName::LA': 'Laos',
            'phonenumber::countryName::UG': 'Uganda',
            'phonenumber::countryName::AD': 'Andorra',
            'phonenumber::countryName::BI': 'Burundi',
            'phonenumber::countryName::ZA': 'South Africa',
            'phonenumber::countryName::FR': 'France',
            'phonenumber::countryName::LY': 'Libya',
            'phonenumber::countryName::MX': 'Mexico',
            'phonenumber::countryName::GA': 'Gabon',
            'phonenumber::countryName::MP': 'Northern Mariana Islands',
            'phonenumber::countryName::MK': 'North Macedonia',
            'phonenumber::countryName::CN': 'China',
            'phonenumber::countryName::YE': 'Yemen',
            'phonenumber::countryName::BL': 'Saint Barthélemy',
            'phonenumber::countryName::SB': 'Solomon Islands',
            'phonenumber::countryName::UZ': 'Uzbekistan',
            'phonenumber::countryName::EG': 'Egypt',
            'phonenumber::countryName::SN': 'Senegal',
            'phonenumber::countryName::LK': 'Sri Lanka',
            'phonenumber::countryName::PS': 'Palestine',
            'phonenumber::countryName::BD': 'Bangladesh',
            'phonenumber::countryName::PE': 'Peru',
            'phonenumber::countryName::SG': 'Singapore',
            'phonenumber::countryName::TR': 'Turkey',
            'phonenumber::countryName::AF': 'Afghanistan',
            'phonenumber::countryName::AW': 'Aruba',
            'phonenumber::countryName::CK': 'Cook Islands',
            'phonenumber::countryName::GB': 'United Kingdom',
            'phonenumber::countryName::ZM': 'Zambia',
            'phonenumber::countryName::FI': 'Finland',
            'phonenumber::countryName::NE': 'Niger',
            'phonenumber::countryName::CX': 'Christmas Island',
            'phonenumber::countryName::TK': 'Tokelau',
            'phonenumber::countryName::GW': 'Guinea-Bissau',
            'phonenumber::countryName::AZ': 'Azerbaijan',
            'phonenumber::countryName::RE': 'Réunion',
            'phonenumber::countryName::DJ': 'Djibouti',
            'phonenumber::countryName::KP': 'North Korea',
            'phonenumber::countryName::MU': 'Mauritius',
            'phonenumber::countryName::MS': 'Montserrat',
            'phonenumber::countryName::CO': 'Colombia',
            'phonenumber::countryName::GR': 'Greece',
            'phonenumber::countryName::HR': 'Croatia',
            'phonenumber::countryName::MA': 'Morocco',
            'phonenumber::countryName::DZ': 'Algeria',
            'phonenumber::countryName::NL': 'Netherlands',
            'phonenumber::countryName::SD': 'Sudan',
            'phonenumber::countryName::FJ': 'Fiji',
            'phonenumber::countryName::LI': 'Liechtenstein',
            'phonenumber::countryName::NP': 'Nepal',
            'phonenumber::countryName::PR': 'Puerto Rico',
            'phonenumber::countryName::GE': 'Georgia',
            'phonenumber::countryName::PK': 'Pakistan',
            'phonenumber::countryName::MC': 'Monaco',
            'phonenumber::countryName::BW': 'Botswana',
            'phonenumber::countryName::LB': 'Lebanon',
            'phonenumber::countryName::PG': 'Papua New Guinea',
            'phonenumber::countryName::YT': 'Mayotte',
            'phonenumber::countryName::DO': 'Dominican Republic',
            'phonenumber::countryName::QA': 'Qatar',
            'phonenumber::countryName::MG': 'Madagascar',
            'phonenumber::countryName::IN': 'India',
            'phonenumber::countryName::SY': 'Syria',
            'phonenumber::countryName::ME': 'Montenegro',
            'phonenumber::countryName::SZ': 'Eswatini',
            'phonenumber::countryName::PY': 'Paraguay',
            'phonenumber::countryName::SV': 'El Salvador',
            'phonenumber::countryName::UA': 'Ukraine',
            'phonenumber::countryName::IM': 'Isle of Man',
            'phonenumber::countryName::NA': 'Namibia',
            'phonenumber::countryName::AE': 'United Arab Emirates',
            'phonenumber::countryName::BG': 'Bulgaria',
            'phonenumber::countryName::GL': 'Greenland',
            'phonenumber::countryName::DE': 'Germany',
            'phonenumber::countryName::KH': 'Cambodia',
            'phonenumber::countryName::IQ': 'Iraq',
            'phonenumber::countryName::SE': 'Sweden',
            'phonenumber::countryName::CU': 'Cuba',
            'phonenumber::countryName::KG': 'Kyrgyzstan',
            'phonenumber::countryName::RU': 'Russia',
            'phonenumber::countryName::MY': 'Malaysia',
            'phonenumber::countryName::ST': 'São Tomé and Príncipe',
            'phonenumber::countryName::CY': 'Cyprus',
            'phonenumber::countryName::CA': 'Canada',
            'phonenumber::countryName::MW': 'Malawi',
            'phonenumber::countryName::SA': 'Saudi Arabia',
            'phonenumber::countryName::BA': 'Bosnia and Herzegovina',
            'phonenumber::countryName::ET': 'Ethiopia',
            'phonenumber::countryName::ES': 'Spain',
            'phonenumber::countryName::SI': 'Slovenia',
            'phonenumber::countryName::OM': 'Oman',
            'phonenumber::countryName::PM': 'Saint Pierre and Miquelon',
            'phonenumber::countryName::MO': 'Macau',
            'phonenumber::countryName::SM': 'San Marino',
            'phonenumber::countryName::LS': 'Lesotho',
            'phonenumber::countryName::MH': 'Marshall Islands',
            'phonenumber::countryName::IS': 'Iceland',
            'phonenumber::countryName::LU': 'Luxembourg',
            'phonenumber::countryName::AR': 'Argentina',
            'phonenumber::countryName::NR': 'Nauru',
            'phonenumber::countryName::CC': 'Cocos (Keeling) Islands',
            'phonenumber::countryName::DM': 'Dominica',
            'phonenumber::countryName::CR': 'Costa Rica',
            'phonenumber::countryName::AU': 'Australia',
            'phonenumber::countryName::TH': 'Thailand',
            'phonenumber::countryName::HT': 'Haiti',
            'phonenumber::countryName::TV': 'Tuvalu',
            'phonenumber::countryName::HN': 'Honduras',
            'phonenumber::countryName::GQ': 'Equatorial Guinea',
            'phonenumber::countryName::PF': 'French Polynesia',
            'phonenumber::countryName::BY': 'Belarus',
            'phonenumber::countryName::LV': 'Latvia',
            'phonenumber::countryName::PW': 'Palau',
            'phonenumber::countryName::GP': 'Guadeloupe',
            'phonenumber::countryName::PH': 'Philippines',
            'phonenumber::countryName::GI': 'Gibraltar',
            'phonenumber::countryName::DK': 'Denmark',
            'phonenumber::countryName::CM': 'Cameroon',
            'phonenumber::countryName::GN': 'Guinea',
            'phonenumber::countryName::BH': 'Bahrain',
            'phonenumber::countryName::SR': 'Suriname',
            'phonenumber::countryName::CD': 'DR Congo',
            'phonenumber::countryName::SO': 'Somalia',
            'phonenumber::countryName::CZ': 'Czechia',
            'phonenumber::countryName::NC': 'New Caledonia',
            'phonenumber::countryName::VU': 'Vanuatu',
            'phonenumber::countryName::SH': 'Saint Helena, Ascension and Tristan da Cunha',
            'phonenumber::countryName::TG': 'Togo',
            'phonenumber::countryName::VG': 'British Virgin Islands',
            'phonenumber::countryName::KE': 'Kenya',
            'phonenumber::countryName::NU': 'Niue',
            'phonenumber::countryName::RW': 'Rwanda',
            'phonenumber::countryName::EE': 'Estonia',
            'phonenumber::countryName::RO': 'Romania',
            'phonenumber::countryName::TT': 'Trinidad and Tobago',
            'phonenumber::countryName::GY': 'Guyana',
            'phonenumber::countryName::TL': 'Timor-Leste',
            'phonenumber::countryName::VN': 'Vietnam',
            'phonenumber::countryName::UY': 'Uruguay',
            'phonenumber::countryName::VA': 'Vatican City',
            'phonenumber::countryName::HK': 'Hong Kong',
            'phonenumber::countryName::AT': 'Austria',
            'phonenumber::countryName::AG': 'Antigua and Barbuda',
            'phonenumber::countryName::TM': 'Turkmenistan',
            'phonenumber::countryName::MZ': 'Mozambique',
            'phonenumber::countryName::PA': 'Panama',
            'phonenumber::countryName::FM': 'Micronesia',
            'phonenumber::countryName::IE': 'Ireland',
            'phonenumber::countryName::CW': 'Curaçao',
            'phonenumber::countryName::GF': 'French Guiana',
            'phonenumber::countryName::NO': 'Norway',
            'phonenumber::countryName::AX': 'Åland Islands',
            'phonenumber::countryName::CF': 'Central African Republic',
            'phonenumber::countryName::BF': 'Burkina Faso',
            'phonenumber::countryName::ER': 'Eritrea',
            'phonenumber::countryName::TZ': 'Tanzania',
            'phonenumber::countryName::KR': 'South Korea',
            'phonenumber::countryName::JO': 'Jordan',
            'phonenumber::countryName::MR': 'Mauritania',
            'phonenumber::countryName::LT': 'Lithuania',
            'phonenumber::countryName::SK': 'Slovakia',
            'phonenumber::countryName::AO': 'Angola',
            'phonenumber::countryName::KZ': 'Kazakhstan',
            'phonenumber::countryName::MD': 'Moldova',
            'phonenumber::countryName::ML': 'Mali',
            'phonenumber::countryName::AM': 'Armenia',
            'phonenumber::countryName::WS': 'Samoa',
            'phonenumber::countryName::JE': 'Jersey',
            'phonenumber::countryName::JP': 'Japan',
            'phonenumber::countryName::BO': 'Bolivia',
            'phonenumber::countryName::CL': 'Chile',
            'phonenumber::countryName::US': 'United States',
            'phonenumber::countryName::BM': 'Bermuda',
            'phonenumber::countryName::SC': 'Seychelles',
            'phonenumber::countryName::IO': 'British Indian Ocean Territory',
            'phonenumber::countryName::GT': 'Guatemala',
            'phonenumber::countryName::EC': 'Ecuador',
            'phonenumber::countryName::MQ': 'Martinique',
            'phonenumber::countryName::TJ': 'Tajikistan',
            'phonenumber::countryName::MT': 'Malta',
            'phonenumber::countryName::GM': 'Gambia',
            'phonenumber::countryName::NG': 'Nigeria',
            'phonenumber::countryName::BS': 'Bahamas',
            'phonenumber::countryName::XK': 'Kosovo',
            'phonenumber::countryName::KW': 'Kuwait',
            'phonenumber::countryName::MV': 'Maldives',
            'phonenumber::countryName::SS': 'South Sudan',
            'phonenumber::countryName::IR': 'Iran',
            'phonenumber::countryName::AL': 'Albania',
            'phonenumber::countryName::BR': 'Brazil',
            'phonenumber::countryName::RS': 'Serbia',
            'phonenumber::countryName::BZ': 'Belize',
            'phonenumber::countryName::MM': 'Myanmar',
            'phonenumber::countryName::BT': 'Bhutan',
            'phonenumber::countryName::VE': 'Venezuela',
            'phonenumber::countryName::LR': 'Liberia',
            'phonenumber::countryName::JM': 'Jamaica',
            'phonenumber::countryName::PL': 'Poland',
            'phonenumber::countryName::KY': 'Cayman Islands',
            'phonenumber::countryName::BN': 'Brunei',
            'phonenumber::countryName::KM': 'Comoros',
            'phonenumber::countryName::GU': 'Guam',
            'phonenumber::countryName::TO': 'Tonga',
            'phonenumber::countryName::KI': 'Kiribati',
            'phonenumber::countryName::GH': 'Ghana',
            'phonenumber::countryName::TD': 'Chad',
            'phonenumber::countryName::ZW': 'Zimbabwe',
            'phonenumber::countryName::MF': 'Saint Martin',
            'phonenumber::countryName::MN': 'Mongolia',
            'phonenumber::countryName::PT': 'Portugal',
            'phonenumber::countryName::AS': 'American Samoa',
            'phonenumber::countryName::CG': 'Republic of the Congo',
            'phonenumber::countryName::BE': 'Belgium',
            'phonenumber::countryName::IL': 'Israel',
            'phonenumber::countryName::NZ': 'New Zealand',
            'phonenumber::countryName::NI': 'Nicaragua',
            'phonenumber::countryName::AI': 'Anguilla'
        },
        'fr-FR': {
            'phonenumber::countryName::GD': 'Grenade',
            'phonenumber::countryName::CH': 'Suisse',
            'phonenumber::countryName::SL': 'Sierra Leone',
            'phonenumber::countryName::HU': 'Hongrie',
            'phonenumber::countryName::TW': 'Taïwan',
            'phonenumber::countryName::WF': 'Wallis-et-Futuna',
            'phonenumber::countryName::BB': 'Barbade',
            'phonenumber::countryName::CI': "Côte d'Ivoire",
            'phonenumber::countryName::TN': 'Tunisie',
            'phonenumber::countryName::IT': 'Italie',
            'phonenumber::countryName::BJ': 'Bénin',
            'phonenumber::countryName::ID': 'Indonésie',
            'phonenumber::countryName::CV': 'Îles du Cap-Vert',
            'phonenumber::countryName::LA': 'Laos',
            'phonenumber::countryName::UG': 'Ouganda',
            'phonenumber::countryName::AD': 'Andorre',
            'phonenumber::countryName::BI': 'Burundi',
            'phonenumber::countryName::ZA': 'Afrique du Sud',
            'phonenumber::countryName::FR': 'France',
            'phonenumber::countryName::LY': 'Libye',
            'phonenumber::countryName::MX': 'Mexique',
            'phonenumber::countryName::GA': 'Gabon',
            'phonenumber::countryName::MP': 'Îles Mariannes du Nord',
            'phonenumber::countryName::MK': 'Macédoine du Nord',
            'phonenumber::countryName::CN': 'Chine',
            'phonenumber::countryName::YE': 'Yémen',
            'phonenumber::countryName::BL': 'Saint-Barthélemy',
            'phonenumber::countryName::SB': 'Îles Salomon',
            'phonenumber::countryName::UZ': 'Ouzbékistan',
            'phonenumber::countryName::EG': 'Égypte',
            'phonenumber::countryName::SN': 'Sénégal',
            'phonenumber::countryName::LK': 'Sri Lanka',
            'phonenumber::countryName::PS': 'Palestine',
            'phonenumber::countryName::BD': 'Bangladesh',
            'phonenumber::countryName::PE': 'Pérou',
            'phonenumber::countryName::SG': 'Singapour',
            'phonenumber::countryName::TR': 'Turquie',
            'phonenumber::countryName::AF': 'Afghanistan',
            'phonenumber::countryName::AW': 'Aruba',
            'phonenumber::countryName::CK': 'Îles Cook',
            'phonenumber::countryName::GB': 'Royaume-Uni',
            'phonenumber::countryName::ZM': 'Zambie',
            'phonenumber::countryName::FI': 'Finlande',
            'phonenumber::countryName::NE': 'Niger',
            'phonenumber::countryName::CX': 'Île Christmas',
            'phonenumber::countryName::TK': 'Tokelau',
            'phonenumber::countryName::GW': 'Guinée-Bissau',
            'phonenumber::countryName::AZ': 'Azerbaïdjan',
            'phonenumber::countryName::RE': 'Réunion',
            'phonenumber::countryName::DJ': 'Djibouti',
            'phonenumber::countryName::KP': 'Corée du Nord',
            'phonenumber::countryName::MU': 'Île Maurice',
            'phonenumber::countryName::MS': 'Montserrat',
            'phonenumber::countryName::CO': 'Colombie',
            'phonenumber::countryName::GR': 'Grèce',
            'phonenumber::countryName::HR': 'Croatie',
            'phonenumber::countryName::MA': 'Maroc',
            'phonenumber::countryName::DZ': 'Algérie',
            'phonenumber::countryName::NL': 'Pays-Bas',
            'phonenumber::countryName::SD': 'Soudan',
            'phonenumber::countryName::FJ': 'Fidji',
            'phonenumber::countryName::LI': 'Liechtenstein',
            'phonenumber::countryName::NP': 'Népal',
            'phonenumber::countryName::PR': 'Porto Rico',
            'phonenumber::countryName::GE': 'Géorgie',
            'phonenumber::countryName::PK': 'Pakistan',
            'phonenumber::countryName::MC': 'Monaco',
            'phonenumber::countryName::BW': 'Botswana',
            'phonenumber::countryName::LB': 'Liban',
            'phonenumber::countryName::PG': 'Papouasie-Nouvelle-Guinée',
            'phonenumber::countryName::YT': 'Mayotte',
            'phonenumber::countryName::DO': 'République dominicaine',
            'phonenumber::countryName::QA': 'Qatar',
            'phonenumber::countryName::MG': 'Madagascar',
            'phonenumber::countryName::IN': 'Inde',
            'phonenumber::countryName::SY': 'Syrie',
            'phonenumber::countryName::ME': 'Monténégro',
            'phonenumber::countryName::SZ': 'Swaziland',
            'phonenumber::countryName::PY': 'Paraguay',
            'phonenumber::countryName::SV': 'Salvador',
            'phonenumber::countryName::UA': 'Ukraine',
            'phonenumber::countryName::IM': 'Île de Man',
            'phonenumber::countryName::NA': 'Namibie',
            'phonenumber::countryName::AE': 'Émirats arabes unis',
            'phonenumber::countryName::BG': 'Bulgarie',
            'phonenumber::countryName::GL': 'Groenland',
            'phonenumber::countryName::DE': 'Allemagne',
            'phonenumber::countryName::KH': 'Cambodge',
            'phonenumber::countryName::IQ': 'Irak',
            'phonenumber::countryName::SE': 'Suède',
            'phonenumber::countryName::CU': 'Cuba',
            'phonenumber::countryName::KG': 'Kirghizistan',
            'phonenumber::countryName::RU': 'Russie',
            'phonenumber::countryName::MY': 'Malaisie',
            'phonenumber::countryName::ST': 'São Tomé et Príncipe',
            'phonenumber::countryName::CY': 'Chypre',
            'phonenumber::countryName::CA': 'Canada',
            'phonenumber::countryName::MW': 'Malawi',
            'phonenumber::countryName::SA': 'Arabie Saoudite',
            'phonenumber::countryName::BA': 'Bosnie-Herzégovine',
            'phonenumber::countryName::ET': 'Éthiopie',
            'phonenumber::countryName::ES': 'Espagne',
            'phonenumber::countryName::SI': 'Slovénie',
            'phonenumber::countryName::OM': 'Oman',
            'phonenumber::countryName::PM': 'Saint-Pierre-et-Miquelon',
            'phonenumber::countryName::MO': 'Macao',
            'phonenumber::countryName::SM': 'Saint-Marin',
            'phonenumber::countryName::LS': 'Lesotho',
            'phonenumber::countryName::MH': 'Îles Marshall',
            'phonenumber::countryName::IS': 'Islande',
            'phonenumber::countryName::LU': 'Luxembourg',
            'phonenumber::countryName::AR': 'Argentine',
            'phonenumber::countryName::NR': 'Nauru',
            'phonenumber::countryName::CC': 'Îles Cocos',
            'phonenumber::countryName::DM': 'Dominique',
            'phonenumber::countryName::CR': 'Costa Rica',
            'phonenumber::countryName::AU': 'Australie',
            'phonenumber::countryName::TH': 'Thaïlande',
            'phonenumber::countryName::HT': 'Haïti',
            'phonenumber::countryName::TV': 'Tuvalu',
            'phonenumber::countryName::HN': 'Honduras',
            'phonenumber::countryName::GQ': 'Guinée équatoriale',
            'phonenumber::countryName::PF': 'Polynésie française',
            'phonenumber::countryName::BY': 'Biélorussie',
            'phonenumber::countryName::LV': 'Lettonie',
            'phonenumber::countryName::PW': 'Palaos (Palau)',
            'phonenumber::countryName::GP': 'Guadeloupe',
            'phonenumber::countryName::PH': 'Philippines',
            'phonenumber::countryName::GI': 'Gibraltar',
            'phonenumber::countryName::DK': 'Danemark',
            'phonenumber::countryName::CM': 'Cameroun',
            'phonenumber::countryName::GN': 'Guinée',
            'phonenumber::countryName::BH': 'Bahreïn',
            'phonenumber::countryName::SR': 'Surinam',
            'phonenumber::countryName::CD': 'Congo (Rép. dém.)',
            'phonenumber::countryName::SO': 'Somalie',
            'phonenumber::countryName::CZ': 'Tchéquie',
            'phonenumber::countryName::NC': 'Nouvelle-Calédonie',
            'phonenumber::countryName::VU': 'Vanuatu',
            'phonenumber::countryName::SH': 'Sainte-Hélène, Ascension et Tristan da Cunha',
            'phonenumber::countryName::TG': 'Togo',
            'phonenumber::countryName::VG': 'Îles Vierges britanniques',
            'phonenumber::countryName::KE': 'Kenya',
            'phonenumber::countryName::NU': 'Niue',
            'phonenumber::countryName::RW': 'Rwanda',
            'phonenumber::countryName::EE': 'Estonie',
            'phonenumber::countryName::RO': 'Roumanie',
            'phonenumber::countryName::TT': 'Trinité-et-Tobago',
            'phonenumber::countryName::GY': 'Guyana',
            'phonenumber::countryName::TL': 'Timor oriental',
            'phonenumber::countryName::VN': 'Viêt Nam',
            'phonenumber::countryName::UY': 'Uruguay',
            'phonenumber::countryName::VA': 'Cité du Vatican',
            'phonenumber::countryName::HK': 'Hong Kong',
            'phonenumber::countryName::AT': 'Autriche',
            'phonenumber::countryName::AG': 'Antigua-et-Barbuda',
            'phonenumber::countryName::TM': 'Turkménistan',
            'phonenumber::countryName::MZ': 'Mozambique',
            'phonenumber::countryName::PA': 'Panama',
            'phonenumber::countryName::FM': 'Micronésie',
            'phonenumber::countryName::IE': 'Irlande',
            'phonenumber::countryName::CW': 'Curaçao',
            'phonenumber::countryName::GF': 'Guyane',
            'phonenumber::countryName::NO': 'Norvège',
            'phonenumber::countryName::AX': 'Ahvenanmaa',
            'phonenumber::countryName::CF': 'République centrafricaine',
            'phonenumber::countryName::BF': 'Burkina Faso',
            'phonenumber::countryName::ER': 'Érythrée',
            'phonenumber::countryName::TZ': 'Tanzanie',
            'phonenumber::countryName::KR': 'Corée du Sud',
            'phonenumber::countryName::JO': 'Jordanie',
            'phonenumber::countryName::MR': 'Mauritanie',
            'phonenumber::countryName::LT': 'Lituanie',
            'phonenumber::countryName::SK': 'Slovaquie',
            'phonenumber::countryName::AO': 'Angola',
            'phonenumber::countryName::KZ': 'Kazakhstan',
            'phonenumber::countryName::MD': 'Moldavie',
            'phonenumber::countryName::ML': 'Mali',
            'phonenumber::countryName::AM': 'Arménie',
            'phonenumber::countryName::WS': 'Samoa',
            'phonenumber::countryName::JE': 'Jersey',
            'phonenumber::countryName::JP': 'Japon',
            'phonenumber::countryName::BO': 'Bolivie',
            'phonenumber::countryName::CL': 'Chili',
            'phonenumber::countryName::US': 'États-Unis',
            'phonenumber::countryName::BM': 'Bermudes',
            'phonenumber::countryName::SC': 'Seychelles',
            'phonenumber::countryName::IO': "Territoire britannique de l'océan Indien",
            'phonenumber::countryName::GT': 'Guatemala',
            'phonenumber::countryName::EC': 'Équateur',
            'phonenumber::countryName::MQ': 'Martinique',
            'phonenumber::countryName::TJ': 'Tadjikistan',
            'phonenumber::countryName::MT': 'Malte',
            'phonenumber::countryName::GM': 'Gambie',
            'phonenumber::countryName::NG': 'Nigéria',
            'phonenumber::countryName::BS': 'Bahamas',
            'phonenumber::countryName::XK': 'Kosovo',
            'phonenumber::countryName::KW': 'Koweït',
            'phonenumber::countryName::MV': 'Maldives',
            'phonenumber::countryName::SS': 'Soudan du Sud',
            'phonenumber::countryName::IR': 'Iran',
            'phonenumber::countryName::AL': 'Albanie',
            'phonenumber::countryName::BR': 'Brésil',
            'phonenumber::countryName::RS': 'Serbie',
            'phonenumber::countryName::BZ': 'Belize',
            'phonenumber::countryName::MM': 'Birmanie',
            'phonenumber::countryName::BT': 'Bhoutan',
            'phonenumber::countryName::VE': 'Venezuela',
            'phonenumber::countryName::LR': 'Liberia',
            'phonenumber::countryName::JM': 'Jamaïque',
            'phonenumber::countryName::PL': 'Pologne',
            'phonenumber::countryName::KY': 'Îles Caïmans',
            'phonenumber::countryName::BN': 'Brunei',
            'phonenumber::countryName::KM': 'Comores',
            'phonenumber::countryName::GU': 'Guam',
            'phonenumber::countryName::TO': 'Tonga',
            'phonenumber::countryName::KI': 'Kiribati',
            'phonenumber::countryName::GH': 'Ghana',
            'phonenumber::countryName::TD': 'Tchad',
            'phonenumber::countryName::ZW': 'Zimbabwe',
            'phonenumber::countryName::MF': 'Saint-Martin',
            'phonenumber::countryName::MN': 'Mongolie',
            'phonenumber::countryName::PT': 'Portugal',
            'phonenumber::countryName::AS': 'Samoa américaines',
            'phonenumber::countryName::CG': 'Congo',
            'phonenumber::countryName::BE': 'Belgique',
            'phonenumber::countryName::IL': 'Israël',
            'phonenumber::countryName::NZ': 'Nouvelle-Zélande',
            'phonenumber::countryName::NI': 'Nicaragua',
            'phonenumber::countryName::AI': 'Anguilla'
        },
        'de-DE': {
            'phonenumber::countryName::GD': 'Grenada',
            'phonenumber::countryName::CH': 'Schweiz',
            'phonenumber::countryName::SL': 'Sierra Leone',
            'phonenumber::countryName::HU': 'Ungarn',
            'phonenumber::countryName::TW': 'Taiwan',
            'phonenumber::countryName::WF': 'Wallis und Futuna',
            'phonenumber::countryName::BB': 'Barbados',
            'phonenumber::countryName::CI': 'Elfenbeinküste',
            'phonenumber::countryName::TN': 'Tunesien',
            'phonenumber::countryName::IT': 'Italien',
            'phonenumber::countryName::BJ': 'Benin',
            'phonenumber::countryName::ID': 'Indonesien',
            'phonenumber::countryName::CV': 'Kap Verde',
            'phonenumber::countryName::LA': 'Laos',
            'phonenumber::countryName::UG': 'Uganda',
            'phonenumber::countryName::AD': 'Andorra',
            'phonenumber::countryName::BI': 'Burundi',
            'phonenumber::countryName::ZA': 'Südafrika',
            'phonenumber::countryName::FR': 'Frankreich',
            'phonenumber::countryName::LY': 'Libyen',
            'phonenumber::countryName::MX': 'Mexiko',
            'phonenumber::countryName::GA': 'Gabun',
            'phonenumber::countryName::MP': 'Nördliche Marianen',
            'phonenumber::countryName::MK': 'Nordmazedonien',
            'phonenumber::countryName::CN': 'China',
            'phonenumber::countryName::YE': 'Jemen',
            'phonenumber::countryName::BL': 'Saint-Barthélemy',
            'phonenumber::countryName::SB': 'Salomonen',
            'phonenumber::countryName::UZ': 'Usbekistan',
            'phonenumber::countryName::EG': 'Ägypten',
            'phonenumber::countryName::SN': 'Senegal',
            'phonenumber::countryName::LK': 'Sri Lanka',
            'phonenumber::countryName::PS': 'Palästina',
            'phonenumber::countryName::BD': 'Bangladesch',
            'phonenumber::countryName::PE': 'Peru',
            'phonenumber::countryName::SG': 'Singapur',
            'phonenumber::countryName::TR': 'Türkei',
            'phonenumber::countryName::AF': 'Afghanistan',
            'phonenumber::countryName::AW': 'Aruba',
            'phonenumber::countryName::CK': 'Cookinseln',
            'phonenumber::countryName::GB': 'Vereinigtes Königreich',
            'phonenumber::countryName::ZM': 'Sambia',
            'phonenumber::countryName::FI': 'Finnland',
            'phonenumber::countryName::NE': 'Niger',
            'phonenumber::countryName::CX': 'Weihnachtsinsel',
            'phonenumber::countryName::TK': 'Tokelau',
            'phonenumber::countryName::GW': 'Guinea-Bissau',
            'phonenumber::countryName::AZ': 'Aserbaidschan',
            'phonenumber::countryName::RE': 'Réunion',
            'phonenumber::countryName::DJ': 'Dschibuti',
            'phonenumber::countryName::KP': 'Nordkorea',
            'phonenumber::countryName::MU': 'Mauritius',
            'phonenumber::countryName::MS': 'Montserrat',
            'phonenumber::countryName::CO': 'Kolumbien',
            'phonenumber::countryName::GR': 'Griechenland',
            'phonenumber::countryName::HR': 'Kroatien',
            'phonenumber::countryName::MA': 'Marokko',
            'phonenumber::countryName::DZ': 'Algerien',
            'phonenumber::countryName::NL': 'Niederlande',
            'phonenumber::countryName::SD': 'Sudan',
            'phonenumber::countryName::FJ': 'Fidschi',
            'phonenumber::countryName::LI': 'Liechtenstein',
            'phonenumber::countryName::NP': 'Nepal',
            'phonenumber::countryName::PR': 'Puerto Rico',
            'phonenumber::countryName::GE': 'Georgien',
            'phonenumber::countryName::PK': 'Pakistan',
            'phonenumber::countryName::MC': 'Monaco',
            'phonenumber::countryName::BW': 'Botswana',
            'phonenumber::countryName::LB': 'Libanon',
            'phonenumber::countryName::PG': 'Papua-Neuguinea',
            'phonenumber::countryName::YT': 'Mayotte',
            'phonenumber::countryName::DO': 'Dominikanische Republik',
            'phonenumber::countryName::QA': 'Katar',
            'phonenumber::countryName::MG': 'Madagaskar',
            'phonenumber::countryName::IN': 'Indien',
            'phonenumber::countryName::SY': 'Syrien',
            'phonenumber::countryName::ME': 'Montenegro',
            'phonenumber::countryName::SZ': 'Swasiland',
            'phonenumber::countryName::PY': 'Paraguay',
            'phonenumber::countryName::SV': 'El Salvador',
            'phonenumber::countryName::UA': 'Ukraine',
            'phonenumber::countryName::IM': 'Insel Man',
            'phonenumber::countryName::NA': 'Namibia',
            'phonenumber::countryName::AE': 'Vereinigte Arabische Emirate',
            'phonenumber::countryName::BG': 'Bulgarien',
            'phonenumber::countryName::GL': 'Grönland',
            'phonenumber::countryName::DE': 'Deutschland',
            'phonenumber::countryName::KH': 'Kambodscha',
            'phonenumber::countryName::IQ': 'Irak',
            'phonenumber::countryName::SE': 'Schweden',
            'phonenumber::countryName::CU': 'Kuba',
            'phonenumber::countryName::KG': 'Kirgisistan',
            'phonenumber::countryName::RU': 'Russland',
            'phonenumber::countryName::MY': 'Malaysia',
            'phonenumber::countryName::ST': 'São Tomé und Príncipe',
            'phonenumber::countryName::CY': 'Zypern',
            'phonenumber::countryName::CA': 'Kanada',
            'phonenumber::countryName::MW': 'Malawi',
            'phonenumber::countryName::SA': 'Saudi-Arabien',
            'phonenumber::countryName::BA': 'Bosnien und Herzegowina',
            'phonenumber::countryName::ET': 'Äthiopien',
            'phonenumber::countryName::ES': 'Spanien',
            'phonenumber::countryName::SI': 'Slowenien',
            'phonenumber::countryName::OM': 'Oman',
            'phonenumber::countryName::PM': 'St. Pierre und Miquelon',
            'phonenumber::countryName::MO': 'Macao',
            'phonenumber::countryName::SM': 'San Marino',
            'phonenumber::countryName::LS': 'Lesotho',
            'phonenumber::countryName::MH': 'Marshallinseln',
            'phonenumber::countryName::IS': 'Island',
            'phonenumber::countryName::LU': 'Luxemburg',
            'phonenumber::countryName::AR': 'Argentinien',
            'phonenumber::countryName::NR': 'Nauru',
            'phonenumber::countryName::CC': 'Kokosinseln',
            'phonenumber::countryName::DM': 'Dominica',
            'phonenumber::countryName::CR': 'Costa Rica',
            'phonenumber::countryName::AU': 'Australien',
            'phonenumber::countryName::TH': 'Thailand',
            'phonenumber::countryName::HT': 'Haiti',
            'phonenumber::countryName::TV': 'Tuvalu',
            'phonenumber::countryName::HN': 'Honduras',
            'phonenumber::countryName::GQ': 'Äquatorialguinea',
            'phonenumber::countryName::PF': 'Französisch-Polynesien',
            'phonenumber::countryName::BY': 'Weißrussland',
            'phonenumber::countryName::LV': 'Lettland',
            'phonenumber::countryName::PW': 'Palau',
            'phonenumber::countryName::GP': 'Guadeloupe',
            'phonenumber::countryName::PH': 'Philippinen',
            'phonenumber::countryName::GI': 'Gibraltar',
            'phonenumber::countryName::DK': 'Dänemark',
            'phonenumber::countryName::CM': 'Kamerun',
            'phonenumber::countryName::GN': 'Guinea',
            'phonenumber::countryName::BH': 'Bahrain',
            'phonenumber::countryName::SR': 'Suriname',
            'phonenumber::countryName::CD': 'Kongo (Dem. Rep.)',
            'phonenumber::countryName::SO': 'Somalia',
            'phonenumber::countryName::CZ': 'Tschechien',
            'phonenumber::countryName::NC': 'Neukaledonien',
            'phonenumber::countryName::VU': 'Vanuatu',
            'phonenumber::countryName::SH': 'St. Helena, Ascension und Tristan da Cunha',
            'phonenumber::countryName::TG': 'Togo',
            'phonenumber::countryName::VG': 'Britische Jungferninseln',
            'phonenumber::countryName::KE': 'Kenia',
            'phonenumber::countryName::NU': 'Niue',
            'phonenumber::countryName::RW': 'Ruanda',
            'phonenumber::countryName::EE': 'Estland',
            'phonenumber::countryName::RO': 'Rumänien',
            'phonenumber::countryName::TT': 'Trinidad und Tobago',
            'phonenumber::countryName::GY': 'Guyana',
            'phonenumber::countryName::TL': 'Osttimor',
            'phonenumber::countryName::VN': 'Vietnam',
            'phonenumber::countryName::UY': 'Uruguay',
            'phonenumber::countryName::VA': 'Vatikanstadt',
            'phonenumber::countryName::HK': 'Hongkong',
            'phonenumber::countryName::AT': 'Österreich',
            'phonenumber::countryName::AG': 'Antigua und Barbuda',
            'phonenumber::countryName::TM': 'Turkmenistan',
            'phonenumber::countryName::MZ': 'Mosambik',
            'phonenumber::countryName::PA': 'Panama',
            'phonenumber::countryName::FM': 'Mikronesien',
            'phonenumber::countryName::IE': 'Irland',
            'phonenumber::countryName::CW': 'Curaçao',
            'phonenumber::countryName::GF': 'Französisch-Guayana',
            'phonenumber::countryName::NO': 'Norwegen',
            'phonenumber::countryName::AX': 'Åland',
            'phonenumber::countryName::CF': 'Zentralafrikanische Republik',
            'phonenumber::countryName::BF': 'Burkina Faso',
            'phonenumber::countryName::ER': 'Eritrea',
            'phonenumber::countryName::TZ': 'Tansania',
            'phonenumber::countryName::KR': 'Südkorea',
            'phonenumber::countryName::JO': 'Jordanien',
            'phonenumber::countryName::MR': 'Mauretanien',
            'phonenumber::countryName::LT': 'Litauen',
            'phonenumber::countryName::SK': 'Slowakei',
            'phonenumber::countryName::AO': 'Angola',
            'phonenumber::countryName::KZ': 'Kasachstan',
            'phonenumber::countryName::MD': 'Moldawien',
            'phonenumber::countryName::ML': 'Mali',
            'phonenumber::countryName::AM': 'Armenien',
            'phonenumber::countryName::WS': 'Samoa',
            'phonenumber::countryName::JE': 'Jersey',
            'phonenumber::countryName::JP': 'Japan',
            'phonenumber::countryName::BO': 'Bolivien',
            'phonenumber::countryName::CL': 'Chile',
            'phonenumber::countryName::US': 'Vereinigte Staaten',
            'phonenumber::countryName::BM': 'Bermuda',
            'phonenumber::countryName::SC': 'Seychellen',
            'phonenumber::countryName::IO': 'Britisches Territorium im Indischen Ozean',
            'phonenumber::countryName::GT': 'Guatemala',
            'phonenumber::countryName::EC': 'Ecuador',
            'phonenumber::countryName::MQ': 'Martinique',
            'phonenumber::countryName::TJ': 'Tadschikistan',
            'phonenumber::countryName::MT': 'Malta',
            'phonenumber::countryName::GM': 'Gambia',
            'phonenumber::countryName::NG': 'Nigeria',
            'phonenumber::countryName::BS': 'Bahamas',
            'phonenumber::countryName::XK': 'Kosovo',
            'phonenumber::countryName::KW': 'Kuwait',
            'phonenumber::countryName::MV': 'Malediven',
            'phonenumber::countryName::SS': 'Südsudan',
            'phonenumber::countryName::IR': 'Iran',
            'phonenumber::countryName::AL': 'Albanien',
            'phonenumber::countryName::BR': 'Brasilien',
            'phonenumber::countryName::RS': 'Serbien',
            'phonenumber::countryName::BZ': 'Belize',
            'phonenumber::countryName::MM': 'Myanmar',
            'phonenumber::countryName::BT': 'Bhutan',
            'phonenumber::countryName::VE': 'Venezuela',
            'phonenumber::countryName::LR': 'Liberia',
            'phonenumber::countryName::JM': 'Jamaika',
            'phonenumber::countryName::PL': 'Polen',
            'phonenumber::countryName::KY': 'Kaimaninseln',
            'phonenumber::countryName::BN': 'Brunei',
            'phonenumber::countryName::KM': 'Komoren',
            'phonenumber::countryName::GU': 'Guam',
            'phonenumber::countryName::TO': 'Tonga',
            'phonenumber::countryName::KI': 'Kiribati',
            'phonenumber::countryName::GH': 'Ghana',
            'phonenumber::countryName::TD': 'Tschad',
            'phonenumber::countryName::ZW': 'Simbabwe',
            'phonenumber::countryName::MF': 'Saint-Martin',
            'phonenumber::countryName::MN': 'Mongolei',
            'phonenumber::countryName::PT': 'Portugal',
            'phonenumber::countryName::AS': 'Amerikanisch-Samoa',
            'phonenumber::countryName::CG': 'Kongo',
            'phonenumber::countryName::BE': 'Belgien',
            'phonenumber::countryName::IL': 'Israel',
            'phonenumber::countryName::NZ': 'Neuseeland',
            'phonenumber::countryName::NI': 'Nicaragua',
            'phonenumber::countryName::AI': 'Anguilla'
        },
        'nl-NL': {
            'phonenumber::countryName::GD': 'Grenada',
            'phonenumber::countryName::CH': 'Zwitserland',
            'phonenumber::countryName::SL': 'Sierra Leone',
            'phonenumber::countryName::HU': 'Hongarije',
            'phonenumber::countryName::TW': 'Taiwan',
            'phonenumber::countryName::WF': 'Wallis en Futuna',
            'phonenumber::countryName::BB': 'Barbados',
            'phonenumber::countryName::CI': 'Ivoorkust',
            'phonenumber::countryName::TN': 'Tunesië',
            'phonenumber::countryName::IT': 'Italië',
            'phonenumber::countryName::BJ': 'Benin',
            'phonenumber::countryName::ID': 'Indonesië',
            'phonenumber::countryName::CV': 'Kaapverdië',
            'phonenumber::countryName::LA': 'Laos',
            'phonenumber::countryName::UG': 'Oeganda',
            'phonenumber::countryName::AD': 'Andorra',
            'phonenumber::countryName::BI': 'Burundi',
            'phonenumber::countryName::ZA': 'Zuid-Afrika',
            'phonenumber::countryName::FR': 'Frankrijk',
            'phonenumber::countryName::LY': 'Libië',
            'phonenumber::countryName::MX': 'Mexico',
            'phonenumber::countryName::GA': 'Gabon',
            'phonenumber::countryName::MP': 'Noordelijke Marianeneilanden',
            'phonenumber::countryName::MK': 'Noord-Macedonië',
            'phonenumber::countryName::CN': 'China',
            'phonenumber::countryName::YE': 'Jemen',
            'phonenumber::countryName::BL': 'Saint Barthélemy',
            'phonenumber::countryName::SB': 'Salomonseilanden',
            'phonenumber::countryName::UZ': 'Oezbekistan',
            'phonenumber::countryName::EG': 'Egypte',
            'phonenumber::countryName::SN': 'Senegal',
            'phonenumber::countryName::LK': 'Sri Lanka',
            'phonenumber::countryName::PS': 'Palestijnse gebieden',
            'phonenumber::countryName::BD': 'Bangladesh',
            'phonenumber::countryName::PE': 'Peru',
            'phonenumber::countryName::SG': 'Singapore',
            'phonenumber::countryName::TR': 'Turkije',
            'phonenumber::countryName::AF': 'Afghanistan',
            'phonenumber::countryName::AW': 'Aruba',
            'phonenumber::countryName::CK': 'Cookeilanden',
            'phonenumber::countryName::GB': 'Verenigd Koninkrijk',
            'phonenumber::countryName::ZM': 'Zambia',
            'phonenumber::countryName::FI': 'Finland',
            'phonenumber::countryName::NE': 'Niger',
            'phonenumber::countryName::CX': 'Christmaseiland',
            'phonenumber::countryName::TK': 'Tokelau',
            'phonenumber::countryName::GW': 'Guinee-Bissau',
            'phonenumber::countryName::AZ': 'Azerbeidzjan',
            'phonenumber::countryName::RE': 'Réunion',
            'phonenumber::countryName::DJ': 'Djibouti',
            'phonenumber::countryName::KP': 'Noord-Korea',
            'phonenumber::countryName::MU': 'Mauritius',
            'phonenumber::countryName::MS': 'Montserrat',
            'phonenumber::countryName::CO': 'Colombia',
            'phonenumber::countryName::GR': 'Griekenland',
            'phonenumber::countryName::HR': 'Kroatië',
            'phonenumber::countryName::MA': 'Marokko',
            'phonenumber::countryName::DZ': 'Algerije',
            'phonenumber::countryName::NL': 'Nederland',
            'phonenumber::countryName::SD': 'Soedan',
            'phonenumber::countryName::FJ': 'Fiji',
            'phonenumber::countryName::LI': 'Liechtenstein',
            'phonenumber::countryName::NP': 'Nepal',
            'phonenumber::countryName::PR': 'Puerto Rico',
            'phonenumber::countryName::GE': 'Georgië',
            'phonenumber::countryName::PK': 'Pakistan',
            'phonenumber::countryName::MC': 'Monaco',
            'phonenumber::countryName::BW': 'Botswana',
            'phonenumber::countryName::LB': 'Libanon',
            'phonenumber::countryName::PG': 'Papoea-Nieuw-Guinea',
            'phonenumber::countryName::YT': 'Mayotte',
            'phonenumber::countryName::DO': 'Dominicaanse Republiek',
            'phonenumber::countryName::QA': 'Qatar',
            'phonenumber::countryName::MG': 'Madagaskar',
            'phonenumber::countryName::IN': 'India',
            'phonenumber::countryName::SY': 'Syrië',
            'phonenumber::countryName::ME': 'Montenegro',
            'phonenumber::countryName::SZ': 'Swaziland',
            'phonenumber::countryName::PY': 'Paraguay',
            'phonenumber::countryName::SV': 'El Salvador',
            'phonenumber::countryName::UA': 'Oekraïne',
            'phonenumber::countryName::IM': 'Isle of Man',
            'phonenumber::countryName::NA': 'Namibië',
            'phonenumber::countryName::AE': 'Verenigde Arabische Emiraten',
            'phonenumber::countryName::BG': 'Bulgarije',
            'phonenumber::countryName::GL': 'Groenland',
            'phonenumber::countryName::DE': 'Duitsland',
            'phonenumber::countryName::KH': 'Cambodja',
            'phonenumber::countryName::IQ': 'Irak',
            'phonenumber::countryName::SE': 'Zweden',
            'phonenumber::countryName::CU': 'Cuba',
            'phonenumber::countryName::KG': 'Kirgizië',
            'phonenumber::countryName::RU': 'Rusland',
            'phonenumber::countryName::MY': 'Maleisië',
            'phonenumber::countryName::ST': 'Sao Tomé en Principe',
            'phonenumber::countryName::CY': 'Cyprus',
            'phonenumber::countryName::CA': 'Canada',
            'phonenumber::countryName::MW': 'Malawi',
            'phonenumber::countryName::SA': 'Saoedi-Arabië',
            'phonenumber::countryName::BA': 'Bosnië en Herzegovina',
            'phonenumber::countryName::ET': 'Ethiopië',
            'phonenumber::countryName::ES': 'Spanje',
            'phonenumber::countryName::SI': 'Slovenië',
            'phonenumber::countryName::OM': 'Oman',
            'phonenumber::countryName::PM': 'Saint Pierre en Miquelon',
            'phonenumber::countryName::MO': 'Macao',
            'phonenumber::countryName::SM': 'San Marino',
            'phonenumber::countryName::LS': 'Lesotho',
            'phonenumber::countryName::MH': 'Marshalleilanden',
            'phonenumber::countryName::IS': 'IJsland',
            'phonenumber::countryName::LU': 'Luxemburg',
            'phonenumber::countryName::AR': 'Argentinië',
            'phonenumber::countryName::NR': 'Nauru',
            'phonenumber::countryName::CC': 'Cocoseilanden',
            'phonenumber::countryName::DM': 'Dominica',
            'phonenumber::countryName::CR': 'Costa Rica',
            'phonenumber::countryName::AU': 'Australië',
            'phonenumber::countryName::TH': 'Thailand',
            'phonenumber::countryName::HT': 'Haïti',
            'phonenumber::countryName::TV': 'Tuvalu',
            'phonenumber::countryName::HN': 'Honduras',
            'phonenumber::countryName::GQ': 'Equatoriaal-Guinea',
            'phonenumber::countryName::PF': 'Frans-Polynesië',
            'phonenumber::countryName::BY': 'Wit-Rusland',
            'phonenumber::countryName::LV': 'Letland',
            'phonenumber::countryName::PW': 'Palau',
            'phonenumber::countryName::GP': 'Guadeloupe',
            'phonenumber::countryName::PH': 'Filipijnen',
            'phonenumber::countryName::GI': 'Gibraltar',
            'phonenumber::countryName::DK': 'Denemarken',
            'phonenumber::countryName::CM': 'Kameroen',
            'phonenumber::countryName::GN': 'Guinee',
            'phonenumber::countryName::BH': 'Bahrein',
            'phonenumber::countryName::SR': 'Suriname',
            'phonenumber::countryName::CD': 'Congo (DRC)',
            'phonenumber::countryName::SO': 'Somalië',
            'phonenumber::countryName::CZ': 'Tsjechië',
            'phonenumber::countryName::NC': 'Nieuw-Caledonië',
            'phonenumber::countryName::VU': 'Vanuatu',
            'phonenumber::countryName::SH': 'Sint-Helena, Ascension en Tristan da Cunha',
            'phonenumber::countryName::TG': 'Togo',
            'phonenumber::countryName::VG': 'Britse Maagdeneilanden',
            'phonenumber::countryName::KE': 'Kenia',
            'phonenumber::countryName::NU': 'Niue',
            'phonenumber::countryName::RW': 'Rwanda',
            'phonenumber::countryName::EE': 'Estland',
            'phonenumber::countryName::RO': 'Roemenië',
            'phonenumber::countryName::TT': 'Trinidad en Tobago',
            'phonenumber::countryName::GY': 'Guyana',
            'phonenumber::countryName::TL': 'Oost-Timor',
            'phonenumber::countryName::VN': 'Vietnam',
            'phonenumber::countryName::UY': 'Uruguay',
            'phonenumber::countryName::VA': 'Vaticaanstad',
            'phonenumber::countryName::HK': 'Hongkong',
            'phonenumber::countryName::AT': 'Oostenrijk',
            'phonenumber::countryName::AG': 'Antigua en Barbuda',
            'phonenumber::countryName::TM': 'Turkmenistan',
            'phonenumber::countryName::MZ': 'Mozambique',
            'phonenumber::countryName::PA': 'Panama',
            'phonenumber::countryName::FM': 'Micronesië',
            'phonenumber::countryName::IE': 'Ierland',
            'phonenumber::countryName::CW': 'Curaçao',
            'phonenumber::countryName::GF': 'Frans-Guyana',
            'phonenumber::countryName::NO': 'Noorwegen',
            'phonenumber::countryName::AX': 'Ålandeilanden',
            'phonenumber::countryName::CF': 'Centraal-Afrikaanse Republiek',
            'phonenumber::countryName::BF': 'Burkina Faso',
            'phonenumber::countryName::ER': 'Eritrea',
            'phonenumber::countryName::TZ': 'Tanzania',
            'phonenumber::countryName::KR': 'Zuid-Korea',
            'phonenumber::countryName::JO': 'Jordanië',
            'phonenumber::countryName::MR': 'Mauritanië',
            'phonenumber::countryName::LT': 'Litouwen',
            'phonenumber::countryName::SK': 'Slowakije',
            'phonenumber::countryName::AO': 'Angola',
            'phonenumber::countryName::KZ': 'Kazachstan',
            'phonenumber::countryName::MD': 'Moldavië',
            'phonenumber::countryName::ML': 'Mali',
            'phonenumber::countryName::AM': 'Armenië',
            'phonenumber::countryName::WS': 'Samoa',
            'phonenumber::countryName::JE': 'Jersey',
            'phonenumber::countryName::JP': 'Japan',
            'phonenumber::countryName::BO': 'Bolivia',
            'phonenumber::countryName::CL': 'Chili',
            'phonenumber::countryName::US': 'Verenigde Staten',
            'phonenumber::countryName::BM': 'Bermuda',
            'phonenumber::countryName::SC': 'Seychellen',
            'phonenumber::countryName::IO': 'Britse Gebieden in de Indische Oceaan',
            'phonenumber::countryName::GT': 'Guatemala',
            'phonenumber::countryName::EC': 'Ecuador',
            'phonenumber::countryName::MQ': 'Martinique',
            'phonenumber::countryName::TJ': 'Tadzjikistan',
            'phonenumber::countryName::MT': 'Malta',
            'phonenumber::countryName::GM': 'Gambia',
            'phonenumber::countryName::NG': 'Nigeria',
            'phonenumber::countryName::BS': 'Bahama’s',
            'phonenumber::countryName::XK': 'Kosovo',
            'phonenumber::countryName::KW': 'Koeweit',
            'phonenumber::countryName::MV': 'Maldiven',
            'phonenumber::countryName::SS': 'Zuid-Soedan',
            'phonenumber::countryName::IR': 'Iran',
            'phonenumber::countryName::AL': 'Albanië',
            'phonenumber::countryName::BR': 'Brazilië',
            'phonenumber::countryName::RS': 'Servië',
            'phonenumber::countryName::BZ': 'Belize',
            'phonenumber::countryName::MM': 'Myanmar',
            'phonenumber::countryName::BT': 'Bhutan',
            'phonenumber::countryName::VE': 'Venezuela',
            'phonenumber::countryName::LR': 'Liberia',
            'phonenumber::countryName::JM': 'Jamaica',
            'phonenumber::countryName::PL': 'Polen',
            'phonenumber::countryName::KY': 'Caymaneilanden',
            'phonenumber::countryName::BN': 'Brunei',
            'phonenumber::countryName::KM': 'Comoren',
            'phonenumber::countryName::GU': 'Guam',
            'phonenumber::countryName::TO': 'Tonga',
            'phonenumber::countryName::KI': 'Kiribati',
            'phonenumber::countryName::GH': 'Ghana',
            'phonenumber::countryName::TD': 'Tsjaad',
            'phonenumber::countryName::ZW': 'Zimbabwe',
            'phonenumber::countryName::MF': 'Saint-Martin',
            'phonenumber::countryName::MN': 'Mongolië',
            'phonenumber::countryName::PT': 'Portugal',
            'phonenumber::countryName::AS': 'Amerikaans Samoa',
            'phonenumber::countryName::CG': 'Congo',
            'phonenumber::countryName::BE': 'België',
            'phonenumber::countryName::IL': 'Israël',
            'phonenumber::countryName::NZ': 'Nieuw-Zeeland',
            'phonenumber::countryName::NI': 'Nicaragua',
            'phonenumber::countryName::AI': 'Anguilla'
        }
    });

const useFormat = ({ countries, selectedItem })=>{
    return useCallback((inputValue)=>{
        const parseOptions = selectedItem?.value ? {
            defaultCountry: selectedItem?.value?.iso
        } : {};
        const asYouType = new AsYouType(parseOptions, countries.metadata);
        const displayValue = asYouType.input(inputValue);
        const e164 = asYouType.getNumberValue() ?? inputValue;
        const countryIso = asYouType.getCountry();
        return {
            displayValue,
            e164,
            countryIso
        };
    }, [
        countries.metadata,
        selectedItem?.value
    ]);
};

const PhoneNumberContext = /*#__PURE__*/ createContext(null);
const PhoneNumberProvider = (props)=>{
    const { countries, defaultCountry, value: valueProp, defaultValue, onChange, isMobile, children, emptyState, prioritizedCountries } = props;
    const t = usePhoneNumberTranslation();
    const locale = useLocale();
    const { contains } = useFilter({
        sensitivity: 'base'
    });
    const [country, setCountry] = useState(()=>{
        const asYouType = new AsYouType({}, countries.metadata);
        if (valueProp ?? defaultValue) asYouType.input(valueProp ?? defaultValue ?? '');
        const countryIso = asYouType.getCountry();
        return countries.list.find((option)=>option.iso === (countryIso ?? defaultCountry));
    });
    const [value, setValue] = useControlledState(valueProp, defaultValue || '', onChange);
    const getCountryNameKey = useCallback((iso)=>t('phonenumber::countryName::'.concat(iso)), [
        t
    ]);
    const sortedCountriesList = useMemo(()=>[
            ...countries.list
        ].sort((a, b)=>{
            const aPriorityIndex = prioritizedCountries?.indexOf(a.iso) ?? -1;
            const bPriorityIndex = prioritizedCountries?.indexOf(b.iso) ?? -1;
            // Both are prioritized - sort by their order in prioritizedCountries
            if (aPriorityIndex !== -1 && bPriorityIndex !== -1) {
                return aPriorityIndex - bPriorityIndex;
            }
            // Only a is prioritized - a comes first
            if (aPriorityIndex !== -1) {
                return -1;
            }
            // Only b is prioritized - b comes first
            if (bPriorityIndex !== -1) {
                return 1;
            }
            // Neither is prioritized - sort alphabetically by name
            return getCountryNameKey(a.iso).localeCompare(getCountryNameKey(b.iso), locale);
        }), [
        countries.list,
        getCountryNameKey,
        locale,
        prioritizedCountries
    ]);
    const state = useComboBoxState({
        defaultItems: sortedCountriesList,
        selectedKey: country?.id ?? null,
        shouldCloseOnBlur: !isMobile,
        allowsEmptyCollection: Boolean(isMobile || emptyState),
        menuTrigger: isMobile ? 'input' : 'focus',
        defaultFilter: contains,
        children: (item)=>/*#__PURE__*/ jsx(Item, {
                children: `${getCountryNameKey(item.iso)} ${item.code}`
            }),
        onSelectionChange: (key)=>{
            const selectedItem = countries.list.find((option)=>option.id === key);
            setCountry(selectedItem);
            setValue('');
        }
    });
    const format = useFormat({
        countries,
        selectedItem: state.selectedItem
    });
    const formattedValue = format(value || '');
    const updateCountry = useCallback((countryIso)=>{
        if (countryIso) {
            const selectedItem = countries.list.find((option)=>option.iso === countryIso);
            setCountry(selectedItem);
        }
    }, [
        countries.list
    ]);
    const context = useMemo(()=>({
            ...state,
            value,
            setValue,
            displayValue: formattedValue.displayValue,
            countries,
            isMobile,
            updateCountry,
            getCountryNameKey,
            shouldCountryFieldExpand: !isMobile && state.isOpen,
            prioritizedCountries,
            format
        }), [
        state,
        value,
        setValue,
        formattedValue.displayValue,
        countries,
        isMobile,
        updateCountry,
        getCountryNameKey,
        prioritizedCountries,
        format
    ]);
    return /*#__PURE__*/ jsx(PhoneNumberContext.Provider, {
        value: context,
        children: children
    });
};
const usePhoneNumberContext = ()=>{
    const contextValue = useContext(PhoneNumberContext);
    if (!contextValue) {
        throw new Error('The `PhoneNumberProvider` has not been instantiated correctly with its corresponding context. Please make sure that it is rendered higher in your app. If the issue persists, report it to Team GEMINI.');
    }
    return contextValue;
};

const useInput = (refs, props)=>{
    const { inputRef } = refs;
    const { onFocus, onBlur, error, isDisabled, isOptional, placeholder, ...fieldRenderProps } = props;
    const context = usePhoneNumberContext();
    const handleOnChange = useCallback((inputValue)=>{
        const inputFormattedValue = context.format(inputValue);
        context.updateCountry(inputFormattedValue.countryIso);
        context.setValue(inputFormattedValue.e164);
    }, [
        context
    ]);
    const { inputProps, isInvalid } = useTextField({
        ...onFocus ? {
            onFocus
        } : {},
        ...onBlur ? {
            onBlur
        } : {},
        autoComplete: 'tel',
        value: context.displayValue,
        isDisabled: Boolean(isDisabled),
        isRequired: !isOptional,
        isInvalid: Boolean(error),
        inputMode: 'text',
        onChange: handleOnChange,
        placeholder: placeholder,
        id: fieldRenderProps['id'],
        'aria-label': fieldRenderProps['aria-label'],
        'aria-labelledby': fieldRenderProps['aria-labelledby'],
        'aria-describedby': fieldRenderProps['aria-describedby']
    }, inputRef);
    return {
        boxProps: {
            input: inputProps,
            hiddenInput: {
                value: context.value
            }
        },
        isInvalid
    };
};

// todo: https://avivgroup.atlassian.net/browse/GEMINI-2244
const Input = ({ hiddenInputRef, onFocus, onBlur, error, isOptional, isDisabled, name, placeholder, ...fieldRenderProps })=>{
    const inputRef = useObjectRef(null);
    const context = usePhoneNumberContext();
    const { boxProps: { input, hiddenInput }, isInvalid } = useInput({
        inputRef
    }, {
        onFocus,
        onBlur,
        error,
        isDisabled,
        isOptional,
        placeholder,
        ...fieldRenderProps
    });
    const inputBase = /*#__PURE__*/ jsx(TextFieldBase, {
        wrapperProps: {
            width: '100%',
            minWidth: 'initial'
        },
        inputRef: inputRef,
        inputProps: input,
        isDisabled: isDisabled,
        isInvalid: isInvalid
    });
    return /*#__PURE__*/ jsxs(Fragment, {
        children: [
            context.shouldCountryFieldExpand ? /*#__PURE__*/ jsx(VisuallyHidden, {
                children: inputBase
            }) : inputBase,
            /*#__PURE__*/ jsx(Box, {
                as: "input",
                type: "hidden",
                ref: hiddenInputRef,
                name: name,
                value: hiddenInput.value
            })
        ]
    });
};

const useCountryCode = (refs, props)=>{
    const { input: inputRef, list: listRef, popover: popoverRef } = refs;
    const { isDisabled, isOptional, error, state, id, onFocus, onBlur, ...fieldRenderProps } = props;
    const { inputProps, listBoxProps: { linkBehavior, ...listBoxProps }, labelProps, descriptionProps, errorMessageProps } = useComboBox({
        ...onFocus ? {
            onFocus
        } : {},
        ...onBlur ? {
            onBlur
        } : {},
        isDisabled: Boolean(isDisabled),
        isRequired: !isOptional,
        popoverRef,
        listBoxRef: listRef,
        inputRef,
        isInvalid: Boolean(error),
        'aria-label': fieldRenderProps['aria-label'],
        'aria-labelledby': fieldRenderProps['aria-labelledby'],
        'aria-describedby': fieldRenderProps['aria-describedby']
    }, state);
    return {
        boxProps: {
            input: inputProps,
            list: listBoxProps,
            label: labelProps,
            error: errorMessageProps,
            helper: descriptionProps
        }
    };
};

const HORIZONTAL_PADDING$1 = 'unsafe_component.textField.spacing.padding.horizontal';
const CARET_SIZE$1 = 'sizing.16';
const FLAG_SIZE$1 = 16;
/**
 * Computes country code input width dynamically taking into account
 * horizontal padding, flag width, gap and caret icon.
 */ const useCountryCodeInputWidth = ()=>{
    const tokens = useTokens();
    const rootFontSizeValue = getRootFontSizeValue();
    return useMemo(()=>2 * parseFloat(tokens[HORIZONTAL_PADDING$1].toString()) * rootFontSizeValue + FLAG_SIZE$1 + parseFloat(tokens['spacing.8'].toString()) * rootFontSizeValue + parseFloat(tokens[CARET_SIZE$1].toString()) * rootFontSizeValue, [
        rootFontSizeValue,
        tokens
    ]);
};

const FLAGS_DATA_URI = 'data:image/webp;base64,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';

/**
 * Image width used per flag for the sprite image
 */ const IMAGE_WIDTH = 96;
/**
 * Sprite image dimensions in pixels, this is used to calculate the background size for the flag different sizes
 */ const SPRITE_IMAGE_WIDTH = 1284;
const SPRITE_IMAGE_HEIGHT = 1668;
const CountryFlag = ({ flagPos, size = 24, ...rest })=>{
    const bgRatio = IMAGE_WIDTH / size;
    return /*#__PURE__*/ jsx(Box, {
        ...rest,
        flexShrink: 0,
        flexGrow: 0,
        "aria-hidden": "true",
        backgroundImage: `url(${FLAGS_DATA_URI})`,
        backgroundRepeat: "no-repeat",
        /**
			 * Put dynamic values in `unsafe_style` to avoid creation of a lot of classes with emotion with the same base64 image
			 */ unsafe_style: {
            width: size,
            height: size * 3 / 4,
            backgroundSize: `${SPRITE_IMAGE_WIDTH / bgRatio}px ${SPRITE_IMAGE_HEIGHT / bgRatio}px`,
            backgroundPosition: `${flagPos.left / bgRatio}px ${flagPos.top / bgRatio}px`
        }
    });
};

const useCountryCodeInput = (ref, { isDisabled })=>{
    const { boxProps: hoverProps, isHovered } = useHoverable({
        isDisabled: Boolean(isDisabled)
    });
    const { boxProps: focusProps, isFocused } = useFocusable(ref, {
        unsafe_ignoreProviderProps: true
    });
    return {
        boxProps: {
            wrapper: {
                ...hoverProps,
                onClick: (event)=>{
                    if (ref.current && event.target !== ref.current) {
                        ref.current?.focus();
                    }
                }
            },
            input: focusProps
        },
        isFocused,
        isHovered
    };
};

const HORIZONTAL_PADDING = 'unsafe_component.textField.spacing.padding.horizontal';
const CARET_SIZE = 'sizing.16';
const FLAG_SIZE = 16;
const CountryCodeInput = ({ inputRef, context, isDisabled, error, ...props })=>{
    const tokens = useTokens();
    const { boxProps: { input, wrapper }, isFocused, isHovered } = useCountryCodeInput(inputRef, {
        isDisabled
    });
    const inputWidth = useCountryCodeInputWidth();
    const borderWidth = isFocused ? tokens['unsafe_component.textField.borderWidth.active'] : tokens['unsafe_component.textField.borderWidth.default'];
    const borderColor = useMemo(()=>{
        const hasError = Boolean(error);
        switch(true){
            case isDisabled:
                return `unsafe_component.textField.color.borderDisabled`;
            case hasError && isHovered && !isFocused:
                return `unsafe_component.textField.color.borderErrorHover`;
            case hasError:
                return `unsafe_component.textField.color.borderError`;
            case isFocused:
                return `unsafe_component.textField.color.borderFocus`;
            case isHovered:
                return `unsafe_component.textField.color.borderHover`;
            default:
                return `unsafe_component.textField.color.border`;
        }
    }, [
        error,
        isDisabled,
        isFocused,
        isHovered
    ]);
    return /*#__PURE__*/ jsxs(Box, {
        ...wrapper,
        display: "flex",
        alignItems: "center",
        width: // Should always be at 100% in mobile inside modal
        context.shouldCountryFieldExpand || context.isMobile ? '100%' : inputWidth,
        minWidth: // Should always be at 100% in mobile inside modal
        context.shouldCountryFieldExpand || context.isMobile ? '100%' : inputWidth,
        borderWidth: isFocused ? 'unsafe_component.textField.borderWidth.active' : 'unsafe_component.textField.borderWidth.default',
        borderStyle: "solid",
        borderColor: borderColor,
        borderRadius: "unsafe_component.textField.radius",
        backgroundColor: isDisabled ? `unsafe_component.textField.color.bgDisabled` : `unsafe_component.textField.color.bg`,
        paddingLeft: `calc(${tokens[HORIZONTAL_PADDING]} - ${borderWidth})`,
        paddingRight: `calc(${tokens[HORIZONTAL_PADDING]} - ${borderWidth})`,
        height: "unsafe_component.textField.sizing.height",
        color: isDisabled ? `unsafe_component.textField.color.textDisabled` : `unsafe_component.textField.color.text`,
        typography: "unsafe_component.textField.typography",
        cursor: isDisabled ? 'not-allowed' : 'text',
        children: [
            context.selectedItem?.value ? /*#__PURE__*/ jsx(CountryFlag, {
                flagPos: context.selectedItem.value.flagPos,
                size: FLAG_SIZE,
                marginRight: "spacing.8"
            }) : null,
            /*#__PURE__*/ jsx(Box, {
                ...mergeProps(input, props),
                ref: inputRef,
                srOnly: !context.shouldCountryFieldExpand && Boolean(context.selectedItem?.value),
                autoComplete: "tel-extension",
                as: "input",
                borderWidth: "borderWidth.none",
                padding: "spacing.none",
                outlineStyle: "none",
                placeholderColor: "unsafe_component.textField.color.placeholder",
                paddingVertical: `calc(${tokens['unsafe_component.textField.spacing.padding.vertical']} - ${borderWidth})`,
                flexGrow: 1,
                minWidth: 0,
                size: 1,
                width: "100%"
            }),
            context.isOpen ? /*#__PURE__*/ jsx(CaretUpFilledIcon, {
                size: CARET_SIZE,
                marginVertical: `calc(${tokens['unsafe_component.textField.spacing.padding.vertical']} - ${borderWidth})`,
                color: "unsafe_component.textField.color.icon"
            }) : /*#__PURE__*/ jsx(CaretDownFilledIcon, {
                size: CARET_SIZE,
                marginVertical: `calc(${tokens['unsafe_component.textField.spacing.padding.vertical']} - ${borderWidth})`,
                color: "unsafe_component.textField.color.icon"
            })
        ]
    });
};

const useCountryCodeItem = (ref, { id, context })=>{
    const { optionProps, labelProps, isSelected, isPressed, isFocused, isDisabled } = useOption({
        key: id
    }, context, ref);
    const { boxProps: hoverProps, isHovered } = useHoverable({
        isDisabled
    });
    return {
        boxProps: {
            option: mergeProps(hoverProps, optionProps, {
                ref
            }),
            label: labelProps
        },
        isSelected,
        isPressed,
        isFocused,
        isHovered
    };
};

const CountryCodeItem = ({ id, flagPos, code, countryName, index, arrayLength, variant, context })=>{
    const ref = useRef(null);
    const { boxProps, isSelected, isPressed, isFocused, isHovered } = useCountryCodeItem(ref, {
        id,
        context
    });
    const backgroundColor = useMemo(()=>mapStateToBackgroundColor({
            isSelected,
            isFocused,
            isPressed,
            isHovered
        }), [
        isSelected,
        isFocused,
        isPressed,
        isHovered
    ]);
    return /*#__PURE__*/ jsx(Box, {
        ...boxProps.option,
        as: "li",
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        padding: "unsafe_component.selectableList.option.spacing.padding",
        marginTop: index === 0 && variant === 'borderless' ? 'unsafe_component.selectableList.spacing.padding.vertical' : undefined,
        marginBottom: index === arrayLength - 1 && variant === 'borderless' ? 'unsafe_component.selectableList.spacing.padding.vertical' : undefined,
        borderRadius: context.isMobile ? 'unsafe_component.selectableList.option.radius.borderless' : 'unsafe_component.selectableList.option.radius.contained',
        color: `unsafe_component.selectableList.option.color.content${isSelected ? 'Selected' : ''}`,
        backgroundColor: backgroundColor,
        cursor: isFocused || isHovered ? 'pointer' : undefined,
        outlineWidth: "borderWidth.none",
        typography: "unsafe_component.selectableList.option.typography.default",
        children: /*#__PURE__*/ jsxs(Box, {
            ...boxProps.label,
            wordBreak: "break-word",
            display: "flex",
            alignItems: "center",
            width: "100%",
            children: [
                /*#__PURE__*/ jsx(CountryFlag, {
                    flagPos: flagPos,
                    marginRight: "spacing.8"
                }),
                `${countryName} (${code})`
            ]
        })
    });
};
const mapStateToBackgroundColor = ({ isSelected, isFocused, isPressed, isHovered })=>{
    if (!isSelected) {
        if (isPressed) return 'unsafe_component.selectableList.option.color.bgPressed';
        if (isFocused || isHovered) return 'unsafe_component.selectableList.option.color.bgFocused';
        return 'unsafe_component.selectableList.option.color.bg';
    }
    if (isPressed) return 'unsafe_component.selectableList.option.color.bgSelectedPressed';
    if (isFocused || isHovered) return 'unsafe_component.selectableList.option.color.bgSelectedFocused';
    return 'unsafe_component.selectableList.option.color.bgSelected';
};

const useCountryCodeList = (ref, props)=>{
    const { list: listRef } = ref;
    const { state, 'aria-label': ariaLabel, autoFocus, shouldUseVirtualFocus, shouldSelectOnPressUp, shouldFocusOnHover } = props;
    const listRefObject = useObjectRef(listRef);
    const [minHeight, setMinHeight] = useState(0);
    const ariaListBoxProps = useListBox({
        'aria-label': ariaLabel,
        ...autoFocus ? {
            autoFocus
        } : {},
        shouldUseVirtualFocus: Boolean(shouldUseVirtualFocus),
        shouldSelectOnPressUp: Boolean(shouldSelectOnPressUp),
        shouldFocusOnHover: Boolean(shouldFocusOnHover)
    }, state, listRefObject);
    useEffect(()=>{
        if (listRefObject.current) {
            let height = 0;
            const itemsLength = Math.min(listRefObject.current.childElementCount, 4);
            for(let i = 0; i < itemsLength; i++){
                height += listRefObject.current.children[i]?.clientHeight || 0;
            }
            setMinHeight(height);
        }
    }, [
        listRefObject,
        state.collection.size
    ]);
    return {
        boxProps: {
            list: {
                ...ariaListBoxProps.listBoxProps,
                ref: listRefObject
            },
            container: {
                minHeight
            }
        }
    };
};

const CountryCodeList = ({ containerRef, listRef, context, variant = 'contained', listWidth, additionalContainerProps, shouldFocusOnHover, shouldSelectOnPressUp, shouldUseVirtualFocus, autoFocus, emptyState, ...listProps })=>{
    const { boxProps: { container, list } } = useCountryCodeList({
        list: listRef
    }, {
        state: context,
        'aria-label': listProps['aria-label'],
        shouldFocusOnHover,
        shouldSelectOnPressUp,
        shouldUseVirtualFocus,
        autoFocus
    });
    const showEmptyState = context.collection.size <= 0 && emptyState;
    const collection = Array.from(context.collection);
    // Find the last prioritized country index when context.collection updates
    const lastPrioritizedIndex = collection.findLastIndex((item)=>item?.value && context.prioritizedCountries?.includes(item.value.iso));
    const listComponent = /*#__PURE__*/ jsx(Box, {
        // Margin should be before because listProps might contain some margins
        margin: "spacing.none",
        ...mergeProps(listProps, list),
        as: showEmptyState ? 'div' : 'ul',
        display: "flex",
        flexDirection: "column",
        alignItems: showEmptyState && variant === 'borderless' ? 'center' : undefined,
        width: "100%",
        height: "100%",
        paddingHorizontal: "spacing.none",
        outlineStyle: "none",
        overflow: variant === 'borderless' ? 'hidden auto' : undefined,
        children: context.collection.size <= 0 ? variant === 'borderless' ? /*#__PURE__*/ jsx(Box, {
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            role: "option",
            children: /*#__PURE__*/ jsx(InfoState, {
                title: emptyState.title,
                description: emptyState.description
            })
        }) : /*#__PURE__*/ jsxs(Box, {
            display: "flex",
            flexDirection: "column",
            role: "option",
            padding: context.collection.size <= 0 ? 'unsafe_component.selectableList.spacing.emptyList.padding' : undefined,
            children: [
                /*#__PURE__*/ jsx(Box, {
                    typography: "unsafe_component.selectableList.typography.emptyListTitle",
                    marginBottom: "unsafe_component.selectableList.spacing.emptyList.gap",
                    children: emptyState.title
                }),
                /*#__PURE__*/ jsx(Box, {
                    typography: "unsafe_component.selectableList.typography.emptyListDescription",
                    children: emptyState.description
                })
            ]
        }) : collection.map(({ value }, index)=>{
            if (!value) return null;
            const showDivider = context.prioritizedCountries && context.prioritizedCountries.length > 0 && index === lastPrioritizedIndex && collection.length > context.prioritizedCountries.length;
            return /*#__PURE__*/ jsxs(Fragment, {
                children: [
                    /*#__PURE__*/ jsx(CountryCodeItem, {
                        id: value.id,
                        flagPos: value.flagPos,
                        code: value.code,
                        countryName: context.getCountryNameKey(value.iso),
                        context: context,
                        variant: variant,
                        index: index,
                        arrayLength: collection.length
                    }),
                    showDivider ? /*#__PURE__*/ jsx(Divider, {}) : null
                ]
            }, value.iso);
        })
    });
    if (variant === 'borderless') return listComponent;
    return /*#__PURE__*/ jsxs(Box, {
        ...mergeProps(additionalContainerProps, container),
        ref: containerRef,
        display: "flex",
        width: listWidth ?? '100%',
        paddingVertical: context.collection.size > 0 ? 'unsafe_component.selectableList.spacing.padding.vertical' : undefined,
        borderRadius: "unsafe_component.selectableList.radius",
        backgroundColor: "unsafe_component.selectableList.color.bg",
        boxShadow: `unsafe_component.selectableList.shadow.${variant}`,
        overflow: [
            'hidden',
            'clip'
        ],
        children: [
            /*#__PURE__*/ jsx(Box, {
                display: "flex",
                width: "100%",
                overflowX: [
                    'hidden',
                    'clip'
                ],
                overflowY: "auto",
                children: listComponent
            }),
            /*#__PURE__*/ jsx(DismissButton, {
                onDismiss: context.close
            })
        ]
    });
};

const useList = (ref, props)=>{
    const { trigger: triggerRef, popover: popoverRef } = ref;
    const { state } = props;
    const tokens = useTokens();
    const rootFontSizeValue = getRootFontSizeValue();
    const { boxProps: popoverBoxProps } = usePopover({
        popover: popoverRef,
        trigger: triggerRef
    }, {
        isOpen: state.isOpen,
        close: state.close,
        shouldFlip: true,
        placement: 'bottom',
        unsafe_isNonModal: true,
        offset: parseFloat(tokens['unsafe_component.textField.borderWidth.active'].toString()) * rootFontSizeValue
    });
    return {
        boxProps: {
            popover: popoverBoxProps.popover
        }
    };
};

const List = ({ containerRef, popoverRef, listRef, context, listWidth, inputRef, emptyState, ...listProps })=>{
    const { boxProps: { popover } } = useList({
        popover: popoverRef,
        trigger: containerRef
    }, {
        state: context
    });
    return /*#__PURE__*/ jsx(CountryCodeList, {
        ...listProps,
        context: context,
        additionalContainerProps: popover,
        containerRef: popoverRef,
        listRef: listRef,
        listWidth: listWidth,
        emptyState: emptyState,
        // change default hover behavior of react-aria list to only apply style on hover
        shouldFocusOnHover: false
    });
};

const DesktopCountryCode = ({ containerRef, listWidth, error, isOptional, isDisabled, label, emptyState, onFocus, onBlur, ...fieldRenderProps })=>{
    const inputRef = useRef(null);
    const popoverRef = useRef(null);
    const listRef = useRef(null);
    const context = usePhoneNumberContext();
    const { boxProps: { input, list } } = useCountryCode({
        list: listRef,
        popover: popoverRef,
        input: inputRef
    }, {
        isDisabled,
        isOptional,
        error,
        state: context,
        onFocus,
        onBlur,
        ...fieldRenderProps
    });
    return /*#__PURE__*/ jsxs(FocusableProvider, {
        position: "relative",
        children: [
            /*#__PURE__*/ jsx(CountryCodeInput, {
                ...input,
                inputRef: inputRef,
                context: context,
                isDisabled: isDisabled,
                error: error
            }),
            /*#__PURE__*/ jsx(Overlay, {
                isOpen: context.isOpen,
                children: /*#__PURE__*/ jsx(List, {
                    ...list,
                    containerRef: containerRef,
                    popoverRef: popoverRef,
                    listRef: listRef,
                    inputRef: inputRef,
                    context: context,
                    emptyState: emptyState,
                    listWidth: listWidth
                })
            })
        ]
    });
};

const useButton = (ref, { isDisabled, onPress })=>{
    const { boxProps: focusableProps, isFocused } = useFocusable(ref, {
        isDisabled
    });
    const { boxProps: hoverProps, isHovered } = useHoverable({
        isDisabled: Boolean(isDisabled)
    });
    const { boxProps: pressProps } = usePressable({
        onPress,
        isDisabled
    });
    return {
        button: mergeProps(focusableProps, pressProps, hoverProps),
        isHovered,
        isFocused
    };
};

const Button = /*#__PURE__*/ forwardRef((props, ref)=>{
    const { inputRef, isDisabled, error, 'aria-labelledby': ariaLabelledBy, id, context, ...restProps } = props;
    const buttonRef = useObjectRef(ref);
    const valueId = useId();
    const tokens = useTokens();
    const { button: buttonProps, isFocused, isHovered } = useButton(buttonRef, {
        isDisabled,
        onPress: ()=>context.open(null, 'manual')
    });
    const inputWidth = useCountryCodeInputWidth();
    const borderWidth = isFocused ? tokens['unsafe_component.textField.borderWidth.active'] : tokens['unsafe_component.textField.borderWidth.default'];
    const borderColor = useMemo(()=>{
        const hasError = Boolean(error);
        switch(true){
            case isDisabled:
                return `unsafe_component.textField.color.borderDisabled`;
            case hasError && isHovered && !isFocused:
                return `unsafe_component.textField.color.borderErrorHover`;
            case hasError:
                return `unsafe_component.textField.color.borderError`;
            case isFocused:
                return `unsafe_component.textField.color.borderFocus`;
            case isHovered:
                return `unsafe_component.textField.color.borderHover`;
            default:
                return `unsafe_component.textField.color.border`;
        }
    }, [
        error,
        isDisabled,
        isFocused,
        isHovered
    ]);
    return /*#__PURE__*/ jsx(Box, {
        position: "relative",
        display: "inline-flex",
        width: context.selectedKey ? undefined : inputWidth,
        children: /*#__PURE__*/ jsxs(Box, {
            ...mergeProps(buttonProps, restProps),
            id: id,
            as: "button",
            type: "button",
            disabled: isDisabled,
            "aria-labelledby": [
                ariaLabelledBy,
                valueId
            ].join(' '),
            ref: buttonRef,
            display: "inline-flex",
            alignItems: "center",
            borderWidth: isFocused ? 'unsafe_component.textField.borderWidth.active' : 'unsafe_component.textField.borderWidth.default',
            borderStyle: "solid",
            borderColor: borderColor,
            borderRadius: "unsafe_component.textField.radius",
            backgroundColor: isDisabled ? `unsafe_component.textField.color.bgDisabled` : `unsafe_component.textField.color.bg`,
            paddingVertical: `calc(${tokens['unsafe_component.textField.spacing.padding.vertical']} - ${borderWidth})`,
            paddingLeft: `calc(${tokens['unsafe_component.textField.spacing.padding.horizontal']} - ${borderWidth})`,
            paddingRight: `calc(${tokens['unsafe_component.textField.spacing.padding.horizontal']} - ${borderWidth})`,
            outlineWidth: "borderWidth.none",
            height: "unsafe_component.textField.sizing.height",
            color: isDisabled ? `unsafe_component.textField.color.textDisabled` : `unsafe_component.textField.color.text`,
            typography: "unsafe_component.textField.typography",
            cursor: isDisabled ? 'not-allowed' : 'pointer',
            width: "100%",
            children: [
                context.selectedItem?.value ? /*#__PURE__*/ jsx(CountryFlag, {
                    flagPos: context.selectedItem.value.flagPos,
                    size: 16,
                    marginRight: "spacing.8"
                }) : null,
                /*#__PURE__*/ jsx(Box, {
                    as: "span",
                    srOnly: true,
                    id: valueId,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    flexGrow: 1,
                    children: context.selectedItem?.textValue
                }),
                /*#__PURE__*/ jsx(CaretDownFilledIcon, {
                    size: "sizing.16",
                    marginVertical: `calc(${tokens['unsafe_component.textField.spacing.padding.vertical']} - ${borderWidth})`,
                    color: "unsafe_component.textField.color.icon"
                })
            ]
        })
    });
});

const useMobileCountryCode = (ref, props)=>{
    const { input: inputRef, popover: popoverRef } = ref;
    const { inputProps, state } = props;
    // Close the software keyboard on scroll to give the user a bigger area to scroll.
    // But only do this if scrolling with touch, otherwise it can cause issues with touch
    // screen readers.
    const isTouchDown = useRef(false);
    const onTouchStart = ()=>{
        isTouchDown.current = true;
    };
    const onTouchEnd = ()=>{
        isTouchDown.current = false;
    };
    const onScroll = useCallback(()=>{
        if (!popoverRef.current || !inputRef.current || document.activeElement !== inputRef.current || !isTouchDown.current) {
            return;
        }
        popoverRef.current.focus();
    }, [
        inputRef,
        popoverRef,
        isTouchDown
    ]);
    const onKeyDown = (e)=>{
        // Close virtual keyboard and modal if user hits Enter w/o any focused options
        if (e.key === 'Enter' && state.selectionManager.focusedKey == null && popoverRef.current) {
            popoverRef.current.focus();
        }
    };
    // Override the role of the input to "searchbox" instead of "combobox".
    // Since the listbox is always visible, the combobox role doesn't really give us anything.
    // VoiceOver on iOS reads "double tap to collapse" when focused on the input rather than
    // "double tap to edit text", as with a textbox or searchbox. We'd like double tapping to
    // open the virtual keyboard rather than closing the tray.
    inputProps.role = 'searchbox';
    inputProps['aria-haspopup'] = 'listbox';
    delete inputProps.onTouchEnd;
    delete inputProps['aria-expanded'];
    delete inputProps['aria-controls'];
    return {
        boxProps: {
            input: mergeProps(inputProps, {
                onKeyDown
            }),
            list: {
                onScroll,
                onTouchStart,
                onTouchEnd
            }
        }
    };
};

const Content = ({ error, isOptional, isDisabled, label, hasNecessityIndicator, emptyState, inputRef, dialogRef, onFocus, onBlur, context, helper, ...fieldRenderProps })=>{
    const listRef = useRef(null);
    // TMP: deactivated as we won't use `inert` for now with duplication of code in `usePopover` and `useModalOverlay`
    // Temporary solution to avoid the non-cleaning of aria-hidden on the outside elements when the tray is closed
    // Certainly due to the fact that combobox and modal concurrently want to either hide or inert the outside elements but cleaning is not able to be done properly on both inert/aria-hidden
    // Reality is that we should not be using combobox without popover in a modal normally, so we should change this component code to replace useCombobox in mobile by useAutocomplete (new hook)
    // But there is other issues of doing that (not the same states, ...)
    // https://github.com/adobe/react-spectrum/issues/8934
    // useEffect(() => {
    // 	if (context.isOpen) {
    // 		return ariaHideOutside(
    // 			[inputRef.current, dialogRef.current].filter(
    // 				(element) => element != null,
    // 			),
    // 			{ shouldUseInert: true },
    // 		);
    // 	}
    // 	return () => {};
    // }, [context.isOpen, inputRef, dialogRef]);
    const { boxProps: { input, list } } = useCountryCode({
        list: listRef,
        popover: dialogRef,
        input: inputRef
    }, {
        isDisabled,
        isOptional,
        error,
        state: context,
        onFocus,
        onBlur,
        ...fieldRenderProps
    });
    const { boxProps: { input: finalInputProps, list: listMobileProps } } = useMobileCountryCode({
        input: inputRef,
        popover: dialogRef
    }, {
        inputProps: input,
        state: context
    });
    useEffect(()=>{
        if (inputRef.current) {
            focusSafely(inputRef.current);
        }
        // When the tray unmounts, set state.isFocused (i.e. the tray input's focus tracker) to false.
        // This is to prevent state.isFocused from being set to true when the tray closes via tapping on the underlay
        // (FocusScope attempts to restore focus to the tray input when tapping outside the tray due to "contain")
        // Have to do this manually since React doesn't call onBlur when a component is unmounted: https://github.com/facebook/react/issues/12363
        return ()=>{
            context.setFocused(false);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return /*#__PURE__*/ jsxs(Box, {
        display: "flex",
        flexDirection: "column",
        padding: "spacing.16",
        paddingBottom: "spacing.none",
        height: "100%",
        children: [
            /*#__PURE__*/ jsx(Field, {
                position: "relative",
                label: typeof label === 'string' ? label : label.isHidden ? label.content : {
                    content: label.content,
                    tooltip: label.tooltip
                },
                isDisabled: isDisabled,
                isOptional: isOptional,
                hasNecessityIndicator: hasNecessityIndicator,
                helper: helper,
                hasScreenReaderHiddenLabel: typeof label !== 'string' && label.isHidden,
                children: ({ fieldRenderProps })=>/*#__PURE__*/ jsx(CountryCodeInput, {
                        ...mergeProps(finalInputProps, fieldRenderProps),
                        inputRef: inputRef,
                        context: context,
                        isDisabled: isDisabled,
                        error: error,
                        onFocus: onFocus,
                        onBlur: onBlur
                    })
            }),
            /*#__PURE__*/ jsx(CountryCodeList, {
                ...mergeProps(list, listMobileProps),
                context: context,
                variant: "borderless",
                listRef: listRef,
                emptyState: emptyState,
                // change default hover behavior of react-aria list to only apply style on hover
                shouldFocusOnHover: false
            })
        ]
    });
};

const MobileCountryCode = ({ containerRef, listWidth, error, isOptional, isDisabled, label, hasNecessityIndicator, helper, emptyState, onFocus, onBlur, ...fieldRenderProps })=>{
    const inputRef = useObjectRef$1(null);
    const dialogRef = useObjectRef$1(null);
    const context = usePhoneNumberContext();
    return /*#__PURE__*/ jsx(Modal, {
        ref: dialogRef,
        title: typeof label === 'string' ? label : label.content,
        hasContentPadding: false,
        isOpen: context.isOpen,
        displayMode: "fullscreen",
        isDismissable: true,
        onOpenChange: (isOpen)=>!isOpen ? context.close() : null,
        trigger: ({ onPress, ...triggerProps })=>/*#__PURE__*/ jsx(MobileCountryCodeButton, {
                ...mergeProps$1(triggerProps, fieldRenderProps),
                inputRef: inputRef,
                isDisabled: isDisabled,
                isOptional: isOptional,
                label: label,
                error: error,
                isOpenModal: context.isOpen,
                context: context
            }),
        children: /*#__PURE__*/ jsx(Content, {
            ...fieldRenderProps,
            label: label,
            isDisabled: isDisabled,
            isOptional: isOptional,
            error: error,
            hasNecessityIndicator: hasNecessityIndicator,
            inputRef: inputRef,
            dialogRef: dialogRef,
            emptyState: emptyState,
            onFocus: onFocus,
            onBlur: onBlur,
            context: context,
            helper: helper
        })
    });
};
const MobileCountryCodeButton = /*#__PURE__*/ forwardRef(({ inputRef, isDisabled, error, 'aria-labelledby': ariaLabelledBy, 'aria-describedby': ariaDescribedby, isOptional, hasNecessityIndicator, label, isOpenModal, context, ...restProps }, ref)=>{
    const hiddenLabelId = useId$1();
    const buttonId = useId$1();
    const isLabelHidden = typeof label !== 'string' && label.isHidden;
    const labelId = isLabelHidden ? hiddenLabelId : ariaLabelledBy;
    return /*#__PURE__*/ jsxs(Fragment, {
        children: [
            isLabelHidden && !isOpenModal ? /*#__PURE__*/ jsx(FieldLabel, {
                label: label,
                isDisabled: isDisabled,
                isOptional: isOptional,
                hasNecessityIndicator: hasNecessityIndicator,
                labelProps: {
                    id: labelId,
                    htmlFor: buttonId
                },
                helperProps: {
                    id: undefined
                },
                hasScreenReaderHiddenLabel: true
            }) : null,
            /*#__PURE__*/ jsx(Button, {
                ...restProps,
                id: buttonId,
                "aria-label": undefined,
                "aria-labelledby": labelId,
                "aria-describedby": ariaDescribedby,
                inputRef: inputRef,
                context: context,
                isDisabled: isDisabled,
                error: error,
                ref: ref
            })
        ]
    });
});

const CountryCode = ({ isMobile, ...props })=>{
    // todo: https://avivgroup.atlassian.net/browse/GEMINI-2244
    if (isMobile) return /*#__PURE__*/ jsx(MobileCountryCode, {
        ...props
    });
    else return /*#__PURE__*/ jsx(DesktopCountryCode, {
        ...props
    });
};

const phoneNumberTokens = {
    spacing: {
        gap: 'spacing.8'
    },
    sizing: {
        minWidth: 'sizing.320'
    }
};

const PhoneNumber = /*#__PURE__*/ forwardRef((props, ref)=>{
    const { onChange, onBlur, onFocus, value, defaultValue, defaultCountry, name, isFullWidth, countries, placeholder, emptyState, prioritizedCountries, ...fieldProps } = props;
    const hiddenInputRef = useObjectRef$1(ref);
    const containerRef = useRef(null);
    const isMobile = useBreakpointValue({
        base: true,
        sm: false
    });
    const [listWidth, setListWidth] = useState(0);
    const textFieldMinWidth = useMemo(()=>mapPhoneNumberWidthProps(isFullWidth), [
        isFullWidth
    ]);
    const onResize = useCallback(()=>{
        if (containerRef.current) {
            const containerWidth = containerRef.current.offsetWidth;
            setListWidth(containerWidth);
        }
    }, [
        setListWidth
    ]);
    useResizeObserver(containerRef, {
        onResize
    });
    useLayoutEffect(onResize, [
        onResize
    ]);
    return /*#__PURE__*/ jsx(PhoneNumberProvider, {
        ...props,
        countries: countries,
        isMobile: isMobile,
        emptyState: emptyState,
        defaultCountry: defaultCountry,
        value: value,
        defaultValue: defaultValue,
        onChange: onChange,
        prioritizedCountries: prioritizedCountries,
        children: /*#__PURE__*/ jsx(Field, {
            position: "relative",
            ...fieldProps,
            children: ({ fieldRenderProps })=>/*#__PURE__*/ jsxs(Box, {
                    ref: containerRef,
                    display: "flex",
                    gap: phoneNumberTokens.spacing.gap,
                    minWidth: textFieldMinWidth,
                    children: [
                        /*#__PURE__*/ jsx(CountryCode, {
                            ...mergeProps({
                                error: fieldProps.error,
                                isOptional: fieldProps.isOptional,
                                isDisabled: fieldProps.isDisabled,
                                label: fieldProps.label,
                                hasNecessityIndicator: fieldProps.hasNecessityIndicator,
                                helper: fieldProps.helper
                            }, fieldRenderProps),
                            containerRef: containerRef,
                            listWidth: listWidth,
                            isMobile: isMobile,
                            emptyState: emptyState,
                            onFocus: onFocus,
                            onBlur: onBlur
                        }),
                        /*#__PURE__*/ jsx(Input, {
                            ...fieldRenderProps,
                            hiddenInputRef: hiddenInputRef,
                            error: fieldProps.error,
                            isOptional: fieldProps.isOptional,
                            isDisabled: fieldProps.isDisabled,
                            name: name,
                            onFocus: onFocus,
                            onBlur: onBlur,
                            placeholder: placeholder
                        })
                    ]
                })
        })
    });
});
const mapPhoneNumberWidthProps = (isFullWidthWithResponsive)=>{
    // if we don't have full width i.E. undefined / false return min width of the field
    if (!isFullWidthWithResponsive) {
        return phoneNumberTokens.sizing.minWidth;
    }
    // if the prop is not an responsive object return 100% directly
    if (typeof isFullWidthWithResponsive === 'boolean') {
        return '100%';
    }
    return Object.entries(isFullWidthWithResponsive).reduce((acc, [breakpoint, isFullWidth])=>{
        return {
            ...acc,
            // apply 100% for full width or default min width of the field
            [breakpoint]: isFullWidth ? '100%' : phoneNumberTokens.sizing.minWidth
        };
    }, {});
};

const usePhoneNumberValidation = ({ countries })=>{
    return useCallback((phoneNumber, options = {
        strict: false
    })=>{
        if (phoneNumber === undefined) return;
        const digitsPhoneNumber = parseDigits(phoneNumber);
        if (digitsPhoneNumber.length > 15) return 'TOO_LONG';
        if (options?.strict && !isValidPhoneNumber(phoneNumber, countries.metadata)) return 'INVALID_NUMBER';
        return validatePhoneNumberLength(phoneNumber, countries.metadata);
    }, [
        countries.metadata
    ]);
};

const EUROPE = [
    {
        id: 'albania_AL',
        flagPos: {
            left: -540,
            top: 0
        },
        code: '+355',
        iso: 'AL'
    },
    {
        id: 'andorra_AD',
        flagPos: {
            left: 0,
            top: 0
        },
        code: '+376',
        iso: 'AD'
    },
    {
        id: 'armenia_AM',
        flagPos: {
            left: -648,
            top: 0
        },
        code: '+374',
        iso: 'AM'
    },
    {
        id: 'austria_AT',
        flagPos: {
            left: -1080,
            top: 0
        },
        code: '+43',
        iso: 'AT'
    },
    {
        id: 'azerbaijan_AZ',
        flagPos: {
            left: -216,
            top: -84
        },
        code: '+994',
        iso: 'AZ'
    },
    {
        id: 'belarus_BY',
        flagPos: {
            left: -864,
            top: -168
        },
        code: '+375',
        iso: 'BY'
    },
    {
        id: 'belgium_BE',
        flagPos: {
            left: -648,
            top: -84
        },
        code: '+32',
        iso: 'BE'
    },
    {
        id: 'bosnia_and_herzegovina_BA',
        flagPos: {
            left: -324,
            top: -84
        },
        code: '+387',
        iso: 'BA'
    },
    {
        id: 'bulgaria_BG',
        flagPos: {
            left: -864,
            top: -84
        },
        code: '+359',
        iso: 'BG'
    },
    {
        id: 'croatia_HR',
        flagPos: {
            left: -432,
            top: -588
        },
        code: '+385',
        iso: 'HR'
    },
    {
        id: 'cyprus_CY',
        flagPos: {
            left: -324,
            top: -336
        },
        code: '+357',
        iso: 'CY'
    },
    {
        id: 'czechia_CZ',
        flagPos: {
            left: -432,
            top: -336
        },
        code: '+420',
        iso: 'CZ'
    },
    {
        id: 'denmark_DK',
        flagPos: {
            left: -756,
            top: -336
        },
        code: '+45',
        iso: 'DK'
    },
    {
        id: 'estonia_EE',
        flagPos: {
            left: 0,
            top: -420
        },
        code: '+372',
        iso: 'EE'
    },
    {
        id: 'finland_FI',
        flagPos: {
            left: -540,
            top: -420
        },
        code: '+358',
        iso: 'FI'
    },
    {
        id: 'france_FR',
        flagPos: {
            left: -864,
            top: -420
        },
        code: '+33',
        iso: 'FR'
    },
    {
        id: 'georgia_GE',
        flagPos: {
            left: 0,
            top: -504
        },
        code: '+995',
        iso: 'GE'
    },
    {
        id: 'germany_DE',
        flagPos: {
            left: -540,
            top: -336
        },
        code: '+49',
        iso: 'DE'
    },
    {
        id: 'greece_GR',
        flagPos: {
            left: -972,
            top: -504
        },
        code: '+30',
        iso: 'GR'
    },
    {
        id: 'hungary_HU',
        flagPos: {
            left: -648,
            top: -588
        },
        code: '+36',
        iso: 'HU'
    },
    {
        id: 'iceland_IS',
        flagPos: {
            left: -324,
            top: -672
        },
        code: '+354',
        iso: 'IS'
    },
    {
        id: 'ireland_IE',
        flagPos: {
            left: -864,
            top: -588
        },
        code: '+353',
        iso: 'IE'
    },
    {
        id: 'italy_IT',
        flagPos: {
            left: -432,
            top: -672
        },
        code: '+39',
        iso: 'IT'
    },
    {
        id: 'kazakhstan_KZ',
        flagPos: {
            left: -648,
            top: -756
        },
        code: '+7',
        iso: 'KZ'
    },
    {
        id: 'kosovo_XK',
        flagPos: {
            left: -864,
            top: -1512
        },
        code: '+383',
        iso: 'XK'
    },
    {
        id: 'latvia_LV',
        flagPos: {
            left: -324,
            top: -840
        },
        code: '+371',
        iso: 'LV'
    },
    {
        id: 'liechtenstein_LI',
        flagPos: {
            left: -972,
            top: -756
        },
        code: '+423',
        iso: 'LI'
    },
    {
        id: 'lithuania_LT',
        flagPos: {
            left: -108,
            top: -840
        },
        code: '+370',
        iso: 'LT'
    },
    {
        id: 'luxembourg_LU',
        flagPos: {
            left: -216,
            top: -840
        },
        code: '+352',
        iso: 'LU'
    },
    {
        id: 'malta_MT',
        flagPos: {
            left: -972,
            top: -924
        },
        code: '+356',
        iso: 'MT'
    },
    {
        id: 'moldova_MD',
        flagPos: {
            left: -756,
            top: -840
        },
        code: '+373',
        iso: 'MD'
    },
    {
        id: 'monaco_MC',
        flagPos: {
            left: -648,
            top: -840
        },
        code: '+377',
        iso: 'MC'
    },
    {
        id: 'montenegro_ME',
        flagPos: {
            left: -864,
            top: -840
        },
        code: '+382',
        iso: 'ME'
    },
    {
        id: 'netherlands_NL',
        flagPos: {
            left: -972,
            top: -1008
        },
        code: '+31',
        iso: 'NL'
    },
    {
        id: 'north_macedonia_MK',
        flagPos: {
            left: 0,
            top: -924
        },
        code: '+389',
        iso: 'MK'
    },
    {
        id: 'norway_NO',
        flagPos: {
            left: -1080,
            top: -1008
        },
        code: '+47',
        iso: 'NO'
    },
    {
        id: 'poland_PL',
        flagPos: {
            left: -1080,
            top: -1092
        },
        code: '+48',
        iso: 'PL'
    },
    {
        id: 'portugal_PT',
        flagPos: {
            left: -216,
            top: -1176
        },
        code: '+351',
        iso: 'PT'
    },
    {
        id: 'romania_RO',
        flagPos: {
            left: -756,
            top: -1176
        },
        code: '+40',
        iso: 'RO'
    },
    {
        id: 'russia_RU',
        flagPos: {
            left: -972,
            top: -1176
        },
        code: '+7',
        iso: 'RU'
    },
    {
        id: 'san_marino_SM',
        flagPos: {
            left: -972,
            top: -1260
        },
        code: '+378',
        iso: 'SM'
    },
    {
        id: 'serbia_RS',
        flagPos: {
            left: -864,
            top: -1176
        },
        code: '+381',
        iso: 'RS'
    },
    {
        id: 'slovakia_SK',
        flagPos: {
            left: -756,
            top: -1260
        },
        code: '+421',
        iso: 'SK'
    },
    {
        id: 'slovenia_SI',
        flagPos: {
            left: -648,
            top: -1260
        },
        code: '+386',
        iso: 'SI'
    },
    {
        id: 'spain_ES',
        flagPos: {
            left: -324,
            top: -420
        },
        code: '+34',
        iso: 'ES'
    },
    {
        id: 'sweden_SE',
        flagPos: {
            left: -324,
            top: -1260
        },
        code: '+46',
        iso: 'SE'
    },
    {
        id: 'switzerland_CH',
        flagPos: {
            left: -324,
            top: -252
        },
        code: '+41',
        iso: 'CH'
    },
    {
        id: 'turkey_TR',
        flagPos: {
            left: -324,
            top: -1428
        },
        code: '+90',
        iso: 'TR'
    },
    {
        id: 'ukraine_UA',
        flagPos: {
            left: -864,
            top: -1428
        },
        code: '+380',
        iso: 'UA'
    },
    {
        id: 'united_kingdom_GB',
        flagPos: {
            left: -1080,
            top: -420
        },
        code: '+44',
        iso: 'GB'
    },
    {
        id: 'vatican City_VA',
        flagPos: {
            left: -108,
            top: -1512
        },
        code: '+39',
        iso: 'VA'
    }
];

var version$1 = 4;
var country_calling_codes$1 = {
	"7": [
		"RU",
		"KZ"
	],
	"30": [
		"GR"
	],
	"31": [
		"NL"
	],
	"32": [
		"BE"
	],
	"33": [
		"FR"
	],
	"34": [
		"ES"
	],
	"36": [
		"HU"
	],
	"39": [
		"IT",
		"VA"
	],
	"40": [
		"RO"
	],
	"41": [
		"CH"
	],
	"43": [
		"AT"
	],
	"44": [
		"GB"
	],
	"45": [
		"DK"
	],
	"46": [
		"SE"
	],
	"47": [
		"NO"
	],
	"48": [
		"PL"
	],
	"49": [
		"DE"
	],
	"90": [
		"TR"
	],
	"351": [
		"PT"
	],
	"352": [
		"LU"
	],
	"353": [
		"IE"
	],
	"354": [
		"IS"
	],
	"355": [
		"AL"
	],
	"356": [
		"MT"
	],
	"357": [
		"CY"
	],
	"358": [
		"FI"
	],
	"359": [
		"BG"
	],
	"370": [
		"LT"
	],
	"371": [
		"LV"
	],
	"372": [
		"EE"
	],
	"373": [
		"MD"
	],
	"374": [
		"AM"
	],
	"375": [
		"BY"
	],
	"376": [
		"AD"
	],
	"377": [
		"MC"
	],
	"378": [
		"SM"
	],
	"380": [
		"UA"
	],
	"381": [
		"RS"
	],
	"382": [
		"ME"
	],
	"383": [
		"XK"
	],
	"385": [
		"HR"
	],
	"386": [
		"SI"
	],
	"387": [
		"BA"
	],
	"389": [
		"MK"
	],
	"420": [
		"CZ"
	],
	"421": [
		"SK"
	],
	"423": [
		"LI"
	],
	"994": [
		"AZ"
	],
	"995": [
		"GE"
	]
};
var countries$1 = {
	AD: [
		"376",
		"00",
		"(?:1|6\\d)\\d{7}|[135-9]\\d{5}",
		[
			6,
			8,
			9
		],
		[
			[
				"(\\d{3})(\\d{3})",
				"$1 $2",
				[
					"[135-9]"
				]
			],
			[
				"(\\d{4})(\\d{4})",
				"$1 $2",
				[
					"1"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"6"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"[78]\\d{5}",
				[
					6
				]
			],
			[
				"690\\d{6}|[356]\\d{5}",
				[
					6,
					9
				]
			]
		]
	],
	AL: [
		"355",
		"00",
		"(?:700\\d\\d|900)\\d{3}|8\\d{5,7}|(?:[2-5]|6\\d)\\d{7}",
		[
			6,
			7,
			8,
			9
		],
		[
			[
				"(\\d{3})(\\d{3,4})",
				"$1 $2",
				[
					"80|9"
				],
				"0$1"
			],
			[
				"(\\d)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"4[2-6]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[2358][2-5]|4"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{5})",
				"$1 $2",
				[
					"[23578]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"6"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"4505[0-2]\\d{3}|(?:[2358][16-9]\\d[2-9]|4410)\\d{4}|(?:[2358][2-5][2-9]|4(?:[2-57-9][2-9]|6\\d))\\d{5}",
				[
					8
				]
			],
			[
				"6(?:[78][2-9]|9\\d)\\d{6}",
				[
					9
				]
			]
		]
	],
	AM: [
		"374",
		"00",
		"(?:[1-489]\\d|55|60|77)\\d{6}",
		[
			8
		],
		[
			[
				"(\\d{3})(\\d{2})(\\d{3})",
				"$1 $2 $3",
				[
					"[89]0"
				],
				"0 $1"
			],
			[
				"(\\d{3})(\\d{5})",
				"$1 $2",
				[
					"2|3[12]"
				],
				"(0$1)"
			],
			[
				"(\\d{2})(\\d{6})",
				"$1 $2",
				[
					"1|47"
				],
				"(0$1)"
			],
			[
				"(\\d{2})(\\d{6})",
				"$1 $2",
				[
					"[3-9]"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:(?:1[0-25]|47)\\d|2(?:2[2-46]|3[1-8]|4[2-69]|5[2-7]|6[1-9]|8[1-7])|3[12]2)\\d{5}"
			],
			[
				"(?:33|4[1349]|55|77|88|9[13-9])\\d{6}"
			]
		]
	],
	AT: [
		"43",
		"00",
		"1\\d{3,12}|2\\d{6,12}|43(?:(?:0\\d|5[02-9])\\d{3,9}|2\\d{4,5}|[3467]\\d{4}|8\\d{4,6}|9\\d{4,7})|5\\d{4,12}|8\\d{7,12}|9\\d{8,12}|(?:[367]\\d|4[0-24-9])\\d{4,11}",
		[
			4,
			5,
			6,
			7,
			8,
			9,
			10,
			11,
			12,
			13
		],
		[
			[
				"(\\d)(\\d{3,12})",
				"$1 $2",
				[
					"1(?:11|[2-9])"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{2})",
				"$1 $2",
				[
					"517"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3,5})",
				"$1 $2",
				[
					"5[079]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3,10})",
				"$1 $2",
				[
					"(?:31|4)6|51|6(?:48|5[0-3579]|[6-9])|7(?:20|32|8)|[89]",
					"(?:31|4)6|51|6(?:485|5[0-3579]|[6-9])|7(?:20|32|8)|[89]"
				],
				"0$1"
			],
			[
				"(\\d{4})(\\d{3,9})",
				"$1 $2",
				[
					"[2-467]|5[2-6]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"5"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{4})(\\d{4,7})",
				"$1 $2 $3",
				[
					"5"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"1(?:11\\d|[2-9]\\d{3,11})|(?:316|463)\\d{3,10}|648[34]\\d{3,9}|(?:51|66|73)2\\d{3,10}|(?:2(?:1[467]|2[13-8]|5[2357]|6[1-46-8]|7[1-8]|8[124-7]|9[1458])|3(?:1[1-578]|3[23568]|4[5-7]|5[1378]|6[1-38]|8[3-68])|4(?:2[1-8]|35|7[1368]|8[2457])|5(?:2[1-8]|3[357]|4[147]|5[12578]|6[37])|6(?:13|2[1-47]|4[135-7]|5[468])|7(?:2[1-8]|35|4[13478]|5[68]|6[16-8]|7[1-6]|9[45]))\\d{4,10}"
			],
			[
				"6(?:485|(?:5[0-3579]|6[013-9]|[7-9]\\d)\\d)\\d{3,9}",
				[
					7,
					8,
					9,
					10,
					11,
					12,
					13
				]
			]
		]
	],
	AZ: [
		"994",
		"00",
		"365\\d{6}|(?:[124579]\\d|60|88)\\d{7}",
		[
			9
		],
		[
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"90"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"1[28]|2|365|46",
					"1[28]|2|365[45]|46",
					"1[28]|2|365(?:4|5[02])|46"
				],
				"(0$1)"
			],
			[
				"(\\d{2})(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[13-9]"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:2[12]428|3655[02])\\d{4}|(?:2(?:22[0-79]|63[0-28])|3654)\\d{5}|(?:(?:1[28]|46)\\d|2(?:[014-6]2|[23]3))\\d{6}"
			],
			[
				"36554\\d{4}|(?:[16]0|4[04]|5[015]|7[07]|99)\\d{7}"
			]
		]
	],
	BA: [
		"387",
		"00",
		"6\\d{8}|(?:[35689]\\d|49|70)\\d{6}",
		[
			8,
			9
		],
		[
			[
				"(\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"6[1-3]|[7-9]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3})",
				"$1 $2-$3",
				[
					"[3-5]|6[56]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{3})",
				"$1 $2 $3 $4",
				[
					"6"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:3(?:[05-79][2-9]|1[4579]|[23][24-9]|4[2-4689]|8[2457-9])|49[2-579]|5(?:0[2-49]|[13][2-9]|[268][2-4679]|4[4689]|5[2-79]|7[2-69]|9[2-4689]))\\d{5}",
				[
					8
				]
			],
			[
				"6040\\d{5}|6(?:03|[1-356]|44|7\\d)\\d{6}"
			]
		]
	],
	BE: [
		"32",
		"00",
		"4\\d{8}|[1-9]\\d{7}",
		[
			8,
			9
		],
		[
			[
				"(\\d{3})(\\d{2})(\\d{3})",
				"$1 $2 $3",
				[
					"(?:80|9)0"
				],
				"0$1"
			],
			[
				"(\\d)(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[239]|4[23]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[15-8]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"4"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"80[2-8]\\d{5}|(?:1[0-69]|[23][2-8]|4[23]|5\\d|6[013-57-9]|71|8[1-79]|9[2-4])\\d{6}",
				[
					8
				]
			],
			[
				"4[5-9]\\d{7}",
				[
					9
				]
			]
		]
	],
	BG: [
		"359",
		"00",
		"00800\\d{7}|[2-7]\\d{6,7}|[89]\\d{6,8}|2\\d{5}",
		[
			6,
			7,
			8,
			9,
			12
		],
		[
			[
				"(\\d)(\\d)(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"2"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"43[1-6]|70[1-9]"
				],
				"0$1"
			],
			[
				"(\\d)(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"2"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{2,3})",
				"$1 $2 $3",
				[
					"[356]|4[124-7]|7[1-9]|8[1-6]|9[1-7]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{2})(\\d{3})",
				"$1 $2 $3",
				[
					"(?:70|8)0"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{2})",
				"$1 $2 $3",
				[
					"43[1-7]|7"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"[48]|9[08]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"9"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"2\\d{5,7}|(?:43[1-6]|70[1-9])\\d{4,5}|(?:[36]\\d|4[124-7]|[57][1-9]|8[1-6]|9[1-7])\\d{5,6}",
				[
					6,
					7,
					8
				]
			],
			[
				"(?:43[07-9]|99[69]\\d)\\d{5}|(?:8[7-9]|98)\\d{7}",
				[
					8,
					9
				]
			]
		]
	],
	BY: [
		"375",
		"810",
		"(?:[12]\\d|33|44|902)\\d{7}|8(?:0[0-79]\\d{5,7}|[1-7]\\d{9})|8(?:1[0-489]|[5-79]\\d)\\d{7}|8[1-79]\\d{6,7}|8[0-79]\\d{5}|8\\d{5}",
		[
			6,
			7,
			8,
			9,
			10,
			11
		],
		[
			[
				"(\\d{3})(\\d{3})",
				"$1 $2",
				[
					"800"
				],
				"8 $1"
			],
			[
				"(\\d{3})(\\d{2})(\\d{2,4})",
				"$1 $2 $3",
				[
					"800"
				],
				"8 $1"
			],
			[
				"(\\d{4})(\\d{2})(\\d{3})",
				"$1 $2-$3",
				[
					"1(?:5[169]|6[3-5]|7[179])|2(?:1[35]|2[34]|3[3-5])",
					"1(?:5[169]|6(?:3[1-3]|4|5[125])|7(?:1[3-9]|7[0-24-6]|9[2-7]))|2(?:1[35]|2[34]|3[3-5])"
				],
				"8 0$1"
			],
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2-$3-$4",
				[
					"1(?:[56]|7[467])|2[1-3]"
				],
				"8 0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2-$3-$4",
				[
					"[1-4]"
				],
				"8 0$1"
			],
			[
				"(\\d{3})(\\d{3,4})(\\d{4})",
				"$1 $2 $3",
				[
					"[89]"
				],
				"8 $1"
			]
		],
		"8",
		0,
		"0|80?",
		0,
		0,
		0,
		[
			[
				"(?:1(?:5(?:1[1-5]|[24]\\d|6[2-4]|9[1-7])|6(?:[235]\\d|4[1-7])|7\\d\\d)|2(?:1(?:[246]\\d|3[0-35-9]|5[1-9])|2(?:[235]\\d|4[0-8])|3(?:[26]\\d|3[02-79]|4[024-7]|5[03-7])))\\d{5}",
				[
					9
				]
			],
			[
				"(?:2(?:5[5-79]|9[1-9])|(?:33|44)\\d)\\d{6}",
				[
					9
				]
			]
		],
		"8~10"
	],
	CH: [
		"41",
		"00",
		"8\\d{11}|[2-9]\\d{8}",
		[
			9,
			12
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"8[047]|90"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[2-79]|81"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{2})(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4 $5",
				[
					"8"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:2[12467]|3[1-4]|4[134]|5[256]|6[12]|[7-9]1)\\d{7}",
				[
					9
				]
			],
			[
				"(?:6[89]|7[235-9])\\d{7}",
				[
					9
				]
			]
		]
	],
	CY: [
		"357",
		"00",
		"(?:[279]\\d|[58]0)\\d{6}",
		[
			8
		],
		[
			[
				"(\\d{2})(\\d{6})",
				"$1 $2",
				[
					"[257-9]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"2[2-6]\\d{6}"
			],
			[
				"9(?:10|[4-79]\\d)\\d{5}"
			]
		]
	],
	CZ: [
		"420",
		"00",
		"(?:[2-578]\\d|60)\\d{7}|9\\d{8,11}",
		[
			9,
			10,
			11,
			12
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[2-8]|9[015-7]"
				]
			],
			[
				"(\\d{2})(\\d{3})(\\d{3})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"96"
				]
			],
			[
				"(\\d{2})(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3 $4",
				[
					"9"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3 $4",
				[
					"9"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:2\\d|3[1257-9]|4[16-9]|5[13-9])\\d{7}",
				[
					9
				]
			],
			[
				"7(?:060\\d|19(?:[0-5]\\d|6[0-6]))\\d{4}|(?:60[1-8]|7(?:0[2-5]|[2379]\\d))\\d{6}",
				[
					9
				]
			]
		]
	],
	DE: [
		"49",
		"00",
		"[2579]\\d{5,14}|49(?:[34]0|69|8\\d)\\d\\d?|49(?:37|49|60|7[089]|9\\d)\\d{1,3}|49(?:2[024-9]|3[2-689]|7[1-7])\\d{1,8}|(?:1|[368]\\d|4[0-8])\\d{3,13}|49(?:[015]\\d|2[13]|31|[46][1-8])\\d{1,9}",
		[
			4,
			5,
			6,
			7,
			8,
			9,
			10,
			11,
			12,
			13,
			14,
			15
		],
		[
			[
				"(\\d{2})(\\d{3,13})",
				"$1 $2",
				[
					"3[02]|40|[68]9"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3,12})",
				"$1 $2",
				[
					"2(?:0[1-389]|1[124]|2[18]|3[14])|3(?:[35-9][15]|4[015])|906|(?:2[4-9]|4[2-9]|[579][1-9]|[68][1-8])1",
					"2(?:0[1-389]|12[0-8])|3(?:[35-9][15]|4[015])|906|2(?:[13][14]|2[18])|(?:2[4-9]|4[2-9]|[579][1-9]|[68][1-8])1"
				],
				"0$1"
			],
			[
				"(\\d{4})(\\d{2,11})",
				"$1 $2",
				[
					"[24-6]|3(?:[3569][02-46-9]|4[2-4679]|7[2-467]|8[2-46-8])|70[2-8]|8(?:0[2-9]|[1-8])|90[7-9]|[79][1-9]",
					"[24-6]|3(?:3(?:0[1-467]|2[127-9]|3[124578]|7[1257-9]|8[1256]|9[145])|4(?:2[135]|4[13578]|9[1346])|5(?:0[14]|2[1-3589]|6[1-4]|7[13468]|8[13568])|6(?:2[1-489]|3[124-6]|6[13]|7[12579]|8[1-356]|9[135])|7(?:2[1-7]|4[145]|6[1-5]|7[1-4])|8(?:21|3[1468]|6|7[1467]|8[136])|9(?:0[12479]|2[1358]|4[134679]|6[1-9]|7[136]|8[147]|9[1468]))|70[2-8]|8(?:0[2-9]|[1-8])|90[7-9]|[79][1-9]|3[68]4[1347]|3(?:47|60)[1356]|3(?:3[46]|46|5[49])[1246]|3[4579]3[1357]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"138"
				],
				"0$1"
			],
			[
				"(\\d{5})(\\d{2,10})",
				"$1 $2",
				[
					"3"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{5,11})",
				"$1 $2",
				[
					"181"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d)(\\d{4,10})",
				"$1 $2 $3",
				[
					"1(?:3|80)|9"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{7,8})",
				"$1 $2",
				[
					"1[67]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{7,12})",
				"$1 $2",
				[
					"8"
				],
				"0$1"
			],
			[
				"(\\d{5})(\\d{6})",
				"$1 $2",
				[
					"185",
					"1850",
					"18500"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"7"
				],
				"0$1"
			],
			[
				"(\\d{4})(\\d{7})",
				"$1 $2",
				[
					"18[68]"
				],
				"0$1"
			],
			[
				"(\\d{4})(\\d{7})",
				"$1 $2",
				[
					"15[1279]"
				],
				"0$1"
			],
			[
				"(\\d{5})(\\d{6})",
				"$1 $2",
				[
					"15[03568]",
					"15(?:[0568]|3[13])"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{8})",
				"$1 $2",
				[
					"18"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{2})(\\d{7,8})",
				"$1 $2 $3",
				[
					"1(?:6[023]|7)"
				],
				"0$1"
			],
			[
				"(\\d{4})(\\d{2})(\\d{7})",
				"$1 $2 $3",
				[
					"15[279]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{2})(\\d{8})",
				"$1 $2 $3",
				[
					"15"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"32\\d{9,11}|49[1-6]\\d{10}|322\\d{6}|49[0-7]\\d{3,9}|(?:[34]0|[68]9)\\d{3,13}|(?:2(?:0[1-689]|[1-3569]\\d|4[0-8]|7[1-7]|8[0-7])|3(?:[3569]\\d|4[0-79]|7[1-7]|8[1-8])|4(?:1[02-9]|[2-48]\\d|5[0-6]|6[0-8]|7[0-79])|5(?:0[2-8]|[124-6]\\d|[38][0-8]|[79][0-7])|6(?:0[02-9]|[1-358]\\d|[47][0-8]|6[1-9])|7(?:0[2-8]|1[1-9]|[27][0-7]|3\\d|[4-6][0-8]|8[0-5]|9[013-7])|8(?:0[2-9]|1[0-79]|2\\d|3[0-46-9]|4[0-6]|5[013-9]|6[1-8]|7[0-8]|8[0-24-6])|9(?:0[6-9]|[1-4]\\d|[589][0-7]|6[0-8]|7[0-467]))\\d{3,12}",
				[
					5,
					6,
					7,
					8,
					9,
					10,
					11,
					12,
					13,
					14,
					15
				]
			],
			[
				"1(?:(?:5(?:[0-25-9]\\d\\d|3(?:10|33))|7[26-9]\\d\\d)\\d{6}|6[023]\\d{7,8})|17\\d{8}",
				[
					10,
					11
				]
			]
		]
	],
	DK: [
		"45",
		"00",
		"[2-9]\\d{7}",
		[
			8
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[2-9]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:2(?:[0-59][1-9]|[6-8]\\d)|3(?:[0-3][1-9]|4[13]|5[1-58]|6[1347-9]|7\\d|8[1-8]|9[1-79])|4(?:[0-25][1-9]|[34][2-9]|6[13-579]|7[13579]|8[1-47]|9[127])|5(?:[0-36][1-9]|4[146-9]|5[3-57-9]|7[568]|8[1-358]|9[1-69])|6(?:[0135][1-9]|2[1-68]|4[2-8]|6[1689]|[78]\\d|9[15689])|7(?:[0-69][1-9]|7[3-9]|8[147])|8(?:[16-9][1-9]|2[1-58])|9(?:[1-47-9][1-9]|6\\d))\\d{5}"
			],
			[
				"(?:2[6-8]|37|6[78]|96)\\d{6}|(?:2[0-59]|3[0-689]|[457]\\d|6[0-69]|8[126-9]|9[1-47-9])[1-9]\\d{5}"
			]
		]
	],
	EE: [
		"372",
		"00",
		"8\\d{9}|[4578]\\d{7}|(?:[3-8]\\d|90)\\d{5}",
		[
			7,
			8,
			10
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"[369]|4[3-8]|5(?:[0-2]|5[0-478]|6[45])|7[1-9]|88",
					"[369]|4[3-8]|5(?:[02]|1(?:[0-8]|95)|5[0-478]|6(?:4[0-4]|5[1-589]))|7[1-9]|88"
				]
			],
			[
				"(\\d{4})(\\d{3,4})",
				"$1 $2",
				[
					"[45]|8(?:00|[1-49])",
					"[45]|8(?:00[1-9]|[1-49])"
				]
			],
			[
				"(\\d{2})(\\d{2})(\\d{4})",
				"$1 $2 $3",
				[
					"7"
				]
			],
			[
				"(\\d{4})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"8"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:3[23589]|4[3-8]|6\\d|7[1-9]|88)\\d{5}",
				[
					7
				]
			],
			[
				"(?:5\\d{5}|8(?:1(?:0(?:0(?:00|[178]\\d)|[3-9]\\d\\d)|(?:1(?:0[2-6]|1\\d)|(?:2[0-59]|[3-79]\\d)\\d)\\d)|2(?:0(?:0(?:00|4\\d)|(?:19|[2-7]\\d)\\d)|(?:(?:[124-69]\\d|3[5-9])\\d|7(?:[0-79]\\d|8[13-9])|8(?:[2-6]\\d|7[01]))\\d)|[349]\\d{4}))\\d\\d|5(?:(?:[02]\\d|5[0-478])\\d|1(?:[0-8]\\d|95)|6(?:4[0-4]|5[1-589]))\\d{3}",
				[
					7,
					8
				]
			]
		]
	],
	ES: [
		"34",
		"00",
		"[5-9]\\d{8}",
		[
			9
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[89]00"
				]
			],
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[5-9]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"96906(?:0[0-8]|1[1-9]|[2-9]\\d)\\d\\d|9(?:69(?:0[0-57-9]|[1-9]\\d)|73(?:[0-8]\\d|9[1-9]))\\d{4}|(?:8(?:[1356]\\d|[28][0-8]|[47][1-9])|9(?:[135]\\d|[268][0-8]|4[1-9]|7[124-9]))\\d{6}"
			],
			[
				"96906(?:09|10)\\d\\d|(?:590(?:10[0-2]|600)|97390\\d)\\d{3}|(?:6\\d|7[1-48])\\d{7}"
			]
		]
	],
	FI: [
		"358",
		"00|99(?:[01469]|5(?:[14]1|3[23]|5[59]|77|88|9[09]))",
		"[1-35689]\\d{4}|7\\d{10,11}|(?:[124-7]\\d|3[0-46-9])\\d{8}|[1-9]\\d{5,8}",
		[
			5,
			6,
			7,
			8,
			9,
			10,
			11,
			12
		],
		[
			[
				"(\\d{5})",
				"$1",
				[
					"20[2-59]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3,7})",
				"$1 $2",
				[
					"(?:[1-3]0|[68])0|70[07-9]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{4,8})",
				"$1 $2",
				[
					"[14]|2[09]|50|7[135]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{6,10})",
				"$1 $2",
				[
					"7"
				],
				"0$1"
			],
			[
				"(\\d)(\\d{4,9})",
				"$1 $2",
				[
					"(?:19|[2568])[1-8]|3(?:0[1-9]|[1-9])|9"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		"1[03-79]|[2-9]",
		[
			[
				"1[3-7][1-8]\\d{3,6}|(?:19[1-8]|[23568][1-8]\\d|9(?:00|[1-8]\\d))\\d{2,6}",
				[
					5,
					6,
					7,
					8,
					9
				]
			],
			[
				"4946\\d{2,6}|(?:4[0-8]|50)\\d{4,8}",
				[
					6,
					7,
					8,
					9,
					10
				]
			]
		],
		"00"
	],
	FR: [
		"33",
		"00",
		"[1-9]\\d{8}",
		[
			9
		],
		[
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"8"
				],
				"0 $1"
			],
			[
				"(\\d)(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4 $5",
				[
					"[1-79]"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:26[013-9]|59[1-35-9])\\d{6}|(?:[13]\\d|2[0-57-9]|4[1-9]|5[0-8])\\d{7}"
			],
			[
				"(?:6(?:[0-24-8]\\d|3[0-8]|9[589])|7[3-9]\\d)\\d{6}"
			]
		]
	],
	GB: [
		"44",
		"00",
		"[1-357-9]\\d{9}|[18]\\d{8}|8\\d{6}",
		[
			7,
			9,
			10
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"800",
					"8001",
					"80011",
					"800111",
					"8001111"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2 $3",
				[
					"845",
					"8454",
					"84546",
					"845464"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{6})",
				"$1 $2",
				[
					"800"
				],
				"0$1"
			],
			[
				"(\\d{5})(\\d{4,5})",
				"$1 $2",
				[
					"1(?:38|5[23]|69|76|94)",
					"1(?:(?:38|69)7|5(?:24|39)|768|946)",
					"1(?:3873|5(?:242|39[4-6])|(?:697|768)[347]|9467)"
				],
				"0$1"
			],
			[
				"(\\d{4})(\\d{5,6})",
				"$1 $2",
				[
					"1(?:[2-69][02-9]|[78])"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"[25]|7(?:0|6[02-9])",
					"[25]|7(?:0|6(?:[03-9]|2[356]))"
				],
				"0$1"
			],
			[
				"(\\d{4})(\\d{6})",
				"$1 $2",
				[
					"7"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[1389]"
				],
				"0$1"
			]
		],
		"0",
		0,
		"0|180020",
		0,
		0,
		0,
		[
			[
				"(?:1(?:1(?:3(?:[0-58]\\d\\d|73[0-5])|4(?:(?:[0-5]\\d|70)\\d|69[7-9])|(?:(?:5[0-26-9]|[78][0-49])\\d|6(?:[0-4]\\d|5[01]))\\d)|(?:2(?:(?:0[024-9]|2[3-9]|3[3-79]|4[1-689]|[58][02-9]|6[0-47-9]|7[013-9]|9\\d)\\d|1(?:[0-7]\\d|8[0-3]))|(?:3(?:0\\d|1[0-8]|[25][02-9]|3[02-579]|[468][0-46-9]|7[1-35-79]|9[2-578])|4(?:0[03-9]|[137]\\d|[28][02-57-9]|4[02-69]|5[0-8]|[69][0-79])|5(?:0[1-35-9]|[16]\\d|2[024-9]|3[015689]|4[02-9]|5[03-9]|7[0-35-9]|8[0-468]|9[0-57-9])|6(?:0[034689]|1\\d|2[0-35689]|[38][013-9]|4[1-467]|5[0-69]|6[13-9]|7[0-8]|9[0-24578])|7(?:0[0246-9]|2\\d|3[0236-8]|4[03-9]|5[0-46-9]|6[013-9]|7[0-35-9]|8[024-9]|9[02-9])|8(?:0[35-9]|2[1-57-9]|3[02-578]|4[0-578]|5[124-9]|6[2-69]|7\\d|8[02-9]|9[02569])|9(?:0[02-589]|[18]\\d|2[02-689]|3[1-57-9]|4[2-9]|5[0-579]|6[2-47-9]|7[0-24578]|9[2-57]))\\d)\\d)|2(?:0[013478]|3[0189]|4[017]|8[0-46-9]|9[0-2])\\d{3})\\d{4}|1(?:2(?:0(?:46[1-4]|87[2-9])|545[1-79]|76(?:2\\d|3[1-8]|6[1-6])|9(?:7(?:2[0-4]|3[2-5])|8(?:2[2-8]|7[0-47-9]|8[3-5])))|3(?:6(?:38[2-5]|47[23])|8(?:47[04-9]|64[0157-9]))|4(?:044[1-7]|20(?:2[23]|8\\d)|6(?:0(?:30|5[2-57]|6[1-8]|7[2-8])|140)|8(?:052|87[1-3]))|5(?:2(?:4(?:3[2-79]|6\\d)|76\\d)|6(?:26[06-9]|686))|6(?:06(?:4\\d|7[4-79])|295[5-7]|35[34]\\d|47(?:24|61)|59(?:5[08]|6[67]|74)|9(?:55[0-4]|77[23]))|7(?:26(?:6[13-9]|7[0-7])|(?:442|688)\\d|50(?:2[0-3]|[3-68]2|76))|8(?:27[56]\\d|37(?:5[2-5]|8[239])|843[2-58])|9(?:0(?:0(?:6[1-8]|85)|52\\d)|3583|4(?:66[1-8]|9(?:2[01]|81))|63(?:23|3[1-4])|9561))\\d{3}",
				[
					9,
					10
				]
			],
			[
				"7(?:457[0-57-9]|700[01]|911[028])\\d{5}|7(?:[1-3]\\d\\d|4(?:[0-46-9]\\d|5[0-689])|5(?:0[0-8]|[13-9]\\d|2[0-35-9])|7(?:0[1-9]|[1-7]\\d|8[02-9]|9[0-689])|8(?:[014-9]\\d|[23][0-8])|9(?:[024-9]\\d|1[02-9]|3[0-689]))\\d{6}",
				[
					10
				]
			]
		],
		0,
		" x"
	],
	GE: [
		"995",
		"00",
		"(?:[3-57]\\d\\d|800)\\d{6}",
		[
			9
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"70"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"32"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[57]"
				]
			],
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[348]"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:3(?:[256]\\d|4[124-9]|7[0-4])|4(?:1\\d|2[2-7]|3[1-79]|4[2-8]|7[239]|9[1-7]))\\d{6}"
			],
			[
				"5(?:(?:(?:0555|1(?:[17]77|555))[5-9]|757(?:7[7-9]|8[01]))\\d|22252[0-4])\\d\\d|5(?:0(?:0[17]0|505)|1(?:0[01]0|1(?:07|33|51))|2(?:0[02]0|2[25]2)|3(?:0[03]0|3[35]3)|(?:40[04]|900)0|5222)[0-4]\\d{3}|(?:5(?:0(?:0(?:0\\d|1[12]|22|3[0-6]|44|5[05]|77|88|9[09])|(?:[14]\\d|77)\\d|22[02])|1(?:1(?:[03][01]|[124]\\d|5[2-6]|7[0-4])|4\\d\\d)|[23]555|4(?:4\\d\\d|555)|5(?:[0157-9]\\d\\d|200|333|444)|6[89]\\d\\d|7(?:(?:[0147-9]\\d|22)\\d|5(?:00|[57]5))|8(?:0(?:[018]\\d|2[0-4])|5(?:55|8[89])|8(?:55|88))|9(?:090|[1-35-9]\\d\\d))|790\\d\\d)\\d{4}"
			]
		]
	],
	GR: [
		"30",
		"00",
		"5005000\\d{3}|8\\d{9,11}|(?:[269]\\d|70)\\d{8}",
		[
			10,
			11,
			12
		],
		[
			[
				"(\\d{2})(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"21|7"
				]
			],
			[
				"(\\d{4})(\\d{6})",
				"$1 $2",
				[
					"2(?:2|3[2-57-9]|4[2-469]|5[2-59]|6[2-9]|7[2-69]|8[2-49])|5"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[2689]"
				]
			],
			[
				"(\\d{3})(\\d{3,4})(\\d{5})",
				"$1 $2 $3",
				[
					"8"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"2(?:1\\d\\d|2(?:2[1-46-9]|[36][1-8]|4[1-7]|5[1-4]|7[1-5]|[89][1-9])|3(?:1\\d|2[1-57]|[35][1-3]|4[13]|7[1-7]|8[124-6]|9[1-79])|4(?:1\\d|2[1-8]|3[1-4]|4[13-5]|6[1-578]|9[1-5])|5(?:1\\d|[29][1-4]|3[1-5]|4[124]|5[1-6])|6(?:1\\d|[269][1-6]|3[1245]|4[1-7]|5[13-9]|7[14]|8[1-5])|7(?:1\\d|2[1-5]|3[1-6]|4[1-7]|5[1-57]|6[135]|9[125-7])|8(?:1\\d|2[1-5]|[34][1-4]|9[1-57]))\\d{6}",
				[
					10
				]
			],
			[
				"68[57-9]\\d{7}|(?:69|94)\\d{8}",
				[
					10
				]
			]
		]
	],
	HR: [
		"385",
		"00",
		"[2-69]\\d{8}|80\\d{5,7}|[1-79]\\d{7}|6\\d{6}",
		[
			7,
			8,
			9
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{3})",
				"$1 $2 $3",
				[
					"6[01]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{2})(\\d{2,3})",
				"$1 $2 $3",
				[
					"8"
				],
				"0$1"
			],
			[
				"(\\d)(\\d{4})(\\d{3})",
				"$1 $2 $3",
				[
					"1"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"6|7[245]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"9"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"[2-57]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"8"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"1\\d{7}|(?:2[0-3]|3[1-5]|4[02-47-9]|5[1-3])\\d{6,7}",
				[
					8,
					9
				]
			],
			[
				"9(?:(?:0[1-9]|[12589]\\d)\\d\\d|7(?:[0679]\\d\\d|5(?:[01]\\d|44|55|77|9[5-79])))\\d{4}|98\\d{6}",
				[
					8,
					9
				]
			]
		]
	],
	HU: [
		"36",
		"00",
		"[235-7]\\d{8}|[1-9]\\d{7}",
		[
			8,
			9
		],
		[
			[
				"(\\d)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"1"
				],
				"(06 $1)"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[27][2-9]|3[2-7]|4[24-9]|5[2-79]|6|8[2-57-9]|9[2-69]"
				],
				"(06 $1)"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"[2-9]"
				],
				"06 $1"
			]
		],
		"06",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:1\\d|[27][2-9]|3[2-7]|4[24-9]|5[2-79]|6[23689]|8[2-57-9]|9[2-69])\\d{6}",
				[
					8
				]
			],
			[
				"(?:[257]0|3[01])\\d{7}",
				[
					9
				]
			]
		]
	],
	IE: [
		"353",
		"00",
		"(?:1\\d|[2569])\\d{6,8}|4\\d{6,9}|7\\d{8}|8\\d{8,9}",
		[
			7,
			8,
			9,
			10
		],
		[
			[
				"(\\d{2})(\\d{5})",
				"$1 $2",
				[
					"2[24-9]|47|58|6[237-9]|9[35-9]"
				],
				"(0$1)"
			],
			[
				"(\\d{3})(\\d{5})",
				"$1 $2",
				[
					"[45]0"
				],
				"(0$1)"
			],
			[
				"(\\d)(\\d{3,4})(\\d{4})",
				"$1 $2 $3",
				[
					"1"
				],
				"(0$1)"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"[2569]|4[1-69]|7[14]"
				],
				"(0$1)"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"70"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"81"
				],
				"(0$1)"
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[78]"
				],
				"0$1"
			],
			[
				"(\\d{4})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"1"
				]
			],
			[
				"(\\d{2})(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"4"
				],
				"(0$1)"
			],
			[
				"(\\d{2})(\\d)(\\d{3})(\\d{4})",
				"$1 $2 $3 $4",
				[
					"8"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:1\\d|21)\\d{6,7}|(?:2[24-9]|4(?:0[24]|5\\d|7)|5(?:0[45]|1\\d|8)|6(?:1\\d|[237-9])|9(?:1\\d|[35-9]))\\d{5}|(?:23|4(?:[1-469]|8\\d)|5[23679]|6[4-6]|7[14]|9[04])\\d{7}"
			],
			[
				"8(?:22|[35-9]\\d)\\d{6}",
				[
					9
				]
			]
		]
	],
	IS: [
		"354",
		"00|1(?:0(?:01|[12]0)|100)",
		"(?:38\\d|[4-9])\\d{6}",
		[
			7,
			9
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"[4-9]"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"3"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:4(?:1[0-24-69]|2[0-7]|[37][0-8]|4[0-24589]|5[0-68]|6\\d|8[0-36-8])|5(?:05|[156]\\d|2[02578]|3[0-579]|4[03-7]|7[0-2578]|8[0-35-9]|9[013-689])|872)\\d{4}",
				[
					7
				]
			],
			[
				"(?:38[589]\\d\\d|6(?:1[1-8]|2[0-6]|3[026-9]|4[014679]|5[0159]|6[0-69]|70|8[06-8]|9\\d)|7(?:5[057]|[6-9]\\d)|8(?:2[0-59]|[3-69]\\d|8[238]))\\d{4}"
			]
		],
		"00"
	],
	IT: [
		"39",
		"00",
		"0\\d{5,11}|1\\d{8,10}|3(?:[0-8]\\d{7,10}|9\\d{7,8})|(?:43|55|70)\\d{8}|8\\d{5}(?:\\d{2,4})?",
		[
			6,
			7,
			8,
			9,
			10,
			11,
			12
		],
		[
			[
				"(\\d{2})(\\d{4,6})",
				"$1 $2",
				[
					"0[26]"
				]
			],
			[
				"(\\d{3})(\\d{3,6})",
				"$1 $2",
				[
					"0[13-57-9][0159]|8(?:03|4[17]|9[2-5])",
					"0[13-57-9][0159]|8(?:03|4[17]|9(?:2|3[04]|[45][0-4]))"
				]
			],
			[
				"(\\d{4})(\\d{2,6})",
				"$1 $2",
				[
					"0(?:[13-579][2-46-8]|8[236-8])"
				]
			],
			[
				"(\\d{4})(\\d{4})",
				"$1 $2",
				[
					"894"
				]
			],
			[
				"(\\d{2})(\\d{3,4})(\\d{4})",
				"$1 $2 $3",
				[
					"0[26]|5"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"1(?:44|[679])|[378]|43"
				]
			],
			[
				"(\\d{3})(\\d{3,4})(\\d{4})",
				"$1 $2 $3",
				[
					"0[13-57-9][0159]|14"
				]
			],
			[
				"(\\d{2})(\\d{4})(\\d{5})",
				"$1 $2 $3",
				[
					"0[26]"
				]
			],
			[
				"(\\d{4})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"0"
				]
			],
			[
				"(\\d{3})(\\d{4})(\\d{4,5})",
				"$1 $2 $3",
				[
					"[03]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"0(?:669[0-79]\\d{1,6}|831\\d{2,8})|0(?:1(?:[0159]\\d|[27][1-5]|31|4[1-4]|6[1356]|8[2-57])|2\\d\\d|3(?:[0159]\\d|2[1-4]|3[12]|[48][1-6]|6[2-59]|7[1-7])|4(?:[0159]\\d|[23][1-9]|4[245]|6[1-5]|7[1-4]|81)|5(?:[0159]\\d|2[1-5]|3[2-6]|4[1-79]|6[4-6]|7[1-578]|8[3-8])|6(?:[0-57-9]\\d|6[0-8])|7(?:[0159]\\d|2[12]|3[1-7]|4[2-46]|6[13569]|7[13-6]|8[1-59])|8(?:[0159]\\d|2[3-578]|3[2356]|[6-8][1-5])|9(?:[0159]\\d|[238][1-5]|4[12]|6[1-8]|7[1-6]))\\d{2,7}"
			],
			[
				"3[2-9]\\d{7,8}|(?:31|43)\\d{8}",
				[
					9,
					10
				]
			]
		]
	],
	KZ: [
		"7",
		"810",
		"(?:33622|8\\d{8})\\d{5}|[78]\\d{9}",
		[
			10,
			14
		],
		0,
		"8",
		0,
		0,
		0,
		0,
		"33622|7",
		[
			[
				"(?:33622|7(?:1(?:0(?:[23]\\d|4[0-3]|59|63)|1(?:[23]\\d|4[0-79]|59)|2(?:[23]\\d|59)|3(?:2\\d|3[0-79]|4[0-35-9]|59)|4(?:[24]\\d|3[013-9]|5[1-9]|97)|5(?:2\\d|3[1-9]|4[0-7]|59)|6(?:[2-4]\\d|5[19]|61)|72\\d|8(?:[27]\\d|3[1-46-9]|4[0-5]|59))|2(?:1(?:[23]\\d|4[46-9]|5[3469])|2(?:2\\d|3[0679]|46|5[12679])|3(?:[2-4]\\d|5[139])|4(?:2\\d|3[1-35-9]|59)|5(?:[23]\\d|4[0-8]|59|61)|6(?:2\\d|3[1-9]|4[0-4]|59)|7(?:[2379]\\d|40|5[279])|8(?:[23]\\d|4[0-3]|59)|9(?:2\\d|3[124578]|59))))\\d{5}",
				[
					10
				]
			],
			[
				"7(?:0[0-25-8]|47|6[0-4]|7[15-8]|85)\\d{7}",
				[
					10
				]
			]
		],
		"8~10"
	],
	LI: [
		"423",
		"00",
		"[68]\\d{8}|(?:[2378]\\d|90)\\d{5}",
		[
			7,
			9
		],
		[
			[
				"(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2 $3",
				[
					"[2379]|8(?:0[09]|7)",
					"[2379]|8(?:0(?:02|9)|7)"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"8"
				]
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"69"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"6"
				]
			]
		],
		"0",
		0,
		"(1001)|0",
		0,
		0,
		0,
		[
			[
				"(?:2(?:01|1[27]|2[024]|3\\d|6[02-578]|96)|3(?:[24]0|33|7[0135-7]|8[048]|9[0269]))\\d{4}",
				[
					7
				]
			],
			[
				"(?:6(?:(?:4[5-9]|5\\d)\\d|6(?:[024-68]\\d|1[01]|3[7-9]|70))\\d|7(?:[37-9]\\d|42|56))\\d{4}"
			]
		]
	],
	LT: [
		"370",
		"00",
		"(?:[3469]\\d|52|[78]0)\\d{6}",
		[
			8
		],
		[
			[
				"(\\d)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"52[0-7]"
				],
				"(0-$1)",
				1
			],
			[
				"(\\d{3})(\\d{2})(\\d{3})",
				"$1 $2 $3",
				[
					"[7-9]"
				],
				"0 $1",
				1
			],
			[
				"(\\d{2})(\\d{6})",
				"$1 $2",
				[
					"37|4(?:[15]|6[1-8])"
				],
				"(0-$1)",
				1
			],
			[
				"(\\d{3})(\\d{5})",
				"$1 $2",
				[
					"[3-6]"
				],
				"(0-$1)",
				1
			]
		],
		"0",
		0,
		"[08]",
		0,
		0,
		0,
		[
			[
				"(?:3[1478]|4[124-6]|52)\\d{6}"
			],
			[
				"6\\d{7}"
			]
		]
	],
	LU: [
		"352",
		"00",
		"35[013-9]\\d{4,8}|6\\d{8}|35\\d{2,4}|(?:[2457-9]\\d|3[0-46-9])\\d{2,9}",
		[
			4,
			5,
			6,
			7,
			8,
			9,
			10,
			11
		],
		[
			[
				"(\\d{2})(\\d{3})",
				"$1 $2",
				[
					"2(?:0[2-689]|[2-9])|[3-57]|8(?:0[2-9]|[13-9])|9(?:0[89]|[2-579])"
				]
			],
			[
				"(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3",
				[
					"2(?:0[2-689]|[2-9])|[3-57]|8(?:0[2-9]|[13-9])|9(?:0[89]|[2-579])"
				]
			],
			[
				"(\\d{2})(\\d{2})(\\d{3})",
				"$1 $2 $3",
				[
					"20[2-689]"
				]
			],
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{1,2})",
				"$1 $2 $3 $4",
				[
					"2(?:[0367]|4[3-8])"
				]
			],
			[
				"(\\d{3})(\\d{2})(\\d{3})",
				"$1 $2 $3",
				[
					"80[01]|90[015]"
				]
			],
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{3})",
				"$1 $2 $3 $4",
				[
					"20"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"6"
				]
			],
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})(\\d{1,2})",
				"$1 $2 $3 $4 $5",
				[
					"2(?:[0367]|4[3-8])"
				]
			],
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{1,5})",
				"$1 $2 $3 $4",
				[
					"[3-57]|8[13-9]|9(?:0[89]|[2-579])|(?:2|80)[2-9]"
				]
			]
		],
		0,
		0,
		"(15(?:0[06]|1[12]|[35]5|4[04]|6[26]|77|88|99)\\d)",
		0,
		0,
		0,
		[
			[
				"(?:35[013-9]|80[2-9]|90[89])\\d{1,8}|(?:2[2-9]|3[0-46-9]|[457]\\d|8[13-9]|9[2-579])\\d{2,9}"
			],
			[
				"6(?:[269][18]|5[1568]|7[189]|81)\\d{6}",
				[
					9
				]
			]
		]
	],
	LV: [
		"371",
		"00",
		"(?:[268]\\d|78|90)\\d{6}",
		[
			8
		],
		[
			[
				"(\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[2679]|8[01]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"6\\d{7}"
			],
			[
				"2333[0-8]\\d{3}|2(?:[0-24-9]\\d\\d|3(?:0[07]|[14-9]\\d|2[02-9]|3[0-24-9]))\\d{4}"
			]
		]
	],
	MC: [
		"377",
		"00",
		"(?:[3489]|6\\d)\\d{7}",
		[
			8,
			9
		],
		[
			[
				"(\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"4"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[389]"
				]
			],
			[
				"(\\d)(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4 $5",
				[
					"6"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:870|9[2-47-9]\\d)\\d{5}",
				[
					8
				]
			],
			[
				"4(?:[469]\\d|5[1-9])\\d{5}|(?:3|6\\d)\\d{7}"
			]
		]
	],
	MD: [
		"373",
		"00",
		"(?:[235-7]\\d|[89]0)\\d{6}",
		[
			8
		],
		[
			[
				"(\\d{3})(\\d{5})",
				"$1 $2",
				[
					"[89]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"22|3"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{2})(\\d{3})",
				"$1 $2 $3",
				[
					"[25-7]"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:(?:2[1-9]|3[1-79])\\d|5(?:33|5[257]))\\d{5}"
			],
			[
				"562\\d{5}|(?:6\\d|7[16-9])\\d{6}"
			]
		]
	],
	ME: [
		"382",
		"00",
		"(?:20|[3-79]\\d)\\d{6}|80\\d{6,7}",
		[
			8,
			9
		],
		[
			[
				"(\\d{2})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"[2-9]"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:20[2-8]|3(?:[0-2][2-7]|3[24-7])|4(?:0[2-467]|1[2467])|5(?:0[2467]|1[24-7]|2[2-467]))\\d{5}",
				[
					8
				]
			],
			[
				"6(?:[07-9]\\d|3[024]|6[0-25])\\d{5}",
				[
					8
				]
			]
		]
	],
	MK: [
		"389",
		"00",
		"[2-578]\\d{7}",
		[
			8
		],
		[
			[
				"(\\d)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"2|34[47]|4(?:[37]7|5[47]|64)"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[347]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d)(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[58]"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:(?:2(?:62|77)0|3444)\\d|4[56]440)\\d{3}|(?:34|4[357])700\\d{3}|(?:2(?:[0-3]\\d|5[0-578]|6[01]|82)|3(?:1[3-68]|[23][2-68]|4[23568])|4(?:[23][2-68]|4[3-68]|5[2568]|6[25-8]|7[24-68]|8[4-68]))\\d{5}"
			],
			[
				"7(?:3555|(?:474|9[019]7)7)\\d{3}|7(?:[0-25-8]\\d\\d|3(?:[1-478]\\d|6[01])|4(?:2\\d|60|7[01578])|9(?:[2-4]\\d|5[01]|7[015]))\\d{4}"
			]
		]
	],
	MT: [
		"356",
		"00",
		"3550\\d{4}|(?:[2579]\\d\\d|800)\\d{5}",
		[
			8
		],
		[
			[
				"(\\d{4})(\\d{4})",
				"$1 $2",
				[
					"[2357-9]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"20(?:3[1-4]|6[059])\\d{4}|2(?:0[19]|[1-357]\\d|60)\\d{5}"
			],
			[
				"(?:7(?:210|[79]\\d\\d)|9(?:[29]\\d\\d|69[67]|8(?:1[1-3]|89|97)))\\d{4}"
			]
		]
	],
	NL: [
		"31",
		"00",
		"(?:[124-7]\\d\\d|3(?:[02-9]\\d|1[0-8]))\\d{6}|8\\d{6,9}|9\\d{6,10}|1\\d{4,5}",
		[
			5,
			6,
			7,
			8,
			9,
			10,
			11
		],
		[
			[
				"(\\d{3})(\\d{4,7})",
				"$1 $2",
				[
					"[89]0"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{7})",
				"$1 $2",
				[
					"66"
				],
				"0$1"
			],
			[
				"(\\d)(\\d{8})",
				"$1 $2",
				[
					"6"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"1[16-8]|2[259]|3[124]|4[17-9]|5[124679]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[1-578]|91"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{5})",
				"$1 $2 $3",
				[
					"9"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:1(?:[035]\\d|1[13-578]|6[124-8]|7[24]|8[0-467])|2(?:[0346]\\d|2[2-46-9]|5[125]|9[479])|3(?:[03568]\\d|1[3-8]|2[01]|4[1-8])|4(?:[0356]\\d|1[1-368]|7[58]|8[15-8]|9[23579])|5(?:[0358]\\d|[19][1-9]|2[1-57-9]|4[13-8]|6[126]|7[0-3578])|7\\d\\d)\\d{6}",
				[
					9
				]
			],
			[
				"(?:6[1-58]|970\\d)\\d{7}",
				[
					9,
					11
				]
			]
		]
	],
	NO: [
		"47",
		"00",
		"(?:0|[2-9]\\d{3})\\d{4}",
		[
			5,
			8
		],
		[
			[
				"(\\d{3})(\\d{2})(\\d{3})",
				"$1 $2 $3",
				[
					"8"
				]
			],
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[2-79]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		"[02-689]|7[0-8]",
		[
			[
				"(?:2[1-4]|3[1-3578]|5[1-35-7]|6[1-4679]|7[0-8])\\d{6}",
				[
					8
				]
			],
			[
				"(?:4[015-8]|9\\d)\\d{6}",
				[
					8
				]
			]
		]
	],
	PL: [
		"48",
		"00",
		"(?:6|8\\d\\d)\\d{7}|[1-9]\\d{6}(?:\\d{2})?|[26]\\d{5}",
		[
			6,
			7,
			8,
			9,
			10
		],
		[
			[
				"(\\d{5})",
				"$1",
				[
					"19"
				]
			],
			[
				"(\\d{3})(\\d{3})",
				"$1 $2",
				[
					"11|20|64"
				]
			],
			[
				"(\\d{2})(\\d{2})(\\d{3})",
				"$1 $2 $3",
				[
					"(?:1[2-8]|2[2-69]|3[2-4]|4[1-468]|5[24-689]|6[1-3578]|7[14-7]|8[1-79]|9[145])1",
					"(?:1[2-8]|2[2-69]|3[2-4]|4[1-468]|5[24-689]|6[1-3578]|7[14-7]|8[1-79]|9[145])19"
				]
			],
			[
				"(\\d{3})(\\d{2})(\\d{2,3})",
				"$1 $2 $3",
				[
					"64"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"21|39|45|5[0137]|6[0469]|7[02389]|8(?:0[14]|8)"
				]
			],
			[
				"(\\d{2})(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"1[2-8]|[2-7]|8[1-79]|9[145]"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"8"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"47\\d{7}|(?:1[2-8]|2[2-69]|3[2-4]|4[1-468]|5[24-689]|6[1-3578]|7[14-7]|8[1-79]|9[145])(?:[02-9]\\d{6}|1(?:[0-8]\\d{5}|9\\d{3}(?:\\d{2})?))",
				[
					7,
					9
				]
			],
			[
				"2131[89]\\d{4}|21(?:1[013-5]|2\\d|3[2-9])\\d{5}|(?:45|5[0137]|6[069]|7[2389]|88)\\d{7}",
				[
					9
				]
			]
		]
	],
	PT: [
		"351",
		"00",
		"1693\\d{5}|(?:[26-9]\\d|30)\\d{7}",
		[
			9
		],
		[
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"2[12]"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"16|[236-9]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"2(?:[12]\\d|3[1-689]|4[1-59]|[57][1-9]|6[1-35689]|8[1-69]|9[1256])\\d{6}"
			],
			[
				"6(?:[06]92(?:30|9\\d)|[35]92(?:[049]\\d|3[034]))\\d{3}|(?:(?:16|6[0356])93|9(?:[1-36]\\d\\d|480))\\d{5}"
			]
		]
	],
	RO: [
		"40",
		"00",
		"(?:[236-8]\\d|90)\\d{7}|[23]\\d{5}",
		[
			6,
			9
		],
		[
			[
				"(\\d{3})(\\d{3})",
				"$1 $2",
				[
					"2[3-6]",
					"2[3-6]\\d9"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{4})",
				"$1 $2",
				[
					"219|31"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[23]1"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[236-9]"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"[23][13-6]\\d{7}|(?:2(?:19\\d|[3-6]\\d9)|31\\d\\d)\\d\\d"
			],
			[
				"(?:630|702)0\\d{5}|(?:6(?:00|2\\d)|7(?:0[013-9]|1[0-3]|[2-7]\\d|8[03-8]|9[0-39]))\\d{6}",
				[
					9
				]
			]
		],
		0,
		" int "
	],
	RS: [
		"381",
		"00",
		"38[02-9]\\d{6,9}|6\\d{7,9}|90\\d{4,8}|38\\d{5,6}|(?:7\\d\\d|800)\\d{3,9}|(?:[12]\\d|3[0-79])\\d{5,10}",
		[
			6,
			7,
			8,
			9,
			10,
			11,
			12
		],
		[
			[
				"(\\d{3})(\\d{3,9})",
				"$1 $2",
				[
					"(?:2[389]|39)0|[7-9]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{5,10})",
				"$1 $2",
				[
					"[1-36]"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:11[1-9]\\d|(?:2[389]|39)(?:0[2-9]|[2-9]\\d))\\d{3,8}|(?:1[02-9]|2[0-24-7]|3[0-8])[2-9]\\d{4,9}",
				[
					7,
					8,
					9,
					10,
					11,
					12
				]
			],
			[
				"6(?:[0-689]|7\\d)\\d{6,7}",
				[
					8,
					9,
					10
				]
			]
		]
	],
	RU: [
		"7",
		"810",
		"8\\d{13}|[347-9]\\d{9}",
		[
			10,
			14
		],
		[
			[
				"(\\d{4})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"7(?:1[0-8]|2[1-9])",
					"7(?:1(?:[0-356]2|4[29]|7|8[27])|2(?:1[23]|[2-9]2))",
					"7(?:1(?:[0-356]2|4[29]|7|8[27])|2(?:13[03-69]|62[013-9]))|72[1-57-9]2"
				],
				"8 ($1)",
				1
			],
			[
				"(\\d{5})(\\d)(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"7(?:1[0-68]|2[1-9])",
					"7(?:1(?:[06][3-6]|[18]|2[35]|[3-5][3-5])|2(?:[13][3-5]|[24-689]|7[457]))",
					"7(?:1(?:0(?:[356]|4[023])|[18]|2(?:3[013-9]|5)|3[45]|43[013-79]|5(?:3[1-8]|4[1-7]|5)|6(?:3[0-35-9]|[4-6]))|2(?:1(?:3[178]|[45])|[24-689]|3[35]|7[457]))|7(?:14|23)4[0-8]|71(?:33|45)[1-79]"
				],
				"8 ($1)",
				1
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"7"
				],
				"8 ($1)",
				1
			],
			[
				"(\\d{3})(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2-$3-$4",
				[
					"[349]|8(?:[02-7]|1[1-8])"
				],
				"8 ($1)",
				1
			],
			[
				"(\\d{4})(\\d{4})(\\d{3})(\\d{3})",
				"$1 $2 $3 $4",
				[
					"8"
				],
				"8 ($1)"
			]
		],
		"8",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"336(?:[013-9]\\d|2[013-9])\\d{5}|(?:3(?:0[12]|4[1-35-79]|5[1-3]|65|8[1-58]|9[0145])|4(?:01|1[1356]|2[13467]|7[1-5]|8[1-7]|9[1-689])|8(?:1[1-8]|2[01]|3[13-6]|4[0-8]|5[15-7]|6[0-35-79]|7[1-37-9]))\\d{7}",
				[
					10
				]
			],
			[
				"9\\d{9}",
				[
					10
				]
			]
		],
		"8~10"
	],
	SE: [
		"46",
		"00",
		"(?:[26]\\d\\d|9)\\d{9}|[1-9]\\d{8}|[1-689]\\d{7}|[1-4689]\\d{6}|2\\d{5}",
		[
			6,
			7,
			8,
			9,
			10,
			12
		],
		[
			[
				"(\\d{2})(\\d{2,3})(\\d{2})",
				"$1-$2 $3",
				[
					"20"
				],
				"0$1",
				0,
				"$1 $2 $3"
			],
			[
				"(\\d{3})(\\d{4})",
				"$1-$2",
				[
					"9(?:00|39|44|9)"
				],
				"0$1",
				0,
				"$1 $2"
			],
			[
				"(\\d{2})(\\d{3})(\\d{2})",
				"$1-$2 $3",
				[
					"[12][136]|3[356]|4[0246]|6[03]|90[1-9]"
				],
				"0$1",
				0,
				"$1 $2 $3"
			],
			[
				"(\\d)(\\d{2,3})(\\d{2})(\\d{2})",
				"$1-$2 $3 $4",
				[
					"8"
				],
				"0$1",
				0,
				"$1 $2 $3 $4"
			],
			[
				"(\\d{3})(\\d{2,3})(\\d{2})",
				"$1-$2 $3",
				[
					"1[2457]|2(?:[247-9]|5[0138])|3[0247-9]|4[1357-9]|5[0-35-9]|6(?:[125689]|4[02-57]|7[0-2])|9(?:[125-8]|3[02-5]|4[0-3])"
				],
				"0$1",
				0,
				"$1 $2 $3"
			],
			[
				"(\\d{3})(\\d{2,3})(\\d{3})",
				"$1-$2 $3",
				[
					"9(?:00|39|44)"
				],
				"0$1",
				0,
				"$1 $2 $3"
			],
			[
				"(\\d{2})(\\d{2,3})(\\d{2})(\\d{2})",
				"$1-$2 $3 $4",
				[
					"1[13689]|2[0136]|3[1356]|4[0246]|54|6[03]|90[1-9]"
				],
				"0$1",
				0,
				"$1 $2 $3 $4"
			],
			[
				"(\\d{2})(\\d{3})(\\d{2})(\\d{2})",
				"$1-$2 $3 $4",
				[
					"10|7"
				],
				"0$1",
				0,
				"$1 $2 $3 $4"
			],
			[
				"(\\d)(\\d{3})(\\d{3})(\\d{2})",
				"$1-$2 $3 $4",
				[
					"8"
				],
				"0$1",
				0,
				"$1 $2 $3 $4"
			],
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1-$2 $3 $4",
				[
					"[13-5]|2(?:[247-9]|5[0138])|6(?:[124-689]|7[0-2])|9(?:[125-8]|3[02-5]|4[0-3])"
				],
				"0$1",
				0,
				"$1 $2 $3 $4"
			],
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{3})",
				"$1-$2 $3 $4",
				[
					"9"
				],
				"0$1",
				0,
				"$1 $2 $3 $4"
			],
			[
				"(\\d{3})(\\d{2})(\\d{3})(\\d{2})(\\d{2})",
				"$1-$2 $3 $4 $5",
				[
					"[26]"
				],
				"0$1",
				0,
				"$1 $2 $3 $4 $5"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:(?:[12][136]|3[356]|4[0246]|6[03]|8\\d)\\d|90[1-9])\\d{4,6}|(?:1(?:2[0-35]|4[0-4]|5[0-25-9]|7[13-6]|[89]\\d)|2(?:2[0-7]|4[0136-8]|5[0138]|7[018]|8[01]|9[0-57])|3(?:0[0-4]|1\\d|2[0-25]|4[056]|7[0-2]|8[0-3]|9[023])|4(?:1[013-8]|3[0135]|5[14-79]|7[0-246-9]|8[0156]|9[0-689])|5(?:0[0-6]|[15][0-5]|2[0-68]|3[0-4]|4\\d|6[03-5]|7[013]|8[0-79]|9[01])|6(?:1[1-3]|2[0-4]|4[02-57]|5[0-37]|6[0-3]|7[0-2]|8[0247]|9[0-356])|9(?:1[0-68]|2\\d|3[02-5]|4[0-3]|5[0-4]|[68][01]|7[0135-8]))\\d{5,6}",
				[
					7,
					8,
					9
				]
			],
			[
				"7[02369]\\d{7}",
				[
					9
				]
			]
		]
	],
	SI: [
		"386",
		"00|10(?:22|66|88|99)",
		"[1-7]\\d{7}|8\\d{4,7}|90\\d{4,6}",
		[
			5,
			6,
			7,
			8
		],
		[
			[
				"(\\d{2})(\\d{3,6})",
				"$1 $2",
				[
					"8[09]|9"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{5})",
				"$1 $2",
				[
					"59|8"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[37][01]|4[0139]|51|6"
				],
				"0$1"
			],
			[
				"(\\d)(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[1-57]"
				],
				"(0$1)"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:[1-357][2-8]|4[24-8])\\d{6}",
				[
					8
				]
			],
			[
				"65(?:[178]\\d|5[56]|6[01])\\d{4}|(?:[37][01]|4[0139]|51|6[489])\\d{6}",
				[
					8
				]
			]
		],
		"00"
	],
	SK: [
		"421",
		"00",
		"[2-689]\\d{8}|[2-59]\\d{6}|[2-5]\\d{5}",
		[
			6,
			7,
			9
		],
		[
			[
				"(\\d)(\\d{2})(\\d{3,4})",
				"$1 $2 $3",
				[
					"21"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{2})(\\d{2,3})",
				"$1 $2 $3",
				[
					"[3-5][1-8]1",
					"[3-5][1-8]1[67]"
				],
				"0$1"
			],
			[
				"(\\d)(\\d{3})(\\d{3})(\\d{2})",
				"$1/$2 $3 $4",
				[
					"2"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[689]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{2})(\\d{2})",
				"$1/$2 $3 $4",
				[
					"[3-5]"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:2(?:16|[2-9]\\d{3})|(?:(?:[3-5][1-8]\\d|819)\\d|601[1-5])\\d)\\d{4}|(?:2|[3-5][1-8])1[67]\\d{3}|[3-5][1-8]16\\d\\d"
			],
			[
				"909[1-9]\\d{5}|9(?:0[1-8]|1[0-24-9]|4[03-57-9]|5\\d)\\d{6}",
				[
					9
				]
			]
		]
	],
	SM: [
		"378",
		"00",
		"(?:0549|[5-7]\\d)\\d{6}",
		[
			8,
			10
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[5-7]"
				]
			],
			[
				"(\\d{4})(\\d{6})",
				"$1 $2",
				[
					"0"
				]
			]
		],
		0,
		0,
		"([89]\\d{5})$",
		"0549$1",
		0,
		0,
		[
			[
				"0549(?:8[0157-9]|9\\d)\\d{4}",
				[
					10
				]
			],
			[
				"6[16]\\d{6}",
				[
					8
				]
			]
		]
	],
	TR: [
		"90",
		"00",
		"4\\d{6}|8\\d{11,12}|(?:[2-58]\\d\\d|900)\\d{7}",
		[
			7,
			10,
			12,
			13
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"512|8[01589]|90"
				],
				"0$1",
				1
			],
			[
				"(\\d{3})(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"5(?:[0-59]|61)",
					"5(?:[0-59]|61[06])",
					"5(?:[0-59]|61[06]1)"
				],
				"0$1",
				1
			],
			[
				"(\\d{3})(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[24][1-8]|3[1-9]"
				],
				"(0$1)",
				1
			],
			[
				"(\\d{3})(\\d{3})(\\d{6,7})",
				"$1 $2 $3",
				[
					"80"
				],
				"0$1",
				1
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:2(?:[13][26]|[28][2468]|[45][268]|[67][246])|3(?:[13][28]|[24-6][2468]|[78][02468]|92)|4(?:[16][246]|[23578][2468]|4[26]))\\d{7}",
				[
					10
				]
			],
			[
				"561(?:011|61\\d)\\d{4}|5(?:0[15-7]|1[06]|24|[34]\\d|5[1-59]|9[46])\\d{7}",
				[
					10
				]
			]
		]
	],
	UA: [
		"380",
		"00",
		"[89]\\d{9}|[3-9]\\d{8}",
		[
			9,
			10
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"6[12][29]|(?:3[1-8]|4[136-8]|5[12457]|6[49])2|(?:56|65)[24]",
					"6[12][29]|(?:35|4[1378]|5[12457]|6[49])2|(?:56|65)[24]|(?:3[1-46-8]|46)2[013-9]"
				],
				"0$1"
			],
			[
				"(\\d{4})(\\d{5})",
				"$1 $2",
				[
					"3[1-8]|4(?:[1367]|[45][6-9]|8[4-6])|5(?:[1-5]|6[0135689]|7[4-6])|6(?:[12][3-7]|[459])",
					"3[1-8]|4(?:[1367]|[45][6-9]|8[4-6])|5(?:[1-5]|6(?:[015689]|3[02389])|7[4-6])|6(?:[12][3-7]|[459])"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[3-7]|89|9[1-9]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"[89]"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:3[1-8]|4[13-8]|5[1-7]|6[12459])\\d{7}",
				[
					9
				]
			],
			[
				"790\\d{6}|(?:39|50|6[36-8]|7[1-357]|9[1-9])\\d{7}",
				[
					9
				]
			]
		],
		"0~0"
	],
	VA: [
		"39",
		"00",
		"0\\d{5,10}|3[0-8]\\d{7,10}|55\\d{8}|8\\d{5}(?:\\d{2,4})?|(?:1\\d|39)\\d{7,8}",
		[
			6,
			7,
			8,
			9,
			10,
			11,
			12
		],
		0,
		0,
		0,
		0,
		0,
		0,
		"06698",
		[
			[
				"06698\\d{1,6}",
				[
					6,
					7,
					8,
					9,
					10,
					11
				]
			],
			[
				"3[1-9]\\d{8}|3[2-9]\\d{7}",
				[
					9,
					10
				]
			]
		]
	],
	XK: [
		"383",
		"00",
		"2\\d{7,8}|3\\d{7,11}|(?:4\\d\\d|[89]00)\\d{5}",
		[
			8,
			9,
			10,
			11,
			12
		],
		[
			[
				"(\\d{3})(\\d{5})",
				"$1 $2",
				[
					"[89]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[2-4]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"2|39"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{7,10})",
				"$1 $2",
				[
					"3"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"38\\d{6,10}|(?:2[89]|39)(?:0\\d{5,6}|[1-9]\\d{5})"
			],
			[
				"4[3-9]\\d{6}",
				[
					8
				]
			]
		]
	]
};
var nonGeographic$1 = {
};
var europe_metadata = {
	version: version$1,
	country_calling_codes: country_calling_codes$1,
	countries: countries$1,
	nonGeographic: nonGeographic$1
};

const EUROPE_COUNTRIES = {
    metadata: europe_metadata,
    list: EUROPE
};

var version = 4;
var country_calling_codes = {
	"1": [
		"US",
		"AG",
		"AI",
		"AS",
		"BB",
		"BM",
		"BS",
		"CA",
		"DM",
		"DO",
		"GD",
		"GU",
		"JM",
		"KN",
		"KY",
		"LC",
		"MP",
		"MS",
		"PR",
		"SX",
		"TC",
		"TT",
		"VC",
		"VG",
		"VI"
	],
	"7": [
		"RU",
		"KZ"
	],
	"20": [
		"EG"
	],
	"27": [
		"ZA"
	],
	"30": [
		"GR"
	],
	"31": [
		"NL"
	],
	"32": [
		"BE"
	],
	"33": [
		"FR"
	],
	"34": [
		"ES"
	],
	"36": [
		"HU"
	],
	"39": [
		"IT",
		"VA"
	],
	"40": [
		"RO"
	],
	"41": [
		"CH"
	],
	"43": [
		"AT"
	],
	"44": [
		"GB",
		"GG",
		"IM",
		"JE"
	],
	"45": [
		"DK"
	],
	"46": [
		"SE"
	],
	"47": [
		"NO",
		"SJ"
	],
	"48": [
		"PL"
	],
	"49": [
		"DE"
	],
	"51": [
		"PE"
	],
	"52": [
		"MX"
	],
	"53": [
		"CU"
	],
	"54": [
		"AR"
	],
	"55": [
		"BR"
	],
	"56": [
		"CL"
	],
	"57": [
		"CO"
	],
	"58": [
		"VE"
	],
	"60": [
		"MY"
	],
	"61": [
		"AU",
		"CC",
		"CX"
	],
	"62": [
		"ID"
	],
	"63": [
		"PH"
	],
	"64": [
		"NZ"
	],
	"65": [
		"SG"
	],
	"66": [
		"TH"
	],
	"81": [
		"JP"
	],
	"82": [
		"KR"
	],
	"84": [
		"VN"
	],
	"86": [
		"CN"
	],
	"90": [
		"TR"
	],
	"91": [
		"IN"
	],
	"92": [
		"PK"
	],
	"93": [
		"AF"
	],
	"94": [
		"LK"
	],
	"95": [
		"MM"
	],
	"98": [
		"IR"
	],
	"211": [
		"SS"
	],
	"212": [
		"MA",
		"EH"
	],
	"213": [
		"DZ"
	],
	"216": [
		"TN"
	],
	"218": [
		"LY"
	],
	"220": [
		"GM"
	],
	"221": [
		"SN"
	],
	"222": [
		"MR"
	],
	"223": [
		"ML"
	],
	"224": [
		"GN"
	],
	"225": [
		"CI"
	],
	"226": [
		"BF"
	],
	"227": [
		"NE"
	],
	"228": [
		"TG"
	],
	"229": [
		"BJ"
	],
	"230": [
		"MU"
	],
	"231": [
		"LR"
	],
	"232": [
		"SL"
	],
	"233": [
		"GH"
	],
	"234": [
		"NG"
	],
	"235": [
		"TD"
	],
	"236": [
		"CF"
	],
	"237": [
		"CM"
	],
	"238": [
		"CV"
	],
	"239": [
		"ST"
	],
	"240": [
		"GQ"
	],
	"241": [
		"GA"
	],
	"242": [
		"CG"
	],
	"243": [
		"CD"
	],
	"244": [
		"AO"
	],
	"245": [
		"GW"
	],
	"246": [
		"IO"
	],
	"247": [
		"AC"
	],
	"248": [
		"SC"
	],
	"249": [
		"SD"
	],
	"250": [
		"RW"
	],
	"251": [
		"ET"
	],
	"252": [
		"SO"
	],
	"253": [
		"DJ"
	],
	"254": [
		"KE"
	],
	"255": [
		"TZ"
	],
	"256": [
		"UG"
	],
	"257": [
		"BI"
	],
	"258": [
		"MZ"
	],
	"260": [
		"ZM"
	],
	"261": [
		"MG"
	],
	"262": [
		"RE",
		"YT"
	],
	"263": [
		"ZW"
	],
	"264": [
		"NA"
	],
	"265": [
		"MW"
	],
	"266": [
		"LS"
	],
	"267": [
		"BW"
	],
	"268": [
		"SZ"
	],
	"269": [
		"KM"
	],
	"290": [
		"SH",
		"TA"
	],
	"291": [
		"ER"
	],
	"297": [
		"AW"
	],
	"298": [
		"FO"
	],
	"299": [
		"GL"
	],
	"350": [
		"GI"
	],
	"351": [
		"PT"
	],
	"352": [
		"LU"
	],
	"353": [
		"IE"
	],
	"354": [
		"IS"
	],
	"355": [
		"AL"
	],
	"356": [
		"MT"
	],
	"357": [
		"CY"
	],
	"358": [
		"FI",
		"AX"
	],
	"359": [
		"BG"
	],
	"370": [
		"LT"
	],
	"371": [
		"LV"
	],
	"372": [
		"EE"
	],
	"373": [
		"MD"
	],
	"374": [
		"AM"
	],
	"375": [
		"BY"
	],
	"376": [
		"AD"
	],
	"377": [
		"MC"
	],
	"378": [
		"SM"
	],
	"380": [
		"UA"
	],
	"381": [
		"RS"
	],
	"382": [
		"ME"
	],
	"383": [
		"XK"
	],
	"385": [
		"HR"
	],
	"386": [
		"SI"
	],
	"387": [
		"BA"
	],
	"389": [
		"MK"
	],
	"420": [
		"CZ"
	],
	"421": [
		"SK"
	],
	"423": [
		"LI"
	],
	"500": [
		"FK"
	],
	"501": [
		"BZ"
	],
	"502": [
		"GT"
	],
	"503": [
		"SV"
	],
	"504": [
		"HN"
	],
	"505": [
		"NI"
	],
	"506": [
		"CR"
	],
	"507": [
		"PA"
	],
	"508": [
		"PM"
	],
	"509": [
		"HT"
	],
	"590": [
		"GP",
		"BL",
		"MF"
	],
	"591": [
		"BO"
	],
	"592": [
		"GY"
	],
	"593": [
		"EC"
	],
	"594": [
		"GF"
	],
	"595": [
		"PY"
	],
	"596": [
		"MQ"
	],
	"597": [
		"SR"
	],
	"598": [
		"UY"
	],
	"599": [
		"CW",
		"BQ"
	],
	"670": [
		"TL"
	],
	"672": [
		"NF"
	],
	"673": [
		"BN"
	],
	"674": [
		"NR"
	],
	"675": [
		"PG"
	],
	"676": [
		"TO"
	],
	"677": [
		"SB"
	],
	"678": [
		"VU"
	],
	"679": [
		"FJ"
	],
	"680": [
		"PW"
	],
	"681": [
		"WF"
	],
	"682": [
		"CK"
	],
	"683": [
		"NU"
	],
	"685": [
		"WS"
	],
	"686": [
		"KI"
	],
	"687": [
		"NC"
	],
	"688": [
		"TV"
	],
	"689": [
		"PF"
	],
	"690": [
		"TK"
	],
	"691": [
		"FM"
	],
	"692": [
		"MH"
	],
	"850": [
		"KP"
	],
	"852": [
		"HK"
	],
	"853": [
		"MO"
	],
	"855": [
		"KH"
	],
	"856": [
		"LA"
	],
	"880": [
		"BD"
	],
	"886": [
		"TW"
	],
	"960": [
		"MV"
	],
	"961": [
		"LB"
	],
	"962": [
		"JO"
	],
	"963": [
		"SY"
	],
	"964": [
		"IQ"
	],
	"965": [
		"KW"
	],
	"966": [
		"SA"
	],
	"967": [
		"YE"
	],
	"968": [
		"OM"
	],
	"970": [
		"PS"
	],
	"971": [
		"AE"
	],
	"972": [
		"IL"
	],
	"973": [
		"BH"
	],
	"974": [
		"QA"
	],
	"975": [
		"BT"
	],
	"976": [
		"MN"
	],
	"977": [
		"NP"
	],
	"992": [
		"TJ"
	],
	"993": [
		"TM"
	],
	"994": [
		"AZ"
	],
	"995": [
		"GE"
	],
	"996": [
		"KG"
	],
	"998": [
		"UZ"
	]
};
var countries = {
	AC: [
		"247",
		"00",
		"(?:[01589]\\d|[46])\\d{4}",
		[
			5,
			6
		],
		0,
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"6[2-467]\\d{3}",
				[
					5
				]
			],
			[
				"4\\d{4}",
				[
					5
				]
			]
		]
	],
	AD: [
		"376",
		"00",
		"(?:1|6\\d)\\d{7}|[135-9]\\d{5}",
		[
			6,
			8,
			9
		],
		[
			[
				"(\\d{3})(\\d{3})",
				"$1 $2",
				[
					"[135-9]"
				]
			],
			[
				"(\\d{4})(\\d{4})",
				"$1 $2",
				[
					"1"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"6"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"[78]\\d{5}",
				[
					6
				]
			],
			[
				"690\\d{6}|[356]\\d{5}",
				[
					6,
					9
				]
			]
		]
	],
	AE: [
		"971",
		"00",
		"(?:[4-7]\\d|9[0-689])\\d{7}|800\\d{2,9}|[2-4679]\\d{7}",
		[
			5,
			6,
			7,
			8,
			9,
			10,
			11,
			12
		],
		[
			[
				"(\\d{3})(\\d{2,9})",
				"$1 $2",
				[
					"60|8"
				]
			],
			[
				"(\\d)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[236]|[479][2-8]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d)(\\d{5})",
				"$1 $2 $3",
				[
					"[479]"
				]
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"5"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"[2-4679][2-8]\\d{6}",
				[
					8
				]
			],
			[
				"5[024-68]\\d{7}",
				[
					9
				]
			]
		]
	],
	AF: [
		"93",
		"00",
		"[2-7]\\d{8}",
		[
			9
		],
		[
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[2-7]"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:[25][0-8]|[34][0-4]|6[0-5])[2-9]\\d{6}"
			],
			[
				"7\\d{8}"
			]
		]
	],
	AG: [
		"1",
		"011",
		"(?:268|[58]\\d\\d|900)\\d{7}",
		[
			10
		],
		0,
		"1",
		0,
		"([457]\\d{6})$|1",
		"268$1",
		0,
		"268",
		[
			[
				"268(?:4(?:6[0-38]|84)|56[0-2])\\d{4}"
			],
			[
				"268(?:464|7(?:1[3-9]|[28]\\d|3[0246]|64|7[0-689]))\\d{4}"
			]
		]
	],
	AI: [
		"1",
		"011",
		"(?:264|[58]\\d\\d|900)\\d{7}",
		[
			10
		],
		0,
		"1",
		0,
		"([2457]\\d{6})$|1",
		"264$1",
		0,
		"264",
		[
			[
				"264(?:292|4(?:6[12]|9[78]))\\d{4}"
			],
			[
				"264(?:235|4(?:69|76)|5(?:3[6-9]|8[1-4])|7(?:29|72))\\d{4}"
			]
		]
	],
	AL: [
		"355",
		"00",
		"(?:700\\d\\d|900)\\d{3}|8\\d{5,7}|(?:[2-5]|6\\d)\\d{7}",
		[
			6,
			7,
			8,
			9
		],
		[
			[
				"(\\d{3})(\\d{3,4})",
				"$1 $2",
				[
					"80|9"
				],
				"0$1"
			],
			[
				"(\\d)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"4[2-6]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[2358][2-5]|4"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{5})",
				"$1 $2",
				[
					"[23578]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"6"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"4505[0-2]\\d{3}|(?:[2358][16-9]\\d[2-9]|4410)\\d{4}|(?:[2358][2-5][2-9]|4(?:[2-57-9][2-9]|6\\d))\\d{5}",
				[
					8
				]
			],
			[
				"6(?:[78][2-9]|9\\d)\\d{6}",
				[
					9
				]
			]
		]
	],
	AM: [
		"374",
		"00",
		"(?:[1-489]\\d|55|60|77)\\d{6}",
		[
			8
		],
		[
			[
				"(\\d{3})(\\d{2})(\\d{3})",
				"$1 $2 $3",
				[
					"[89]0"
				],
				"0 $1"
			],
			[
				"(\\d{3})(\\d{5})",
				"$1 $2",
				[
					"2|3[12]"
				],
				"(0$1)"
			],
			[
				"(\\d{2})(\\d{6})",
				"$1 $2",
				[
					"1|47"
				],
				"(0$1)"
			],
			[
				"(\\d{2})(\\d{6})",
				"$1 $2",
				[
					"[3-9]"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:(?:1[0-25]|47)\\d|2(?:2[2-46]|3[1-8]|4[2-69]|5[2-7]|6[1-9]|8[1-7])|3[12]2)\\d{5}"
			],
			[
				"(?:33|4[1349]|55|77|88|9[13-9])\\d{6}"
			]
		]
	],
	AO: [
		"244",
		"00",
		"[29]\\d{8}",
		[
			9
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[29]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"2\\d(?:[0134][25-9]|[25-9]\\d)\\d{5}"
			],
			[
				"9[1-79]\\d{7}"
			]
		]
	],
	AR: [
		"54",
		"00",
		"(?:11|[89]\\d\\d)\\d{8}|[2368]\\d{9}",
		[
			10,
			11
		],
		[
			[
				"(\\d{4})(\\d{2})(\\d{4})",
				"$1 $2-$3",
				[
					"2(?:2[024-9]|3[0-59]|47|6[245]|9[02-8])|3(?:3[28]|4[03-9]|5[2-46-8]|7[1-578]|8[2-9])",
					"2(?:[23]02|6(?:[25]|4[6-8])|9(?:[02356]|4[02568]|72|8[23]))|3(?:3[28]|4(?:[04679]|3[5-8]|5[4-68]|8[2379])|5(?:[2467]|3[237]|8[2-5])|7[1-578]|8(?:[2469]|3[2578]|5[4-8]|7[36-8]|8[5-8]))|2(?:2[24-9]|3[1-59]|47)",
					"2(?:[23]02|6(?:[25]|4(?:64|[78]))|9(?:[02356]|4(?:[0268]|5[2-6])|72|8[23]))|3(?:3[28]|4(?:[04679]|3[78]|5(?:4[46]|8)|8[2379])|5(?:[2467]|3[237]|8[23])|7[1-578]|8(?:[2469]|3[278]|5[56][46]|86[3-6]))|2(?:2[24-9]|3[1-59]|47)|38(?:[58][78]|7[378])|3(?:4[35][56]|58[45]|8(?:[38]5|54|76))[4-6]",
					"2(?:[23]02|6(?:[25]|4(?:64|[78]))|9(?:[02356]|4(?:[0268]|5[2-6])|72|8[23]))|3(?:3[28]|4(?:[04679]|3(?:5(?:4[0-25689]|[56])|[78])|58|8[2379])|5(?:[2467]|3[237]|8(?:[23]|4(?:[45]|60)|5(?:4[0-39]|5|64)))|7[1-578]|8(?:[2469]|3[278]|54(?:4|5[13-7]|6[89])|86[3-6]))|2(?:2[24-9]|3[1-59]|47)|38(?:[58][78]|7[378])|3(?:454|85[56])[46]|3(?:4(?:36|5[56])|8(?:[38]5|76))[4-6]"
				],
				"0$1",
				1
			],
			[
				"(\\d{2})(\\d{4})(\\d{4})",
				"$1 $2-$3",
				[
					"1"
				],
				"0$1",
				1
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1-$2-$3",
				[
					"[68]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2-$3",
				[
					"[23]"
				],
				"0$1",
				1
			],
			[
				"(\\d)(\\d{4})(\\d{2})(\\d{4})",
				"$2 15-$3-$4",
				[
					"9(?:2[2-469]|3[3-578])",
					"9(?:2(?:2[024-9]|3[0-59]|47|6[245]|9[02-8])|3(?:3[28]|4[03-9]|5[2-46-8]|7[1-578]|8[2-9]))",
					"9(?:2(?:[23]02|6(?:[25]|4[6-8])|9(?:[02356]|4[02568]|72|8[23]))|3(?:3[28]|4(?:[04679]|3[5-8]|5[4-68]|8[2379])|5(?:[2467]|3[237]|8[2-5])|7[1-578]|8(?:[2469]|3[2578]|5[4-8]|7[36-8]|8[5-8])))|92(?:2[24-9]|3[1-59]|47)",
					"9(?:2(?:[23]02|6(?:[25]|4(?:64|[78]))|9(?:[02356]|4(?:[0268]|5[2-6])|72|8[23]))|3(?:3[28]|4(?:[04679]|3[78]|5(?:4[46]|8)|8[2379])|5(?:[2467]|3[237]|8[23])|7[1-578]|8(?:[2469]|3[278]|5(?:[56][46]|[78])|7[378]|8(?:6[3-6]|[78]))))|92(?:2[24-9]|3[1-59]|47)|93(?:4[35][56]|58[45]|8(?:[38]5|54|76))[4-6]",
					"9(?:2(?:[23]02|6(?:[25]|4(?:64|[78]))|9(?:[02356]|4(?:[0268]|5[2-6])|72|8[23]))|3(?:3[28]|4(?:[04679]|3(?:5(?:4[0-25689]|[56])|[78])|5(?:4[46]|8)|8[2379])|5(?:[2467]|3[237]|8(?:[23]|4(?:[45]|60)|5(?:4[0-39]|5|64)))|7[1-578]|8(?:[2469]|3[278]|5(?:4(?:4|5[13-7]|6[89])|[56][46]|[78])|7[378]|8(?:6[3-6]|[78]))))|92(?:2[24-9]|3[1-59]|47)|93(?:4(?:36|5[56])|8(?:[38]5|76))[4-6]"
				],
				"0$1",
				0,
				"$1 $2 $3-$4"
			],
			[
				"(\\d)(\\d{2})(\\d{4})(\\d{4})",
				"$2 15-$3-$4",
				[
					"91"
				],
				"0$1",
				0,
				"$1 $2 $3-$4"
			],
			[
				"(\\d{3})(\\d{3})(\\d{5})",
				"$1-$2-$3",
				[
					"8"
				],
				"0$1"
			],
			[
				"(\\d)(\\d{3})(\\d{3})(\\d{4})",
				"$2 15-$3-$4",
				[
					"9"
				],
				"0$1",
				0,
				"$1 $2 $3-$4"
			]
		],
		"0",
		0,
		"0?(?:(11|2(?:2(?:02?|[13]|2[13-79]|4[1-6]|5[2457]|6[124-8]|7[1-4]|8[13-6]|9[1267])|3(?:02?|1[467]|2[03-6]|3[13-8]|[49][2-6]|5[2-8]|[67])|4(?:7[3-578]|9)|6(?:[0136]|2[24-6]|4[6-8]?|5[15-8])|80|9(?:0[1-3]|[19]|2\\d|3[1-6]|4[02568]?|5[2-4]|6[2-46]|72?|8[23]?))|3(?:3(?:2[79]|6|8[2578])|4(?:0[0-24-9]|[12]|3[5-8]?|4[24-7]|5[4-68]?|6[02-9]|7[126]|8[2379]?|9[1-36-8])|5(?:1|2[1245]|3[237]?|4[1-46-9]|6[2-4]|7[1-6]|8[2-5]?)|6[24]|7(?:[069]|1[1568]|2[15]|3[145]|4[13]|5[14-8]|7[2-57]|8[126])|8(?:[01]|2[15-7]|3[2578]?|4[13-6]|5[4-8]?|6[1-357-9]|7[36-8]?|8[5-8]?|9[124])))15)?",
		"9$1",
		0,
		0,
		[
			[
				"3(?:7(?:1[15]|81)|8(?:21|4[16]|69|9[12]))[46]\\d{5}|(?:2(?:2(?:2[59]|44|52)|3(?:26|44)|47[35]|9(?:[07]2|2[26]|34|46))|3327)[45]\\d{5}|(?:2(?:657|9(?:54|66))|3(?:48[27]|7(?:55|77)|8(?:65|78)))[2-8]\\d{5}|(?:2(?:284|3(?:02|23)|477|622|920)|3(?:4(?:46|89|92)|541))[2-7]\\d{5}|(?:(?:11[1-8]|670)\\d|2(?:2(?:0[45]|1[2-6]|3[3-6])|3(?:[06]4|7[45])|494|6(?:04|1[2-8]|[36][45]|4[3-6])|80[45]|9(?:[17][4-6]|[48][45]|9[3-6]))|3(?:364|4(?:1[2-8]|[25][4-6]|3[3-6]|84)|5(?:1[2-9]|[38][4-6])|6(?:2[45]|44)|7[069][45]|8(?:0[45]|1[2-7]|3[4-6]|5[3-6]|7[2-6]|8[3-68])))\\d{6}|(?:2(?:2(?:62|81)|320|9(?:42|83))|3(?:329|4(?:62|7[16])|5(?:43|64)|7(?:18|5[17])))[2-6]\\d{5}|2(?:2(?:21|4[23]|6[145]|7[1-4]|8[356]|9[267])|3(?:16|3[13-8]|43|5[346-8]|9[3-5])|6(?:2[46]|4[78]|5[1568])|9(?:03|2[1457-9]|3[1356]|4[08]|[56][23]|82))4\\d{5}|(?:2(?:257|3(?:24|46|92)|9(?:01|23|64))|3(?:4(?:42|64)|5(?:25|37|4[47]|71)|7(?:35|72)|825))[3-6]\\d{5}|(?:2(?:2(?:02|2[3467]|4[156]|5[45]|6[6-8]|91)|3(?:1[47]|25|[45][25]|96)|47[48]|625|932)|3(?:38[2578]|4(?:0[0-24-9]|3[78]|4[457]|58|6[035-9]|72|83|9[136-8])|5(?:2[124]|[368][23]|4[2689]|7[2-6])|7(?:16|2[15]|3[14]|4[13]|5[468]|7[3-5]|8[26])|8(?:2[67]|3[278]|4[3-5]|5[78]|6[1-378]|[78]7|94)))[4-6]\\d{5}",
				[
					10
				]
			],
			[
				"93(?:7(?:1[15]|81)|8(?:21|4[16]|69|9[12]))[46]\\d{5}|9(?:2(?:2(?:2[59]|44|52)|3(?:26|44)|47[35]|9(?:[07]2|2[26]|34|46))|3327)[45]\\d{5}|9(?:2(?:657|9(?:54|66))|3(?:48[27]|7(?:55|77)|8(?:65|78)))[2-8]\\d{5}|9(?:2(?:284|3(?:02|23)|477|622|920)|3(?:4(?:46|89|92)|541))[2-7]\\d{5}|(?:675\\d|9(?:11[1-8]\\d|2(?:2(?:0[45]|1[2-6]|3[3-6])|3(?:[06]4|7[45])|494|6(?:04|1[2-8]|[36][45]|4[3-6])|80[45]|9(?:[17][4-6]|[48][45]|9[3-6]))|3(?:364|4(?:1[2-8]|[25][4-6]|3[3-6]|84)|5(?:1[2-9]|[38][4-6])|6(?:2[45]|44)|7[069][45]|8(?:0[45]|1[2-7]|3[4-6]|5[3-6]|7[2-6]|8[3-68]))))\\d{6}|9(?:2(?:2(?:62|81)|320|9(?:42|83))|3(?:329|4(?:62|7[16])|5(?:43|64)|7(?:18|5[17])))[2-6]\\d{5}|92(?:2(?:21|4[23]|6[145]|7[1-4]|8[356]|9[267])|3(?:16|3[13-8]|43|5[346-8]|9[3-5])|6(?:2[46]|4[78]|5[1568])|9(?:03|2[1457-9]|3[1356]|4[08]|[56][23]|82))4\\d{5}|9(?:2(?:257|3(?:24|46|92)|9(?:01|23|64))|3(?:4(?:42|64)|5(?:25|37|4[47]|71)|7(?:35|72)|825))[3-6]\\d{5}|9(?:2(?:2(?:02|2[3467]|4[156]|5[45]|6[6-8]|91)|3(?:1[47]|25|[45][25]|96)|47[48]|625|932)|3(?:38[2578]|4(?:0[0-24-9]|3[78]|4[457]|58|6[035-9]|72|83|9[136-8])|5(?:2[124]|[368][23]|4[2689]|7[2-6])|7(?:16|2[15]|3[14]|4[13]|5[468]|7[3-5]|8[26])|8(?:2[67]|3[278]|4[3-5]|5[78]|6[1-378]|[78]7|94)))[4-6]\\d{5}"
			]
		]
	],
	AS: [
		"1",
		"011",
		"(?:[58]\\d\\d|684|900)\\d{7}",
		[
			10
		],
		0,
		"1",
		0,
		"([267]\\d{6})$|1",
		"684$1",
		0,
		"684",
		[
			[
				"6846(?:22|33|44|55|77|88|9[19])\\d{4}"
			],
			[
				"684(?:2(?:48|5[2468]|7[26])|7(?:3[13]|70|82))\\d{4}"
			]
		]
	],
	AT: [
		"43",
		"00",
		"1\\d{3,12}|2\\d{6,12}|43(?:(?:0\\d|5[02-9])\\d{3,9}|2\\d{4,5}|[3467]\\d{4}|8\\d{4,6}|9\\d{4,7})|5\\d{4,12}|8\\d{7,12}|9\\d{8,12}|(?:[367]\\d|4[0-24-9])\\d{4,11}",
		[
			4,
			5,
			6,
			7,
			8,
			9,
			10,
			11,
			12,
			13
		],
		[
			[
				"(\\d)(\\d{3,12})",
				"$1 $2",
				[
					"1(?:11|[2-9])"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{2})",
				"$1 $2",
				[
					"517"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3,5})",
				"$1 $2",
				[
					"5[079]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3,10})",
				"$1 $2",
				[
					"(?:31|4)6|51|6(?:48|5[0-3579]|[6-9])|7(?:20|32|8)|[89]",
					"(?:31|4)6|51|6(?:485|5[0-3579]|[6-9])|7(?:20|32|8)|[89]"
				],
				"0$1"
			],
			[
				"(\\d{4})(\\d{3,9})",
				"$1 $2",
				[
					"[2-467]|5[2-6]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"5"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{4})(\\d{4,7})",
				"$1 $2 $3",
				[
					"5"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"1(?:11\\d|[2-9]\\d{3,11})|(?:316|463)\\d{3,10}|648[34]\\d{3,9}|(?:51|66|73)2\\d{3,10}|(?:2(?:1[467]|2[13-8]|5[2357]|6[1-46-8]|7[1-8]|8[124-7]|9[1458])|3(?:1[1-578]|3[23568]|4[5-7]|5[1378]|6[1-38]|8[3-68])|4(?:2[1-8]|35|7[1368]|8[2457])|5(?:2[1-8]|3[357]|4[147]|5[12578]|6[37])|6(?:13|2[1-47]|4[135-7]|5[468])|7(?:2[1-8]|35|4[13478]|5[68]|6[16-8]|7[1-6]|9[45]))\\d{4,10}"
			],
			[
				"6(?:485|(?:5[0-3579]|6[013-9]|[7-9]\\d)\\d)\\d{3,9}",
				[
					7,
					8,
					9,
					10,
					11,
					12,
					13
				]
			]
		]
	],
	AU: [
		"61",
		"001[14-689]|14(?:1[14]|34|4[17]|[56]6|7[47]|88)0011",
		"1(?:[0-79]\\d{7}(?:\\d(?:\\d{2})?)?|8[0-24-9]\\d{7})|[2-478]\\d{8}|1\\d{4,7}",
		[
			5,
			6,
			7,
			8,
			9,
			10,
			12
		],
		[
			[
				"(\\d{2})(\\d{3,4})",
				"$1 $2",
				[
					"16"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{2,4})",
				"$1 $2 $3",
				[
					"16"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"14|4"
				],
				"0$1"
			],
			[
				"(\\d)(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"[2378]"
				],
				"(0$1)"
			],
			[
				"(\\d{4})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"1(?:30|[89])"
				]
			]
		],
		"0",
		0,
		"(183[12])|0",
		0,
		0,
		0,
		[
			[
				"(?:(?:2(?:(?:[0-26-9]\\d|3[0-8]|5[0135-9])\\d|4(?:[02-9]\\d|10))|3(?:(?:[0-3589]\\d|6[1-9]|7[0-35-9])\\d|4(?:[0-578]\\d|90))|7(?:[013-57-9]\\d|2[0-8])\\d)\\d\\d|8(?:51(?:0(?:0[03-9]|[12479]\\d|3[2-9]|5[0-8]|6[1-9]|8[0-7])|1(?:[0235689]\\d|1[0-69]|4[0-589]|7[0-47-9])|2(?:0[0-79]|[18][13579]|2[14-9]|3[0-46-9]|[4-6]\\d|7[89]|9[0-4])|[34]\\d\\d)|(?:6[0-8]|[78]\\d)\\d{3}|9(?:[02-9]\\d{3}|1(?:(?:[0-58]\\d|6[0135-9])\\d|7(?:0[0-24-9]|[1-9]\\d)|9(?:[0-46-9]\\d|5[0-79])))))\\d{3}",
				[
					9
				]
			],
			[
				"4(?:79[01]|83[0-36-9])\\d{5}|4(?:[0-36]\\d|4[047-9]|[58][0-24-9]|7[02-8]|9[0-47-9])\\d{6}",
				[
					9
				]
			]
		],
		"0011"
	],
	AW: [
		"297",
		"00",
		"(?:[25-79]\\d\\d|800)\\d{4}",
		[
			7
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"[25-9]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"5(?:2\\d|8[1-9])\\d{4}"
			],
			[
				"(?:290|5[69]\\d|6(?:[03]0|22|4[0-2]|[69]\\d)|7(?:[34]\\d|7[07])|9(?:6[45]|9[4-8]))\\d{4}"
			]
		]
	],
	AX: [
		"358",
		"00|99(?:[01469]|5(?:[14]1|3[23]|5[59]|77|88|9[09]))",
		"2\\d{4,9}|35\\d{4,5}|(?:60\\d\\d|800)\\d{4,6}|7\\d{5,11}|(?:[14]\\d|3[0-46-9]|50)\\d{4,8}",
		[
			5,
			6,
			7,
			8,
			9,
			10,
			11,
			12
		],
		0,
		"0",
		0,
		0,
		0,
		0,
		"18",
		[
			[
				"18[1-8]\\d{3,6}",
				[
					6,
					7,
					8,
					9
				]
			],
			[
				"4946\\d{2,6}|(?:4[0-8]|50)\\d{4,8}",
				[
					6,
					7,
					8,
					9,
					10
				]
			]
		],
		"00"
	],
	AZ: [
		"994",
		"00",
		"365\\d{6}|(?:[124579]\\d|60|88)\\d{7}",
		[
			9
		],
		[
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"90"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"1[28]|2|365|46",
					"1[28]|2|365[45]|46",
					"1[28]|2|365(?:4|5[02])|46"
				],
				"(0$1)"
			],
			[
				"(\\d{2})(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[13-9]"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:2[12]428|3655[02])\\d{4}|(?:2(?:22[0-79]|63[0-28])|3654)\\d{5}|(?:(?:1[28]|46)\\d|2(?:[014-6]2|[23]3))\\d{6}"
			],
			[
				"36554\\d{4}|(?:[16]0|4[04]|5[015]|7[07]|99)\\d{7}"
			]
		]
	],
	BA: [
		"387",
		"00",
		"6\\d{8}|(?:[35689]\\d|49|70)\\d{6}",
		[
			8,
			9
		],
		[
			[
				"(\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"6[1-3]|[7-9]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3})",
				"$1 $2-$3",
				[
					"[3-5]|6[56]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{3})",
				"$1 $2 $3 $4",
				[
					"6"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:3(?:[05-79][2-9]|1[4579]|[23][24-9]|4[2-4689]|8[2457-9])|49[2-579]|5(?:0[2-49]|[13][2-9]|[268][2-4679]|4[4689]|5[2-79]|7[2-69]|9[2-4689]))\\d{5}",
				[
					8
				]
			],
			[
				"6040\\d{5}|6(?:03|[1-356]|44|7\\d)\\d{6}"
			]
		]
	],
	BB: [
		"1",
		"011",
		"(?:246|[58]\\d\\d|900)\\d{7}",
		[
			10
		],
		0,
		"1",
		0,
		"([2-9]\\d{6})$|1",
		"246$1",
		0,
		"246",
		[
			[
				"246521[0369]\\d{3}|246(?:2(?:2[78]|7[0-4])|4(?:1[024-6]|2\\d|3[2-9])|5(?:20|[34]\\d|54|7[1-3])|6(?:2\\d|38)|7[35]7|9(?:1[89]|63))\\d{4}"
			],
			[
				"246(?:(?:2(?:[3568]\\d|4[0-57-9])|3(?:5[2-9]|6[0-6])|4(?:46|5\\d)|69[5-7]|8(?:[2-5]\\d|83))\\d|52(?:1[147]|20))\\d{3}"
			]
		]
	],
	BD: [
		"880",
		"00",
		"[1-469]\\d{9}|8[0-79]\\d{7,8}|[2-79]\\d{8}|[2-9]\\d{7}|[3-9]\\d{6}|[57-9]\\d{5}",
		[
			6,
			7,
			8,
			9,
			10
		],
		[
			[
				"(\\d{2})(\\d{4,6})",
				"$1-$2",
				[
					"31[5-8]|[459]1"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3,7})",
				"$1-$2",
				[
					"3(?:[67]|8[013-9])|4(?:6[168]|7|[89][18])|5(?:6[128]|9)|6(?:[15]|28|4[14])|7[2-589]|8(?:0[014-9]|[12])|9[358]|(?:3[2-5]|4[235]|5[2-578]|6[0389]|76|8[3-7]|9[24])1|(?:44|66)[01346-9]"
				],
				"0$1"
			],
			[
				"(\\d{4})(\\d{3,6})",
				"$1-$2",
				[
					"[13-9]|2[23]"
				],
				"0$1"
			],
			[
				"(\\d)(\\d{7,8})",
				"$1-$2",
				[
					"2"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:4(?:31\\d\\d|423)|5222)\\d{3}(?:\\d{2})?|8332[6-9]\\d\\d|(?:3(?:03[56]|224)|4(?:22[25]|653))\\d{3,4}|(?:3(?:42[47]|529|823)|4(?:027|525|65(?:28|8))|562|6257|7(?:1(?:5[3-5]|6[12]|7[156]|89)|22[589]56|32|42675|52(?:[25689](?:56|8)|[347]8)|71(?:6[1267]|75|89)|92374)|82(?:2[59]|32)56|9(?:03[23]56|23(?:256|373)|31|5(?:1|2[4589]56)))\\d{3}|(?:3(?:02[348]|22[35]|324|422)|4(?:22[67]|32[236-9]|6(?:2[46]|5[57])|953)|5526|6(?:024|6655)|81)\\d{4,5}|(?:2(?:7(?:1[0-267]|2[0-289]|3[0-29]|4[01]|5[1-3]|6[013]|7[0178]|91)|8(?:0[125]|1[1-6]|2[0157-9]|3[1-69]|41|6[1-35]|7[1-5]|8[1-8]|9[0-6])|9(?:0[0-2]|1[0-4]|2[568]|3[3-6]|5[5-7]|6[0136-9]|7[0-7]|8[014-9]))|3(?:0(?:2[025-79]|3[2-4])|181|22[12]|32[2356]|824)|4(?:02[09]|22[348]|32[045]|523|6(?:27|54))|666(?:22|53)|7(?:22[57-9]|42[56]|82[35])8|8(?:0[124-9]|2(?:181|2[02-4679]8)|4[12]|[5-7]2)|9(?:[04]2|2(?:2|328)|81))\\d{4}|(?:2(?:[23]\\d|[45])\\d\\d|3(?:1(?:2[5-7]|[5-7])|425|822)|4(?:033|1\\d|[257]1|332|4(?:2[246]|5[25])|6(?:2[35]|56|62)|8(?:23|54)|92[2-5])|5(?:02[03489]|22[457]|32[35-79]|42[46]|6(?:[18]|53)|724|826)|6(?:023|2(?:2[2-5]|5[3-5]|8)|32[3478]|42[34]|52[47]|6(?:[18]|6(?:2[34]|5[24]))|[78]2[2-5]|92[2-6])|7(?:02|21\\d|[3-589]1|6[12]|72[24])|8(?:217|3[12]|[5-7]1)|9[24]1)\\d{5}|(?:(?:3[2-8]|5[2-57-9]|6[03-589])1|4[4689][18])\\d{5}|[59]1\\d{5}"
			],
			[
				"(?:1[13-9]\\d|644)\\d{7}|(?:3[78]|44|66)[02-9]\\d{7}",
				[
					10
				]
			]
		]
	],
	BE: [
		"32",
		"00",
		"4\\d{8}|[1-9]\\d{7}",
		[
			8,
			9
		],
		[
			[
				"(\\d{3})(\\d{2})(\\d{3})",
				"$1 $2 $3",
				[
					"(?:80|9)0"
				],
				"0$1"
			],
			[
				"(\\d)(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[239]|4[23]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[15-8]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"4"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"80[2-8]\\d{5}|(?:1[0-69]|[23][2-8]|4[23]|5\\d|6[013-57-9]|71|8[1-79]|9[2-4])\\d{6}",
				[
					8
				]
			],
			[
				"4[5-9]\\d{7}",
				[
					9
				]
			]
		]
	],
	BF: [
		"226",
		"00",
		"(?:[025-7]\\d|44)\\d{6}",
		[
			8
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[024-7]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"2(?:0(?:49|5[23]|6[5-7]|9[016-9])|4(?:4[569]|5[4-6]|6[5-7]|7[0179])|5(?:[34]\\d|50|6[5-7]))\\d{4}"
			],
			[
				"(?:0[1-7]|44|5[0-8]|[67]\\d)\\d{6}"
			]
		]
	],
	BG: [
		"359",
		"00",
		"00800\\d{7}|[2-7]\\d{6,7}|[89]\\d{6,8}|2\\d{5}",
		[
			6,
			7,
			8,
			9,
			12
		],
		[
			[
				"(\\d)(\\d)(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"2"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"43[1-6]|70[1-9]"
				],
				"0$1"
			],
			[
				"(\\d)(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"2"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{2,3})",
				"$1 $2 $3",
				[
					"[356]|4[124-7]|7[1-9]|8[1-6]|9[1-7]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{2})(\\d{3})",
				"$1 $2 $3",
				[
					"(?:70|8)0"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{2})",
				"$1 $2 $3",
				[
					"43[1-7]|7"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"[48]|9[08]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"9"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"2\\d{5,7}|(?:43[1-6]|70[1-9])\\d{4,5}|(?:[36]\\d|4[124-7]|[57][1-9]|8[1-6]|9[1-7])\\d{5,6}",
				[
					6,
					7,
					8
				]
			],
			[
				"(?:43[07-9]|99[69]\\d)\\d{5}|(?:8[7-9]|98)\\d{7}",
				[
					8,
					9
				]
			]
		]
	],
	BH: [
		"973",
		"00",
		"[136-9]\\d{7}",
		[
			8
		],
		[
			[
				"(\\d{4})(\\d{4})",
				"$1 $2",
				[
					"[13679]|8[02-4679]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:1(?:3[1356]|6[0156]|7\\d)\\d|6(?:1[16]\\d|500|6(?:0\\d|3[12]|44|55|7[7-9]|88)|9[69][69])|7(?:[07]\\d\\d|1(?:11|78)))\\d{4}"
			],
			[
				"(?:3(?:[0-79]\\d|8[0-57-9])\\d|6(?:3(?:00|33|6[16])|441|6(?:3[03-9]|[69]\\d|7[0-689])))\\d{4}"
			]
		]
	],
	BI: [
		"257",
		"00",
		"(?:[267]\\d|31)\\d{6}",
		[
			8
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[2367]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:22|31)\\d{6}"
			],
			[
				"(?:29|6[124-9]|7[125-9])\\d{6}"
			]
		]
	],
	BJ: [
		"229",
		"00",
		"(?:01\\d|[24-689])\\d{7}",
		[
			8,
			10
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[24-689]"
				]
			],
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4 $5",
				[
					"0"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"2090\\d{4}|(?:012\\d\\d|2(?:02|1[037]|2[45]|3[68]|4\\d))\\d{5}"
			],
			[
				"(?:01(?:2[5-9]|[4-69]\\d)|4[0-8]|[56]\\d|9[013-9])\\d{6}"
			]
		]
	],
	BL: [
		"590",
		"00",
		"(?:590\\d|7090)\\d{5}|(?:69|80|9\\d)\\d{7}",
		[
			9
		],
		0,
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"590(?:2[7-9]|3[3-7]|5[12]|87)\\d{4}"
			],
			[
				"(?:69(?:0\\d\\d|1(?:2[2-9]|3[0-5])|4(?:0[89]|1[2-6]|9\\d)|6(?:1[016-9]|5[0-4]|[67]\\d))|7090[0-4])\\d{4}"
			]
		]
	],
	BM: [
		"1",
		"011",
		"(?:441|[58]\\d\\d|900)\\d{7}",
		[
			10
		],
		0,
		"1",
		0,
		"([2-9]\\d{6})$|1",
		"441$1",
		0,
		"441",
		[
			[
				"441(?:[46]\\d\\d|5(?:4\\d|60|89))\\d{4}"
			],
			[
				"441(?:[2378]\\d|5[0-39]|9[02])\\d{5}"
			]
		]
	],
	BN: [
		"673",
		"00",
		"[2-578]\\d{6}",
		[
			7
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"[2-578]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"22[0-7]\\d{4}|(?:2[013-9]|[34]\\d|5[0-25-9])\\d{5}"
			],
			[
				"(?:22[89]|[78]\\d\\d)\\d{4}"
			]
		]
	],
	BO: [
		"591",
		"00(?:1\\d)?",
		"8001\\d{5}|(?:[2-467]\\d|50)\\d{6}",
		[
			8,
			9
		],
		[
			[
				"(\\d)(\\d{7})",
				"$1 $2",
				[
					"[235]|4[46]"
				]
			],
			[
				"(\\d{8})",
				"$1",
				[
					"[67]"
				]
			],
			[
				"(\\d{3})(\\d{2})(\\d{4})",
				"$1 $2 $3",
				[
					"8"
				]
			]
		],
		"0",
		0,
		"0(1\\d)?",
		0,
		0,
		0,
		[
			[
				"(?:2(?:2\\d\\d|5(?:11|[258]\\d|9[67])|6(?:12|2\\d|9[34])|8(?:2[34]|39|62))|3(?:3\\d\\d|4(?:6\\d|8[24])|8(?:25|42|5[257]|86|9[25])|9(?:[27]\\d|3[2-4]|4[248]|5[24]|6[2-6]))|4(?:4\\d\\d|6(?:11|[24689]\\d|72)))\\d{4}",
				[
					8
				]
			],
			[
				"[67]\\d{7}",
				[
					8
				]
			]
		]
	],
	BQ: [
		"599",
		"00",
		"(?:[34]1|7\\d)\\d{5}",
		[
			7
		],
		0,
		0,
		0,
		0,
		0,
		0,
		"[347]",
		[
			[
				"(?:318[023]|41(?:6[023]|70)|7(?:1[578]|2[05]|50)\\d)\\d{3}"
			],
			[
				"(?:31(?:8[14-8]|9[14578])|416[14-9]|7(?:0[01]|7[07]|8\\d|9[056])\\d)\\d{3}"
			]
		]
	],
	BR: [
		"55",
		"00(?:1[245]|2[1-35]|31|4[13]|[56]5|99)",
		"[1-467]\\d{9,10}|55[0-46-9]\\d{8}|[34]\\d{7}|55\\d{7,8}|(?:5[0-46-9]|[89]\\d)\\d{7,9}",
		[
			8,
			9,
			10,
			11
		],
		[
			[
				"(\\d{4})(\\d{4})",
				"$1-$2",
				[
					"300|4(?:0[02]|37|86)",
					"300|4(?:0(?:0|20)|370|864)"
				]
			],
			[
				"(\\d{3})(\\d{2,3})(\\d{4})",
				"$1 $2 $3",
				[
					"(?:[358]|90)0"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{4})(\\d{4})",
				"$1 $2-$3",
				[
					"(?:[14689][1-9]|2[12478]|3[1-578]|5[13-5]|7[13-579])[2-57]"
				],
				"($1)"
			],
			[
				"(\\d{2})(\\d{5})(\\d{4})",
				"$1 $2-$3",
				[
					"[16][1-9]|[2-57-9]"
				],
				"($1)"
			]
		],
		"0",
		0,
		"(?:0|90)(?:(1[245]|2[1-35]|31|4[13]|[56]5|99)(\\d{10,11}))?",
		"$2",
		0,
		0,
		[
			[
				"(?:[14689][1-9]|2[12478]|3[1-578]|5[13-5]|7[13-579])[2-5]\\d{7}",
				[
					10
				]
			],
			[
				"(?:[14689][1-9]|2[12478]|3[1-578]|5[13-5]|7[13-579])(?:7|9\\d)\\d{7}",
				[
					10,
					11
				]
			]
		]
	],
	BS: [
		"1",
		"011",
		"(?:242|[58]\\d\\d|900)\\d{7}",
		[
			10
		],
		0,
		"1",
		0,
		"([3-8]\\d{6})$|1",
		"242$1",
		0,
		"242",
		[
			[
				"242(?:3(?:02|[236][1-9]|4[0-24-9]|5[0-68]|7[347]|8[0-4]|9[2-467])|461|502|6(?:0[1-5]|12|2[013]|[45]0|7[67]|8[78]|9[89])|7(?:02|88))\\d{4}"
			],
			[
				"242(?:3(?:5[79]|7[56]|95)|4(?:[23][1-9]|4[1-35-9]|5[1-8]|6[2-8]|7\\d|81)|5(?:2[45]|3[35]|44|5[1-46-9]|65|77)|6[34]6|7(?:27|38)|8(?:0[1-9]|1[02-9]|2\\d|3[0-4]|[89]9))\\d{4}"
			]
		]
	],
	BT: [
		"975",
		"00",
		"[178]\\d{7}|[2-8]\\d{6}",
		[
			7,
			8
		],
		[
			[
				"(\\d)(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[2-6]|7[246]|8[2-4]"
				]
			],
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"1[67]|[78]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:2[3-6]|[34][5-7]|5[236]|6[2-46]|7[246]|8[2-4])\\d{5}",
				[
					7
				]
			],
			[
				"(?:1[67]|[78]7)\\d{6}",
				[
					8
				]
			]
		]
	],
	BW: [
		"267",
		"00",
		"(?:0800|(?:[37]|800)\\d)\\d{6}|(?:[2-6]\\d|90)\\d{5}",
		[
			7,
			8,
			10
		],
		[
			[
				"(\\d{2})(\\d{5})",
				"$1 $2",
				[
					"90"
				]
			],
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"[24-6]|3[15-9]"
				]
			],
			[
				"(\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[37]"
				]
			],
			[
				"(\\d{4})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"0"
				]
			],
			[
				"(\\d{3})(\\d{4})(\\d{3})",
				"$1 $2 $3",
				[
					"8"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:2(?:4[0-48]|6[0-24]|9[0578])|3(?:1[0-35-9]|55|[69]\\d|7[013]|81)|4(?:6[03]|7[1267]|9[0-5])|5(?:3[03489]|4[0489]|7[1-47]|88|9[0-49])|6(?:2[1-35]|5[149]|8[013467]))\\d{4}",
				[
					7
				]
			],
			[
				"(?:321|7[1-8]\\d)\\d{5}",
				[
					8
				]
			]
		]
	],
	BY: [
		"375",
		"810",
		"(?:[12]\\d|33|44|902)\\d{7}|8(?:0[0-79]\\d{5,7}|[1-7]\\d{9})|8(?:1[0-489]|[5-79]\\d)\\d{7}|8[1-79]\\d{6,7}|8[0-79]\\d{5}|8\\d{5}",
		[
			6,
			7,
			8,
			9,
			10,
			11
		],
		[
			[
				"(\\d{3})(\\d{3})",
				"$1 $2",
				[
					"800"
				],
				"8 $1"
			],
			[
				"(\\d{3})(\\d{2})(\\d{2,4})",
				"$1 $2 $3",
				[
					"800"
				],
				"8 $1"
			],
			[
				"(\\d{4})(\\d{2})(\\d{3})",
				"$1 $2-$3",
				[
					"1(?:5[169]|6[3-5]|7[179])|2(?:1[35]|2[34]|3[3-5])",
					"1(?:5[169]|6(?:3[1-3]|4|5[125])|7(?:1[3-9]|7[0-24-6]|9[2-7]))|2(?:1[35]|2[34]|3[3-5])"
				],
				"8 0$1"
			],
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2-$3-$4",
				[
					"1(?:[56]|7[467])|2[1-3]"
				],
				"8 0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2-$3-$4",
				[
					"[1-4]"
				],
				"8 0$1"
			],
			[
				"(\\d{3})(\\d{3,4})(\\d{4})",
				"$1 $2 $3",
				[
					"[89]"
				],
				"8 $1"
			]
		],
		"8",
		0,
		"0|80?",
		0,
		0,
		0,
		[
			[
				"(?:1(?:5(?:1[1-5]|[24]\\d|6[2-4]|9[1-7])|6(?:[235]\\d|4[1-7])|7\\d\\d)|2(?:1(?:[246]\\d|3[0-35-9]|5[1-9])|2(?:[235]\\d|4[0-8])|3(?:[26]\\d|3[02-79]|4[024-7]|5[03-7])))\\d{5}",
				[
					9
				]
			],
			[
				"(?:2(?:5[5-79]|9[1-9])|(?:33|44)\\d)\\d{6}",
				[
					9
				]
			]
		],
		"8~10"
	],
	BZ: [
		"501",
		"00",
		"(?:0800\\d|[2-8])\\d{6}",
		[
			7,
			11
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1-$2",
				[
					"[2-8]"
				]
			],
			[
				"(\\d)(\\d{3})(\\d{4})(\\d{3})",
				"$1-$2-$3-$4",
				[
					"0"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:2(?:[02]\\d|36|[68]0)|[3-58](?:[02]\\d|[68]0)|7(?:[02]\\d|32|[68]0))\\d{4}",
				[
					7
				]
			],
			[
				"6[0-35-7]\\d{5}",
				[
					7
				]
			]
		]
	],
	CA: [
		"1",
		"011",
		"[2-9]\\d{9}|3\\d{6}",
		[
			7,
			10
		],
		0,
		"1",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:2(?:04|[23]6|[48]9|5[07]|63)|3(?:06|43|54|6[578]|82)|4(?:03|1[68]|[26]8|3[178]|50|74)|5(?:06|1[49]|48|79|8[147])|6(?:04|[18]3|39|47|72)|7(?:0[59]|42|53|78|8[02])|8(?:[06]7|19|25|7[39])|9(?:0[25]|42))[2-9]\\d{6}",
				[
					10
				]
			],
			[
				"(?:2(?:04|[23]6|[48]9|5[07]|63)|3(?:06|43|54|6[578]|82)|4(?:03|1[68]|[26]8|3[178]|50|74)|5(?:06|1[49]|48|79|8[147])|6(?:04|[18]3|39|47|72)|7(?:0[59]|42|53|78|8[02])|8(?:[06]7|19|25|7[39])|9(?:0[25]|42))[2-9]\\d{6}",
				[
					10
				]
			]
		]
	],
	CC: [
		"61",
		"001[14-689]|14(?:1[14]|34|4[17]|[56]6|7[47]|88)0011",
		"1(?:[0-79]\\d{8}(?:\\d{2})?|8[0-24-9]\\d{7})|[148]\\d{8}|1\\d{5,7}",
		[
			6,
			7,
			8,
			9,
			10,
			12
		],
		0,
		"0",
		0,
		"([59]\\d{7})$|0",
		"8$1",
		0,
		0,
		[
			[
				"8(?:51(?:0(?:02|31|60|89)|1(?:18|76)|223)|91(?:0(?:1[0-2]|29)|1(?:[28]2|50|79)|2(?:10|64)|3(?:[06]8|22)|4[29]8|62\\d|70[23]|959))\\d{3}",
				[
					9
				]
			],
			[
				"4(?:79[01]|83[0-36-9])\\d{5}|4(?:[0-36]\\d|4[047-9]|[58][0-24-9]|7[02-8]|9[0-47-9])\\d{6}",
				[
					9
				]
			]
		],
		"0011"
	],
	CD: [
		"243",
		"00",
		"(?:(?:[189]|5\\d)\\d|2)\\d{7}|[1-68]\\d{6}",
		[
			7,
			8,
			9,
			10
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{3})",
				"$1 $2 $3",
				[
					"88"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{5})",
				"$1 $2",
				[
					"[1-6]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{2})(\\d{4})",
				"$1 $2 $3",
				[
					"2"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"1"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[89]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3 $4",
				[
					"5"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:(?:12|573)\\d\\d|276)\\d{5}|[1-6]\\d{6}"
			],
			[
				"88\\d{5}|(?:8[0-69]|9[017-9])\\d{7}",
				[
					7,
					9
				]
			]
		]
	],
	CF: [
		"236",
		"00",
		"(?:[27]\\d{3}|8776)\\d{4}",
		[
			8
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[278]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"2[12]\\d{6}"
			],
			[
				"7[024-7]\\d{6}"
			]
		]
	],
	CG: [
		"242",
		"00",
		"222\\d{6}|(?:0\\d|80)\\d{7}",
		[
			9
		],
		[
			[
				"(\\d)(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"8"
				]
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[02]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"222[1-589]\\d{5}"
			],
			[
				"026(?:1[0-5]|6[6-9])\\d{4}|0(?:[14-6]\\d\\d|2(?:40|5[5-8]|6[07-9]))\\d{5}"
			]
		]
	],
	CH: [
		"41",
		"00",
		"8\\d{11}|[2-9]\\d{8}",
		[
			9,
			12
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"8[047]|90"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[2-79]|81"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{2})(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4 $5",
				[
					"8"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:2[12467]|3[1-4]|4[134]|5[256]|6[12]|[7-9]1)\\d{7}",
				[
					9
				]
			],
			[
				"(?:6[89]|7[235-9])\\d{7}",
				[
					9
				]
			]
		]
	],
	CI: [
		"225",
		"00",
		"[02]\\d{9}",
		[
			10
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d)(\\d{5})",
				"$1 $2 $3 $4",
				[
					"2"
				]
			],
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{4})",
				"$1 $2 $3 $4",
				[
					"0"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"2(?:[15]\\d{3}|7(?:2(?:0[23]|1[2357]|2[245]|3[45]|4[3-5])|3(?:06|1[69]|[2-6]7)))\\d{5}"
			],
			[
				"0[157]\\d{8}"
			]
		]
	],
	CK: [
		"682",
		"00",
		"[2-578]\\d{4}",
		[
			5
		],
		[
			[
				"(\\d{2})(\\d{3})",
				"$1 $2",
				[
					"[2-578]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:2\\d|3[13-7]|4[1-5])\\d{3}"
			],
			[
				"[578]\\d{4}"
			]
		]
	],
	CL: [
		"56",
		"(?:0|1(?:1[0-69]|2[02-5]|5[13-58]|69|7[0167]|8[018]))0",
		"12300\\d{6}|6\\d{9,10}|[2-9]\\d{8}",
		[
			9,
			10,
			11
		],
		[
			[
				"(\\d{5})(\\d{4})",
				"$1 $2",
				[
					"219",
					"2196"
				],
				"($1)"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"60|809"
				]
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"44"
				]
			],
			[
				"(\\d)(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"2[1-36]"
				],
				"($1)"
			],
			[
				"(\\d)(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"9(?:10|[2-9])"
				]
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"3[2-5]|[47]|5[1-3578]|6[13-57]|8(?:0[1-8]|[1-9])"
				],
				"($1)"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"60|8"
				]
			],
			[
				"(\\d{4})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"1"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{2})(\\d{3})",
				"$1 $2 $3 $4",
				[
					"60"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"2(?:1982[0-6]|3314[05-9])\\d{3}|(?:2(?:1(?:160|962)|2\\d{3}|3(?:(?:2\\d|50)\\d|3(?:[03467]\\d|1[0-35-9]|2[1-9]|5[0-24-9]|8[0-389]|9[0-8])|600)|646[59])|(?:(?:3[2-5]|[47][1-35]|5[1-3578])\\d|6(?:00|[13-57]\\d)|8(?:0[1-9]|[1-9]\\d))\\d\\d|9(?:(?:10[01]|(?:[2458]\\d|7[1-9])\\d)\\d|3(?:[0-57-9]\\d\\d|6(?:0[02-9]|[1-9]\\d))|6(?:[0-8]\\d\\d|9(?:[02-79]\\d|1[05-9]))|9(?:[03-9]\\d\\d|1(?:[0235-9]\\d|4[0-24-9])|2(?:[0-79]\\d|8[0-46-9]))))\\d{4}",
				[
					9
				]
			],
			[
				"2(?:1982[0-6]|3314[05-9])\\d{3}|(?:2(?:1(?:160|962)|2\\d{3}|3(?:(?:2\\d|50)\\d|3(?:[03467]\\d|1[0-35-9]|2[1-9]|5[0-24-9]|8[0-389]|9[0-8])|600)|646[59])|(?:(?:3[2-5]|[47][1-35]|5[1-3578]|6[13-57])\\d|8(?:0[1-8]|[1-9]\\d))\\d\\d|9(?:(?:10[01]|(?:[2458]\\d|7[1-9])\\d)\\d|3(?:[0-57-9]\\d\\d|6(?:0[02-9]|[1-9]\\d))|6(?:[0-8]\\d\\d|9(?:[02-79]\\d|1[05-9]))|9(?:[03-9]\\d\\d|1(?:[0235-9]\\d|4[0-24-9])|2(?:[0-79]\\d|8[0-46-9]))))\\d{4}",
				[
					9
				]
			]
		]
	],
	CM: [
		"237",
		"00",
		"[26]\\d{8}|88\\d{6,7}",
		[
			8,
			9
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"88"
				]
			],
			[
				"(\\d)(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4 $5",
				[
					"[26]|88"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"2(?:22|33)\\d{6}",
				[
					9
				]
			],
			[
				"(?:24[23]|6(?:[25-9]\\d|40))\\d{6}",
				[
					9
				]
			]
		]
	],
	CN: [
		"86",
		"00|1(?:[12]\\d|79)\\d\\d00",
		"(?:(?:1[03-689]|2\\d)\\d\\d|6)\\d{8}|1\\d{10}|[126]\\d{6}(?:\\d(?:\\d{2})?)?|86\\d{5,6}|(?:[3-579]\\d|8[0-57-9])\\d{5,9}",
		[
			7,
			8,
			9,
			10,
			11,
			12
		],
		[
			[
				"(\\d{2})(\\d{5,6})",
				"$1 $2",
				[
					"(?:10|2[0-57-9])[19]|3(?:[157]|35|49|9[1-68])|4(?:1[124-9]|2[179]|6[47-9]|7|8[23])|5(?:[1357]|2[37]|4[36]|6[1-46]|80)|6(?:3[1-5]|6[0238]|9[12])|7(?:01|[1579]|2[248]|3[014-9]|4[3-6]|6[023689])|8(?:07|1[236-8]|2[5-7]|[37]|8[36-8]|9[1-8])|9(?:0[1-3689]|1[1-79]|3|4[13]|5[1-5]|7[0-79]|9[0-35-9])|(?:4[35]|59|85)[1-9]",
					"(?:10|2[0-57-9])(?:1[02]|9[56])|8078|(?:3(?:[157]\\d|35|49|9[1-68])|4(?:1[124-9]|2[179]|[35][1-9]|6[47-9]|7\\d|8[23])|5(?:[1357]\\d|2[37]|4[36]|6[1-46]|80|9[1-9])|6(?:3[1-5]|6[0238]|9[12])|7(?:01|[1579]\\d|2[248]|3[014-9]|4[3-6]|6[023689])|8(?:1[236-8]|2[5-7]|[37]\\d|5[1-9]|8[36-8]|9[1-8])|9(?:0[1-3689]|1[1-79]|3\\d|4[13]|5[1-5]|7[0-79]|9[0-35-9]))1",
					"10(?:1(?:0|23)|9[56])|2[0-57-9](?:1(?:00|23)|9[56])|80781|(?:3(?:[157]\\d|35|49|9[1-68])|4(?:1[124-9]|2[179]|[35][1-9]|6[47-9]|7\\d|8[23])|5(?:[1357]\\d|2[37]|4[36]|6[1-46]|80|9[1-9])|6(?:3[1-5]|6[0238]|9[12])|7(?:01|[1579]\\d|2[248]|3[014-9]|4[3-6]|6[023689])|8(?:1[236-8]|2[5-7]|[37]\\d|5[1-9]|8[36-8]|9[1-8])|9(?:0[1-3689]|1[1-79]|3\\d|4[13]|5[1-5]|7[0-79]|9[0-35-9]))12",
					"10(?:1(?:0|23)|9[56])|2[0-57-9](?:1(?:00|23)|9[56])|807812|(?:3(?:[157]\\d|35|49|9[1-68])|4(?:1[124-9]|2[179]|[35][1-9]|6[47-9]|7\\d|8[23])|5(?:[1357]\\d|2[37]|4[36]|6[1-46]|80|9[1-9])|6(?:3[1-5]|6[0238]|9[12])|7(?:01|[1579]\\d|2[248]|3[014-9]|4[3-6]|6[023689])|8(?:1[236-8]|2[5-7]|[37]\\d|5[1-9]|8[36-8]|9[1-8])|9(?:0[1-3689]|1[1-79]|3\\d|4[13]|5[1-5]|7[0-79]|9[0-35-9]))123",
					"10(?:1(?:0|23)|9[56])|2[0-57-9](?:1(?:00|23)|9[56])|(?:3(?:[157]\\d|35|49|9[1-68])|4(?:1[124-9]|2[179]|[35][1-9]|6[47-9]|7\\d|8[23])|5(?:[1357]\\d|2[37]|4[36]|6[1-46]|80|9[1-9])|6(?:3[1-5]|6[0238]|9[12])|7(?:01|[1579]\\d|2[248]|3[014-9]|4[3-6]|6[023689])|8(?:078|1[236-8]|2[5-7]|[37]\\d|5[1-9]|8[36-8]|9[1-8])|9(?:0[1-3689]|1[1-79]|3\\d|4[13]|5[1-5]|7[0-79]|9[0-35-9]))123"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{5,6})",
				"$1 $2",
				[
					"3(?:[157]|35|49|9[1-68])|4(?:[17]|2[179]|6[47-9]|8[23])|5(?:[1357]|2[37]|4[36]|6[1-46]|80)|6(?:3[1-5]|6[0238]|9[12])|7(?:01|[1579]|2[248]|3[014-9]|4[3-6]|6[023689])|8(?:1[236-8]|2[5-7]|[37]|8[36-8]|9[1-8])|9(?:0[1-3689]|1[1-79]|[379]|4[13]|5[1-5])|(?:4[35]|59|85)[1-9]",
					"(?:3(?:[157]\\d|35|49|9[1-68])|4(?:[17]\\d|2[179]|[35][1-9]|6[47-9]|8[23])|5(?:[1357]\\d|2[37]|4[36]|6[1-46]|80|9[1-9])|6(?:3[1-5]|6[0238]|9[12])|7(?:01|[1579]\\d|2[248]|3[014-9]|4[3-6]|6[023689])|8(?:1[236-8]|2[5-7]|[37]\\d|5[1-9]|8[36-8]|9[1-8])|9(?:0[1-3689]|1[1-79]|[379]\\d|4[13]|5[1-5]))[19]",
					"85[23](?:10|95)|(?:3(?:[157]\\d|35|49|9[1-68])|4(?:[17]\\d|2[179]|[35][1-9]|6[47-9]|8[23])|5(?:[1357]\\d|2[37]|4[36]|6[1-46]|80|9[1-9])|6(?:3[1-5]|6[0238]|9[12])|7(?:01|[1579]\\d|2[248]|3[014-9]|4[3-6]|6[023689])|8(?:1[236-8]|2[5-7]|[37]\\d|5[14-9]|8[36-8]|9[1-8])|9(?:0[1-3689]|1[1-79]|[379]\\d|4[13]|5[1-5]))(?:10|9[56])",
					"85[23](?:100|95)|(?:3(?:[157]\\d|35|49|9[1-68])|4(?:[17]\\d|2[179]|[35][1-9]|6[47-9]|8[23])|5(?:[1357]\\d|2[37]|4[36]|6[1-46]|80|9[1-9])|6(?:3[1-5]|6[0238]|9[12])|7(?:01|[1579]\\d|2[248]|3[014-9]|4[3-6]|6[023689])|8(?:1[236-8]|2[5-7]|[37]\\d|5[14-9]|8[36-8]|9[1-8])|9(?:0[1-3689]|1[1-79]|[379]\\d|4[13]|5[1-5]))(?:100|9[56])"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"(?:4|80)0"
				]
			],
			[
				"(\\d{2})(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"10|2(?:[02-57-9]|1[1-9])",
					"10|2(?:[02-57-9]|1[1-9])",
					"10[0-79]|2(?:[02-57-9]|1[1-79])|(?:10|21)8(?:0[1-9]|[1-9])"
				],
				"0$1",
				1
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"3(?:[3-59]|7[02-68])|4(?:[26-8]|3[3-9]|5[2-9])|5(?:3[03-9]|[468]|7[028]|9[2-46-9])|6|7(?:[0-247]|3[04-9]|5[0-4689]|6[2368])|8(?:[1-358]|9[1-7])|9(?:[013479]|5[1-5])|(?:[34]1|55|79|87)[02-9]"
				],
				"0$1",
				1
			],
			[
				"(\\d{3})(\\d{7,8})",
				"$1 $2",
				[
					"9"
				]
			],
			[
				"(\\d{4})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"80"
				],
				"0$1",
				1
			],
			[
				"(\\d{3})(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"[3-578]"
				],
				"0$1",
				1
			],
			[
				"(\\d{3})(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"1[3-9]"
				]
			],
			[
				"(\\d{2})(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3 $4",
				[
					"[12]"
				],
				"0$1",
				1
			]
		],
		"0",
		0,
		"(1(?:[12]\\d|79)\\d\\d)|0",
		0,
		0,
		0,
		[
			[
				"(?:10(?:[02-79]\\d\\d|[18](?:0[1-9]|[1-9]\\d))|2(?:[02-57-9]\\d{3}|1(?:[18](?:0[1-9]|[1-9]\\d)|[2-79]\\d\\d))|(?:41[03]|8078|9(?:78|94))\\d\\d)\\d{5}|(?:10|2[0-57-9])(?:1(?:00|23)\\d\\d|95\\d{3,4})|(?:41[03]|9(?:78|94))(?:100\\d\\d|95\\d{3,4})|8078123|(?:43[35]|754|851)\\d{7,8}|(?:43[35]|754|851)(?:1(?:00\\d|23)\\d|95\\d{3,4})|(?:3(?:11|7[179])|4(?:[15]1|3[12])|5(?:1\\d|2[37]|3[12]|51|7[13-79]|9[15])|7(?:[39]1|5[57]|6[09])|8(?:71|98))(?:[02-8]\\d{7}|1(?:0(?:0\\d\\d(?:\\d{3})?|[1-9]\\d{5})|[13-9]\\d{6}|2(?:[0-24-9]\\d{5}|3\\d(?:\\d{4})?))|9(?:[0-46-9]\\d{6}|5\\d{3}(?:\\d(?:\\d{2})?)?))|(?:3(?:1[02-9]|35|49|5\\d|7[02-68]|9[1-68])|4(?:1[24-9]|2[179]|3[46-9]|5[2-9]|6[47-9]|7\\d|8[23])|5(?:3[03-9]|4[36]|5[02-9]|6[1-46]|7[028]|80|9[2-46-9])|6(?:3[1-5]|6[0238]|9[12])|7(?:01|[17]\\d|2[248]|3[04-9]|4[3-6]|5[0-3689]|6[2368]|9[02-9])|8(?:1[236-8]|2[5-7]|3\\d|5[2-9]|7[02-9]|8[36-8]|9[1-7])|9(?:0[1-3689]|1[1-79]|3\\d|4[13]|5[1-5]|7[0-79]|9[0-35-9]))(?:[02-8]\\d{6}|1(?:0(?:0\\d\\d(?:\\d{2})?|[1-9]\\d{4})|[13-9]\\d{5}|2(?:[0-24-9]\\d{4}|3\\d(?:\\d{3})?))|9(?:[0-46-9]\\d{5}|5\\d{3,5}))",
				[
					7,
					8,
					9,
					10,
					11
				]
			],
			[
				"1740[0-5]\\d{6}|1(?:[38]\\d|4[57]|[59][0-35-9]|6[25-7]|7[0-35-8])\\d{8}",
				[
					11
				]
			]
		],
		"00"
	],
	CO: [
		"57",
		"00(?:4(?:[14]4|56)|[579])",
		"(?:46|60\\d\\d)\\d{6}|(?:1\\d|[39])\\d{9}",
		[
			8,
			10,
			11
		],
		[
			[
				"(\\d{4})(\\d{4})",
				"$1 $2",
				[
					"46"
				]
			],
			[
				"(\\d{3})(\\d{7})",
				"$1 $2",
				[
					"6|90"
				],
				"($1)"
			],
			[
				"(\\d{3})(\\d{7})",
				"$1 $2",
				[
					"3[0-357]|9[14]"
				]
			],
			[
				"(\\d)(\\d{3})(\\d{7})",
				"$1-$2-$3",
				[
					"1"
				],
				"0$1",
				0,
				"$1 $2 $3"
			]
		],
		"0",
		0,
		"0([3579]|4(?:[14]4|56))?",
		0,
		0,
		0,
		[
			[
				"601055(?:[0-4]\\d|50)\\d\\d|6010(?:[0-4]\\d|5[0-4])\\d{4}|(?:46|60(?:[18][1-9]|[24-7][2-9]))\\d{6}",
				[
					8,
					10
				]
			],
			[
				"333301[0-5]\\d{3}|3333(?:00|2[5-9]|[3-9]\\d)\\d{4}|(?:3(?:(?:0[0-5]|1\\d|5[01]|70)\\d|2(?:[0-3]\\d|4[1-9])|3(?:00|3[0-24-9]))|9(?:101|408))\\d{6}",
				[
					10
				]
			]
		]
	],
	CR: [
		"506",
		"00",
		"(?:8\\d|90)\\d{8}|(?:[24-8]\\d{3}|3005)\\d{4}",
		[
			8,
			10
		],
		[
			[
				"(\\d{4})(\\d{4})",
				"$1 $2",
				[
					"[2-7]|8[3-9]"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1-$2-$3",
				[
					"[89]"
				]
			]
		],
		0,
		0,
		"(19(?:0[0-2468]|1[09]|20|66|77|99))",
		0,
		0,
		0,
		[
			[
				"210[7-9]\\d{4}|2(?:[024-7]\\d|1[1-9])\\d{5}",
				[
					8
				]
			],
			[
				"(?:3005\\d|6500[01])\\d{3}|(?:5[07]|6[0-4]|7[0-3]|8[3-9])\\d{6}",
				[
					8
				]
			]
		]
	],
	CU: [
		"53",
		"119",
		"(?:[2-7]|8\\d\\d)\\d{7}|[2-47]\\d{6}|[34]\\d{5}",
		[
			6,
			7,
			8,
			10
		],
		[
			[
				"(\\d{2})(\\d{4,6})",
				"$1 $2",
				[
					"2[1-4]|[34]"
				],
				"(0$1)"
			],
			[
				"(\\d)(\\d{6,7})",
				"$1 $2",
				[
					"7"
				],
				"(0$1)"
			],
			[
				"(\\d)(\\d{7})",
				"$1 $2",
				[
					"[56]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{7})",
				"$1 $2",
				[
					"8"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:3[23]|4[89])\\d{4,6}|(?:31|4[36]|8(?:0[25]|78)\\d)\\d{6}|(?:2[1-4]|4[1257]|7\\d)\\d{5,6}"
			],
			[
				"(?:5\\d|6[2-4])\\d{6}",
				[
					8
				]
			]
		]
	],
	CV: [
		"238",
		"0",
		"(?:[2-59]\\d\\d|800)\\d{4}",
		[
			7
		],
		[
			[
				"(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2 $3",
				[
					"[2-589]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"2(?:2[1-7]|3[0-8]|4[12]|5[1256]|6\\d|7[1-3]|8[1-5])\\d{4}"
			],
			[
				"(?:36|5[1-389]|9\\d)\\d{5}"
			]
		]
	],
	CW: [
		"599",
		"00",
		"(?:[34]1|60|(?:7|9\\d)\\d)\\d{5}",
		[
			7,
			8
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"[3467]"
				]
			],
			[
				"(\\d)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"9[4-8]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		"[69]",
		[
			[
				"9(?:4(?:3[0-5]|4[14]|6\\d)|50\\d|7(?:2[014]|3[02-9]|4[4-9]|6[357]|77|8[7-9])|8(?:3[39]|[46]\\d|7[01]|8[57-9]))\\d{4}"
			],
			[
				"953[01]\\d{4}|9(?:5[12467]|6[5-9])\\d{5}"
			]
		]
	],
	CX: [
		"61",
		"001[14-689]|14(?:1[14]|34|4[17]|[56]6|7[47]|88)0011",
		"1(?:[0-79]\\d{8}(?:\\d{2})?|8[0-24-9]\\d{7})|[148]\\d{8}|1\\d{5,7}",
		[
			6,
			7,
			8,
			9,
			10,
			12
		],
		0,
		"0",
		0,
		"([59]\\d{7})$|0",
		"8$1",
		0,
		0,
		[
			[
				"8(?:51(?:0(?:01|30|59|88)|1(?:17|46|75)|2(?:22|35))|91(?:00[6-9]|1(?:[28]1|49|78)|2(?:09|63)|3(?:12|26|75)|4(?:56|97)|64\\d|7(?:0[01]|1[0-2])|958))\\d{3}",
				[
					9
				]
			],
			[
				"4(?:79[01]|83[0-36-9])\\d{5}|4(?:[0-36]\\d|4[047-9]|[58][0-24-9]|7[02-8]|9[0-47-9])\\d{6}",
				[
					9
				]
			]
		],
		"0011"
	],
	CY: [
		"357",
		"00",
		"(?:[279]\\d|[58]0)\\d{6}",
		[
			8
		],
		[
			[
				"(\\d{2})(\\d{6})",
				"$1 $2",
				[
					"[257-9]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"2[2-6]\\d{6}"
			],
			[
				"9(?:10|[4-79]\\d)\\d{5}"
			]
		]
	],
	CZ: [
		"420",
		"00",
		"(?:[2-578]\\d|60)\\d{7}|9\\d{8,11}",
		[
			9,
			10,
			11,
			12
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[2-8]|9[015-7]"
				]
			],
			[
				"(\\d{2})(\\d{3})(\\d{3})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"96"
				]
			],
			[
				"(\\d{2})(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3 $4",
				[
					"9"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3 $4",
				[
					"9"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:2\\d|3[1257-9]|4[16-9]|5[13-9])\\d{7}",
				[
					9
				]
			],
			[
				"7(?:060\\d|19(?:[0-5]\\d|6[0-6]))\\d{4}|(?:60[1-8]|7(?:0[2-5]|[2379]\\d))\\d{6}",
				[
					9
				]
			]
		]
	],
	DE: [
		"49",
		"00",
		"[2579]\\d{5,14}|49(?:[34]0|69|8\\d)\\d\\d?|49(?:37|49|60|7[089]|9\\d)\\d{1,3}|49(?:2[024-9]|3[2-689]|7[1-7])\\d{1,8}|(?:1|[368]\\d|4[0-8])\\d{3,13}|49(?:[015]\\d|2[13]|31|[46][1-8])\\d{1,9}",
		[
			4,
			5,
			6,
			7,
			8,
			9,
			10,
			11,
			12,
			13,
			14,
			15
		],
		[
			[
				"(\\d{2})(\\d{3,13})",
				"$1 $2",
				[
					"3[02]|40|[68]9"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3,12})",
				"$1 $2",
				[
					"2(?:0[1-389]|1[124]|2[18]|3[14])|3(?:[35-9][15]|4[015])|906|(?:2[4-9]|4[2-9]|[579][1-9]|[68][1-8])1",
					"2(?:0[1-389]|12[0-8])|3(?:[35-9][15]|4[015])|906|2(?:[13][14]|2[18])|(?:2[4-9]|4[2-9]|[579][1-9]|[68][1-8])1"
				],
				"0$1"
			],
			[
				"(\\d{4})(\\d{2,11})",
				"$1 $2",
				[
					"[24-6]|3(?:[3569][02-46-9]|4[2-4679]|7[2-467]|8[2-46-8])|70[2-8]|8(?:0[2-9]|[1-8])|90[7-9]|[79][1-9]",
					"[24-6]|3(?:3(?:0[1-467]|2[127-9]|3[124578]|7[1257-9]|8[1256]|9[145])|4(?:2[135]|4[13578]|9[1346])|5(?:0[14]|2[1-3589]|6[1-4]|7[13468]|8[13568])|6(?:2[1-489]|3[124-6]|6[13]|7[12579]|8[1-356]|9[135])|7(?:2[1-7]|4[145]|6[1-5]|7[1-4])|8(?:21|3[1468]|6|7[1467]|8[136])|9(?:0[12479]|2[1358]|4[134679]|6[1-9]|7[136]|8[147]|9[1468]))|70[2-8]|8(?:0[2-9]|[1-8])|90[7-9]|[79][1-9]|3[68]4[1347]|3(?:47|60)[1356]|3(?:3[46]|46|5[49])[1246]|3[4579]3[1357]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"138"
				],
				"0$1"
			],
			[
				"(\\d{5})(\\d{2,10})",
				"$1 $2",
				[
					"3"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{5,11})",
				"$1 $2",
				[
					"181"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d)(\\d{4,10})",
				"$1 $2 $3",
				[
					"1(?:3|80)|9"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{7,8})",
				"$1 $2",
				[
					"1[67]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{7,12})",
				"$1 $2",
				[
					"8"
				],
				"0$1"
			],
			[
				"(\\d{5})(\\d{6})",
				"$1 $2",
				[
					"185",
					"1850",
					"18500"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"7"
				],
				"0$1"
			],
			[
				"(\\d{4})(\\d{7})",
				"$1 $2",
				[
					"18[68]"
				],
				"0$1"
			],
			[
				"(\\d{4})(\\d{7})",
				"$1 $2",
				[
					"15[1279]"
				],
				"0$1"
			],
			[
				"(\\d{5})(\\d{6})",
				"$1 $2",
				[
					"15[03568]",
					"15(?:[0568]|3[13])"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{8})",
				"$1 $2",
				[
					"18"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{2})(\\d{7,8})",
				"$1 $2 $3",
				[
					"1(?:6[023]|7)"
				],
				"0$1"
			],
			[
				"(\\d{4})(\\d{2})(\\d{7})",
				"$1 $2 $3",
				[
					"15[279]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{2})(\\d{8})",
				"$1 $2 $3",
				[
					"15"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"32\\d{9,11}|49[1-6]\\d{10}|322\\d{6}|49[0-7]\\d{3,9}|(?:[34]0|[68]9)\\d{3,13}|(?:2(?:0[1-689]|[1-3569]\\d|4[0-8]|7[1-7]|8[0-7])|3(?:[3569]\\d|4[0-79]|7[1-7]|8[1-8])|4(?:1[02-9]|[2-48]\\d|5[0-6]|6[0-8]|7[0-79])|5(?:0[2-8]|[124-6]\\d|[38][0-8]|[79][0-7])|6(?:0[02-9]|[1-358]\\d|[47][0-8]|6[1-9])|7(?:0[2-8]|1[1-9]|[27][0-7]|3\\d|[4-6][0-8]|8[0-5]|9[013-7])|8(?:0[2-9]|1[0-79]|2\\d|3[0-46-9]|4[0-6]|5[013-9]|6[1-8]|7[0-8]|8[0-24-6])|9(?:0[6-9]|[1-4]\\d|[589][0-7]|6[0-8]|7[0-467]))\\d{3,12}",
				[
					5,
					6,
					7,
					8,
					9,
					10,
					11,
					12,
					13,
					14,
					15
				]
			],
			[
				"1(?:(?:5(?:[0-25-9]\\d\\d|3(?:10|33))|7[26-9]\\d\\d)\\d{6}|6[023]\\d{7,8})|17\\d{8}",
				[
					10,
					11
				]
			]
		]
	],
	DJ: [
		"253",
		"00",
		"(?:2\\d|77)\\d{6}",
		[
			8
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[27]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"2(?:1[2-5]|7[45])\\d{5}"
			],
			[
				"77\\d{6}"
			]
		]
	],
	DK: [
		"45",
		"00",
		"[2-9]\\d{7}",
		[
			8
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[2-9]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:2(?:[0-59][1-9]|[6-8]\\d)|3(?:[0-3][1-9]|4[13]|5[1-58]|6[1347-9]|7\\d|8[1-8]|9[1-79])|4(?:[0-25][1-9]|[34][2-9]|6[13-579]|7[13579]|8[1-47]|9[127])|5(?:[0-36][1-9]|4[146-9]|5[3-57-9]|7[568]|8[1-358]|9[1-69])|6(?:[0135][1-9]|2[1-68]|4[2-8]|6[1689]|[78]\\d|9[15689])|7(?:[0-69][1-9]|7[3-9]|8[147])|8(?:[16-9][1-9]|2[1-58])|9(?:[1-47-9][1-9]|6\\d))\\d{5}"
			],
			[
				"(?:2[6-8]|37|6[78]|96)\\d{6}|(?:2[0-59]|3[0-689]|[457]\\d|6[0-69]|8[126-9]|9[1-47-9])[1-9]\\d{5}"
			]
		]
	],
	DM: [
		"1",
		"011",
		"(?:[58]\\d\\d|767|900)\\d{7}",
		[
			10
		],
		0,
		"1",
		0,
		"([2-7]\\d{6})$|1",
		"767$1",
		0,
		"767",
		[
			[
				"767(?:2(?:55|66)|4(?:2[01]|4[0-25-9])|50[0-4])\\d{4}"
			],
			[
				"767(?:2(?:[2-4689]5|7[5-7])|31[5-7]|61[1-8]|70[1-6])\\d{4}"
			]
		]
	],
	DO: [
		"1",
		"011",
		"(?:[58]\\d\\d|900)\\d{7}",
		[
			10
		],
		0,
		"1",
		0,
		0,
		0,
		0,
		"8001|8[024]9",
		[
			[
				"8(?:[04]9[2-9]\\d\\d|29(?:2(?:[0-59]\\d|6[04-9]|7[0-27]|8[0237-9])|3(?:[0-35-9]\\d|4[7-9])|[45]\\d\\d|6(?:[0-27-9]\\d|[3-5][1-9]|6[0135-8])|7(?:0[013-9]|[1-37]\\d|4[1-35689]|5[1-4689]|6[1-57-9]|8[1-79]|9[1-8])|8(?:0[146-9]|1[0-48]|[248]\\d|3[1-79]|5[01589]|6[013-68]|7[124-8]|9[0-8])|9(?:[0-24]\\d|3[02-46-9]|5[0-79]|60|7[0169]|8[57-9]|9[02-9])))\\d{4}"
			],
			[
				"8[024]9[2-9]\\d{6}"
			]
		]
	],
	DZ: [
		"213",
		"00",
		"(?:[1-4]|[5-79]\\d|80)\\d{7}",
		[
			8,
			9
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[1-4]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"9"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[5-8]"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"9619\\d{5}|(?:1\\d|2[013-79]|3[0-8]|4[013-689])\\d{6}"
			],
			[
				"(?:5(?:4[0-29]|5\\d|6[0-3])|6(?:[569]\\d|7[0-6])|7[7-9]\\d)\\d{6}",
				[
					9
				]
			]
		]
	],
	EC: [
		"593",
		"00",
		"1\\d{9,10}|(?:[2-7]|9\\d)\\d{7}",
		[
			8,
			9,
			10,
			11
		],
		[
			[
				"(\\d)(\\d{3})(\\d{4})",
				"$1 $2-$3",
				[
					"[2-7]"
				],
				"(0$1)",
				0,
				"$1-$2-$3"
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"9"
				],
				"0$1"
			],
			[
				"(\\d{4})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"1"
				]
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"[2-7][2-7]\\d{6}",
				[
					8
				]
			],
			[
				"964[0-2]\\d{5}|9(?:39|[57][89]|6[0-36-9]|[89]\\d)\\d{6}",
				[
					9
				]
			]
		]
	],
	EE: [
		"372",
		"00",
		"8\\d{9}|[4578]\\d{7}|(?:[3-8]\\d|90)\\d{5}",
		[
			7,
			8,
			10
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"[369]|4[3-8]|5(?:[0-2]|5[0-478]|6[45])|7[1-9]|88",
					"[369]|4[3-8]|5(?:[02]|1(?:[0-8]|95)|5[0-478]|6(?:4[0-4]|5[1-589]))|7[1-9]|88"
				]
			],
			[
				"(\\d{4})(\\d{3,4})",
				"$1 $2",
				[
					"[45]|8(?:00|[1-49])",
					"[45]|8(?:00[1-9]|[1-49])"
				]
			],
			[
				"(\\d{2})(\\d{2})(\\d{4})",
				"$1 $2 $3",
				[
					"7"
				]
			],
			[
				"(\\d{4})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"8"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:3[23589]|4[3-8]|6\\d|7[1-9]|88)\\d{5}",
				[
					7
				]
			],
			[
				"(?:5\\d{5}|8(?:1(?:0(?:0(?:00|[178]\\d)|[3-9]\\d\\d)|(?:1(?:0[2-6]|1\\d)|(?:2[0-59]|[3-79]\\d)\\d)\\d)|2(?:0(?:0(?:00|4\\d)|(?:19|[2-7]\\d)\\d)|(?:(?:[124-69]\\d|3[5-9])\\d|7(?:[0-79]\\d|8[13-9])|8(?:[2-6]\\d|7[01]))\\d)|[349]\\d{4}))\\d\\d|5(?:(?:[02]\\d|5[0-478])\\d|1(?:[0-8]\\d|95)|6(?:4[0-4]|5[1-589]))\\d{3}",
				[
					7,
					8
				]
			]
		]
	],
	EG: [
		"20",
		"00",
		"[189]\\d{8,9}|[24-6]\\d{8}|[135]\\d{7}",
		[
			8,
			9,
			10
		],
		[
			[
				"(\\d)(\\d{7,8})",
				"$1 $2",
				[
					"[23]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{6,7})",
				"$1 $2",
				[
					"1[35]|[4-6]|8[2468]|9[235-7]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[89]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{8})",
				"$1 $2",
				[
					"1"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"13[23]\\d{6}|(?:15|57)\\d{6,7}|(?:2\\d|3|4[05-8]|5[05]|6[24-689]|8[2468]|9[235-7])\\d{7}",
				[
					8,
					9
				]
			],
			[
				"1[0-25]\\d{8}",
				[
					10
				]
			]
		]
	],
	EH: [
		"212",
		"00",
		"[5-8]\\d{8}",
		[
			9
		],
		0,
		"0",
		0,
		0,
		0,
		0,
		"528[89]",
		[
			[
				"528[89]\\d{5}"
			],
			[
				"(?:6(?:[0-79]\\d|8[0-247-9])|7(?:[0167]\\d|2[0-8]|5[0-5]|8[0-7]))\\d{6}"
			]
		]
	],
	ER: [
		"291",
		"00",
		"[178]\\d{6}",
		[
			7
		],
		[
			[
				"(\\d)(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[178]"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:1(?:1[12568]|[24]0|55|6[146])|8\\d\\d)\\d{4}"
			],
			[
				"(?:17[1-3]|7\\d\\d)\\d{4}"
			]
		]
	],
	ES: [
		"34",
		"00",
		"[5-9]\\d{8}",
		[
			9
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[89]00"
				]
			],
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[5-9]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"96906(?:0[0-8]|1[1-9]|[2-9]\\d)\\d\\d|9(?:69(?:0[0-57-9]|[1-9]\\d)|73(?:[0-8]\\d|9[1-9]))\\d{4}|(?:8(?:[1356]\\d|[28][0-8]|[47][1-9])|9(?:[135]\\d|[268][0-8]|4[1-9]|7[124-9]))\\d{6}"
			],
			[
				"96906(?:09|10)\\d\\d|(?:590(?:10[0-2]|600)|97390\\d)\\d{3}|(?:6\\d|7[1-48])\\d{7}"
			]
		]
	],
	ET: [
		"251",
		"00",
		"(?:11|[2-579]\\d)\\d{7}",
		[
			9
		],
		[
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[1-579]"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"11667[01]\\d{3}|(?:11(?:1(?:1[1-468]|2[2-7]|3[1-5]|5[5-8]|8[6-8])|2(?:13|3[6-8]|5[89]|7[05-9]|8[2-6])|3(?:2[01]|3[0-289]|4[1289]|7[1-4]|87)|4(?:1[69]|3[2-49]|4[0-3]|6[5-8]|7\\d)|5(?:1[578]|44|5[0-4])|6(?:1[578]|2[69]|39|4[5-7]|5[0-5]|6[0-59]|8[015-8]))|2(?:2(?:11[1-9]|22[0-7]|33\\d|44[1467]|66[1-68])|5(?:11[124-6]|33[2-8]|44[1467]|55[14]|66[1-3679]|77[124-79]|880))|3(?:3(?:11[0-46-8]|(?:22|55)[0-6]|33[0134689]|44[04]|66[01467])|4(?:44[0-8]|55[0-69]|66[0-3]|77[1-5]))|4(?:6(?:119|22[0-24-7]|33[1-5]|44[13-69]|55[14-689]|660|88[1-4])|7(?:(?:11|22)[1-9]|33[13-7]|44[13-6]|55[1-689]))|5(?:7(?:227|55[05]|(?:66|77)[14-8])|8(?:11[149]|22[013-79]|33[0-68]|44[013-8]|550|66[1-5]|77\\d)))\\d{4}"
			],
			[
				"700[1-9]\\d{5}|(?:7(?:0[1-9]|1[0-8]|22|77|86|99)|9\\d\\d)\\d{6}"
			]
		]
	],
	FI: [
		"358",
		"00|99(?:[01469]|5(?:[14]1|3[23]|5[59]|77|88|9[09]))",
		"[1-35689]\\d{4}|7\\d{10,11}|(?:[124-7]\\d|3[0-46-9])\\d{8}|[1-9]\\d{5,8}",
		[
			5,
			6,
			7,
			8,
			9,
			10,
			11,
			12
		],
		[
			[
				"(\\d{5})",
				"$1",
				[
					"20[2-59]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3,7})",
				"$1 $2",
				[
					"(?:[1-3]0|[68])0|70[07-9]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{4,8})",
				"$1 $2",
				[
					"[14]|2[09]|50|7[135]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{6,10})",
				"$1 $2",
				[
					"7"
				],
				"0$1"
			],
			[
				"(\\d)(\\d{4,9})",
				"$1 $2",
				[
					"(?:19|[2568])[1-8]|3(?:0[1-9]|[1-9])|9"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		"1[03-79]|[2-9]",
		[
			[
				"1[3-7][1-8]\\d{3,6}|(?:19[1-8]|[23568][1-8]\\d|9(?:00|[1-8]\\d))\\d{2,6}",
				[
					5,
					6,
					7,
					8,
					9
				]
			],
			[
				"4946\\d{2,6}|(?:4[0-8]|50)\\d{4,8}",
				[
					6,
					7,
					8,
					9,
					10
				]
			]
		],
		"00"
	],
	FJ: [
		"679",
		"0(?:0|52)",
		"45\\d{5}|(?:0800\\d|[235-9])\\d{6}",
		[
			7,
			11
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"[235-9]|45"
				]
			],
			[
				"(\\d{4})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"0"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"603\\d{4}|(?:3[0-5]|6[25-7]|8[58])\\d{5}",
				[
					7
				]
			],
			[
				"(?:[279]\\d|45|5[01568]|8[034679])\\d{5}",
				[
					7
				]
			]
		],
		"00"
	],
	FK: [
		"500",
		"00",
		"[2-7]\\d{4}",
		[
			5
		],
		0,
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"[2-47]\\d{4}"
			],
			[
				"[56]\\d{4}"
			]
		]
	],
	FM: [
		"691",
		"00",
		"(?:[39]\\d\\d|820)\\d{4}",
		[
			7
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"[389]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"31(?:00[67]|208|309)\\d\\d|(?:3(?:[2357]0[1-9]|602|804|905)|(?:820|9[2-6]\\d)\\d)\\d{3}"
			],
			[
				"31(?:00[67]|208|309)\\d\\d|(?:3(?:[2357]0[1-9]|602|804|905)|(?:820|9[2-7]\\d)\\d)\\d{3}"
			]
		]
	],
	FO: [
		"298",
		"00",
		"[2-9]\\d{5}",
		[
			6
		],
		[
			[
				"(\\d{6})",
				"$1",
				[
					"[2-9]"
				]
			]
		],
		0,
		0,
		"(10(?:01|[12]0|88))",
		0,
		0,
		0,
		[
			[
				"(?:20|[34]\\d|8[19])\\d{4}"
			],
			[
				"(?:[27][1-9]|5\\d|9[16])\\d{4}"
			]
		]
	],
	FR: [
		"33",
		"00",
		"[1-9]\\d{8}",
		[
			9
		],
		[
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"8"
				],
				"0 $1"
			],
			[
				"(\\d)(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4 $5",
				[
					"[1-79]"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:26[013-9]|59[1-35-9])\\d{6}|(?:[13]\\d|2[0-57-9]|4[1-9]|5[0-8])\\d{7}"
			],
			[
				"(?:6(?:[0-24-8]\\d|3[0-8]|9[589])|7[3-9]\\d)\\d{6}"
			]
		]
	],
	GA: [
		"241",
		"00",
		"(?:[067]\\d|11)\\d{6}|[2-7]\\d{6}",
		[
			7,
			8
		],
		[
			[
				"(\\d)(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[2-7]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"0"
				]
			],
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"11|[67]"
				],
				"0$1"
			]
		],
		0,
		0,
		"0(11\\d{6}|60\\d{6}|61\\d{6}|6[256]\\d{6}|7[467]\\d{6})",
		"$1",
		0,
		0,
		[
			[
				"[01]1\\d{6}",
				[
					8
				]
			],
			[
				"(?:(?:0[2-7]|7[467])\\d|6(?:0[0-4]|10|[256]\\d))\\d{5}|[2-7]\\d{6}"
			]
		]
	],
	GB: [
		"44",
		"00",
		"[1-357-9]\\d{9}|[18]\\d{8}|8\\d{6}",
		[
			7,
			9,
			10
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"800",
					"8001",
					"80011",
					"800111",
					"8001111"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2 $3",
				[
					"845",
					"8454",
					"84546",
					"845464"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{6})",
				"$1 $2",
				[
					"800"
				],
				"0$1"
			],
			[
				"(\\d{5})(\\d{4,5})",
				"$1 $2",
				[
					"1(?:38|5[23]|69|76|94)",
					"1(?:(?:38|69)7|5(?:24|39)|768|946)",
					"1(?:3873|5(?:242|39[4-6])|(?:697|768)[347]|9467)"
				],
				"0$1"
			],
			[
				"(\\d{4})(\\d{5,6})",
				"$1 $2",
				[
					"1(?:[2-69][02-9]|[78])"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"[25]|7(?:0|6[02-9])",
					"[25]|7(?:0|6(?:[03-9]|2[356]))"
				],
				"0$1"
			],
			[
				"(\\d{4})(\\d{6})",
				"$1 $2",
				[
					"7"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[1389]"
				],
				"0$1"
			]
		],
		"0",
		0,
		"0|180020",
		0,
		0,
		0,
		[
			[
				"(?:1(?:1(?:3(?:[0-58]\\d\\d|73[0-5])|4(?:(?:[0-5]\\d|70)\\d|69[7-9])|(?:(?:5[0-26-9]|[78][0-49])\\d|6(?:[0-4]\\d|5[01]))\\d)|(?:2(?:(?:0[024-9]|2[3-9]|3[3-79]|4[1-689]|[58][02-9]|6[0-47-9]|7[013-9]|9\\d)\\d|1(?:[0-7]\\d|8[0-3]))|(?:3(?:0\\d|1[0-8]|[25][02-9]|3[02-579]|[468][0-46-9]|7[1-35-79]|9[2-578])|4(?:0[03-9]|[137]\\d|[28][02-57-9]|4[02-69]|5[0-8]|[69][0-79])|5(?:0[1-35-9]|[16]\\d|2[024-9]|3[015689]|4[02-9]|5[03-9]|7[0-35-9]|8[0-468]|9[0-57-9])|6(?:0[034689]|1\\d|2[0-35689]|[38][013-9]|4[1-467]|5[0-69]|6[13-9]|7[0-8]|9[0-24578])|7(?:0[0246-9]|2\\d|3[0236-8]|4[03-9]|5[0-46-9]|6[013-9]|7[0-35-9]|8[024-9]|9[02-9])|8(?:0[35-9]|2[1-57-9]|3[02-578]|4[0-578]|5[124-9]|6[2-69]|7\\d|8[02-9]|9[02569])|9(?:0[02-589]|[18]\\d|2[02-689]|3[1-57-9]|4[2-9]|5[0-579]|6[2-47-9]|7[0-24578]|9[2-57]))\\d)\\d)|2(?:0[013478]|3[0189]|4[017]|8[0-46-9]|9[0-2])\\d{3})\\d{4}|1(?:2(?:0(?:46[1-4]|87[2-9])|545[1-79]|76(?:2\\d|3[1-8]|6[1-6])|9(?:7(?:2[0-4]|3[2-5])|8(?:2[2-8]|7[0-47-9]|8[3-5])))|3(?:6(?:38[2-5]|47[23])|8(?:47[04-9]|64[0157-9]))|4(?:044[1-7]|20(?:2[23]|8\\d)|6(?:0(?:30|5[2-57]|6[1-8]|7[2-8])|140)|8(?:052|87[1-3]))|5(?:2(?:4(?:3[2-79]|6\\d)|76\\d)|6(?:26[06-9]|686))|6(?:06(?:4\\d|7[4-79])|295[5-7]|35[34]\\d|47(?:24|61)|59(?:5[08]|6[67]|74)|9(?:55[0-4]|77[23]))|7(?:26(?:6[13-9]|7[0-7])|(?:442|688)\\d|50(?:2[0-3]|[3-68]2|76))|8(?:27[56]\\d|37(?:5[2-5]|8[239])|843[2-58])|9(?:0(?:0(?:6[1-8]|85)|52\\d)|3583|4(?:66[1-8]|9(?:2[01]|81))|63(?:23|3[1-4])|9561))\\d{3}",
				[
					9,
					10
				]
			],
			[
				"7(?:457[0-57-9]|700[01]|911[028])\\d{5}|7(?:[1-3]\\d\\d|4(?:[0-46-9]\\d|5[0-689])|5(?:0[0-8]|[13-9]\\d|2[0-35-9])|7(?:0[1-9]|[1-7]\\d|8[02-9]|9[0-689])|8(?:[014-9]\\d|[23][0-8])|9(?:[024-9]\\d|1[02-9]|3[0-689]))\\d{6}",
				[
					10
				]
			]
		],
		0,
		" x"
	],
	GD: [
		"1",
		"011",
		"(?:473|[58]\\d\\d|900)\\d{7}",
		[
			10
		],
		0,
		"1",
		0,
		"([2-9]\\d{6})$|1",
		"473$1",
		0,
		"473",
		[
			[
				"473(?:2(?:3[0-2]|69)|3(?:2[89]|86)|4(?:[06]8|3[5-9]|4[0-4]|5[59]|73|90)|63[68]|7(?:58|84)|800|938)\\d{4}"
			],
			[
				"473(?:4(?:0[2-79]|1[04-9]|2[0-5]|49|5[6-8])|5(?:2[01]|3[3-8])|901)\\d{4}"
			]
		]
	],
	GE: [
		"995",
		"00",
		"(?:[3-57]\\d\\d|800)\\d{6}",
		[
			9
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"70"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"32"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[57]"
				]
			],
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[348]"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:3(?:[256]\\d|4[124-9]|7[0-4])|4(?:1\\d|2[2-7]|3[1-79]|4[2-8]|7[239]|9[1-7]))\\d{6}"
			],
			[
				"5(?:(?:(?:0555|1(?:[17]77|555))[5-9]|757(?:7[7-9]|8[01]))\\d|22252[0-4])\\d\\d|5(?:0(?:0[17]0|505)|1(?:0[01]0|1(?:07|33|51))|2(?:0[02]0|2[25]2)|3(?:0[03]0|3[35]3)|(?:40[04]|900)0|5222)[0-4]\\d{3}|(?:5(?:0(?:0(?:0\\d|1[12]|22|3[0-6]|44|5[05]|77|88|9[09])|(?:[14]\\d|77)\\d|22[02])|1(?:1(?:[03][01]|[124]\\d|5[2-6]|7[0-4])|4\\d\\d)|[23]555|4(?:4\\d\\d|555)|5(?:[0157-9]\\d\\d|200|333|444)|6[89]\\d\\d|7(?:(?:[0147-9]\\d|22)\\d|5(?:00|[57]5))|8(?:0(?:[018]\\d|2[0-4])|5(?:55|8[89])|8(?:55|88))|9(?:090|[1-35-9]\\d\\d))|790\\d\\d)\\d{4}"
			]
		]
	],
	GF: [
		"594",
		"00",
		"(?:[56]94\\d|7093)\\d{5}|(?:80|9\\d)\\d{7}",
		[
			9
		],
		[
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[5-7]|9[47]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[89]"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"594(?:[02-49]\\d|1[0-5]|5[6-9]|6[0-3]|80)\\d{4}"
			],
			[
				"(?:694(?:[0-249]\\d|3[0-8])|7093[0-3])\\d{4}"
			]
		]
	],
	GG: [
		"44",
		"00",
		"(?:1481|[357-9]\\d{3})\\d{6}|8\\d{6}(?:\\d{2})?",
		[
			7,
			9,
			10
		],
		0,
		"0",
		0,
		"([25-9]\\d{5})$|0|180020",
		"1481$1",
		0,
		0,
		[
			[
				"1481[25-9]\\d{5}",
				[
					10
				]
			],
			[
				"7(?:(?:781|839)\\d|911[17])\\d{5}",
				[
					10
				]
			]
		]
	],
	GH: [
		"233",
		"00",
		"(?:[235]\\d{3}|800)\\d{5}",
		[
			8,
			9
		],
		[
			[
				"(\\d{3})(\\d{5})",
				"$1 $2",
				[
					"8"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[235]"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"3082[0-5]\\d{4}|3(?:0(?:[237]\\d|8[01])|[167](?:2[0-6]|7\\d|80)|2(?:2[0-5]|7\\d|80)|3(?:2[0-3]|7\\d|80)|4(?:2[013-9]|3[01]|7\\d|80)|5(?:2[0-7]|7\\d|80)|8(?:2[0-2]|7\\d|80)|9(?:[28]0|7\\d))\\d{5}",
				[
					9
				]
			],
			[
				"(?:2(?:[0346-9]\\d|5[67])|5(?:[03-7]\\d|9[1-9]))\\d{6}",
				[
					9
				]
			]
		]
	],
	GI: [
		"350",
		"00",
		"(?:[25]\\d|60)\\d{6}",
		[
			8
		],
		[
			[
				"(\\d{3})(\\d{5})",
				"$1 $2",
				[
					"2"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"2190[0-2]\\d{3}|2(?:0(?:[02]\\d|3[01])|16[24-9]|2[2-5]\\d)\\d{4}"
			],
			[
				"5251[0-4]\\d{3}|(?:5(?:[146-8]\\d\\d|250)|60(?:1[01]|6\\d))\\d{4}"
			]
		]
	],
	GL: [
		"299",
		"00",
		"(?:19|[2-689]\\d|70)\\d{4}",
		[
			6
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3",
				[
					"19|[2-9]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:19|3[1-7]|[68][1-9]|70|9\\d)\\d{4}"
			],
			[
				"[245]\\d{5}"
			]
		]
	],
	GM: [
		"220",
		"00",
		"[2-9]\\d{6}",
		[
			7
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"[2-9]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:4(?:[23]\\d\\d|4(?:1[024679]|[6-9]\\d))|5(?:5(?:3\\d|4[0-7])|6[67]\\d|7(?:1[04]|2[035]|3[58]|48))|8[0-589]\\d\\d)\\d{3}"
			],
			[
				"556\\d{4}|(?:[23679]\\d|4[015]|5[0-489]|8[67])\\d{5}"
			]
		]
	],
	GN: [
		"224",
		"00",
		"722\\d{6}|(?:3|6\\d)\\d{7}",
		[
			8,
			9
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"3"
				]
			],
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[67]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"3(?:0(?:24|3[12]|4[1-35-7]|5[13]|6[189]|[78]1|9[1478])|1\\d\\d)\\d{4}",
				[
					8
				]
			],
			[
				"6[0-356]\\d{7}",
				[
					9
				]
			]
		]
	],
	GP: [
		"590",
		"00",
		"(?:590\\d|7090)\\d{5}|(?:69|80|9\\d)\\d{7}",
		[
			9
		],
		[
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[5-79]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"8"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"590(?:0[1-68]|[14][0-24-9]|2[0-68]|3[1-9]|5[3-579]|[68][0-689]|7[08]|9\\d)\\d{4}"
			],
			[
				"(?:69(?:0\\d\\d|1(?:2[2-9]|3[0-5])|4(?:0[89]|1[2-6]|9\\d)|6(?:1[016-9]|5[0-4]|[67]\\d))|7090[0-4])\\d{4}"
			]
		]
	],
	GQ: [
		"240",
		"00",
		"222\\d{6}|(?:3\\d|55|[89]0)\\d{7}",
		[
			9
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[235]"
				]
			],
			[
				"(\\d{3})(\\d{6})",
				"$1 $2",
				[
					"[89]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"33[0-24-9]\\d[46]\\d{4}|3(?:33|5\\d)\\d[7-9]\\d{4}"
			],
			[
				"(?:222|55\\d)\\d{6}"
			]
		]
	],
	GR: [
		"30",
		"00",
		"5005000\\d{3}|8\\d{9,11}|(?:[269]\\d|70)\\d{8}",
		[
			10,
			11,
			12
		],
		[
			[
				"(\\d{2})(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"21|7"
				]
			],
			[
				"(\\d{4})(\\d{6})",
				"$1 $2",
				[
					"2(?:2|3[2-57-9]|4[2-469]|5[2-59]|6[2-9]|7[2-69]|8[2-49])|5"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[2689]"
				]
			],
			[
				"(\\d{3})(\\d{3,4})(\\d{5})",
				"$1 $2 $3",
				[
					"8"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"2(?:1\\d\\d|2(?:2[1-46-9]|[36][1-8]|4[1-7]|5[1-4]|7[1-5]|[89][1-9])|3(?:1\\d|2[1-57]|[35][1-3]|4[13]|7[1-7]|8[124-6]|9[1-79])|4(?:1\\d|2[1-8]|3[1-4]|4[13-5]|6[1-578]|9[1-5])|5(?:1\\d|[29][1-4]|3[1-5]|4[124]|5[1-6])|6(?:1\\d|[269][1-6]|3[1245]|4[1-7]|5[13-9]|7[14]|8[1-5])|7(?:1\\d|2[1-5]|3[1-6]|4[1-7]|5[1-57]|6[135]|9[125-7])|8(?:1\\d|2[1-5]|[34][1-4]|9[1-57]))\\d{6}",
				[
					10
				]
			],
			[
				"68[57-9]\\d{7}|(?:69|94)\\d{8}",
				[
					10
				]
			]
		]
	],
	GT: [
		"502",
		"00",
		"80\\d{6}|(?:1\\d{3}|[2-7])\\d{7}",
		[
			8,
			11
		],
		[
			[
				"(\\d{4})(\\d{4})",
				"$1 $2",
				[
					"[2-8]"
				]
			],
			[
				"(\\d{4})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"1"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"[267][2-9]\\d{6}",
				[
					8
				]
			],
			[
				"(?:[3-5]\\d\\d|80[0-4])\\d{5}",
				[
					8
				]
			]
		]
	],
	GU: [
		"1",
		"011",
		"(?:[58]\\d\\d|671|900)\\d{7}",
		[
			10
		],
		0,
		"1",
		0,
		"([2-9]\\d{6})$|1",
		"671$1",
		0,
		"671",
		[
			[
				"671(?:2\\d\\d|3(?:00|3[39]|4[349]|55|6[26])|4(?:00|56|7[1-9]|8[02-9])|5(?:55|6[2-5]|88)|6(?:3[2-578]|4[24-9]|5[34]|78|8[235-9])|7(?:[0479]7|2[0167]|3[45]|8[7-9])|8(?:[2-57-9]8|6[478])|9(?:2[29]|6[79]|7[1279]|8[7-9]|9[78]))\\d{4}"
			],
			[
				"671(?:2\\d\\d|3(?:00|3[39]|4[349]|55|6[26])|4(?:00|56|7[1-9]|8[02-9])|5(?:55|6[2-5]|88)|6(?:3[2-578]|4[24-9]|5[34]|78|8[235-9])|7(?:[0479]7|2[0167]|3[45]|8[7-9])|8(?:[2-57-9]8|6[478])|9(?:2[29]|6[79]|7[1279]|8[7-9]|9[78]))\\d{4}"
			]
		]
	],
	GW: [
		"245",
		"00",
		"[49]\\d{8}|4\\d{6}",
		[
			7,
			9
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"40"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[49]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"443\\d{6}",
				[
					9
				]
			],
			[
				"9(?:5\\d|6[569]|77)\\d{6}",
				[
					9
				]
			]
		]
	],
	GY: [
		"592",
		"001",
		"(?:[2-8]\\d{3}|9008)\\d{3}",
		[
			7
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"[2-9]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:2(?:1[6-9]|2[0-35-9]|3[1-4]|5[3-9]|6\\d|7[0-79])|3(?:2[25-9]|3\\d)|4(?:4[0-24]|5[56])|50[0-6]|77[1-57])\\d{4}"
			],
			[
				"(?:51[01]|6\\d\\d|7(?:[0-5]\\d|6[0-39]|70))\\d{4}"
			]
		]
	],
	HK: [
		"852",
		"00(?:30|5[09]|[126-9]?)",
		"8[0-46-9]\\d{6,7}|9\\d{4,7}|(?:[2-7]|9\\d{3})\\d{7}",
		[
			5,
			6,
			7,
			8,
			9,
			11
		],
		[
			[
				"(\\d{3})(\\d{2,5})",
				"$1 $2",
				[
					"900",
					"9003"
				]
			],
			[
				"(\\d{4})(\\d{4})",
				"$1 $2",
				[
					"[2-7]|8[1-4]|9(?:0[1-9]|[1-8])"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"8"
				]
			],
			[
				"(\\d{3})(\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3 $4",
				[
					"9"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:2(?:[13-9]\\d|2[013-9])\\d|3(?:(?:[1569][0-24-9]|4[0-246-9]|7[0-24-69])\\d|8(?:4[0-8]|[579]\\d|6[0-5]))|58(?:0[1-9]|1[2-9]))\\d{4}",
				[
					8
				]
			],
			[
				"(?:4(?:44[0-35-9]|6(?:4[0-57-9]|6[0-6])|7(?:3[0-4]|4[0-48]|6[0-5]))|5(?:35[4-8]|73[0-6]|95[0-8])|6(?:26[013-8]|(?:66|78)[0-5])|70(?:7[1-8]|8[0-8])|84(?:4[0-2]|8[0-35-9])|9(?:29[013-9]|39[014-9]|59[0-467]|899))\\d{4}|(?:4(?:4[0-35-9]|6[0-357-9]|7[0-25])|5(?:[1-59][0-46-9]|6[0-4689]|7[0-246-9])|6(?:0[1-9]|[13-59]\\d|[268][0-57-9]|7[0-79])|70[1-59]|84[0-39]|9(?:0[1-9]|1[02-9]|[2358][0-8]|[467]\\d))\\d{5}",
				[
					8
				]
			]
		],
		"00"
	],
	HN: [
		"504",
		"00",
		"8\\d{10}|[237-9]\\d{7}",
		[
			8,
			11
		],
		[
			[
				"(\\d{4})(\\d{4})",
				"$1-$2",
				[
					"[237-9]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"2(?:2(?:0[0-59]|1[1-9]|[23]\\d|4[02-7]|5[57]|6[245]|7[0135689]|8[01346-9]|9[0-2])|4(?:0[578]|2[3-59]|3[13-9]|4[0-68]|5[1-3589])|5(?:0[2357-9]|1[1-356]|4[03-5]|5\\d|6[014-69]|7[04]|80)|6(?:[056]\\d|17|2[067]|3[047]|4[0-378]|[78][0-8]|9[01])|7(?:0[5-79]|6[46-9]|7[02-9]|8[034]|91)|8(?:79|8[0-357-9]|9[1-57-9]))\\d{4}",
				[
					8
				]
			],
			[
				"[37-9]\\d{7}",
				[
					8
				]
			]
		]
	],
	HR: [
		"385",
		"00",
		"[2-69]\\d{8}|80\\d{5,7}|[1-79]\\d{7}|6\\d{6}",
		[
			7,
			8,
			9
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{3})",
				"$1 $2 $3",
				[
					"6[01]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{2})(\\d{2,3})",
				"$1 $2 $3",
				[
					"8"
				],
				"0$1"
			],
			[
				"(\\d)(\\d{4})(\\d{3})",
				"$1 $2 $3",
				[
					"1"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"6|7[245]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"9"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"[2-57]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"8"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"1\\d{7}|(?:2[0-3]|3[1-5]|4[02-47-9]|5[1-3])\\d{6,7}",
				[
					8,
					9
				]
			],
			[
				"9(?:(?:0[1-9]|[12589]\\d)\\d\\d|7(?:[0679]\\d\\d|5(?:[01]\\d|44|55|77|9[5-79])))\\d{4}|98\\d{6}",
				[
					8,
					9
				]
			]
		]
	],
	HT: [
		"509",
		"00",
		"[2-589]\\d{7}",
		[
			8
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{4})",
				"$1 $2 $3",
				[
					"[2-589]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"2(?:2\\d|5[1-5]|81|9[149])\\d{5}"
			],
			[
				"(?:[34]\\d|5[56])\\d{6}"
			]
		]
	],
	HU: [
		"36",
		"00",
		"[235-7]\\d{8}|[1-9]\\d{7}",
		[
			8,
			9
		],
		[
			[
				"(\\d)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"1"
				],
				"(06 $1)"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[27][2-9]|3[2-7]|4[24-9]|5[2-79]|6|8[2-57-9]|9[2-69]"
				],
				"(06 $1)"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"[2-9]"
				],
				"06 $1"
			]
		],
		"06",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:1\\d|[27][2-9]|3[2-7]|4[24-9]|5[2-79]|6[23689]|8[2-57-9]|9[2-69])\\d{6}",
				[
					8
				]
			],
			[
				"(?:[257]0|3[01])\\d{7}",
				[
					9
				]
			]
		]
	],
	ID: [
		"62",
		"00[89]",
		"00[1-9]\\d{9,14}|(?:[1-36]|8\\d{5})\\d{6}|00\\d{9}|[1-9]\\d{8,10}|[2-9]\\d{7}",
		[
			7,
			8,
			9,
			10,
			11,
			12,
			13,
			14,
			15,
			16,
			17
		],
		[
			[
				"(\\d)(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"15"
				]
			],
			[
				"(\\d{2})(\\d{5,9})",
				"$1 $2",
				[
					"2[124]|[36]1"
				],
				"(0$1)"
			],
			[
				"(\\d{3})(\\d{5,7})",
				"$1 $2",
				[
					"800"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{5,8})",
				"$1 $2",
				[
					"[2-79]"
				],
				"(0$1)"
			],
			[
				"(\\d{3})(\\d{3,4})(\\d{3})",
				"$1-$2-$3",
				[
					"8[1-35-9]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{6,8})",
				"$1 $2",
				[
					"1"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"804"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d)(\\d{3})(\\d{3})",
				"$1 $2 $3 $4",
				[
					"80"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{4})(\\d{4,5})",
				"$1-$2-$3",
				[
					"8"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"2[124]\\d{7,8}|619\\d{8}|2(?:1(?:14|500)|2\\d{3})\\d{3}|61\\d{5,8}|(?:2(?:[35][1-4]|6[0-8]|7[1-6]|8\\d|9[1-8])|3(?:1|[25][1-8]|3[1-68]|4[1-3]|6[1-3568]|7[0-469]|8\\d)|4(?:0[1-589]|1[01347-9]|2[0-36-8]|3[0-24-68]|43|5[1-378]|6[1-5]|7[134]|8[1245])|5(?:1[1-35-9]|2[25-8]|3[124-9]|4[1-3589]|5[1-46]|6[1-8])|6(?:[25]\\d|3[1-69]|4[1-6])|7(?:02|[125][1-9]|[36]\\d|4[1-8]|7[0-36-9])|9(?:0[12]|1[013-8]|2[0-479]|5[125-8]|6[23679]|7[159]|8[01346]))\\d{5,8}",
				[
					7,
					8,
					9,
					10,
					11
				]
			],
			[
				"8[1-35-9]\\d{7,10}",
				[
					9,
					10,
					11,
					12
				]
			]
		]
	],
	IE: [
		"353",
		"00",
		"(?:1\\d|[2569])\\d{6,8}|4\\d{6,9}|7\\d{8}|8\\d{8,9}",
		[
			7,
			8,
			9,
			10
		],
		[
			[
				"(\\d{2})(\\d{5})",
				"$1 $2",
				[
					"2[24-9]|47|58|6[237-9]|9[35-9]"
				],
				"(0$1)"
			],
			[
				"(\\d{3})(\\d{5})",
				"$1 $2",
				[
					"[45]0"
				],
				"(0$1)"
			],
			[
				"(\\d)(\\d{3,4})(\\d{4})",
				"$1 $2 $3",
				[
					"1"
				],
				"(0$1)"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"[2569]|4[1-69]|7[14]"
				],
				"(0$1)"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"70"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"81"
				],
				"(0$1)"
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[78]"
				],
				"0$1"
			],
			[
				"(\\d{4})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"1"
				]
			],
			[
				"(\\d{2})(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"4"
				],
				"(0$1)"
			],
			[
				"(\\d{2})(\\d)(\\d{3})(\\d{4})",
				"$1 $2 $3 $4",
				[
					"8"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:1\\d|21)\\d{6,7}|(?:2[24-9]|4(?:0[24]|5\\d|7)|5(?:0[45]|1\\d|8)|6(?:1\\d|[237-9])|9(?:1\\d|[35-9]))\\d{5}|(?:23|4(?:[1-469]|8\\d)|5[23679]|6[4-6]|7[14]|9[04])\\d{7}"
			],
			[
				"8(?:22|[35-9]\\d)\\d{6}",
				[
					9
				]
			]
		]
	],
	IL: [
		"972",
		"0(?:0|1[2-9])",
		"1\\d{6}(?:\\d{3,5})?|[57]\\d{8}|[1-489]\\d{7}",
		[
			7,
			8,
			9,
			10,
			11,
			12
		],
		[
			[
				"(\\d{4})(\\d{3})",
				"$1-$2",
				[
					"125"
				]
			],
			[
				"(\\d{4})(\\d{2})(\\d{2})",
				"$1-$2-$3",
				[
					"121"
				]
			],
			[
				"(\\d)(\\d{3})(\\d{4})",
				"$1-$2-$3",
				[
					"[2-489]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1-$2-$3",
				[
					"[57]"
				],
				"0$1"
			],
			[
				"(\\d{4})(\\d{3})(\\d{3})",
				"$1-$2-$3",
				[
					"12"
				]
			],
			[
				"(\\d{4})(\\d{6})",
				"$1-$2",
				[
					"159"
				]
			],
			[
				"(\\d)(\\d{3})(\\d{3})(\\d{3})",
				"$1-$2-$3-$4",
				[
					"1[7-9]"
				]
			],
			[
				"(\\d{3})(\\d{1,2})(\\d{3})(\\d{4})",
				"$1-$2 $3-$4",
				[
					"15"
				]
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"153\\d{8,9}|29[1-9]\\d{5}|(?:2[0-8]|[3489]\\d)\\d{6}",
				[
					8,
					11,
					12
				]
			],
			[
				"55(?:4(?:0[01]|10|5[0-7])|57[0-289])\\d{4}|5(?:(?:[0-2][02-9]|[36]\\d|[49][2-9]|8[3-7])\\d|5(?:01|2\\d|3[0-3]|4[34]|5[0-25689]|6[6-8]|7[0-267]|8[7-9]|9[1-9]))\\d{5}",
				[
					9
				]
			]
		]
	],
	IM: [
		"44",
		"00",
		"1624\\d{6}|(?:[3578]\\d|90)\\d{8}",
		[
			10
		],
		0,
		"0",
		0,
		"([25-8]\\d{5})$|0|180020",
		"1624$1",
		0,
		"74576|(?:16|7[56])24",
		[
			[
				"1624(?:230|[5-8]\\d\\d)\\d{3}"
			],
			[
				"76245[06]\\d{4}|7(?:4576|[59]24\\d|624[0-4689])\\d{5}"
			]
		]
	],
	IN: [
		"91",
		"00",
		"(?:000800|[2-9]\\d\\d)\\d{7}|1\\d{7,12}",
		[
			8,
			9,
			10,
			11,
			12,
			13
		],
		[
			[
				"(\\d{8})",
				"$1",
				[
					"5(?:0|2[23]|3[03]|[67]1|88)",
					"5(?:0|2(?:21|3)|3(?:0|3[23])|616|717|888)",
					"5(?:0|2(?:21|3)|3(?:0|3[23])|616|717|8888)"
				],
				0,
				1
			],
			[
				"(\\d{4})(\\d{4,5})",
				"$1 $2",
				[
					"180",
					"1800"
				],
				0,
				1
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"140"
				],
				0,
				1
			],
			[
				"(\\d{2})(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"11|2[02]|33|4[04]|79[1-7]|80[2-46]",
					"11|2[02]|33|4[04]|79(?:[1-6]|7[19])|80(?:[2-4]|6[0-589])",
					"11|2[02]|33|4[04]|79(?:[124-6]|3(?:[02-9]|1[0-24-9])|7(?:1|9[1-6]))|80(?:[2-4]|6[0-589])"
				],
				"0$1",
				1
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"1(?:2[0-249]|3[0-25]|4[145]|[68]|7[1257])|2(?:1[257]|3[013]|4[01]|5[0137]|6[0158]|78|8[1568])|3(?:26|4[1-3]|5[34]|6[01489]|7[02-46]|8[159])|4(?:1[36]|2[1-47]|5[12]|6[0-26-9]|7[0-24-9]|8[013-57]|9[014-7])|5(?:1[025]|22|[36][25]|4[28]|5[12]|[78]1)|6(?:12|[2-4]1|5[17]|6[13]|80)|7(?:12|3[134]|4[47]|61|88)|8(?:16|2[014]|3[126]|6[136]|7[078]|8[34]|91)|(?:43|59|75)[15]|(?:1[59]|29|67|72)[14]",
					"1(?:2[0-24]|3[0-25]|4[145]|[59][14]|6[1-9]|7[1257]|8[1-57-9])|2(?:1[257]|3[013]|4[01]|5[0137]|6[058]|78|8[1568]|9[14])|3(?:26|4[1-3]|5[34]|6[01489]|7[02-46]|8[159])|4(?:1[36]|2[1-47]|3[15]|5[12]|6[0-26-9]|7[0-24-9]|8[013-57]|9[014-7])|5(?:1[025]|22|[36][25]|4[28]|[578]1|9[15])|674|7(?:(?:2[14]|3[34]|5[15])[2-6]|61[346]|88[0-8])|8(?:70[2-6]|84[235-7]|91[3-7])|(?:1(?:29|60|8[06])|261|552|6(?:12|[2-47]1|5[17]|6[13]|80)|7(?:12|31|4[47])|8(?:16|2[014]|3[126]|6[136]|7[78]|83))[2-7]",
					"1(?:2[0-24]|3[0-25]|4[145]|[59][14]|6[1-9]|7[1257]|8[1-57-9])|2(?:1[257]|3[013]|4[01]|5[0137]|6[058]|78|8[1568]|9[14])|3(?:26|4[1-3]|5[34]|6[01489]|7[02-46]|8[159])|4(?:1[36]|2[1-47]|3[15]|5[12]|6[0-26-9]|7[0-24-9]|8[013-57]|9[014-7])|5(?:1[025]|22|[36][25]|4[28]|[578]1|9[15])|6(?:12(?:[2-6]|7[0-8])|74[2-7])|7(?:(?:2[14]|5[15])[2-6]|3171|61[346]|88(?:[2-7]|82))|8(?:70[2-6]|84(?:[2356]|7[19])|91(?:[3-6]|7[19]))|73[134][2-6]|(?:74[47]|8(?:16|2[014]|3[126]|6[136]|7[78]|83))(?:[2-6]|7[19])|(?:1(?:29|60|8[06])|261|552|6(?:[2-4]1|5[17]|6[13]|7(?:1|4[0189])|80)|7(?:12|88[01]))[2-7]"
				],
				"0$1",
				1
			],
			[
				"(\\d{4})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"1(?:[2-479]|5[0235-9])|[2-5]|6(?:1[1358]|2[2457-9]|3[2-5]|4[235-7]|5[2-689]|6[24578]|7[235689]|8[1-6])|7(?:1[013-9]|28|3[129]|4[1-35689]|5[29]|6[02-5]|70)|807",
					"1(?:[2-479]|5[0235-9])|[2-5]|6(?:1[1358]|2(?:[2457]|84|95)|3(?:[2-4]|55)|4[235-7]|5[2-689]|6[24578]|7[235689]|8[1-6])|7(?:1(?:[013-8]|9[6-9])|28[6-8]|3(?:17|2[0-49]|9[2-57])|4(?:1[2-4]|[29][0-7]|3[0-8]|[56]|8[0-24-7])|5(?:2[1-3]|9[0-6])|6(?:0[5689]|2[5-9]|3[02-8]|4|5[0-367])|70[13-7])|807[19]",
					"1(?:[2-479]|5(?:[0236-9]|5[013-9]))|[2-5]|6(?:2(?:84|95)|355|83)|73179|807(?:1|9[1-3])|(?:1552|6(?:1[1358]|2[2457]|3[2-4]|4[235-7]|5[2-689]|6[24578]|7[235689]|8[124-6])\\d|7(?:1(?:[013-8]\\d|9[6-9])|28[6-8]|3(?:2[0-49]|9[2-57])|4(?:1[2-4]|[29][0-7]|3[0-8]|[56]\\d|8[0-24-7])|5(?:2[1-3]|9[0-6])|6(?:0[5689]|2[5-9]|3[02-8]|4\\d|5[0-367])|70[13-7]))[2-7]"
				],
				"0$1",
				1
			],
			[
				"(\\d{5})(\\d{5})",
				"$1 $2",
				[
					"[6-9]"
				],
				"0$1",
				1
			],
			[
				"(\\d{4})(\\d{2,4})(\\d{4})",
				"$1 $2 $3",
				[
					"1(?:6|8[06])",
					"1(?:6|8[06]0)"
				],
				0,
				1
			],
			[
				"(\\d{4})(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3 $4",
				[
					"18"
				],
				0,
				1
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"2717(?:[2-7]\\d|95)\\d{4}|(?:271[0-689]|782[0-6])[2-7]\\d{5}|(?:170[24]|2(?:(?:[02][2-79]|90)\\d|80[13468])|(?:3(?:23|80)|683|79[1-7])\\d|4(?:20[24]|72[2-8])|552[1-7])\\d{6}|(?:11|33|4[04]|80)[2-7]\\d{7}|(?:342|674|788)(?:[0189][2-7]|[2-7]\\d)\\d{5}|(?:1(?:2[0-249]|3[0-25]|4[145]|[59][14]|6[014]|7[1257]|8[01346])|2(?:1[257]|3[013]|4[01]|5[0137]|6[0158]|78|8[1568]|9[14])|3(?:26|4[13]|5[34]|6[01489]|7[02-46]|8[159])|4(?:1[36]|2[1-47]|3[15]|5[12]|6[0-26-9]|7[014-9]|8[013-57]|9[014-7])|5(?:1[025]|22|[36][25]|4[28]|[578]1|9[15])|6(?:12|[2-47]1|5[17]|6[13]|80)|7(?:12|2[14]|3[134]|4[47]|5[15]|[67]1)|8(?:16|2[014]|3[126]|6[136]|7[078]|8[34]|91))[2-7]\\d{6}|(?:1(?:2[35-8]|3[346-9]|4[236-9]|[59][0235-9]|6[235-9]|7[34689]|8[257-9])|2(?:1[134689]|3[24-8]|4[2-8]|5[25689]|6[2-4679]|7[3-79]|8[2-479]|9[235-9])|3(?:01|1[79]|2[1245]|4[5-8]|5[125689]|6[235-7]|7[157-9]|8[2-46-8])|4(?:1[14578]|2[5689]|3[2-467]|5[4-7]|6[35]|73|8[2689]|9[2389])|5(?:[16][146-9]|2[14-8]|3[1346]|4[14-69]|5[46]|7[2-4]|8[2-8]|9[246])|6(?:1[1358]|2[2457]|3[2-4]|4[235-7]|5[2-689]|6[24578]|7[235689]|8[124-6])|7(?:1[013-9]|2[0235-9]|3[2679]|4[1-35689]|5[2-46-9]|[67][02-9]|8[013-7]|9[089])|8(?:1[1357-9]|2[235-8]|3[03-57-9]|4[0-24-9]|5\\d|6[2457-9]|7[1-6]|8[1256]|9[2-4]))\\d[2-7]\\d{5}",
				[
					10
				]
			],
			[
				"(?:61279|7(?:887[02-9]|9(?:313|79[07-9]))|8(?:079[04-9]|(?:84|91)7[02-8]))\\d{5}|(?:6(?:12|[2-47]1|5[17]|6[13]|80)[0189]|7(?:1(?:2[0189]|9[0-5])|2(?:[14][017-9]|8[0-59])|3(?:2[5-8]|[34][017-9]|9[016-9])|4(?:1[015-9]|[29][89]|39|8[389])|5(?:[15][017-9]|2[04-9]|9[7-9])|6(?:0[0-47]|1[0-257-9]|2[0-4]|3[19]|5[4589])|70[0289]|88[089]|97[02-8])|8(?:0(?:6[67]|7[02-8])|70[017-9]|84[01489]|91[0-289]))\\d{6}|(?:7(?:31|4[47])|8(?:16|2[014]|3[126]|6[136]|7[78]|83))(?:[0189]\\d|7[02-8])\\d{5}|(?:6(?:[09]\\d|1[04679]|2[03689]|3[05-9]|4[0489]|50|6[069]|7[07]|8[7-9])|7(?:0\\d|2[0235-79]|3[05-8]|40|5[0346-8]|6[6-9]|7[1-9]|8[0-79]|9[089])|8(?:0[01589]|1[0-57-9]|2[235-9]|3[03-57-9]|[45]\\d|6[02457-9]|7[1-69]|8[0-25-9]|9[02-9])|9\\d\\d)\\d{7}|(?:6(?:(?:1[1358]|2[2457]|3[2-4]|4[235-7]|5[2-689]|6[24578]|8[124-6])\\d|7(?:[235689]\\d|4[0189]))|7(?:1(?:[013-8]\\d|9[6-9])|28[6-8]|3(?:2[0-49]|9[2-5])|4(?:1[2-4]|[29][0-7]|3[0-8]|[56]\\d|8[0-24-7])|5(?:2[1-3]|9[0-6])|6(?:0[5689]|2[5-9]|3[02-8]|4\\d|5[0-367])|70[13-7]|881))[0189]\\d{5}",
				[
					10
				]
			]
		]
	],
	IO: [
		"246",
		"00",
		"3\\d{6}",
		[
			7
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"3"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"37\\d{5}"
			],
			[
				"38\\d{5}"
			]
		]
	],
	IQ: [
		"964",
		"00",
		"(?:1|7\\d\\d)\\d{7}|[2-6]\\d{7,8}",
		[
			8,
			9,
			10
		],
		[
			[
				"(\\d)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"1"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"[2-6]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"7"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"1\\d{7}|(?:2[13-5]|3[02367]|4[023]|5[03]|6[026])\\d{6,7}",
				[
					8,
					9
				]
			],
			[
				"7[3-9]\\d{8}",
				[
					10
				]
			]
		]
	],
	IR: [
		"98",
		"00",
		"[1-9]\\d{9}|(?:[1-8]\\d\\d|9)\\d{3,4}",
		[
			4,
			5,
			6,
			7,
			10
		],
		[
			[
				"(\\d{4,5})",
				"$1",
				[
					"96"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{4,5})",
				"$1 $2",
				[
					"(?:1[137]|2[13-68]|3[1458]|4[145]|5[1468]|6[16]|7[1467]|8[13467])[12689]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"9"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"[1-8]"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:1[137]|2[13-68]|3[1458]|4[145]|5[1468]|6[16]|7[1467]|8[13467])(?:[03-57]\\d{7}|[16]\\d{3}(?:\\d{4})?|[289]\\d{3}(?:\\d(?:\\d{3})?)?)|94(?:000[09]|(?:12\\d|30[0-2])\\d|2(?:121|[2689]0\\d)|4(?:111|40\\d))\\d{4}",
				[
					6,
					7,
					10
				]
			],
			[
				"9(?:(?:0[0-5]|[13]\\d|2[0-3])\\d\\d|9(?:[0-46]\\d\\d|5(?:10|5\\d)|8(?:[12]\\d|88)|9(?:0[0-3]|[19]\\d|21|69|77|8[7-9])))\\d{5}",
				[
					10
				]
			]
		]
	],
	IS: [
		"354",
		"00|1(?:0(?:01|[12]0)|100)",
		"(?:38\\d|[4-9])\\d{6}",
		[
			7,
			9
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"[4-9]"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"3"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:4(?:1[0-24-69]|2[0-7]|[37][0-8]|4[0-24589]|5[0-68]|6\\d|8[0-36-8])|5(?:05|[156]\\d|2[02578]|3[0-579]|4[03-7]|7[0-2578]|8[0-35-9]|9[013-689])|872)\\d{4}",
				[
					7
				]
			],
			[
				"(?:38[589]\\d\\d|6(?:1[1-8]|2[0-6]|3[026-9]|4[014679]|5[0159]|6[0-69]|70|8[06-8]|9\\d)|7(?:5[057]|[6-9]\\d)|8(?:2[0-59]|[3-69]\\d|8[238]))\\d{4}"
			]
		],
		"00"
	],
	IT: [
		"39",
		"00",
		"0\\d{5,11}|1\\d{8,10}|3(?:[0-8]\\d{7,10}|9\\d{7,8})|(?:43|55|70)\\d{8}|8\\d{5}(?:\\d{2,4})?",
		[
			6,
			7,
			8,
			9,
			10,
			11,
			12
		],
		[
			[
				"(\\d{2})(\\d{4,6})",
				"$1 $2",
				[
					"0[26]"
				]
			],
			[
				"(\\d{3})(\\d{3,6})",
				"$1 $2",
				[
					"0[13-57-9][0159]|8(?:03|4[17]|9[2-5])",
					"0[13-57-9][0159]|8(?:03|4[17]|9(?:2|3[04]|[45][0-4]))"
				]
			],
			[
				"(\\d{4})(\\d{2,6})",
				"$1 $2",
				[
					"0(?:[13-579][2-46-8]|8[236-8])"
				]
			],
			[
				"(\\d{4})(\\d{4})",
				"$1 $2",
				[
					"894"
				]
			],
			[
				"(\\d{2})(\\d{3,4})(\\d{4})",
				"$1 $2 $3",
				[
					"0[26]|5"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"1(?:44|[679])|[378]|43"
				]
			],
			[
				"(\\d{3})(\\d{3,4})(\\d{4})",
				"$1 $2 $3",
				[
					"0[13-57-9][0159]|14"
				]
			],
			[
				"(\\d{2})(\\d{4})(\\d{5})",
				"$1 $2 $3",
				[
					"0[26]"
				]
			],
			[
				"(\\d{4})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"0"
				]
			],
			[
				"(\\d{3})(\\d{4})(\\d{4,5})",
				"$1 $2 $3",
				[
					"[03]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"0(?:669[0-79]\\d{1,6}|831\\d{2,8})|0(?:1(?:[0159]\\d|[27][1-5]|31|4[1-4]|6[1356]|8[2-57])|2\\d\\d|3(?:[0159]\\d|2[1-4]|3[12]|[48][1-6]|6[2-59]|7[1-7])|4(?:[0159]\\d|[23][1-9]|4[245]|6[1-5]|7[1-4]|81)|5(?:[0159]\\d|2[1-5]|3[2-6]|4[1-79]|6[4-6]|7[1-578]|8[3-8])|6(?:[0-57-9]\\d|6[0-8])|7(?:[0159]\\d|2[12]|3[1-7]|4[2-46]|6[13569]|7[13-6]|8[1-59])|8(?:[0159]\\d|2[3-578]|3[2356]|[6-8][1-5])|9(?:[0159]\\d|[238][1-5]|4[12]|6[1-8]|7[1-6]))\\d{2,7}"
			],
			[
				"3[2-9]\\d{7,8}|(?:31|43)\\d{8}",
				[
					9,
					10
				]
			]
		]
	],
	JE: [
		"44",
		"00",
		"1534\\d{6}|(?:[3578]\\d|90)\\d{8}",
		[
			10
		],
		0,
		"0",
		0,
		"([0-24-8]\\d{5})$|0|180020",
		"1534$1",
		0,
		0,
		[
			[
				"1534[0-24-8]\\d{5}"
			],
			[
				"7(?:(?:(?:50|82)9|937)\\d|7(?:00[378]|97\\d))\\d{5}"
			]
		]
	],
	JM: [
		"1",
		"011",
		"(?:[58]\\d\\d|658|900)\\d{7}",
		[
			10
		],
		0,
		"1",
		0,
		0,
		0,
		0,
		"658|876",
		[
			[
				"8766060\\d{3}|(?:658(?:2(?:[0-8]\\d|9[0-46-9])|[3-9]\\d\\d)|876(?:52[35]|6(?:0[1-3579]|1[0235-9]|[23]\\d|40|5[06]|6[2-589]|7[0-25-9]|8[04]|9[4-9])|7(?:0[2-689]|[1-6]\\d|8[056]|9[45])|9(?:0[1-8]|1[02378]|[2-8]\\d|9[2-468])))\\d{4}"
			],
			[
				"(?:658295|876(?:2(?:0[1-9]|[13-9]\\d|2[013-9])|[348]\\d\\d|5(?:0[1-9]|[1-9]\\d)|6(?:4[89]|6[67])|7(?:0[07]|7\\d|8[1-47-9]|9[0-36-9])|9(?:[01]9|9[0579])))\\d{4}"
			]
		]
	],
	JO: [
		"962",
		"00",
		"(?:(?:[2689]|7\\d)\\d|32|427|53)\\d{6}",
		[
			8,
			9
		],
		[
			[
				"(\\d)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[2356]|87"
				],
				"(0$1)"
			],
			[
				"(\\d{3})(\\d{5,6})",
				"$1 $2",
				[
					"[89]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{7})",
				"$1 $2",
				[
					"70"
				],
				"0$1"
			],
			[
				"(\\d)(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"[47]"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"87(?:000|90[01])\\d{3}|(?:2(?:6(?:2[0-35-9]|3[0-578]|4[24-7]|5[0-24-8]|[6-8][023]|9[0-3])|7(?:0[1-79]|10|2[014-7]|3[0-689]|4[019]|5[0-3578]))|32(?:0[1-69]|1[1-35-7]|2[024-7]|3\\d|4[0-3]|[5-7][023])|53(?:0[0-3]|[13][023]|2[0-59]|49|5[0-35-9]|6[15]|7[45]|8[1-6]|9[0-36-9])|6(?:2(?:[05]0|22)|3(?:00|33)|4(?:0[0-25]|1[2-7]|2[0569]|[38][07-9]|4[025689]|6[0-589]|7\\d|9[0-2])|5(?:[01][056]|2[034]|3[0-57-9]|4[178]|5[0-69]|6[0-35-9]|7[1-379]|8[0-68]|9[0239]))|87(?:20|7[078]|99))\\d{4}",
				[
					8
				]
			],
			[
				"(?:427|7(?:[78][0-25-9]|9\\d))\\d{6}",
				[
					9
				]
			]
		]
	],
	JP: [
		"81",
		"010",
		"00[1-9]\\d{6,14}|[25-9]\\d{9}|(?:00|[1-9]\\d\\d)\\d{6}",
		[
			8,
			9,
			10,
			11,
			12,
			13,
			14,
			15,
			16,
			17
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1-$2-$3",
				[
					"(?:12|57|99)0"
				],
				"0$1"
			],
			[
				"(\\d{4})(\\d)(\\d{4})",
				"$1-$2-$3",
				[
					"1(?:26|3[79]|4[56]|5[4-68]|6[3-5])|499|5(?:76|97)|746|8(?:3[89]|47|51)|9(?:80|9[16])",
					"1(?:267|3(?:7[247]|9[278])|466|5(?:47|58|64)|6(?:3[245]|48|5[4-68]))|499[2468]|5(?:76|97)9|7468|8(?:3(?:8[7-9]|96)|477|51[2-9])|9(?:802|9(?:1[23]|69))|1(?:45|58)[67]",
					"1(?:267|3(?:7[247]|9[278])|466|5(?:47|58|64)|6(?:3[245]|48|5[4-68]))|499[2468]|5(?:769|979[2-69])|7468|8(?:3(?:8[7-9]|96[2457-9])|477|51[2-9])|9(?:802|9(?:1[23]|69))|1(?:45|58)[67]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1-$2-$3",
				[
					"60"
				],
				"0$1"
			],
			[
				"(\\d)(\\d{4})(\\d{4})",
				"$1-$2-$3",
				[
					"3|4(?:2[09]|7[01])|6[1-9]",
					"3|4(?:2(?:0|9[02-69])|7(?:0[019]|1))|6[1-9]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1-$2-$3",
				[
					"1(?:1|5[45]|77|88|9[69])|2(?:2[1-37]|3[0-269]|4[59]|5|6[24]|7[1-358]|8[1369]|9[0-38])|4(?:[28][1-9]|3[0-57]|[45]|6[248]|7[2-579]|9[29])|5(?:2|3[0459]|4[0-369]|5[29]|8[02389]|9[0-389])|7(?:2[02-46-9]|34|[58]|6[0249]|7[57]|9[2-6])|8(?:2[124589]|3[26-9]|49|51|6|7[0-468]|8[68]|9[019])|9(?:[23][1-9]|4[15]|5[138]|6[1-3]|7[156]|8[189]|9[1-489])",
					"1(?:1|5(?:4[018]|5[017])|77|88|9[69])|2(?:2(?:[127]|3[014-9])|3[0-269]|4[59]|5(?:[1-3]|5[0-69]|9[19])|62|7(?:[1-35]|8[0189])|8(?:[16]|3[0134]|9[0-5])|9(?:[028]|17))|4(?:2(?:[13-79]|8[014-6])|3[0-57]|[45]|6[248]|7[2-47]|8[1-9]|9[29])|5(?:2|3(?:[045]|9[0-8])|4[0-369]|5[29]|8[02389]|9[0-3])|7(?:2[02-46-9]|34|[58]|6[0249]|7[57]|9(?:[23]|4[0-59]|5[01569]|6[0167]))|8(?:2(?:[1258]|4[0-39]|9[0-2469])|3(?:[29]|60)|49|51|6(?:[0-24]|36|5[0-3589]|7[23]|9[01459])|7[0-468]|8[68])|9(?:[23][1-9]|4[15]|5[138]|6[1-3]|7[156]|8[189]|9(?:[1289]|3[34]|4[0178]))|(?:264|837)[016-9]|2(?:57|93)[015-9]|(?:25[0468]|422|838)[01]|(?:47[59]|59[89]|8(?:6[68]|9))[019]",
					"1(?:1|5(?:4[018]|5[017])|77|88|9[69])|2(?:2[127]|3[0-269]|4[59]|5(?:[1-3]|5[0-69]|9(?:17|99))|6(?:2|4[016-9])|7(?:[1-35]|8[0189])|8(?:[16]|3[0134]|9[0-5])|9(?:[028]|17))|4(?:2(?:[13-79]|8[014-6])|3[0-57]|[45]|6[248]|7[2-47]|9[29])|5(?:2|3(?:[045]|9(?:[0-58]|6[4-9]|7[0-35689]))|4[0-369]|5[29]|8[02389]|9[0-3])|7(?:2[02-46-9]|34|[58]|6[0249]|7[57]|9(?:[23]|4[0-59]|5[01569]|6[0167]))|8(?:2(?:[1258]|4[0-39]|9[0169])|3(?:[29]|60|7(?:[017-9]|6[6-8]))|49|51|6(?:[0-24]|36[2-57-9]|5(?:[0-389]|5[23])|6(?:[01]|9[178])|7(?:2[2-468]|3[78])|9[0145])|7[0-468]|8[68])|9(?:4[15]|5[138]|7[156]|8[189]|9(?:[1289]|3(?:31|4[357])|4[0178]))|(?:8294|96)[1-3]|2(?:57|93)[015-9]|(?:223|8699)[014-9]|(?:25[0468]|422|838)[01]|(?:48|8292|9[23])[1-9]|(?:47[59]|59[89]|8(?:68|9))[019]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{2})(\\d{4})",
				"$1-$2-$3",
				[
					"[14]|[289][2-9]|5[3-9]|7[2-4679]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1-$2-$3",
				[
					"800"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{4})(\\d{4})",
				"$1-$2-$3",
				[
					"[25-9]"
				],
				"0$1"
			]
		],
		"0",
		0,
		"(000[2569]\\d{4,6})$|(?:(?:003768)0?)|0",
		"$1",
		0,
		0,
		[
			[
				"(?:1(?:1[235-8]|2[3-6]|3[3-9]|4[2-6]|[58][2-8]|6[2-7]|7[2-9]|9[1-9])|(?:2[2-9]|[36][1-9])\\d|4(?:[2-578]\\d|6[02-8]|9[2-59])|5(?:[2-589]\\d|6[1-9]|7[2-8])|7(?:[25-9]\\d|3[4-9]|4[02-9])|8(?:[2679]\\d|3[2-9]|4[5-9]|5[1-9]|8[03-9])|9(?:[2-58]\\d|[679][1-9]))\\d{6}",
				[
					9
				]
			],
			[
				"(?:601[0-4]0|[7-9]0[1-9]\\d\\d)\\d{5}",
				[
					10
				]
			]
		]
	],
	KE: [
		"254",
		"000",
		"(?:[17]\\d\\d|900)\\d{6}|(?:2|80)0\\d{6,7}|[4-6]\\d{6,8}",
		[
			7,
			8,
			9,
			10
		],
		[
			[
				"(\\d{2})(\\d{5,7})",
				"$1 $2",
				[
					"[24-6]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{6})",
				"$1 $2",
				[
					"[17]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"[89]"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:4[245]|5[1-79]|6[01457-9])\\d{5,7}|(?:4[136]|5[08]|62)\\d{7}|(?:[24]0|66)\\d{6,7}",
				[
					7,
					8,
					9
				]
			],
			[
				"(?:1(?:0[0-8]|1\\d|2[014]|[34]0)|7\\d\\d)\\d{6}",
				[
					9
				]
			]
		]
	],
	KG: [
		"996",
		"00",
		"8\\d{9}|[235-9]\\d{8}",
		[
			9,
			10
		],
		[
			[
				"(\\d{4})(\\d{5})",
				"$1 $2",
				[
					"3(?:1[346]|[24-79])"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[235-79]|88"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d)(\\d{2,3})",
				"$1 $2 $3 $4",
				[
					"8"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"312(?:5[0-79]\\d|9(?:[0-689]\\d|7[0-24-9]))\\d{3}|(?:3(?:1(?:2[0-46-8]|3[1-9]|47|[56]\\d)|2(?:22|3[0-479]|6[0-7])|4(?:22|5[6-9]|6\\d)|5(?:22|3[4-7]|59|6\\d)|6(?:22|5[35-7]|6\\d)|7(?:22|3[468]|4[1-9]|59|[67]\\d)|9(?:22|4[1-8]|6\\d))|6(?:09|12|2[2-4])\\d)\\d{5}",
				[
					9
				]
			],
			[
				"312(?:58\\d|973)\\d{3}|(?:2(?:0[0-35]|2\\d)|5[0-24-7]\\d|600|7(?:[07]\\d|55)|88[08]|9(?:12|9[05-9]))\\d{6}",
				[
					9
				]
			]
		]
	],
	KH: [
		"855",
		"00[14-9]",
		"1\\d{9}|[1-9]\\d{7,8}",
		[
			8,
			9,
			10
		],
		[
			[
				"(\\d{2})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"[1-9]"
				],
				"0$1"
			],
			[
				"(\\d{4})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"1"
				]
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"23(?:4(?:[2-4]|[56]\\d)|[568]\\d\\d)\\d{4}|23[236-9]\\d{5}|(?:2[4-6]|3[2-6]|4[2-4]|[5-7][2-5])(?:(?:[237-9]|4[56]|5\\d)\\d{5}|6\\d{5,6})",
				[
					8,
					9
				]
			],
			[
				"(?:(?:1[28]|3[18]|9[67])\\d|6[016-9]|7(?:[07-9]|[16]\\d)|8(?:[013-79]|8\\d))\\d{6}|(?:1\\d|9[0-57-9])\\d{6}|(?:2[3-6]|3[2-6]|4[2-4]|[5-7][2-5])48\\d{5}",
				[
					8,
					9
				]
			]
		]
	],
	KI: [
		"686",
		"00",
		"(?:[37]\\d|6[0-79])\\d{6}|(?:[2-48]\\d|50)\\d{3}",
		[
			5,
			8
		],
		0,
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:[24]\\d|3[1-9]|50|65(?:02[12]|12[56]|22[89]|[3-5]00)|7(?:27\\d\\d|3100|5(?:02[12]|12[56]|22[89]|[34](?:00|81)|500))|8[0-5])\\d{3}"
			],
			[
				"(?:6200[01]|7(?:310[1-9]|5(?:02[03-9]|12[0-47-9]|22[0-7]|[34](?:0[1-9]|8[02-9])|50[1-9])))\\d{3}|(?:63\\d\\d|7(?:(?:[0146-9]\\d|2[0-689])\\d|3(?:[02-9]\\d|1[1-9])|5(?:[0-2][013-9]|[34][1-79]|5[1-9]|[6-9]\\d)))\\d{4}",
				[
					8
				]
			]
		]
	],
	KM: [
		"269",
		"00",
		"[3478]\\d{6}",
		[
			7
		],
		[
			[
				"(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2 $3",
				[
					"[3478]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"7[4-7]\\d{5}"
			],
			[
				"[34]\\d{6}"
			]
		]
	],
	KN: [
		"1",
		"011",
		"(?:[58]\\d\\d|900)\\d{7}",
		[
			10
		],
		0,
		"1",
		0,
		"([2-7]\\d{6})$|1",
		"869$1",
		0,
		"869",
		[
			[
				"869(?:2(?:29|36)|302|4(?:6[015-9]|70)|56[5-7])\\d{4}"
			],
			[
				"869(?:48[89]|55[6-8]|66\\d|76[02-7])\\d{4}"
			]
		]
	],
	KP: [
		"850",
		"00|99",
		"85\\d{6}|(?:19\\d|[2-7])\\d{7}",
		[
			8,
			10
		],
		[
			[
				"(\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"8"
				],
				"0$1"
			],
			[
				"(\\d)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[2-7]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"1"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:(?:195|2)\\d|3[19]|4[159]|5[37]|6[17]|7[39]|85)\\d{6}"
			],
			[
				"19[1-3]\\d{7}",
				[
					10
				]
			]
		]
	],
	KR: [
		"82",
		"00(?:[125689]|3(?:[46]5|91)|7(?:00|27|3|55|6[126]))",
		"00[1-9]\\d{8,11}|(?:[12]|5\\d{3})\\d{7}|[13-6]\\d{9}|(?:[1-6]\\d|80)\\d{7}|[3-6]\\d{4,5}|(?:00|7)0\\d{8}",
		[
			5,
			6,
			8,
			9,
			10,
			11,
			12,
			13,
			14
		],
		[
			[
				"(\\d{2})(\\d{3,4})",
				"$1-$2",
				[
					"(?:3[1-3]|[46][1-4]|5[1-5])1"
				],
				"0$1"
			],
			[
				"(\\d{4})(\\d{4})",
				"$1-$2",
				[
					"1"
				]
			],
			[
				"(\\d)(\\d{3,4})(\\d{4})",
				"$1-$2-$3",
				[
					"2"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1-$2-$3",
				[
					"[36]0|8"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3,4})(\\d{4})",
				"$1-$2-$3",
				[
					"[1346]|5[1-5]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{4})(\\d{4})",
				"$1-$2-$3",
				[
					"[57]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{5})(\\d{4})",
				"$1-$2-$3",
				[
					"5"
				],
				"0$1"
			]
		],
		"0",
		0,
		"0(8(?:[1-46-8]|5\\d\\d))?",
		0,
		0,
		0,
		[
			[
				"(?:2|3[1-3]|[46][1-4]|5[1-5])[1-9]\\d{6,7}|(?:3[1-3]|[46][1-4]|5[1-5])1\\d{2,3}",
				[
					5,
					6,
					8,
					9,
					10
				]
			],
			[
				"1(?:05(?:[0-8]\\d|9[0-6])|22[13]\\d)\\d{4,5}|1(?:0[0-46-9]|[16-9]\\d|2[013-9])\\d{6,7}",
				[
					9,
					10
				]
			]
		]
	],
	KW: [
		"965",
		"00",
		"18\\d{5}|(?:[2569]\\d|41)\\d{6}",
		[
			7,
			8
		],
		[
			[
				"(\\d{4})(\\d{3,4})",
				"$1 $2",
				[
					"[169]|2(?:[235]|4[1-35-9])|52"
				]
			],
			[
				"(\\d{3})(\\d{5})",
				"$1 $2",
				[
					"[245]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"2(?:[23]\\d\\d|4(?:[1-35-9]\\d|44)|5(?:0[034]|[2-46]\\d|5[1-3]|7[1-7]))\\d{4}",
				[
					8
				]
			],
			[
				"(?:41\\d\\d|5(?:(?:[05]\\d|1[0-7]|6[56])\\d|2(?:22|5[25])|7(?:55|77)|88[58])|6(?:(?:0[034679]|5[015-9]|6\\d)\\d|1(?:00|11|6[16])|2[26]2|3[36]3|4[46]4|7(?:0[013-9]|[67]\\d)|8[68]8|9(?:[069]\\d|3[039]))|9(?:(?:[04679]\\d|8[057-9])\\d|1(?:00|1[01]|99)|2(?:00|2\\d)|3(?:00|3[03])|5(?:00|5\\d)))\\d{4}",
				[
					8
				]
			]
		]
	],
	KY: [
		"1",
		"011",
		"(?:345|[58]\\d\\d|900)\\d{7}",
		[
			10
		],
		0,
		"1",
		0,
		"([2-9]\\d{6})$|1",
		"345$1",
		0,
		"345",
		[
			[
				"345(?:2(?:22|3[23]|44|66)|333|444|6(?:23|38|40)|7(?:30|4[35-79]|6[6-9]|77)|8(?:00|1[45]|4[89]|88)|9(?:14|4[035-9]))\\d{4}"
			],
			[
				"345(?:32[1-9]|42[0-4]|5(?:1[67]|2[5-79]|4[6-9]|50|76)|649|82[56]|9(?:1[679]|2[2-9]|3[06-9]|90))\\d{4}"
			]
		]
	],
	KZ: [
		"7",
		"810",
		"(?:33622|8\\d{8})\\d{5}|[78]\\d{9}",
		[
			10,
			14
		],
		0,
		"8",
		0,
		0,
		0,
		0,
		"33622|7",
		[
			[
				"(?:33622|7(?:1(?:0(?:[23]\\d|4[0-3]|59|63)|1(?:[23]\\d|4[0-79]|59)|2(?:[23]\\d|59)|3(?:2\\d|3[0-79]|4[0-35-9]|59)|4(?:[24]\\d|3[013-9]|5[1-9]|97)|5(?:2\\d|3[1-9]|4[0-7]|59)|6(?:[2-4]\\d|5[19]|61)|72\\d|8(?:[27]\\d|3[1-46-9]|4[0-5]|59))|2(?:1(?:[23]\\d|4[46-9]|5[3469])|2(?:2\\d|3[0679]|46|5[12679])|3(?:[2-4]\\d|5[139])|4(?:2\\d|3[1-35-9]|59)|5(?:[23]\\d|4[0-8]|59|61)|6(?:2\\d|3[1-9]|4[0-4]|59)|7(?:[2379]\\d|40|5[279])|8(?:[23]\\d|4[0-3]|59)|9(?:2\\d|3[124578]|59))))\\d{5}",
				[
					10
				]
			],
			[
				"7(?:0[0-25-8]|47|6[0-4]|7[15-8]|85)\\d{7}",
				[
					10
				]
			]
		],
		"8~10"
	],
	LA: [
		"856",
		"00",
		"[23]\\d{9}|3\\d{8}|(?:[235-8]\\d|41)\\d{6}",
		[
			8,
			9,
			10
		],
		[
			[
				"(\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"2[13]|3[14]|[4-8]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{3})",
				"$1 $2 $3 $4",
				[
					"3"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3 $4",
				[
					"[23]"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:2[13]|[35-7][14]|41|8[1468])\\d{6}",
				[
					8
				]
			],
			[
				"(?:20(?:[23579]\\d|8[78])|30[24]\\d)\\d{6}|30\\d{7}",
				[
					9,
					10
				]
			]
		]
	],
	LB: [
		"961",
		"00",
		"[27-9]\\d{7}|[13-9]\\d{6}",
		[
			7,
			8
		],
		[
			[
				"(\\d)(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[13-69]|7(?:[2-57]|62|8[0-6]|9[04-9])|8[02-9]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[27-9]"
				]
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"7(?:62|8[0-6]|9[04-9])\\d{4}|(?:[14-69]\\d|2(?:[14-69]\\d|[78][1-9])|7[2-57]|8[02-9])\\d{5}"
			],
			[
				"(?:(?:3|81)\\d|7(?:[01]\\d|6[013-9]|8[7-9]|9[0-3]))\\d{5}"
			]
		]
	],
	LC: [
		"1",
		"011",
		"(?:[58]\\d\\d|758|900)\\d{7}",
		[
			10
		],
		0,
		"1",
		0,
		"([2-8]\\d{6})$|1",
		"758$1",
		0,
		"758",
		[
			[
				"758(?:234|4(?:30|5\\d|6[2-9]|8[0-2])|57[0-2]|(?:63|75)8)\\d{4}"
			],
			[
				"758(?:28[4-7]|384|4(?:6[01]|8[4-9])|5(?:1[89]|20|84)|7(?:1[2-9]|2\\d|3[0-3])|812)\\d{4}"
			]
		]
	],
	LI: [
		"423",
		"00",
		"[68]\\d{8}|(?:[2378]\\d|90)\\d{5}",
		[
			7,
			9
		],
		[
			[
				"(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2 $3",
				[
					"[2379]|8(?:0[09]|7)",
					"[2379]|8(?:0(?:02|9)|7)"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"8"
				]
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"69"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"6"
				]
			]
		],
		"0",
		0,
		"(1001)|0",
		0,
		0,
		0,
		[
			[
				"(?:2(?:01|1[27]|2[024]|3\\d|6[02-578]|96)|3(?:[24]0|33|7[0135-7]|8[048]|9[0269]))\\d{4}",
				[
					7
				]
			],
			[
				"(?:6(?:(?:4[5-9]|5\\d)\\d|6(?:[024-68]\\d|1[01]|3[7-9]|70))\\d|7(?:[37-9]\\d|42|56))\\d{4}"
			]
		]
	],
	LK: [
		"94",
		"00",
		"[1-9]\\d{8}",
		[
			9
		],
		[
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"7"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[1-689]"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:12[2-9]|602|8[12]\\d|9(?:1\\d|22|9[245]))\\d{6}|(?:11|2[13-7]|3[1-8]|4[157]|5[12457]|6[35-7])[2-57]\\d{6}"
			],
			[
				"7(?:[0-25-8]\\d|4[0-4])\\d{6}"
			]
		]
	],
	LR: [
		"231",
		"00",
		"(?:[2457]\\d|33|88)\\d{7}|(?:2\\d|[4-6])\\d{6}",
		[
			7,
			8,
			9
		],
		[
			[
				"(\\d)(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"4[67]|[56]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"2"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[2-578]"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"2\\d{7}",
				[
					8
				]
			],
			[
				"(?:(?:(?:22|33)0|555|7(?:6[01]|7\\d)|88\\d)\\d|4(?:240|[67]))\\d{5}|[56]\\d{6}",
				[
					7,
					9
				]
			]
		]
	],
	LS: [
		"266",
		"00",
		"(?:[256]\\d\\d|800)\\d{5}",
		[
			8
		],
		[
			[
				"(\\d{4})(\\d{4})",
				"$1 $2",
				[
					"[2568]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"2\\d{7}"
			],
			[
				"[56]\\d{7}"
			]
		]
	],
	LT: [
		"370",
		"00",
		"(?:[3469]\\d|52|[78]0)\\d{6}",
		[
			8
		],
		[
			[
				"(\\d)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"52[0-7]"
				],
				"(0-$1)",
				1
			],
			[
				"(\\d{3})(\\d{2})(\\d{3})",
				"$1 $2 $3",
				[
					"[7-9]"
				],
				"0 $1",
				1
			],
			[
				"(\\d{2})(\\d{6})",
				"$1 $2",
				[
					"37|4(?:[15]|6[1-8])"
				],
				"(0-$1)",
				1
			],
			[
				"(\\d{3})(\\d{5})",
				"$1 $2",
				[
					"[3-6]"
				],
				"(0-$1)",
				1
			]
		],
		"0",
		0,
		"[08]",
		0,
		0,
		0,
		[
			[
				"(?:3[1478]|4[124-6]|52)\\d{6}"
			],
			[
				"6\\d{7}"
			]
		]
	],
	LU: [
		"352",
		"00",
		"35[013-9]\\d{4,8}|6\\d{8}|35\\d{2,4}|(?:[2457-9]\\d|3[0-46-9])\\d{2,9}",
		[
			4,
			5,
			6,
			7,
			8,
			9,
			10,
			11
		],
		[
			[
				"(\\d{2})(\\d{3})",
				"$1 $2",
				[
					"2(?:0[2-689]|[2-9])|[3-57]|8(?:0[2-9]|[13-9])|9(?:0[89]|[2-579])"
				]
			],
			[
				"(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3",
				[
					"2(?:0[2-689]|[2-9])|[3-57]|8(?:0[2-9]|[13-9])|9(?:0[89]|[2-579])"
				]
			],
			[
				"(\\d{2})(\\d{2})(\\d{3})",
				"$1 $2 $3",
				[
					"20[2-689]"
				]
			],
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{1,2})",
				"$1 $2 $3 $4",
				[
					"2(?:[0367]|4[3-8])"
				]
			],
			[
				"(\\d{3})(\\d{2})(\\d{3})",
				"$1 $2 $3",
				[
					"80[01]|90[015]"
				]
			],
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{3})",
				"$1 $2 $3 $4",
				[
					"20"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"6"
				]
			],
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})(\\d{1,2})",
				"$1 $2 $3 $4 $5",
				[
					"2(?:[0367]|4[3-8])"
				]
			],
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{1,5})",
				"$1 $2 $3 $4",
				[
					"[3-57]|8[13-9]|9(?:0[89]|[2-579])|(?:2|80)[2-9]"
				]
			]
		],
		0,
		0,
		"(15(?:0[06]|1[12]|[35]5|4[04]|6[26]|77|88|99)\\d)",
		0,
		0,
		0,
		[
			[
				"(?:35[013-9]|80[2-9]|90[89])\\d{1,8}|(?:2[2-9]|3[0-46-9]|[457]\\d|8[13-9]|9[2-579])\\d{2,9}"
			],
			[
				"6(?:[269][18]|5[1568]|7[189]|81)\\d{6}",
				[
					9
				]
			]
		]
	],
	LV: [
		"371",
		"00",
		"(?:[268]\\d|78|90)\\d{6}",
		[
			8
		],
		[
			[
				"(\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[2679]|8[01]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"6\\d{7}"
			],
			[
				"2333[0-8]\\d{3}|2(?:[0-24-9]\\d\\d|3(?:0[07]|[14-9]\\d|2[02-9]|3[0-24-9]))\\d{4}"
			]
		]
	],
	LY: [
		"218",
		"00",
		"[2-9]\\d{8}",
		[
			9
		],
		[
			[
				"(\\d{2})(\\d{7})",
				"$1-$2",
				[
					"[2-9]"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:2(?:0[56]|[1-6]\\d|7[124579]|8[124])|3(?:1\\d|2[2356])|4(?:[17]\\d|2[1-357]|5[2-4]|8[124])|5(?:[1347]\\d|2[1-469]|5[13-5]|8[1-4])|6(?:[1-479]\\d|5[2-57]|8[1-5])|7(?:[13]\\d|2[13-79])|8(?:[124]\\d|5[124]|84))\\d{6}"
			],
			[
				"9[1-6]\\d{7}"
			]
		]
	],
	MA: [
		"212",
		"00",
		"[5-8]\\d{8}",
		[
			9
		],
		[
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"5[45]"
				],
				"0$1"
			],
			[
				"(\\d{4})(\\d{5})",
				"$1-$2",
				[
					"5(?:2[2-46-9]|3[3-9]|9)|8(?:0[89]|92)"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{7})",
				"$1-$2",
				[
					"8"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{6})",
				"$1-$2",
				[
					"[5-7]"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"5(?:2(?:[0-25-79]\\d|3[1-578]|4[02-46-8]|8[0235-7])|3(?:[0-47]\\d|5[02-9]|6[02-8]|8[014-9]|9[3-9])|(?:4[067]|5[03])\\d)\\d{5}"
			],
			[
				"(?:6(?:[0-79]\\d|8[0-247-9])|7(?:[0167]\\d|2[0-8]|5[0-5]|8[0-7]))\\d{6}"
			]
		]
	],
	MC: [
		"377",
		"00",
		"(?:[3489]|6\\d)\\d{7}",
		[
			8,
			9
		],
		[
			[
				"(\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"4"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[389]"
				]
			],
			[
				"(\\d)(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4 $5",
				[
					"6"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:870|9[2-47-9]\\d)\\d{5}",
				[
					8
				]
			],
			[
				"4(?:[469]\\d|5[1-9])\\d{5}|(?:3|6\\d)\\d{7}"
			]
		]
	],
	MD: [
		"373",
		"00",
		"(?:[235-7]\\d|[89]0)\\d{6}",
		[
			8
		],
		[
			[
				"(\\d{3})(\\d{5})",
				"$1 $2",
				[
					"[89]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"22|3"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{2})(\\d{3})",
				"$1 $2 $3",
				[
					"[25-7]"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:(?:2[1-9]|3[1-79])\\d|5(?:33|5[257]))\\d{5}"
			],
			[
				"562\\d{5}|(?:6\\d|7[16-9])\\d{6}"
			]
		]
	],
	ME: [
		"382",
		"00",
		"(?:20|[3-79]\\d)\\d{6}|80\\d{6,7}",
		[
			8,
			9
		],
		[
			[
				"(\\d{2})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"[2-9]"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:20[2-8]|3(?:[0-2][2-7]|3[24-7])|4(?:0[2-467]|1[2467])|5(?:0[2467]|1[24-7]|2[2-467]))\\d{5}",
				[
					8
				]
			],
			[
				"6(?:[07-9]\\d|3[024]|6[0-25])\\d{5}",
				[
					8
				]
			]
		]
	],
	MF: [
		"590",
		"00",
		"(?:590\\d|7090)\\d{5}|(?:69|80|9\\d)\\d{7}",
		[
			9
		],
		0,
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"590(?:0[079]|[14]3|[27][79]|3[03-7]|5[0-268]|87)\\d{4}"
			],
			[
				"(?:69(?:0\\d\\d|1(?:2[2-9]|3[0-5])|4(?:0[89]|1[2-6]|9\\d)|6(?:1[016-9]|5[0-4]|[67]\\d))|7090[0-4])\\d{4}"
			]
		]
	],
	MG: [
		"261",
		"00",
		"[23]\\d{8}",
		[
			9
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{3})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[23]"
				],
				"0$1"
			]
		],
		"0",
		0,
		"([24-9]\\d{6})$|0",
		"20$1",
		0,
		0,
		[
			[
				"2072[29]\\d{4}|20(?:2\\d|4[47]|5[3467]|6[279]|7[356]|8[268]|9[2457])\\d{5}"
			],
			[
				"3[2-46-9]\\d{7}"
			]
		]
	],
	MH: [
		"692",
		"011",
		"329\\d{4}|(?:[256]\\d|45)\\d{5}",
		[
			7
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1-$2",
				[
					"[2-6]"
				]
			]
		],
		"1",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:247|528|625)\\d{4}"
			],
			[
				"(?:(?:23|54)5|329|45[35-8])\\d{4}"
			]
		]
	],
	MK: [
		"389",
		"00",
		"[2-578]\\d{7}",
		[
			8
		],
		[
			[
				"(\\d)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"2|34[47]|4(?:[37]7|5[47]|64)"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[347]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d)(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[58]"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:(?:2(?:62|77)0|3444)\\d|4[56]440)\\d{3}|(?:34|4[357])700\\d{3}|(?:2(?:[0-3]\\d|5[0-578]|6[01]|82)|3(?:1[3-68]|[23][2-68]|4[23568])|4(?:[23][2-68]|4[3-68]|5[2568]|6[25-8]|7[24-68]|8[4-68]))\\d{5}"
			],
			[
				"7(?:3555|(?:474|9[019]7)7)\\d{3}|7(?:[0-25-8]\\d\\d|3(?:[1-478]\\d|6[01])|4(?:2\\d|60|7[01578])|9(?:[2-4]\\d|5[01]|7[015]))\\d{4}"
			]
		]
	],
	ML: [
		"223",
		"00",
		"[24-9]\\d{7}",
		[
			8
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[24-9]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"2(?:07[0-8]|12[67])\\d{4}|(?:2(?:02|1[4-689])|4(?:0[0-4]|4[1-59]))\\d{5}"
			],
			[
				"2(?:0(?:01|79)|17\\d)\\d{4}|(?:5[0-3]|[679]\\d|8[2-59])\\d{6}"
			]
		]
	],
	MM: [
		"95",
		"00",
		"1\\d{5,7}|95\\d{6}|(?:[4-7]|9[0-46-9])\\d{6,8}|(?:2|8\\d)\\d{5,8}",
		[
			6,
			7,
			8,
			9,
			10
		],
		[
			[
				"(\\d)(\\d{2})(\\d{3})",
				"$1 $2 $3",
				[
					"16|2"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{2})(\\d{3})",
				"$1 $2 $3",
				[
					"4(?:[2-46]|5[3-5])|5|6(?:[1-689]|7[235-7])|7(?:[0-4]|5[2-7])|8[1-5]|(?:60|86)[23]"
				],
				"0$1"
			],
			[
				"(\\d)(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"[12]|452|678|86",
					"[12]|452|6788|86"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"[4-7]|8[1-35]"
				],
				"0$1"
			],
			[
				"(\\d)(\\d{3})(\\d{4,6})",
				"$1 $2 $3",
				[
					"9(?:2[0-4]|[35-9]|4[137-9])"
				],
				"0$1"
			],
			[
				"(\\d)(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"2"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"8"
				],
				"0$1"
			],
			[
				"(\\d)(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3 $4",
				[
					"92"
				],
				"0$1"
			],
			[
				"(\\d)(\\d{5})(\\d{4})",
				"$1 $2 $3",
				[
					"9"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:1(?:(?:12|[28]\\d|3[56]|7[3-6]|9[0-6])\\d|4(?:2[29]|7[0-2]|83)|6)|2(?:2(?:00|8[34])|4(?:0\\d|22|7[0-2]|83)|51\\d\\d)|4(?:2(?:2\\d\\d|48[013])|3(?:20\\d|4(?:70|83)|56)|420\\d|5(?:2\\d|470))|6(?:0(?:[23]|88\\d)|(?:124|[56]2\\d)\\d|2472|3(?:20\\d|470)|4(?:2[04]\\d|472)|7(?:3\\d\\d|4[67]0|8(?:[01459]\\d|8))))\\d{4}|5(?:2(?:2\\d{5,6}|47[02]\\d{4})|(?:3472|4(?:2(?:1|86)|470)|522\\d|6(?:20\\d|483)|7(?:20\\d|48[01])|8(?:20\\d|47[02])|9(?:20\\d|470))\\d{4})|7(?:(?:0470|4(?:25\\d|470)|5(?:202|470|96\\d))\\d{4}|1(?:20\\d{4,5}|4(?:70|83)\\d{4}))|8(?:1(?:2\\d{5,6}|4(?:10|7[01]\\d)\\d{3})|2(?:2\\d{5,6}|(?:320|490\\d)\\d{3})|(?:3(?:2\\d\\d|470)|4[24-7]|5(?:(?:2\\d|51)\\d|4(?:[1-35-9]\\d|4[0-57-9]))|6[23])\\d{4})|(?:1[2-6]\\d|4(?:2[24-8]|3[2-7]|[46][2-6]|5[3-5])|5(?:[27][2-8]|3[2-68]|4[24-8]|5[23]|6[2-4]|8[24-7]|9[2-7])|6(?:[19]20|42[03-6]|(?:52|7[45])\\d)|7(?:[04][24-8]|[15][2-7]|22|3[2-4])|8(?:1[2-689]|2[2-8]|(?:[35]2|64)\\d))\\d{4}|25\\d{5,6}|(?:2[2-9]|6(?:1[2356]|[24][2-6]|3[24-6]|5[2-4]|6[2-8]|7[235-7]|8[245]|9[24])|8(?:3[24]|5[245]))\\d{4}",
				[
					6,
					7,
					8,
					9
				]
			],
			[
				"(?:17[01]|9(?:2(?:[0-4]|[56]\\d\\d)|(?:3(?:[0-36]|4\\d)|(?:6\\d|8[89]|9[4-8])\\d|7(?:3|40|[5-9]\\d))\\d|4(?:(?:[0245]\\d|[1379])\\d|88)|5[0-6])\\d)\\d{4}|9[69]1\\d{6}|9(?:[68]\\d|9[089])\\d{5}",
				[
					7,
					8,
					9,
					10
				]
			]
		]
	],
	MN: [
		"976",
		"001",
		"[12]\\d{7,9}|[5-9]\\d{7}",
		[
			8,
			9,
			10
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{4})",
				"$1 $2 $3",
				[
					"[12]1"
				],
				"0$1"
			],
			[
				"(\\d{4})(\\d{4})",
				"$1 $2",
				[
					"[5-9]"
				]
			],
			[
				"(\\d{3})(\\d{5,6})",
				"$1 $2",
				[
					"[12]2[1-3]"
				],
				"0$1"
			],
			[
				"(\\d{4})(\\d{5,6})",
				"$1 $2",
				[
					"[12](?:27|3[2-8]|4[2-68]|5[1-4689])",
					"[12](?:27|3[2-8]|4[2-68]|5[1-4689])[0-3]"
				],
				"0$1"
			],
			[
				"(\\d{5})(\\d{4,5})",
				"$1 $2",
				[
					"[12]"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"[12]2[1-3]\\d{5,6}|(?:(?:[12](?:1|27)|5[368])\\d\\d|7(?:0(?:[0-5]\\d|7[078]|80)|128))\\d{4}|[12](?:3[2-8]|4[2-68]|5[1-4689])\\d{6,7}"
			],
			[
				"92[0139]\\d{5}|(?:5[05]|6[069]|7[28]|8[0135689]|9[013-9])\\d{6}",
				[
					8
				]
			]
		]
	],
	MO: [
		"853",
		"00",
		"0800\\d{3}|(?:28|[68]\\d)\\d{6}",
		[
			7,
			8
		],
		[
			[
				"(\\d{4})(\\d{3})",
				"$1 $2",
				[
					"0"
				]
			],
			[
				"(\\d{4})(\\d{4})",
				"$1 $2",
				[
					"[268]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:28[2-9]|8(?:11|[2-57-9]\\d))\\d{5}",
				[
					8
				]
			],
			[
				"6800[0-79]\\d{3}|6(?:[235]\\d\\d|6(?:0[0-5]|[1-9]\\d)|8(?:0[1-9]|[14-8]\\d|2[5-9]|[39][0-4]))\\d{4}",
				[
					8
				]
			]
		]
	],
	MP: [
		"1",
		"011",
		"[58]\\d{9}|(?:67|90)0\\d{7}",
		[
			10
		],
		0,
		"1",
		0,
		"([2-9]\\d{6})$|1",
		"670$1",
		0,
		"670",
		[
			[
				"670(?:2(?:3[3-7]|56|8[4-8])|32[1-38]|4(?:33|8[348])|5(?:32|55|88)|6(?:64|70|82)|78[3589]|8[3-9]8|989)\\d{4}"
			],
			[
				"670(?:2(?:3[3-7]|56|8[4-8])|32[1-38]|4(?:33|8[348])|5(?:32|55|88)|6(?:64|70|82)|78[3589]|8[3-9]8|989)\\d{4}"
			]
		]
	],
	MQ: [
		"596",
		"00",
		"(?:596\\d|7091)\\d{5}|(?:69|[89]\\d)\\d{7}",
		[
			9
		],
		[
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[5-79]|8(?:0[6-9]|[36])"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"8"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:596(?:[03-7]\\d|1[05]|2[7-9]|8[0-39]|9[04-9])|80[6-9]\\d\\d|9(?:477[6-9]|767[4589]))\\d{4}"
			],
			[
				"(?:69[67]\\d\\d|7091[0-3])\\d{4}"
			]
		]
	],
	MR: [
		"222",
		"00",
		"(?:[2-4]\\d\\d|800)\\d{5}",
		[
			8
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[2-48]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:25[08]|35\\d|45[1-7])\\d{5}"
			],
			[
				"[2-4][0-46-9]\\d{6}"
			]
		]
	],
	MS: [
		"1",
		"011",
		"(?:[58]\\d\\d|664|900)\\d{7}",
		[
			10
		],
		0,
		"1",
		0,
		"([34]\\d{6})$|1",
		"664$1",
		0,
		"664",
		[
			[
				"6644(?:1[0-3]|91)\\d{4}"
			],
			[
				"664(?:3(?:49|9[1-6])|49[2-6])\\d{4}"
			]
		]
	],
	MT: [
		"356",
		"00",
		"3550\\d{4}|(?:[2579]\\d\\d|800)\\d{5}",
		[
			8
		],
		[
			[
				"(\\d{4})(\\d{4})",
				"$1 $2",
				[
					"[2357-9]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"20(?:3[1-4]|6[059])\\d{4}|2(?:0[19]|[1-357]\\d|60)\\d{5}"
			],
			[
				"(?:7(?:210|[79]\\d\\d)|9(?:[29]\\d\\d|69[67]|8(?:1[1-3]|89|97)))\\d{4}"
			]
		]
	],
	MU: [
		"230",
		"0(?:0|[24-7]0|3[03])",
		"(?:[57]|8\\d\\d)\\d{7}|[2-468]\\d{6}",
		[
			7,
			8,
			10
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"[2-46]|8[013]"
				]
			],
			[
				"(\\d{4})(\\d{4})",
				"$1 $2",
				[
					"[57]"
				]
			],
			[
				"(\\d{5})(\\d{5})",
				"$1 $2",
				[
					"8"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:2(?:[0346-8]\\d|1[0-8])|4(?:[013568]\\d|2[4-8]|71|90)|54(?:[3-5]\\d|71)|6\\d\\d|8(?:14|3[129]))\\d{4}",
				[
					7,
					8
				]
			],
			[
				"5(?:4(?:2[1-389]|7[1-9])|87[15-8])\\d{4}|(?:5(?:2[5-9]|4[3-689]|[57]\\d|8[0-689]|9[0-8])|7(?:0[0-6]|3[013]))\\d{5}",
				[
					8
				]
			]
		],
		"020"
	],
	MV: [
		"960",
		"0(?:0|19)",
		"(?:800|9[0-57-9]\\d)\\d{7}|[34679]\\d{6}",
		[
			7,
			10
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1-$2",
				[
					"[34679]"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[89]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:3(?:0[0-4]|3[0-59])|6(?:[58][024689]|6[024-68]|7[02468]))\\d{4}",
				[
					7
				]
			],
			[
				"(?:46[46]|[79]\\d\\d)\\d{4}",
				[
					7
				]
			]
		],
		"00"
	],
	MW: [
		"265",
		"00",
		"(?:[1289]\\d|31|77)\\d{7}|1\\d{6}",
		[
			7,
			9
		],
		[
			[
				"(\\d)(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"1[2-9]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"2"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[137-9]"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:1[2-9]|2[12]\\d\\d)\\d{5}"
			],
			[
				"111\\d{6}|(?:31|77|[89][89])\\d{7}",
				[
					9
				]
			]
		]
	],
	MX: [
		"52",
		"0[09]",
		"[2-9]\\d{9}",
		[
			10
		],
		[
			[
				"(\\d{2})(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"33|5[56]|81"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[2-9]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:2(?:0[01]|2\\d|3[1-35-8]|4[13-9]|7[1-689]|8[1-578]|9[467])|3(?:1[1-79]|[2458][1-9]|3\\d|7[1-8]|9[1-5])|4(?:1[1-57-9]|[267][1-9]|3[1-8]|[45]\\d|8[1-35-9]|9[2-689])|5(?:[56]\\d|88|9[1-79])|6(?:1[2-68]|[2-4][1-9]|5[1-36-9]|6[0-57-9]|7[1-7]|8[67]|9[4-8])|7(?:[1346][1-9]|[27]\\d|5[13-9]|8[1-69]|9[17])|8(?:1\\d|2[13-689]|3[1-6]|4[124-6]|6[1246-9]|7[0-378]|9[12479])|9(?:1[346-9]|2[1-4]|3[2-46-8]|5[1348]|[69]\\d|7[12]|8[1-8]))\\d{7}"
			],
			[
				"(?:2(?:2\\d|3[1-35-8]|4[13-9]|7[1-689]|8[1-578]|9[467])|3(?:1[1-79]|[2458][1-9]|3\\d|7[1-8]|9[1-5])|4(?:1[1-57-9]|[267][1-9]|3[1-8]|[45]\\d|8[1-35-9]|9[2-689])|5(?:[56]\\d|88|9[1-79])|6(?:1[2-68]|[2-4][1-9]|5[1-36-9]|6[0-57-9]|7[1-7]|8[67]|9[4-8])|7(?:[1346][1-9]|[27]\\d|5[13-9]|8[1-69]|9[17])|8(?:1\\d|2[13-689]|3[1-6]|4[124-6]|6[1246-9]|7[0-378]|9[12479])|9(?:1[346-9]|2[1-4]|3[2-46-8]|5[1348]|[69]\\d|7[12]|8[1-8]))\\d{7}"
			]
		],
		"00"
	],
	MY: [
		"60",
		"00",
		"1\\d{8,9}|(?:3\\d|[4-9])\\d{7}",
		[
			8,
			9,
			10
		],
		[
			[
				"(\\d)(\\d{3})(\\d{4})",
				"$1-$2 $3",
				[
					"[4-79]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3,4})",
				"$1-$2 $3",
				[
					"1(?:[02469]|[378][1-9]|53)|8",
					"1(?:[02469]|[37][1-9]|53|8(?:[1-46-9]|5[7-9]))|8"
				],
				"0$1"
			],
			[
				"(\\d)(\\d{4})(\\d{4})",
				"$1-$2 $3",
				[
					"3"
				],
				"0$1"
			],
			[
				"(\\d)(\\d{3})(\\d{2})(\\d{4})",
				"$1-$2-$3-$4",
				[
					"1(?:[367]|80)"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1-$2 $3",
				[
					"15"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{4})(\\d{4})",
				"$1-$2 $3",
				[
					"1"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"427[01]\\d{4}|(?:3(?:2[0-36-9]|3[0-368]|4[0-278]|5[0-24-8]|6[0-467]|7[1246-9]|8\\d|9[0-57])\\d|4(?:2[0-689]|[3-79]\\d|8[1-35689])|5(?:2[0-589]|[3468]\\d|5[0-489]|7[1-9]|9[23])|6(?:2[2-9]|3[1357-9]|[46]\\d|5[0-6]|7[0-35-9]|85|9[015-8])|7(?:[2579]\\d|3[03-68]|4[0-8]|6[5-9]|8[0-35-9])|8(?:[24][2-8]|3[2-5]|5[2-7]|6[2-589]|7[2-578]|[89][2-9])|9(?:0[57]|13|[25-7]\\d|[3489][0-8]))\\d{5}",
				[
					8,
					9
				]
			],
			[
				"1(?:1888[689]|4400|8(?:47|8[27])[0-4])\\d{4}|1(?:0(?:[23568]\\d|4[0-6]|7[016-9]|9[0-8])|1(?:[1-5]\\d\\d|6(?:0[5-9]|[1-9]\\d)|7(?:[0-4]\\d|5[0-7]))|(?:[269]\\d|[37][1-9]|4[235-9])\\d|5(?:31|9\\d\\d)|8(?:1[23]|[236]\\d|4[06]|5(?:46|[7-9])|7[016-9]|8[01]|9[0-8]))\\d{5}",
				[
					9,
					10
				]
			]
		]
	],
	MZ: [
		"258",
		"00",
		"(?:2|8\\d)\\d{7}",
		[
			8,
			9
		],
		[
			[
				"(\\d{2})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"2|8[2-79]"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"8"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"2(?:[1346]\\d|5[0-2]|[78][12]|93)\\d{5}",
				[
					8
				]
			],
			[
				"8[2-79]\\d{7}",
				[
					9
				]
			]
		]
	],
	NA: [
		"264",
		"00",
		"[68]\\d{7,8}",
		[
			8,
			9
		],
		[
			[
				"(\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"88"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"6"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"87"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"8"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"64426\\d{3}|6(?:1(?:2[2-7]|3[01378]|4[0-4])|254|32[0237]|4(?:27|41|5[25])|52[236-8]|626|7(?:2[2-4]|30))\\d{4,5}|6(?:1(?:(?:0\\d|2[0189]|3[24-69]|4[5-9])\\d|17|69|7[014])|2(?:17|5[0-36-8]|69|70)|3(?:17|2[14-689]|34|6[289]|7[01]|81)|4(?:17|2[0-2]|4[06]|5[0137]|69|7[01])|5(?:17|2[0459]|69|7[01])|6(?:17|25|38|42|69|7[01])|7(?:17|2[569]|3[13]|6[89]|7[01]))\\d{4}"
			],
			[
				"(?:60|8[1245])\\d{7}",
				[
					9
				]
			]
		]
	],
	NC: [
		"687",
		"00",
		"(?:050|[2-57-9]\\d\\d)\\d{3}",
		[
			6
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})",
				"$1.$2.$3",
				[
					"[02-57-9]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:2[03-9]|3[0-5]|4[1-7]|88)\\d{4}"
			],
			[
				"(?:[579]\\d|8[0-79])\\d{4}"
			]
		]
	],
	NE: [
		"227",
		"00",
		"[027-9]\\d{7}",
		[
			8
		],
		[
			[
				"(\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"08"
				]
			],
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[089]|2[013]|7[0467]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"2(?:0(?:20|3[1-8]|4[13-5]|5[14]|6[14578]|7[1-578])|1(?:4[145]|5[14]|6[14-68]|7[169]|88))\\d{4}"
			],
			[
				"(?:23|7[0467]|[89]\\d)\\d{6}"
			]
		]
	],
	NF: [
		"672",
		"00",
		"[13]\\d{5}",
		[
			6
		],
		[
			[
				"(\\d{2})(\\d{4})",
				"$1 $2",
				[
					"1[0-3]"
				]
			],
			[
				"(\\d)(\\d{5})",
				"$1 $2",
				[
					"[13]"
				]
			]
		],
		0,
		0,
		"([0-258]\\d{4})$",
		"3$1",
		0,
		0,
		[
			[
				"(?:1(?:06|17|28|39)|3[0-2]\\d)\\d{3}"
			],
			[
				"(?:14|3[58])\\d{4}"
			]
		]
	],
	NG: [
		"234",
		"009",
		"(?:20|9\\d)\\d{8}|[78]\\d{9,13}",
		[
			10,
			11,
			12,
			13,
			14
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"[7-9]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"20[129]"
				],
				"0$1"
			],
			[
				"(\\d{4})(\\d{2})(\\d{4})",
				"$1 $2 $3",
				[
					"2"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{4})(\\d{4,5})",
				"$1 $2 $3",
				[
					"[78]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{5})(\\d{5,6})",
				"$1 $2 $3",
				[
					"[78]"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"20(?:[1259]\\d|3[013-9]|4[1-8]|6[024-689]|7[1-79]|8[2-9])\\d{6}",
				[
					10
				]
			],
			[
				"(?:702[0-24-9]|819[01])\\d{6}|(?:7(?:0[13-9]|[12]\\d)|8(?:0[1-9]|1[0-8])|9(?:0[1-9]|1[1-6]))\\d{7}",
				[
					10
				]
			]
		]
	],
	NI: [
		"505",
		"00",
		"(?:1800|[25-8]\\d{3})\\d{4}",
		[
			8
		],
		[
			[
				"(\\d{4})(\\d{4})",
				"$1 $2",
				[
					"[125-8]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"2\\d{7}"
			],
			[
				"(?:5(?:5[0-7]|[78]\\d)|6(?:20|3[035]|4[045]|5[05]|77|8[1-9]|9[059])|(?:7[5-8]|8\\d)\\d)\\d{5}"
			]
		]
	],
	NL: [
		"31",
		"00",
		"(?:[124-7]\\d\\d|3(?:[02-9]\\d|1[0-8]))\\d{6}|8\\d{6,9}|9\\d{6,10}|1\\d{4,5}",
		[
			5,
			6,
			7,
			8,
			9,
			10,
			11
		],
		[
			[
				"(\\d{3})(\\d{4,7})",
				"$1 $2",
				[
					"[89]0"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{7})",
				"$1 $2",
				[
					"66"
				],
				"0$1"
			],
			[
				"(\\d)(\\d{8})",
				"$1 $2",
				[
					"6"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"1[16-8]|2[259]|3[124]|4[17-9]|5[124679]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[1-578]|91"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{5})",
				"$1 $2 $3",
				[
					"9"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:1(?:[035]\\d|1[13-578]|6[124-8]|7[24]|8[0-467])|2(?:[0346]\\d|2[2-46-9]|5[125]|9[479])|3(?:[03568]\\d|1[3-8]|2[01]|4[1-8])|4(?:[0356]\\d|1[1-368]|7[58]|8[15-8]|9[23579])|5(?:[0358]\\d|[19][1-9]|2[1-57-9]|4[13-8]|6[126]|7[0-3578])|7\\d\\d)\\d{6}",
				[
					9
				]
			],
			[
				"(?:6[1-58]|970\\d)\\d{7}",
				[
					9,
					11
				]
			]
		]
	],
	NO: [
		"47",
		"00",
		"(?:0|[2-9]\\d{3})\\d{4}",
		[
			5,
			8
		],
		[
			[
				"(\\d{3})(\\d{2})(\\d{3})",
				"$1 $2 $3",
				[
					"8"
				]
			],
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[2-79]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		"[02-689]|7[0-8]",
		[
			[
				"(?:2[1-4]|3[1-3578]|5[1-35-7]|6[1-4679]|7[0-8])\\d{6}",
				[
					8
				]
			],
			[
				"(?:4[015-8]|9\\d)\\d{6}",
				[
					8
				]
			]
		]
	],
	NP: [
		"977",
		"00",
		"(?:1\\d|9)\\d{9}|[1-9]\\d{7}",
		[
			8,
			10,
			11
		],
		[
			[
				"(\\d)(\\d{7})",
				"$1-$2",
				[
					"1[2-6]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{6})",
				"$1-$2",
				[
					"1[01]|[2-8]|9(?:[1-59]|[67][2-6])"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{7})",
				"$1-$2",
				[
					"9"
				]
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:1[0-6]\\d|99[02-6])\\d{5}|(?:2[13-79]|3[135-8]|4[146-9]|5[135-7]|6[13-9]|7[15-9]|8[1-46-9]|9[1-7])[2-6]\\d{5}",
				[
					8
				]
			],
			[
				"9(?:00|6[0-3]|7[0-24-6]|8[0-24-68])\\d{7}",
				[
					10
				]
			]
		]
	],
	NR: [
		"674",
		"00",
		"(?:222|444|(?:55|8\\d)\\d|666|777|999)\\d{4}",
		[
			7
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"[24-9]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"444\\d{4}"
			],
			[
				"(?:222|55[3-9]|666|777|8\\d\\d|999)\\d{4}"
			]
		]
	],
	NU: [
		"683",
		"00",
		"(?:[4-7]|888\\d)\\d{3}",
		[
			4,
			7
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"8"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"[47]\\d{3}",
				[
					4
				]
			],
			[
				"(?:[56]|888[1-9])\\d{3}"
			]
		]
	],
	NZ: [
		"64",
		"0(?:0|161)",
		"[1289]\\d{9}|50\\d{5}(?:\\d{2,3})?|[27-9]\\d{7,8}|(?:[34]\\d|6[0-35-9])\\d{6}|8\\d{4,6}",
		[
			5,
			6,
			7,
			8,
			9,
			10
		],
		[
			[
				"(\\d{2})(\\d{3,8})",
				"$1 $2",
				[
					"8[1-79]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{2})(\\d{2,3})",
				"$1 $2 $3",
				[
					"50[036-8]|8|90",
					"50(?:[0367]|88)|8|90"
				],
				"0$1"
			],
			[
				"(\\d)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"24|[346]|7[2-57-9]|9[2-9]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"2(?:10|74)|[589]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3,4})(\\d{4})",
				"$1 $2 $3",
				[
					"1|2[028]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3,5})",
				"$1 $2 $3",
				[
					"2(?:[169]|7[0-35-9])|7"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"240\\d{5}|(?:3[2-79]|[49][2-9]|6[235-9]|7[2-57-9])\\d{6}",
				[
					8
				]
			],
			[
				"2(?:[0-27-9]\\d|6)\\d{6,7}|2(?:1\\d|75)\\d{5}",
				[
					8,
					9,
					10
				]
			]
		],
		"00"
	],
	OM: [
		"968",
		"00",
		"(?:1505|[279]\\d{3}|500)\\d{4}|800\\d{5,6}",
		[
			7,
			8,
			9
		],
		[
			[
				"(\\d{3})(\\d{4,6})",
				"$1 $2",
				[
					"[58]"
				]
			],
			[
				"(\\d{2})(\\d{6})",
				"$1 $2",
				[
					"2"
				]
			],
			[
				"(\\d{4})(\\d{4})",
				"$1 $2",
				[
					"[179]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"2[1-6]\\d{6}",
				[
					8
				]
			],
			[
				"1505\\d{4}|(?:7(?:[125-9]\\d|41)|9(?:0[1-9]|[1-9]\\d))\\d{5}",
				[
					8
				]
			]
		]
	],
	PA: [
		"507",
		"00",
		"(?:00800|8\\d{3})\\d{6}|[68]\\d{7}|[1-57-9]\\d{6}",
		[
			7,
			8,
			10,
			11
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1-$2",
				[
					"[1-57-9]"
				]
			],
			[
				"(\\d{4})(\\d{4})",
				"$1-$2",
				[
					"[68]"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"8"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:1(?:0\\d|1[0479]|2[37]|3[0137]|4[17]|5[05]|6[058]|7[0167]|8[2358]|9[1389])|2(?:[0235-79]\\d|1[0-7]|4[013-9]|8[02-9])|3(?:[047-9]\\d|1[0-8]|2[0-5]|33|5[0-35]|6[068])|4(?:00|3[0-579]|4\\d|7[0-57-9])|5(?:[01]\\d|2[0-7]|[56]0|79)|7(?:0[09]|2[0-26-8]|3[03]|4[04]|5[05-9]|6[0156]|7[0-24-9]|8[4-9]|90)|8(?:09|2[89]|3\\d|4[0-24-689]|5[014]|8[02])|9(?:0[5-9]|1[0135-8]|2[036-9]|3[35-79]|40|5[0457-9]|6[05-9]|7[04-9]|8[35-8]|9\\d))\\d{4}",
				[
					7
				]
			],
			[
				"(?:1[16]1|21[89]|6\\d{3}|8(?:1[01]|7[23]))\\d{4}",
				[
					7,
					8
				]
			]
		]
	],
	PE: [
		"51",
		"00|19(?:1[124]|77|90)00",
		"(?:[14-8]|9\\d)\\d{7}",
		[
			8,
			9
		],
		[
			[
				"(\\d{3})(\\d{5})",
				"$1 $2",
				[
					"80"
				],
				"(0$1)"
			],
			[
				"(\\d)(\\d{7})",
				"$1 $2",
				[
					"1"
				],
				"(0$1)"
			],
			[
				"(\\d{2})(\\d{6})",
				"$1 $2",
				[
					"[4-8]"
				],
				"(0$1)"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"9"
				]
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:(?:(?:4[34]|5[14])[0-8]|687)\\d|7(?:173|(?:3[0-8]|55)\\d)|8(?:10[05689]|6(?:0[06-9]|1[6-9]|29)|7(?:0[0569]|[56]0)))\\d{4}|(?:1[0-8]|4[12]|5[236]|6[1-7]|7[246]|8[2-4])\\d{6}",
				[
					8
				]
			],
			[
				"9\\d{8}",
				[
					9
				]
			]
		],
		"00",
		" Anexo "
	],
	PF: [
		"689",
		"00",
		"4\\d{5}(?:\\d{2})?|8\\d{7,8}",
		[
			6,
			8,
			9
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3",
				[
					"44"
				]
			],
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"4|8[7-9]"
				]
			],
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"8"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"4(?:0[4-689]|9[4-68])\\d{5}",
				[
					8
				]
			],
			[
				"8[7-9]\\d{6}",
				[
					8
				]
			]
		]
	],
	PG: [
		"675",
		"00|140[1-3]",
		"(?:180|[78]\\d{3})\\d{4}|(?:[2-589]\\d|64)\\d{5}",
		[
			7,
			8
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"18|[2-69]|85"
				]
			],
			[
				"(\\d{4})(\\d{4})",
				"$1 $2",
				[
					"[78]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:(?:3[0-2]|4[257]|5[34]|9[78])\\d|64[1-9]|85[02-46-9])\\d{4}",
				[
					7
				]
			],
			[
				"(?:7\\d|8[1-48])\\d{6}",
				[
					8
				]
			]
		],
		"00"
	],
	PH: [
		"63",
		"00",
		"(?:[2-7]|9\\d)\\d{8}|2\\d{5}|(?:1800|8)\\d{7,9}",
		[
			6,
			8,
			9,
			10,
			11,
			12,
			13
		],
		[
			[
				"(\\d)(\\d{5})",
				"$1 $2",
				[
					"2"
				],
				"(0$1)"
			],
			[
				"(\\d{4})(\\d{4,6})",
				"$1 $2",
				[
					"3(?:23|39|46)|4(?:2[3-6]|[35]9|4[26]|76)|544|88[245]|(?:52|64|86)2",
					"3(?:230|397|461)|4(?:2(?:35|[46]4|51)|396|4(?:22|63)|59[347]|76[15])|5(?:221|446)|642[23]|8(?:622|8(?:[24]2|5[13]))"
				],
				"(0$1)"
			],
			[
				"(\\d{5})(\\d{4})",
				"$1 $2",
				[
					"346|4(?:27|9[35])|883",
					"3469|4(?:279|9(?:30|56))|8834"
				],
				"(0$1)"
			],
			[
				"(\\d)(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"2"
				],
				"(0$1)"
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[3-7]|8[2-8]"
				],
				"(0$1)"
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[89]"
				],
				"0$1"
			],
			[
				"(\\d{4})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"1"
				]
			],
			[
				"(\\d{4})(\\d{1,2})(\\d{3})(\\d{4})",
				"$1 $2 $3 $4",
				[
					"1"
				]
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:(?:2[3-8]|3[2-68]|4[2-9]|5[2-6]|6[2-58]|7[24578])\\d{3}|88(?:22\\d\\d|42))\\d{4}|(?:2|8[2-8]\\d\\d)\\d{5}",
				[
					6,
					8,
					9,
					10
				]
			],
			[
				"(?:8(?:1[37]|9[5-8])|9(?:0[5-9]|1[0-24-9]|[235-7]\\d|4[2-9]|8[135-9]|9[1-9]))\\d{7}",
				[
					10
				]
			]
		]
	],
	PK: [
		"92",
		"00",
		"122\\d{6}|[24-8]\\d{10,11}|9(?:[013-9]\\d{8,10}|2(?:[01]\\d\\d|2(?:[06-8]\\d|1[01]))\\d{7})|(?:[2-8]\\d{3}|92(?:[0-7]\\d|8[1-9]))\\d{6}|[24-9]\\d{8}|[89]\\d{7}",
		[
			8,
			9,
			10,
			11,
			12
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{2,7})",
				"$1 $2 $3",
				[
					"[89]0"
				],
				"0$1"
			],
			[
				"(\\d{4})(\\d{5})",
				"$1 $2",
				[
					"1"
				]
			],
			[
				"(\\d{3})(\\d{6,7})",
				"$1 $2",
				[
					"2(?:3[2358]|4[2-4]|9[2-8])|45[3479]|54[2-467]|60[468]|72[236]|8(?:2[2-689]|3[23578]|4[3478]|5[2356])|9(?:2[2-8]|3[27-9]|4[2-6]|6[3569]|9[25-8])",
					"9(?:2[3-8]|98)|(?:2(?:3[2358]|4[2-4]|9[2-8])|45[3479]|54[2-467]|60[468]|72[236]|8(?:2[2-689]|3[23578]|4[3478]|5[2356])|9(?:22|3[27-9]|4[2-6]|6[3569]|9[25-7]))[2-9]"
				],
				"(0$1)"
			],
			[
				"(\\d{2})(\\d{7,8})",
				"$1 $2",
				[
					"(?:2[125]|4[0-246-9]|5[1-35-7]|6[1-8]|7[14]|8[16]|91)[2-9]"
				],
				"(0$1)"
			],
			[
				"(\\d{5})(\\d{5})",
				"$1 $2",
				[
					"58"
				],
				"(0$1)"
			],
			[
				"(\\d{3})(\\d{7})",
				"$1 $2",
				[
					"3"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3 $4",
				[
					"2[125]|4[0-246-9]|5[1-35-7]|6[1-8]|7[14]|8[16]|91"
				],
				"(0$1)"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3 $4",
				[
					"[24-9]"
				],
				"(0$1)"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:(?:21|42)[2-9]|58[126])\\d{7}|(?:2[25]|4[0146-9]|5[1-35-7]|6[1-8]|7[14]|8[16]|91)[2-9]\\d{6,7}|(?:2(?:3[2358]|4[2-4]|9[2-8])|45[3479]|54[2-467]|60[468]|72[236]|8(?:2[2-689]|3[23578]|4[3478]|5[2356])|9(?:2[2-8]|3[27-9]|4[2-6]|6[3569]|9[25-8]))[2-9]\\d{5,6}",
				[
					9,
					10
				]
			],
			[
				"3(?:[0-247]\\d|3[0-79]|55|64)\\d{7}",
				[
					10
				]
			]
		]
	],
	PL: [
		"48",
		"00",
		"(?:6|8\\d\\d)\\d{7}|[1-9]\\d{6}(?:\\d{2})?|[26]\\d{5}",
		[
			6,
			7,
			8,
			9,
			10
		],
		[
			[
				"(\\d{5})",
				"$1",
				[
					"19"
				]
			],
			[
				"(\\d{3})(\\d{3})",
				"$1 $2",
				[
					"11|20|64"
				]
			],
			[
				"(\\d{2})(\\d{2})(\\d{3})",
				"$1 $2 $3",
				[
					"(?:1[2-8]|2[2-69]|3[2-4]|4[1-468]|5[24-689]|6[1-3578]|7[14-7]|8[1-79]|9[145])1",
					"(?:1[2-8]|2[2-69]|3[2-4]|4[1-468]|5[24-689]|6[1-3578]|7[14-7]|8[1-79]|9[145])19"
				]
			],
			[
				"(\\d{3})(\\d{2})(\\d{2,3})",
				"$1 $2 $3",
				[
					"64"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"21|39|45|5[0137]|6[0469]|7[02389]|8(?:0[14]|8)"
				]
			],
			[
				"(\\d{2})(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"1[2-8]|[2-7]|8[1-79]|9[145]"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"8"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"47\\d{7}|(?:1[2-8]|2[2-69]|3[2-4]|4[1-468]|5[24-689]|6[1-3578]|7[14-7]|8[1-79]|9[145])(?:[02-9]\\d{6}|1(?:[0-8]\\d{5}|9\\d{3}(?:\\d{2})?))",
				[
					7,
					9
				]
			],
			[
				"2131[89]\\d{4}|21(?:1[013-5]|2\\d|3[2-9])\\d{5}|(?:45|5[0137]|6[069]|7[2389]|88)\\d{7}",
				[
					9
				]
			]
		]
	],
	PM: [
		"508",
		"00",
		"[45]\\d{5}|(?:708|8\\d\\d)\\d{6}",
		[
			6,
			9
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3",
				[
					"[45]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"7"
				]
			],
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"8"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:4[1-35-9]|5[0-47-9]|80[6-9]\\d\\d)\\d{4}"
			],
			[
				"(?:4[02-489]|5[02-9]|708(?:4[0-5]|5[0-6]))\\d{4}"
			]
		]
	],
	PR: [
		"1",
		"011",
		"(?:[589]\\d\\d|787)\\d{7}",
		[
			10
		],
		0,
		"1",
		0,
		0,
		0,
		0,
		"787|939",
		[
			[
				"(?:787|939)[2-9]\\d{6}"
			],
			[
				"(?:787|939)[2-9]\\d{6}"
			]
		]
	],
	PS: [
		"970",
		"00",
		"[2489]2\\d{6}|(?:1\\d|5)\\d{8}",
		[
			8,
			9,
			10
		],
		[
			[
				"(\\d)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[2489]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"5"
				],
				"0$1"
			],
			[
				"(\\d{4})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"1"
				]
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:22[2-47-9]|42[45]|82[014-68]|92[3569])\\d{5}",
				[
					8
				]
			],
			[
				"5[69]\\d{7}",
				[
					9
				]
			]
		]
	],
	PT: [
		"351",
		"00",
		"1693\\d{5}|(?:[26-9]\\d|30)\\d{7}",
		[
			9
		],
		[
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"2[12]"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"16|[236-9]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"2(?:[12]\\d|3[1-689]|4[1-59]|[57][1-9]|6[1-35689]|8[1-69]|9[1256])\\d{6}"
			],
			[
				"6(?:[06]92(?:30|9\\d)|[35]92(?:[049]\\d|3[034]))\\d{3}|(?:(?:16|6[0356])93|9(?:[1-36]\\d\\d|480))\\d{5}"
			]
		]
	],
	PW: [
		"680",
		"01[12]",
		"(?:[24-8]\\d\\d|345|900)\\d{4}",
		[
			7
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"[2-9]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:2(?:55|77)|345|488|5(?:35|44|87)|6(?:22|54|79)|7(?:33|47)|8(?:24|55|76)|900)\\d{4}"
			],
			[
				"(?:(?:46|83)[0-5]|(?:6[2-4689]|78)0)\\d{4}|(?:45|77|88)\\d{5}"
			]
		]
	],
	PY: [
		"595",
		"00",
		"59\\d{4,6}|9\\d{5,10}|(?:[2-46-8]\\d|5[0-8])\\d{4,7}",
		[
			6,
			7,
			8,
			9,
			10,
			11
		],
		[
			[
				"(\\d{3})(\\d{3,6})",
				"$1 $2",
				[
					"[2-9]0"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{5})",
				"$1 $2",
				[
					"[26]1|3[289]|4[1246-8]|7[1-3]|8[1-36]"
				],
				"(0$1)"
			],
			[
				"(\\d{3})(\\d{4,5})",
				"$1 $2",
				[
					"2[279]|3[13-5]|4[359]|5|6(?:[34]|7[1-46-8])|7[46-8]|85"
				],
				"(0$1)"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"2[14-68]|3[26-9]|4[1246-8]|6(?:1|75)|7[1-35]|8[1-36]"
				],
				"(0$1)"
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"87"
				]
			],
			[
				"(\\d{3})(\\d{6})",
				"$1 $2",
				[
					"9(?:[5-79]|8[1-7])"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[2-8]"
				],
				"0$1"
			],
			[
				"(\\d{4})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"9"
				]
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:[26]1|3[289]|4[1246-8]|7[1-3]|8[1-36])\\d{5,7}|(?:2(?:2[4-68]|[4-68]\\d|7[15]|9[1-5])|3(?:18|3[167]|4[2357]|51|[67]\\d)|4(?:3[12]|5[13]|9[1-47])|5(?:[1-4]\\d|5[02-4])|6(?:3[1-3]|44|7[1-8])|7(?:4[0-4]|5\\d|6[1-578]|75|8[0-8])|858)\\d{5,6}",
				[
					7,
					8,
					9
				]
			],
			[
				"9(?:51|6[129]|7[1-6]|8[1-7]|9[1-5])\\d{6}",
				[
					9
				]
			]
		]
	],
	QA: [
		"974",
		"00",
		"800\\d{4}|(?:2|800)\\d{6}|(?:0080|[3-7])\\d{7}",
		[
			7,
			8,
			9,
			11
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"2[136]|8"
				]
			],
			[
				"(\\d{4})(\\d{4})",
				"$1 $2",
				[
					"[3-7]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"4(?:1111|2022)\\d{3}|4(?:[04]\\d\\d|14[0-6]|999)\\d{4}",
				[
					8
				]
			],
			[
				"[35-7]\\d{7}",
				[
					8
				]
			]
		]
	],
	RE: [
		"262",
		"00",
		"709\\d{6}|(?:26|[689]\\d)\\d{7}",
		[
			9
		],
		[
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[26-9]"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"26(?:2\\d\\d|3(?:0\\d|1[0-6]))\\d{4}"
			],
			[
				"(?:69(?:2\\d\\d|3(?:[06][0-6]|1[0-3]|2[0-2]|3[0-39]|4\\d|5[0-5]|7[0-37]|8[0-8]|9[0-479]))|7092[0-3])\\d{4}"
			]
		]
	],
	RO: [
		"40",
		"00",
		"(?:[236-8]\\d|90)\\d{7}|[23]\\d{5}",
		[
			6,
			9
		],
		[
			[
				"(\\d{3})(\\d{3})",
				"$1 $2",
				[
					"2[3-6]",
					"2[3-6]\\d9"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{4})",
				"$1 $2",
				[
					"219|31"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[23]1"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[236-9]"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"[23][13-6]\\d{7}|(?:2(?:19\\d|[3-6]\\d9)|31\\d\\d)\\d\\d"
			],
			[
				"(?:630|702)0\\d{5}|(?:6(?:00|2\\d)|7(?:0[013-9]|1[0-3]|[2-7]\\d|8[03-8]|9[0-39]))\\d{6}",
				[
					9
				]
			]
		],
		0,
		" int "
	],
	RS: [
		"381",
		"00",
		"38[02-9]\\d{6,9}|6\\d{7,9}|90\\d{4,8}|38\\d{5,6}|(?:7\\d\\d|800)\\d{3,9}|(?:[12]\\d|3[0-79])\\d{5,10}",
		[
			6,
			7,
			8,
			9,
			10,
			11,
			12
		],
		[
			[
				"(\\d{3})(\\d{3,9})",
				"$1 $2",
				[
					"(?:2[389]|39)0|[7-9]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{5,10})",
				"$1 $2",
				[
					"[1-36]"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:11[1-9]\\d|(?:2[389]|39)(?:0[2-9]|[2-9]\\d))\\d{3,8}|(?:1[02-9]|2[0-24-7]|3[0-8])[2-9]\\d{4,9}",
				[
					7,
					8,
					9,
					10,
					11,
					12
				]
			],
			[
				"6(?:[0-689]|7\\d)\\d{6,7}",
				[
					8,
					9,
					10
				]
			]
		]
	],
	RU: [
		"7",
		"810",
		"8\\d{13}|[347-9]\\d{9}",
		[
			10,
			14
		],
		[
			[
				"(\\d{4})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"7(?:1[0-8]|2[1-9])",
					"7(?:1(?:[0-356]2|4[29]|7|8[27])|2(?:1[23]|[2-9]2))",
					"7(?:1(?:[0-356]2|4[29]|7|8[27])|2(?:13[03-69]|62[013-9]))|72[1-57-9]2"
				],
				"8 ($1)",
				1
			],
			[
				"(\\d{5})(\\d)(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"7(?:1[0-68]|2[1-9])",
					"7(?:1(?:[06][3-6]|[18]|2[35]|[3-5][3-5])|2(?:[13][3-5]|[24-689]|7[457]))",
					"7(?:1(?:0(?:[356]|4[023])|[18]|2(?:3[013-9]|5)|3[45]|43[013-79]|5(?:3[1-8]|4[1-7]|5)|6(?:3[0-35-9]|[4-6]))|2(?:1(?:3[178]|[45])|[24-689]|3[35]|7[457]))|7(?:14|23)4[0-8]|71(?:33|45)[1-79]"
				],
				"8 ($1)",
				1
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"7"
				],
				"8 ($1)",
				1
			],
			[
				"(\\d{3})(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2-$3-$4",
				[
					"[349]|8(?:[02-7]|1[1-8])"
				],
				"8 ($1)",
				1
			],
			[
				"(\\d{4})(\\d{4})(\\d{3})(\\d{3})",
				"$1 $2 $3 $4",
				[
					"8"
				],
				"8 ($1)"
			]
		],
		"8",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"336(?:[013-9]\\d|2[013-9])\\d{5}|(?:3(?:0[12]|4[1-35-79]|5[1-3]|65|8[1-58]|9[0145])|4(?:01|1[1356]|2[13467]|7[1-5]|8[1-7]|9[1-689])|8(?:1[1-8]|2[01]|3[13-6]|4[0-8]|5[15-7]|6[0-35-79]|7[1-37-9]))\\d{7}",
				[
					10
				]
			],
			[
				"9\\d{9}",
				[
					10
				]
			]
		],
		"8~10"
	],
	RW: [
		"250",
		"00",
		"(?:06|[27]\\d\\d|[89]00)\\d{6}",
		[
			8,
			9
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"0"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"2"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[7-9]"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:06|2[23568]\\d)\\d{6}"
			],
			[
				"7[237-9]\\d{7}",
				[
					9
				]
			]
		]
	],
	SA: [
		"966",
		"00",
		"(?:[15]\\d|800|92)\\d{7}",
		[
			9,
			10
		],
		[
			[
				"(\\d{4})(\\d{5})",
				"$1 $2",
				[
					"9"
				]
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"1"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"5"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"8"
				]
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"1(?:1\\d|2[24-8]|3[35-8]|4[3-68]|6[2-5]|7[235-7])\\d{6}",
				[
					9
				]
			],
			[
				"579[01]\\d{5}|5(?:[013-689]\\d|7[0-8])\\d{6}",
				[
					9
				]
			]
		]
	],
	SB: [
		"677",
		"0[01]",
		"[6-9]\\d{6}|[1-6]\\d{4}",
		[
			5,
			7
		],
		[
			[
				"(\\d{2})(\\d{5})",
				"$1 $2",
				[
					"6[89]|7|8[4-9]|9(?:[1-8]|9[0-8])"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:1[4-79]|[23]\\d|4[0-2]|5[03]|6[0-37])\\d{3}",
				[
					5
				]
			],
			[
				"48\\d{3}|(?:(?:6[89]|7[1-9]|8[4-9])\\d|9(?:1[2-9]|2[013-9]|3[0-2]|[46]\\d|5[0-46-9]|7[0-689]|8[0-79]|9[0-8]))\\d{4}"
			]
		]
	],
	SC: [
		"248",
		"010|0[0-2]",
		"(?:[2489]\\d|64)\\d{5}",
		[
			7
		],
		[
			[
				"(\\d)(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[246]|9[57]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"4[2-46]\\d{5}"
			],
			[
				"2[125-8]\\d{5}"
			]
		],
		"00"
	],
	SD: [
		"249",
		"00",
		"[19]\\d{8}",
		[
			9
		],
		[
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[19]"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"1(?:5\\d|8[35-7])\\d{6}"
			],
			[
				"(?:1[0-2]|9[0-3569])\\d{7}"
			]
		]
	],
	SE: [
		"46",
		"00",
		"(?:[26]\\d\\d|9)\\d{9}|[1-9]\\d{8}|[1-689]\\d{7}|[1-4689]\\d{6}|2\\d{5}",
		[
			6,
			7,
			8,
			9,
			10,
			12
		],
		[
			[
				"(\\d{2})(\\d{2,3})(\\d{2})",
				"$1-$2 $3",
				[
					"20"
				],
				"0$1",
				0,
				"$1 $2 $3"
			],
			[
				"(\\d{3})(\\d{4})",
				"$1-$2",
				[
					"9(?:00|39|44|9)"
				],
				"0$1",
				0,
				"$1 $2"
			],
			[
				"(\\d{2})(\\d{3})(\\d{2})",
				"$1-$2 $3",
				[
					"[12][136]|3[356]|4[0246]|6[03]|90[1-9]"
				],
				"0$1",
				0,
				"$1 $2 $3"
			],
			[
				"(\\d)(\\d{2,3})(\\d{2})(\\d{2})",
				"$1-$2 $3 $4",
				[
					"8"
				],
				"0$1",
				0,
				"$1 $2 $3 $4"
			],
			[
				"(\\d{3})(\\d{2,3})(\\d{2})",
				"$1-$2 $3",
				[
					"1[2457]|2(?:[247-9]|5[0138])|3[0247-9]|4[1357-9]|5[0-35-9]|6(?:[125689]|4[02-57]|7[0-2])|9(?:[125-8]|3[02-5]|4[0-3])"
				],
				"0$1",
				0,
				"$1 $2 $3"
			],
			[
				"(\\d{3})(\\d{2,3})(\\d{3})",
				"$1-$2 $3",
				[
					"9(?:00|39|44)"
				],
				"0$1",
				0,
				"$1 $2 $3"
			],
			[
				"(\\d{2})(\\d{2,3})(\\d{2})(\\d{2})",
				"$1-$2 $3 $4",
				[
					"1[13689]|2[0136]|3[1356]|4[0246]|54|6[03]|90[1-9]"
				],
				"0$1",
				0,
				"$1 $2 $3 $4"
			],
			[
				"(\\d{2})(\\d{3})(\\d{2})(\\d{2})",
				"$1-$2 $3 $4",
				[
					"10|7"
				],
				"0$1",
				0,
				"$1 $2 $3 $4"
			],
			[
				"(\\d)(\\d{3})(\\d{3})(\\d{2})",
				"$1-$2 $3 $4",
				[
					"8"
				],
				"0$1",
				0,
				"$1 $2 $3 $4"
			],
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1-$2 $3 $4",
				[
					"[13-5]|2(?:[247-9]|5[0138])|6(?:[124-689]|7[0-2])|9(?:[125-8]|3[02-5]|4[0-3])"
				],
				"0$1",
				0,
				"$1 $2 $3 $4"
			],
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{3})",
				"$1-$2 $3 $4",
				[
					"9"
				],
				"0$1",
				0,
				"$1 $2 $3 $4"
			],
			[
				"(\\d{3})(\\d{2})(\\d{3})(\\d{2})(\\d{2})",
				"$1-$2 $3 $4 $5",
				[
					"[26]"
				],
				"0$1",
				0,
				"$1 $2 $3 $4 $5"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:(?:[12][136]|3[356]|4[0246]|6[03]|8\\d)\\d|90[1-9])\\d{4,6}|(?:1(?:2[0-35]|4[0-4]|5[0-25-9]|7[13-6]|[89]\\d)|2(?:2[0-7]|4[0136-8]|5[0138]|7[018]|8[01]|9[0-57])|3(?:0[0-4]|1\\d|2[0-25]|4[056]|7[0-2]|8[0-3]|9[023])|4(?:1[013-8]|3[0135]|5[14-79]|7[0-246-9]|8[0156]|9[0-689])|5(?:0[0-6]|[15][0-5]|2[0-68]|3[0-4]|4\\d|6[03-5]|7[013]|8[0-79]|9[01])|6(?:1[1-3]|2[0-4]|4[02-57]|5[0-37]|6[0-3]|7[0-2]|8[0247]|9[0-356])|9(?:1[0-68]|2\\d|3[02-5]|4[0-3]|5[0-4]|[68][01]|7[0135-8]))\\d{5,6}",
				[
					7,
					8,
					9
				]
			],
			[
				"7[02369]\\d{7}",
				[
					9
				]
			]
		]
	],
	SG: [
		"65",
		"0[0-3]\\d",
		"(?:(?:1\\d|8)\\d\\d|7000)\\d{7}|[3689]\\d{7}",
		[
			8,
			10,
			11
		],
		[
			[
				"(\\d{4})(\\d{4})",
				"$1 $2",
				[
					"[369]|8(?:0[1-9]|[1-9])"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"8"
				]
			],
			[
				"(\\d{4})(\\d{4})(\\d{3})",
				"$1 $2 $3",
				[
					"7"
				]
			],
			[
				"(\\d{4})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"1"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"662[0-24-9]\\d{4}|6(?:[0-578]\\d|6[013-57-9]|9[0-35-9])\\d{5}",
				[
					8
				]
			],
			[
				"898[02-8]\\d{4}|(?:8(?:0[1-9]|[1-8]\\d|9[0-7])|9[0-8]\\d)\\d{5}",
				[
					8
				]
			]
		]
	],
	SH: [
		"290",
		"00",
		"(?:[256]\\d|8)\\d{3}",
		[
			4,
			5
		],
		0,
		0,
		0,
		0,
		0,
		0,
		"[256]",
		[
			[
				"2(?:[0-57-9]\\d|6[4-9])\\d\\d"
			],
			[
				"[56]\\d{4}",
				[
					5
				]
			]
		]
	],
	SI: [
		"386",
		"00|10(?:22|66|88|99)",
		"[1-7]\\d{7}|8\\d{4,7}|90\\d{4,6}",
		[
			5,
			6,
			7,
			8
		],
		[
			[
				"(\\d{2})(\\d{3,6})",
				"$1 $2",
				[
					"8[09]|9"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{5})",
				"$1 $2",
				[
					"59|8"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[37][01]|4[0139]|51|6"
				],
				"0$1"
			],
			[
				"(\\d)(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[1-57]"
				],
				"(0$1)"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:[1-357][2-8]|4[24-8])\\d{6}",
				[
					8
				]
			],
			[
				"65(?:[178]\\d|5[56]|6[01])\\d{4}|(?:[37][01]|4[0139]|51|6[489])\\d{6}",
				[
					8
				]
			]
		],
		"00"
	],
	SJ: [
		"47",
		"00",
		"0\\d{4}|(?:[489]\\d|79)\\d{6}",
		[
			5,
			8
		],
		0,
		0,
		0,
		0,
		0,
		0,
		"79",
		[
			[
				"79\\d{6}",
				[
					8
				]
			],
			[
				"(?:4[015-8]|9\\d)\\d{6}",
				[
					8
				]
			]
		]
	],
	SK: [
		"421",
		"00",
		"[2-689]\\d{8}|[2-59]\\d{6}|[2-5]\\d{5}",
		[
			6,
			7,
			9
		],
		[
			[
				"(\\d)(\\d{2})(\\d{3,4})",
				"$1 $2 $3",
				[
					"21"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{2})(\\d{2,3})",
				"$1 $2 $3",
				[
					"[3-5][1-8]1",
					"[3-5][1-8]1[67]"
				],
				"0$1"
			],
			[
				"(\\d)(\\d{3})(\\d{3})(\\d{2})",
				"$1/$2 $3 $4",
				[
					"2"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[689]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{2})(\\d{2})",
				"$1/$2 $3 $4",
				[
					"[3-5]"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:2(?:16|[2-9]\\d{3})|(?:(?:[3-5][1-8]\\d|819)\\d|601[1-5])\\d)\\d{4}|(?:2|[3-5][1-8])1[67]\\d{3}|[3-5][1-8]16\\d\\d"
			],
			[
				"909[1-9]\\d{5}|9(?:0[1-8]|1[0-24-9]|4[03-57-9]|5\\d)\\d{6}",
				[
					9
				]
			]
		]
	],
	SL: [
		"232",
		"00",
		"(?:[237-9]\\d|66)\\d{6}",
		[
			8
		],
		[
			[
				"(\\d{2})(\\d{6})",
				"$1 $2",
				[
					"[236-9]"
				],
				"(0$1)"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"22[2-4][2-9]\\d{4}"
			],
			[
				"(?:25|3[0-5]|66|7[1-9]|8[08]|9[09])\\d{6}"
			]
		]
	],
	SM: [
		"378",
		"00",
		"(?:0549|[5-7]\\d)\\d{6}",
		[
			8,
			10
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[5-7]"
				]
			],
			[
				"(\\d{4})(\\d{6})",
				"$1 $2",
				[
					"0"
				]
			]
		],
		0,
		0,
		"([89]\\d{5})$",
		"0549$1",
		0,
		0,
		[
			[
				"0549(?:8[0157-9]|9\\d)\\d{4}",
				[
					10
				]
			],
			[
				"6[16]\\d{6}",
				[
					8
				]
			]
		]
	],
	SN: [
		"221",
		"00",
		"(?:[378]\\d|93)\\d{7}",
		[
			9
		],
		[
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"8"
				]
			],
			[
				"(\\d{2})(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[379]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"3(?:0(?:1[0-2]|80)|282|3(?:8[1-9]|9[3-9])|611)\\d{5}"
			],
			[
				"7(?:[05-8]\\d|[19]0|21)\\d{6}"
			]
		]
	],
	SO: [
		"252",
		"00",
		"[346-9]\\d{8}|[12679]\\d{7}|[1-5]\\d{6}|[1348]\\d{5}",
		[
			6,
			7,
			8,
			9
		],
		[
			[
				"(\\d{2})(\\d{4})",
				"$1 $2",
				[
					"8[125]"
				]
			],
			[
				"(\\d{6})",
				"$1",
				[
					"[134]"
				]
			],
			[
				"(\\d)(\\d{6})",
				"$1 $2",
				[
					"[15]|2[0-79]|3[0-46-8]|4[0-7]"
				]
			],
			[
				"(\\d)(\\d{7})",
				"$1 $2",
				[
					"(?:2|90)4|[67]"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[348]|64|79|90"
				]
			],
			[
				"(\\d{2})(\\d{5,7})",
				"$1 $2",
				[
					"1|28|6[0-35-9]|7[67]|9[2-9]"
				]
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:1\\d|2[0-79]|3[0-46-8]|4[0-7]|5[57-9])\\d{5}|(?:[134]\\d|8[125])\\d{4}",
				[
					6,
					7
				]
			],
			[
				"(?:(?:15|(?:3[59]|4[89]|6\\d|7[679]|8[08])\\d|9(?:0\\d|[2-9]))\\d|2(?:4\\d|8))\\d{5}|(?:[67]\\d\\d|904)\\d{5}",
				[
					7,
					8,
					9
				]
			]
		]
	],
	SR: [
		"597",
		"00",
		"(?:[2-5]|[6-8]\\d|90)\\d{5}",
		[
			6,
			7
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})",
				"$1-$2-$3",
				[
					"56"
				]
			],
			[
				"(\\d{3})(\\d{3})",
				"$1-$2",
				[
					"[2-5]"
				]
			],
			[
				"(\\d{3})(\\d{4})",
				"$1-$2",
				[
					"[6-9]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:2[1-3]|3[0-7]|4\\d|5[2-58])\\d{4}",
				[
					6
				]
			],
			[
				"(?:6[08]|7[124-7]|8[1-9])\\d{5}",
				[
					7
				]
			]
		]
	],
	SS: [
		"211",
		"00",
		"[19]\\d{8}",
		[
			9
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[19]"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"1[89]\\d{7}"
			],
			[
				"(?:12|9[1257-9])\\d{7}"
			]
		]
	],
	ST: [
		"239",
		"00",
		"(?:22|9\\d)\\d{5}",
		[
			7
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"[29]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"22\\d{5}"
			],
			[
				"900[5-9]\\d{3}|9(?:0[1-9]|[89]\\d)\\d{4}"
			]
		]
	],
	SV: [
		"503",
		"00",
		"[267]\\d{7}|(?:80\\d|900)\\d{4}(?:\\d{4})?",
		[
			7,
			8,
			11
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"[89]"
				]
			],
			[
				"(\\d{4})(\\d{4})",
				"$1 $2",
				[
					"[267]"
				]
			],
			[
				"(\\d{3})(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"[89]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"2(?:79(?:0[0347-9]|[1-9]\\d)|89(?:0[024589]|[1-9]\\d))\\d{3}|2(?:[1-69]\\d|[78][0-8])\\d{5}",
				[
					8
				]
			],
			[
				"[67]\\d{7}",
				[
					8
				]
			]
		]
	],
	SX: [
		"1",
		"011",
		"7215\\d{6}|(?:[58]\\d\\d|900)\\d{7}",
		[
			10
		],
		0,
		"1",
		0,
		"(5\\d{6})$|1",
		"721$1",
		0,
		"721",
		[
			[
				"7215(?:4[2-8]|8[239]|9[056])\\d{4}"
			],
			[
				"7215(?:1[02]|2\\d|5[034679]|8[014-8])\\d{4}"
			]
		]
	],
	SY: [
		"963",
		"00",
		"[1-359]\\d{8}|[1-5]\\d{7}",
		[
			8,
			9
		],
		[
			[
				"(\\d{2})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"[1-4]|5[1-3]"
				],
				"0$1",
				1
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[59]"
				],
				"0$1",
				1
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"21\\d{6,7}|(?:1(?:[14]\\d|[2356])|2[235]|3(?:[13]\\d|4)|4[134]|5[1-3])\\d{6}"
			],
			[
				"(?:50|9[1-9])\\d{7}",
				[
					9
				]
			]
		]
	],
	SZ: [
		"268",
		"00",
		"0800\\d{4}|(?:[237]\\d|900)\\d{6}",
		[
			8,
			9
		],
		[
			[
				"(\\d{4})(\\d{4})",
				"$1 $2",
				[
					"[0237]"
				]
			],
			[
				"(\\d{5})(\\d{4})",
				"$1 $2",
				[
					"9"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"[23][2-5]\\d{6}",
				[
					8
				]
			],
			[
				"7[5-9]\\d{6}",
				[
					8
				]
			]
		]
	],
	TA: [
		"290",
		"00",
		"8\\d{3}",
		[
			4
		],
		0,
		0,
		0,
		0,
		0,
		0,
		"8",
		[
			[
				"8\\d{3}"
			]
		]
	],
	TC: [
		"1",
		"011",
		"(?:[58]\\d\\d|649|900)\\d{7}",
		[
			10
		],
		0,
		"1",
		0,
		"([2-479]\\d{6})$|1",
		"649$1",
		0,
		"649",
		[
			[
				"649(?:266|712|9(?:4\\d|50))\\d{4}"
			],
			[
				"649(?:2(?:3[129]|4[1-79])|3\\d\\d|4[34][1-3])\\d{4}"
			]
		]
	],
	TD: [
		"235",
		"00|16",
		"(?:22|30|[689]\\d|77)\\d{6}",
		[
			8
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[236-9]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"22(?:[37-9]0|5[0-5]|6[89])\\d{4}"
			],
			[
				"(?:30|[69]\\d|77|8[56])\\d{6}"
			]
		],
		"00"
	],
	TG: [
		"228",
		"00",
		"[279]\\d{7}",
		[
			8
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[279]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"2(?:2[2-7]|3[23]|4[45]|55|6[67]|77)\\d{5}"
			],
			[
				"(?:7[0-29]|9[0-36-9])\\d{6}"
			]
		]
	],
	TH: [
		"66",
		"00[1-9]",
		"(?:001800|[2-57]|[689]\\d)\\d{7}|1\\d{7,9}",
		[
			8,
			9,
			10,
			13
		],
		[
			[
				"(\\d)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"2"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"[13-9]"
				],
				"0$1"
			],
			[
				"(\\d{4})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"1"
				]
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:1[0689]|2\\d|3[2-9]|4[2-5]|5[2-6]|7[3-7])\\d{6}",
				[
					8
				]
			],
			[
				"67(?:1[0-8]|2[4-7])\\d{5}|(?:14|6[1-6]|[89]\\d)\\d{7}",
				[
					9
				]
			]
		]
	],
	TJ: [
		"992",
		"810",
		"[0-57-9]\\d{8}",
		[
			9
		],
		[
			[
				"(\\d{6})(\\d)(\\d{2})",
				"$1 $2 $3",
				[
					"331",
					"3317"
				]
			],
			[
				"(\\d{3})(\\d{2})(\\d{4})",
				"$1 $2 $3",
				[
					"44[02-479]|[34]7"
				]
			],
			[
				"(\\d{4})(\\d)(\\d{4})",
				"$1 $2 $3",
				[
					"3(?:[1245]|3[12])"
				]
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[0-57-9]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:3(?:1[3-5]|2[245]|3[12]|4[24-7]|5[25]|72)|4(?:46|74|87))\\d{6}"
			],
			[
				"(?:33[03-9]|4(?:1[18]|4[02-479])|81[1-9])\\d{6}|(?:[09]\\d|1[0-27-9]|2[0-27]|[34]0|5[05]|7[01578]|8[078])\\d{7}"
			]
		],
		"8~10"
	],
	TK: [
		"690",
		"00",
		"[2-47]\\d{3,6}",
		[
			4,
			5,
			6,
			7
		],
		0,
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:2[2-4]|[34]\\d)\\d{2,5}"
			],
			[
				"7[2-4]\\d{2,5}"
			]
		]
	],
	TL: [
		"670",
		"00",
		"7\\d{7}|(?:[2-47]\\d|[89]0)\\d{5}",
		[
			7,
			8
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"[2-489]|70"
				]
			],
			[
				"(\\d{4})(\\d{4})",
				"$1 $2",
				[
					"7"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:2[1-5]|3[1-9]|4[1-4])\\d{5}",
				[
					7
				]
			],
			[
				"7[2-8]\\d{6}",
				[
					8
				]
			]
		]
	],
	TM: [
		"993",
		"810",
		"(?:[1-6]\\d|71)\\d{6}",
		[
			8
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2-$3-$4",
				[
					"12"
				],
				"(8 $1)"
			],
			[
				"(\\d{3})(\\d)(\\d{2})(\\d{2})",
				"$1 $2-$3-$4",
				[
					"[1-5]"
				],
				"(8 $1)"
			],
			[
				"(\\d{2})(\\d{6})",
				"$1 $2",
				[
					"[67]"
				],
				"8 $1"
			]
		],
		"8",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:1(?:2\\d|3[1-9])|2(?:22|4[0-35-8])|3(?:22|4[03-9])|4(?:22|3[128]|4\\d|6[15])|5(?:22|5[7-9]|6[014-689]))\\d{5}"
			],
			[
				"(?:6\\d|71)\\d{6}"
			]
		],
		"8~10"
	],
	TN: [
		"216",
		"00",
		"[2-57-9]\\d{7}",
		[
			8
		],
		[
			[
				"(\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[2-57-9]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"81200\\d{3}|(?:3[0-2]|7\\d)\\d{6}"
			],
			[
				"3(?:001|[12]40)\\d{4}|(?:(?:[259]\\d|4[0-8])\\d|3(?:1[1-35]|6[0-4]|91))\\d{5}"
			]
		]
	],
	TO: [
		"676",
		"00",
		"(?:0800|(?:[5-8]\\d\\d|999)\\d)\\d{3}|[2-8]\\d{4}",
		[
			5,
			7
		],
		[
			[
				"(\\d{2})(\\d{3})",
				"$1-$2",
				[
					"[2-4]|50|6[09]|7[0-24-69]|8[05]"
				]
			],
			[
				"(\\d{4})(\\d{3})",
				"$1 $2",
				[
					"0"
				]
			],
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"[5-9]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:2\\d|3[0-8]|4[0-4]|50|6[09]|7[0-24-69]|8[05])\\d{3}",
				[
					5
				]
			],
			[
				"(?:5(?:4[0-5]|5[4-6])|6(?:[09]\\d|3[02]|8[15-9])|(?:7\\d|8[46-9])\\d|999)\\d{4}",
				[
					7
				]
			]
		]
	],
	TR: [
		"90",
		"00",
		"4\\d{6}|8\\d{11,12}|(?:[2-58]\\d\\d|900)\\d{7}",
		[
			7,
			10,
			12,
			13
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"512|8[01589]|90"
				],
				"0$1",
				1
			],
			[
				"(\\d{3})(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"5(?:[0-59]|61)",
					"5(?:[0-59]|61[06])",
					"5(?:[0-59]|61[06]1)"
				],
				"0$1",
				1
			],
			[
				"(\\d{3})(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[24][1-8]|3[1-9]"
				],
				"(0$1)",
				1
			],
			[
				"(\\d{3})(\\d{3})(\\d{6,7})",
				"$1 $2 $3",
				[
					"80"
				],
				"0$1",
				1
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:2(?:[13][26]|[28][2468]|[45][268]|[67][246])|3(?:[13][28]|[24-6][2468]|[78][02468]|92)|4(?:[16][246]|[23578][2468]|4[26]))\\d{7}",
				[
					10
				]
			],
			[
				"561(?:011|61\\d)\\d{4}|5(?:0[15-7]|1[06]|24|[34]\\d|5[1-59]|9[46])\\d{7}",
				[
					10
				]
			]
		]
	],
	TT: [
		"1",
		"011",
		"(?:[58]\\d\\d|900)\\d{7}",
		[
			10
		],
		0,
		"1",
		0,
		"([2-46-8]\\d{6})$|1",
		"868$1",
		0,
		"868",
		[
			[
				"868(?:2(?:01|1[5-9]|[23]\\d|4[0-2])|6(?:0[7-9]|1[02-8]|2[1-9]|[3-69]\\d|7[0-79])|82[124])\\d{4}"
			],
			[
				"868(?:(?:2[5-9]|3\\d)\\d|4(?:3[0-6]|[6-9]\\d)|6(?:20|78|8\\d)|7(?:0[1-9]|1[02-9]|[2-9]\\d))\\d{4}"
			]
		]
	],
	TV: [
		"688",
		"00",
		"(?:2|7\\d\\d|90)\\d{4}",
		[
			5,
			6,
			7
		],
		[
			[
				"(\\d{2})(\\d{3})",
				"$1 $2",
				[
					"2"
				]
			],
			[
				"(\\d{2})(\\d{4})",
				"$1 $2",
				[
					"90"
				]
			],
			[
				"(\\d{2})(\\d{5})",
				"$1 $2",
				[
					"7"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"2[02-9]\\d{3}",
				[
					5
				]
			],
			[
				"(?:7[01]\\d|90)\\d{4}",
				[
					6,
					7
				]
			]
		]
	],
	TW: [
		"886",
		"0(?:0[25-79]|19)",
		"[2-689]\\d{8}|7\\d{9,10}|[2-8]\\d{7}|2\\d{6}",
		[
			7,
			8,
			9,
			10,
			11
		],
		[
			[
				"(\\d{2})(\\d)(\\d{4})",
				"$1 $2 $3",
				[
					"202"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"[258]0"
				],
				"0$1"
			],
			[
				"(\\d)(\\d{3,4})(\\d{4})",
				"$1 $2 $3",
				[
					"[23568]|4(?:0[02-48]|[1-47-9])|7[1-9]",
					"[23568]|4(?:0[2-48]|[1-47-9])|(?:400|7)[1-9]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[49]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{4})(\\d{4,5})",
				"$1 $2 $3",
				[
					"7"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:2[2-8]\\d|370|55[01]|7[1-9])\\d{6}|4(?:(?:0(?:0[1-9]|[2-48]\\d)|1[023]\\d)\\d{4,5}|(?:[239]\\d\\d|4(?:0[56]|12|49))\\d{5})|6(?:[01]\\d{7}|4(?:0[56]|12|24|4[09])\\d{4,5})|8(?:(?:2(?:3\\d|4[0-269]|[578]0|66)|36[24-9]|90\\d\\d)\\d{4}|4(?:0[56]|12|24|4[09])\\d{4,5})|(?:2(?:2(?:0\\d\\d|4(?:0[68]|[249]0|3[0-467]|5[0-25-9]|6[0235689]))|(?:3(?:[09]\\d|1[0-4])|(?:4\\d|5[0-49]|6[0-29]|7[0-5])\\d)\\d)|(?:(?:3[2-9]|5[2-8]|6[0-35-79]|8[7-9])\\d\\d|4(?:2(?:[089]\\d|7[1-9])|(?:3[0-4]|[78]\\d|9[01])\\d))\\d)\\d{3}",
				[
					8,
					9
				]
			],
			[
				"(?:40001[0-2]|9[0-8]\\d{4})\\d{3}",
				[
					9
				]
			]
		],
		0,
		"#"
	],
	TZ: [
		"255",
		"00[056]",
		"(?:[25-8]\\d|41|90)\\d{7}",
		[
			9
		],
		[
			[
				"(\\d{3})(\\d{2})(\\d{4})",
				"$1 $2 $3",
				[
					"[89]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[24]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{7})",
				"$1 $2",
				[
					"5"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[67]"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"2[2-8]\\d{7}"
			],
			[
				"(?:6[125-9]|7[13-9])\\d{7}"
			]
		]
	],
	UA: [
		"380",
		"00",
		"[89]\\d{9}|[3-9]\\d{8}",
		[
			9,
			10
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"6[12][29]|(?:3[1-8]|4[136-8]|5[12457]|6[49])2|(?:56|65)[24]",
					"6[12][29]|(?:35|4[1378]|5[12457]|6[49])2|(?:56|65)[24]|(?:3[1-46-8]|46)2[013-9]"
				],
				"0$1"
			],
			[
				"(\\d{4})(\\d{5})",
				"$1 $2",
				[
					"3[1-8]|4(?:[1367]|[45][6-9]|8[4-6])|5(?:[1-5]|6[0135689]|7[4-6])|6(?:[12][3-7]|[459])",
					"3[1-8]|4(?:[1367]|[45][6-9]|8[4-6])|5(?:[1-5]|6(?:[015689]|3[02389])|7[4-6])|6(?:[12][3-7]|[459])"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[3-7]|89|9[1-9]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"[89]"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:3[1-8]|4[13-8]|5[1-7]|6[12459])\\d{7}",
				[
					9
				]
			],
			[
				"790\\d{6}|(?:39|50|6[36-8]|7[1-357]|9[1-9])\\d{7}",
				[
					9
				]
			]
		],
		"0~0"
	],
	UG: [
		"256",
		"00[057]",
		"800\\d{6}|(?:[29]0|[347]\\d)\\d{7}",
		[
			9
		],
		[
			[
				"(\\d{4})(\\d{5})",
				"$1 $2",
				[
					"202",
					"2024"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{6})",
				"$1 $2",
				[
					"[27-9]|4(?:6[45]|[7-9])"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{7})",
				"$1 $2",
				[
					"[34]"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"20(?:(?:240|30[67])\\d|6(?:00[0-2]|30[0-4]))\\d{3}|(?:20(?:[017]\\d|2[5-9]|3[1-4]|5[0-4]|6[15-9])|[34]\\d{3})\\d{5}"
			],
			[
				"72[48]0\\d{5}|7(?:[014-8]\\d|2[067]|36|9[0-289])\\d{6}"
			]
		]
	],
	US: [
		"1",
		"011",
		"[2-9]\\d{9}|3\\d{6}",
		[
			10
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1-$2",
				[
					"310"
				],
				0,
				1
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"($1) $2-$3",
				[
					"[2-9]"
				],
				0,
				1,
				"$1-$2-$3"
			]
		],
		"1",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"3052(?:0[0-8]|[1-9]\\d)\\d{4}|(?:2742|305[3-9])\\d{6}|(?:472|983)[2-47-9]\\d{6}|(?:2(?:0[1-35-9]|1[02-9]|2[03-57-9]|3[1459]|4[08]|5[1-46]|6[0279]|7[0269]|8[13])|3(?:0[1-47-9]|1[02-9]|2[013-79]|3[0-24679]|4[167]|5[0-2]|6[01349]|8[056])|4(?:0[124-9]|1[02-579]|2[3-5]|3[0245]|4[023578]|58|6[349]|7[0589]|8[04])|5(?:0[1-57-9]|1[0235-8]|20|3[0149]|4[01]|5[179]|6[1-47]|7[0-5]|8[0256])|6(?:0[1-35-9]|1[024-9]|2[03689]|3[016]|4[0156]|5[01679]|6[0-279]|78|8[0-269])|7(?:0[1-46-8]|1[2-9]|2[04-8]|3[0-247]|4[0378]|5[47]|6[02359]|7[0-59]|8[156])|8(?:0[1-68]|1[02-8]|2[0168]|3[0-2589]|4[03578]|5[046-9]|6[02-5]|7[028])|9(?:0[1346-9]|1[02-9]|2[0589]|3[0146-8]|4[01357-9]|5[12469]|7[0-3589]|8[04-69]))[2-9]\\d{6}"
			],
			[
				"3052(?:0[0-8]|[1-9]\\d)\\d{4}|(?:2742|305[3-9])\\d{6}|(?:472|983)[2-47-9]\\d{6}|(?:2(?:0[1-35-9]|1[02-9]|2[03-57-9]|3[1459]|4[08]|5[1-46]|6[0279]|7[0269]|8[13])|3(?:0[1-47-9]|1[02-9]|2[013-79]|3[0-24679]|4[167]|5[0-2]|6[01349]|8[056])|4(?:0[124-9]|1[02-579]|2[3-5]|3[0245]|4[023578]|58|6[349]|7[0589]|8[04])|5(?:0[1-57-9]|1[0235-8]|20|3[0149]|4[01]|5[179]|6[1-47]|7[0-5]|8[0256])|6(?:0[1-35-9]|1[024-9]|2[03689]|3[016]|4[0156]|5[01679]|6[0-279]|78|8[0-269])|7(?:0[1-46-8]|1[2-9]|2[04-8]|3[0-247]|4[0378]|5[47]|6[02359]|7[0-59]|8[156])|8(?:0[1-68]|1[02-8]|2[0168]|3[0-2589]|4[03578]|5[046-9]|6[02-5]|7[028])|9(?:0[1346-9]|1[02-9]|2[0589]|3[0146-8]|4[01357-9]|5[12469]|7[0-3589]|8[04-69]))[2-9]\\d{6}"
			]
		]
	],
	UY: [
		"598",
		"0(?:0|1[3-9]\\d)",
		"0004\\d{2,9}|[1249]\\d{7}|2\\d{3,4}|(?:[49]\\d|80)\\d{5}",
		[
			4,
			5,
			6,
			7,
			8,
			9,
			10,
			11,
			12,
			13
		],
		[
			[
				"(\\d{4,5})",
				"$1",
				[
					"21"
				]
			],
			[
				"(\\d{3})(\\d{3,4})",
				"$1 $2",
				[
					"0"
				]
			],
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"[49]0|8"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"9"
				],
				"0$1"
			],
			[
				"(\\d{4})(\\d{4})",
				"$1 $2",
				[
					"[124]"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{2,4})",
				"$1 $2 $3",
				[
					"0"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})(\\d{2,4})",
				"$1 $2 $3 $4",
				[
					"0"
				]
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:1(?:770|9(?:20|[89]7))|(?:2\\d|4[2-7])\\d\\d)\\d{4}",
				[
					8
				]
			],
			[
				"9[1-9]\\d{6}",
				[
					8
				]
			]
		],
		"00",
		" int. "
	],
	UZ: [
		"998",
		"00",
		"(?:20|33|[5-9]\\d)\\d{7}",
		[
			9
		],
		[
			[
				"(\\d{2})(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[235-9]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:55\\d\\d|6(?:1(?:22|3[124]|4[1-4]|5[1-3578]|64)|2(?:22|3[0-57-9]|41)|5(?:22|3[3-7]|5[024-8])|[69]\\d\\d|7(?:[23]\\d|7[69]))|7(?:0(?:5[4-9]|6[0146]|7[124-6]|9[135-8])|[168]\\d\\d|2(?:22|3[13-57-9]|4[1-3579]|5[14])|3(?:2\\d|3[1578]|4[1-35-7]|5[1-57]|61)|4(?:2\\d|3[1-579]|7[1-79])|5(?:22|5[1-9]|6[1457])|9(?:22|5[1-9])))\\d{5}"
			],
			[
				"(?:(?:[25]0|33|8[78]|9[0-57-9])\\d{3}|6(?:1(?:2(?:2[01]|98)|35[0-4]|50\\d|61[23]|7(?:[01][017]|4\\d|55|9[5-9]))|2(?:(?:11|7\\d)\\d|2(?:[12]1|9[01379])|5(?:[126]\\d|3[0-4]))|5(?:19[01]|2(?:27|9[26])|(?:30|59|7\\d)\\d)|6(?:2(?:1[5-9]|2[0367]|38|41|52|60)|(?:3[79]|9[0-3])\\d|4(?:56|83)|7(?:[07]\\d|1[017]|3[07]|4[047]|5[057]|67|8[0178]|9[79]))|7(?:2(?:24|3[237]|4[5-9]|7[15-8])|5(?:7[12]|8[0589])|7(?:0\\d|[39][07])|9(?:0\\d|7[079])))|7(?:[07]\\d{3}|2(?:2(?:2[79]|95)|3(?:2[5-9]|6[0-6])|57\\d|7(?:0\\d|1[17]|2[27]|3[37]|44|5[057]|66|88))|3(?:2(?:1[0-6]|21|3[469]|7[159])|(?:33|9[4-6])\\d|5(?:0[0-4]|5[579]|9\\d)|7(?:[0-3579]\\d|4[0467]|6[67]|8[078]))|4(?:2(?:29|5[0257]|6[0-7]|7[1-57])|5(?:1[0-4]|8\\d|9[5-9])|7(?:0\\d|1[024589]|2[0-27]|3[0137]|[46][07]|5[01]|7[5-9]|9[079])|9(?:7[015-9]|[89]\\d))|5(?:112|2(?:0\\d|2[29]|[49]4)|3[1568]\\d|52[6-9]|7(?:0[01578]|1[017]|[23]7|4[047]|[5-7]\\d|8[78]|9[079]))|9(?:22[128]|3(?:2[0-4]|7\\d)|57[02569]|7(?:2[05-9]|3[37]|4\\d|60|7[2579]|87|9[07]))))\\d{4}"
			]
		]
	],
	VA: [
		"39",
		"00",
		"0\\d{5,10}|3[0-8]\\d{7,10}|55\\d{8}|8\\d{5}(?:\\d{2,4})?|(?:1\\d|39)\\d{7,8}",
		[
			6,
			7,
			8,
			9,
			10,
			11,
			12
		],
		0,
		0,
		0,
		0,
		0,
		0,
		"06698",
		[
			[
				"06698\\d{1,6}",
				[
					6,
					7,
					8,
					9,
					10,
					11
				]
			],
			[
				"3[1-9]\\d{8}|3[2-9]\\d{7}",
				[
					9,
					10
				]
			]
		]
	],
	VC: [
		"1",
		"011",
		"(?:[58]\\d\\d|784|900)\\d{7}",
		[
			10
		],
		0,
		"1",
		0,
		"([2-7]\\d{6})$|1",
		"784$1",
		0,
		"784",
		[
			[
				"784(?:266|3(?:6[6-9]|7\\d|8[0-6])|4(?:38|5[0-36-8]|8[0-8])|5(?:55|7[0-2]|93)|638|784)\\d{4}"
			],
			[
				"784(?:4(?:3[0-5]|5[45]|89|9[0-8])|5(?:2[6-9]|3[0-4])|720)\\d{4}"
			]
		]
	],
	VE: [
		"58",
		"00",
		"[68]00\\d{7}|(?:[24]\\d|[59]0)\\d{8}",
		[
			10
		],
		[
			[
				"(\\d{3})(\\d{7})",
				"$1-$2",
				[
					"[24-689]"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:2(?:12|3[457-9]|[467]\\d|[58][1-9]|9[1-6])|[4-6]00)\\d{7}"
			],
			[
				"4(?:1[24-8]|2[246])\\d{7}"
			]
		]
	],
	VG: [
		"1",
		"011",
		"(?:284|[58]\\d\\d|900)\\d{7}",
		[
			10
		],
		0,
		"1",
		0,
		"([2-578]\\d{6})$|1",
		"284$1",
		0,
		"284",
		[
			[
				"284(?:229|4(?:22|9[45])|774|8(?:52|6[459]))\\d{4}"
			],
			[
				"284(?:245|3(?:0[0-3]|4[0-7]|68|9[34])|4(?:4[0-6]|68|9[69])|5(?:4[0-7]|68|9[69]))\\d{4}"
			]
		]
	],
	VI: [
		"1",
		"011",
		"[58]\\d{9}|(?:34|90)0\\d{7}",
		[
			10
		],
		0,
		"1",
		0,
		"([2-9]\\d{6})$|1",
		"340$1",
		0,
		"340",
		[
			[
				"340(?:2(?:0\\d|10|2[06-8]|4[49]|77)|3(?:32|44)|4(?:2[23]|44|7[34]|89)|5(?:1[34]|55)|6(?:2[56]|4[23]|77|9[023])|7(?:1[2-57-9]|2[57]|7\\d)|884|998)\\d{4}"
			],
			[
				"340(?:2(?:0\\d|10|2[06-8]|4[49]|77)|3(?:32|44)|4(?:2[23]|44|7[34]|89)|5(?:1[34]|55)|6(?:2[56]|4[23]|77|9[023])|7(?:1[2-57-9]|2[57]|7\\d)|884|998)\\d{4}"
			]
		]
	],
	VN: [
		"84",
		"00",
		"[12]\\d{9}|[135-9]\\d{8}|[16]\\d{7}|[16-8]\\d{6}",
		[
			7,
			8,
			9,
			10
		],
		[
			[
				"(\\d{2})(\\d{5})",
				"$1 $2",
				[
					"80"
				],
				"0$1",
				1
			],
			[
				"(\\d{4})(\\d{4,6})",
				"$1 $2",
				[
					"1"
				],
				0,
				1
			],
			[
				"(\\d{2})(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"6"
				],
				"0$1",
				1
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[357-9]"
				],
				"0$1",
				1
			],
			[
				"(\\d{2})(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"2[48]"
				],
				"0$1",
				1
			],
			[
				"(\\d{3})(\\d{4})(\\d{3})",
				"$1 $2 $3",
				[
					"2"
				],
				"0$1",
				1
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"2(?:0[3-9]|1[0-689]|2[0-25-9]|[38][2-9]|4[2-8]|5[124-9]|6[0-39]|7[0-7]|9[0-4679])\\d{7}",
				[
					10
				]
			],
			[
				"(?:5(?:2[238]|59)|89[6-9]|99[013-9])\\d{6}|(?:3\\d|5[1689]|7[06-9]|8[1-8]|9[0-8])\\d{7}",
				[
					9
				]
			]
		]
	],
	VU: [
		"678",
		"00",
		"[57-9]\\d{6}|(?:[238]\\d|48)\\d{3}",
		[
			5,
			7
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"[57-9]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:38[0-8]|48[4-9])\\d\\d|(?:2[02-9]|3[4-7]|88)\\d{3}",
				[
					5
				]
			],
			[
				"(?:[58]\\d|7[013-7])\\d{5}",
				[
					7
				]
			]
		]
	],
	WF: [
		"681",
		"00",
		"(?:40|72|8\\d{4})\\d{4}|[89]\\d{5}",
		[
			6,
			9
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3",
				[
					"[47-9]"
				]
			],
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"8"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"72\\d{4}",
				[
					6
				]
			],
			[
				"(?:72|8[23])\\d{4}",
				[
					6
				]
			]
		]
	],
	WS: [
		"685",
		"0",
		"(?:[2-6]|8\\d{5})\\d{4}|[78]\\d{6}|[68]\\d{5}",
		[
			5,
			6,
			7,
			10
		],
		[
			[
				"(\\d{5})",
				"$1",
				[
					"[2-5]|6[1-9]"
				]
			],
			[
				"(\\d{3})(\\d{3,7})",
				"$1 $2",
				[
					"[68]"
				]
			],
			[
				"(\\d{2})(\\d{5})",
				"$1 $2",
				[
					"7"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"6[1-9]\\d{3}|(?:[2-5]|60)\\d{4}",
				[
					5,
					6
				]
			],
			[
				"(?:7[1-35-8]|8(?:[3-7]|9\\d{3}))\\d{5}",
				[
					7,
					10
				]
			]
		]
	],
	XK: [
		"383",
		"00",
		"2\\d{7,8}|3\\d{7,11}|(?:4\\d\\d|[89]00)\\d{5}",
		[
			8,
			9,
			10,
			11,
			12
		],
		[
			[
				"(\\d{3})(\\d{5})",
				"$1 $2",
				[
					"[89]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[2-4]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"2|39"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{7,10})",
				"$1 $2",
				[
					"3"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"38\\d{6,10}|(?:2[89]|39)(?:0\\d{5,6}|[1-9]\\d{5})"
			],
			[
				"4[3-9]\\d{6}",
				[
					8
				]
			]
		]
	],
	YE: [
		"967",
		"00",
		"(?:1|7\\d)\\d{7}|[1-7]\\d{6}",
		[
			7,
			8,
			9
		],
		[
			[
				"(\\d)(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"[1-6]|7(?:[24-6]|8[0-7])"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"7"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"78[0-7]\\d{4}|17\\d{6}|(?:[12][2-68]|3[2358]|4[2-58]|5[2-6]|6[3-58]|7[24-6])\\d{5}",
				[
					7,
					8
				]
			],
			[
				"7[01378]\\d{7}",
				[
					9
				]
			]
		]
	],
	YT: [
		"262",
		"00",
		"7093\\d{5}|(?:80|9\\d)\\d{7}|(?:26|63)9\\d{6}",
		[
			9
		],
		0,
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"269(?:0[0-467]|15|5[0-4]|6\\d|[78]0)\\d{4}"
			],
			[
				"(?:639(?:0[0-79]|1[019]|[267]\\d|3[09]|40|5[05-9]|9[04-79])|7093[5-7])\\d{4}"
			]
		]
	],
	ZA: [
		"27",
		"00",
		"[1-79]\\d{8}|8\\d{4,9}",
		[
			5,
			6,
			7,
			8,
			9,
			10
		],
		[
			[
				"(\\d{2})(\\d{3,4})",
				"$1 $2",
				[
					"8[1-4]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{2,3})",
				"$1 $2 $3",
				[
					"8[1-4]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"860"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[1-9]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"8"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:2(?:0330|4302)|52087)0\\d{3}|(?:1[0-8]|2[1-378]|3[1-69]|4\\d|5[1346-8])\\d{7}",
				[
					9
				]
			],
			[
				"(?:1(?:3492[0-25]|4495[0235]|549(?:20|5[01]))|4[34]492[01])\\d{3}|8[1-4]\\d{3,7}|(?:2[27]|47|54)4950\\d{3}|(?:1(?:049[2-4]|9[12]\\d\\d)|(?:50[0-2]|6\\d\\d|7(?:[0-46-9]\\d|5[0-4]))\\d\\d|8(?:5\\d{3}|7(?:08[67]|158|28[5-9]|310)))\\d{4}|(?:1[6-8]|28|3[2-69]|4[025689]|5[36-8])4920\\d{3}|(?:12|[2-5]1)492\\d{4}",
				[
					5,
					6,
					7,
					8,
					9
				]
			]
		]
	],
	ZM: [
		"260",
		"00",
		"800\\d{6}|(?:21|[579]\\d|63)\\d{7}",
		[
			9
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[28]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{7})",
				"$1 $2",
				[
					"[579]"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"21[1-8]\\d{6}"
			],
			[
				"(?:[59][5-8]|7[5-9])\\d{7}"
			]
		]
	],
	ZW: [
		"263",
		"00",
		"2(?:[0-57-9]\\d{6,8}|6[0-24-9]\\d{6,7})|[38]\\d{9}|[35-8]\\d{8}|[3-6]\\d{7}|[1-689]\\d{6}|[1-3569]\\d{5}|[1356]\\d{4}",
		[
			5,
			6,
			7,
			8,
			9,
			10
		],
		[
			[
				"(\\d{3})(\\d{3,5})",
				"$1 $2",
				[
					"2(?:0[45]|2[278]|[49]8)|3(?:[09]8|17)|6(?:[29]8|37|75)|[23][78]|(?:33|5[15]|6[68])[78]"
				],
				"0$1"
			],
			[
				"(\\d)(\\d{3})(\\d{2,4})",
				"$1 $2 $3",
				[
					"[49]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"80"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{7})",
				"$1 $2",
				[
					"24|8[13-59]|(?:2[05-79]|39|5[45]|6[15-8])2",
					"2(?:02[014]|4|[56]20|[79]2)|392|5(?:42|525)|6(?:[16-8]21|52[013])|8[13-59]"
				],
				"(0$1)"
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"7"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"2(?:1[39]|2[0157]|[378]|[56][14])|3(?:12|29)",
					"2(?:1[39]|2[0157]|[378]|[56][14])|3(?:123|29)"
				],
				"0$1"
			],
			[
				"(\\d{4})(\\d{6})",
				"$1 $2",
				[
					"8"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3,5})",
				"$1 $2",
				[
					"1|2(?:0[0-36-9]|12|29|[56])|3(?:1[0-689]|[24-6])|5(?:[0236-9]|1[2-4])|6(?:[013-59]|7[0-46-9])|(?:33|55|6[68])[0-69]|(?:29|3[09]|62)[0-79]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"29[013-9]|39|54"
				],
				"0$1"
			],
			[
				"(\\d{4})(\\d{3,5})",
				"$1 $2",
				[
					"(?:25|54)8",
					"258|5483"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:1(?:(?:3\\d|9)\\d|[4-8])|2(?:(?:(?:0(?:2[014]|5)|(?:2[0157]|31|84|9)\\d\\d|[56](?:[14]\\d\\d|20)|7(?:[089]|2[03]|[35]\\d\\d))\\d|4(?:2\\d\\d|8))\\d|1(?:2|[39]\\d{4}))|3(?:(?:123|(?:29\\d|92)\\d)\\d\\d|7(?:[19]|[56]\\d))|5(?:0|1[2-478]|26|[37]2|4(?:2\\d{3}|83)|5(?:25\\d\\d|[78])|[689]\\d)|6(?:(?:[16-8]21|28|52[013])\\d\\d|[39])|8(?:[1349]28|523)\\d\\d)\\d{3}|(?:4\\d\\d|9[2-9])\\d{4,5}|(?:(?:2(?:(?:(?:0|8[146])\\d|7[1-7])\\d|2(?:[278]\\d|92)|58(?:2\\d|3))|3(?:[26]|9\\d{3})|5(?:4\\d|5)\\d\\d)\\d|6(?:(?:(?:[0-246]|[78]\\d)\\d|37)\\d|5[2-8]))\\d\\d|(?:2(?:[569]\\d|8[2-57-9])|3(?:[013-59]\\d|8[37])|6[89]8)\\d{3}"
			],
			[
				"7(?:[1278]\\d|3[1-9])\\d{6}",
				[
					9
				]
			]
		]
	]
};
var nonGeographic = {
	"800": [
		"800",
		0,
		"(?:00|[1-9]\\d)\\d{6}",
		[
			8
		],
		[
			[
				"(\\d{4})(\\d{4})",
				"$1 $2",
				[
					"\\d"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			0,
			0,
			[
				"(?:00|[1-9]\\d)\\d{6}"
			]
		]
	],
	"808": [
		"808",
		0,
		"[1-9]\\d{7}",
		[
			8
		],
		[
			[
				"(\\d{4})(\\d{4})",
				"$1 $2",
				[
					"[1-9]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			0,
			0,
			0,
			0,
			0,
			0,
			0,
			0,
			0,
			[
				"[1-9]\\d{7}"
			]
		]
	],
	"870": [
		"870",
		0,
		"7\\d{11}|[235-7]\\d{8}",
		[
			9,
			12
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[235-7]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			0,
			[
				"(?:[356]|774[45])\\d{8}|7[6-8]\\d{7}"
			],
			0,
			0,
			0,
			0,
			0,
			0,
			[
				"2\\d{8}",
				[
					9
				]
			]
		]
	],
	"878": [
		"878",
		0,
		"10\\d{10}",
		[
			12
		],
		[
			[
				"(\\d{2})(\\d{5})(\\d{5})",
				"$1 $2 $3",
				[
					"1"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			0,
			0,
			0,
			0,
			0,
			0,
			0,
			0,
			[
				"10\\d{10}"
			]
		]
	],
	"881": [
		"881",
		0,
		"6\\d{9}|[0-36-9]\\d{8}",
		[
			9,
			10
		],
		[
			[
				"(\\d)(\\d{3})(\\d{5})",
				"$1 $2 $3",
				[
					"[0-37-9]"
				]
			],
			[
				"(\\d)(\\d{3})(\\d{5,6})",
				"$1 $2 $3",
				[
					"6"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			0,
			[
				"6\\d{9}|[0-36-9]\\d{8}"
			]
		]
	],
	"882": [
		"882",
		0,
		"[13]\\d{6}(?:\\d{2,5})?|[19]\\d{7}|(?:[25]\\d\\d|4)\\d{7}(?:\\d{2})?",
		[
			7,
			8,
			9,
			10,
			11,
			12
		],
		[
			[
				"(\\d{2})(\\d{5})",
				"$1 $2",
				[
					"16|342"
				]
			],
			[
				"(\\d{2})(\\d{6})",
				"$1 $2",
				[
					"49"
				]
			],
			[
				"(\\d{2})(\\d{2})(\\d{4})",
				"$1 $2 $3",
				[
					"1[36]|9"
				]
			],
			[
				"(\\d{2})(\\d{4})(\\d{3})",
				"$1 $2 $3",
				[
					"3[23]"
				]
			],
			[
				"(\\d{2})(\\d{3,4})(\\d{4})",
				"$1 $2 $3",
				[
					"16"
				]
			],
			[
				"(\\d{2})(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"10|23|3(?:[15]|4[57])|4|51"
				]
			],
			[
				"(\\d{3})(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"34"
				]
			],
			[
				"(\\d{2})(\\d{4,5})(\\d{5})",
				"$1 $2 $3",
				[
					"[1-35]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			0,
			[
				"342\\d{4}|(?:337|49)\\d{6}|(?:3(?:2|47|7\\d{3})|50\\d{3})\\d{7}",
				[
					7,
					8,
					9,
					10,
					12
				]
			],
			0,
			0,
			0,
			[
				"348[57]\\d{7}",
				[
					11
				]
			],
			0,
			0,
			[
				"1(?:3(?:0[0347]|[13][0139]|2[035]|4[013568]|6[0459]|7[06]|8[15-8]|9[0689])\\d{4}|6\\d{5,10})|(?:345\\d|9[89])\\d{6}|(?:10|2(?:3|85\\d)|3(?:[15]|[69]\\d\\d)|4[15-8]|51)\\d{8}"
			]
		]
	],
	"883": [
		"883",
		0,
		"(?:[1-4]\\d|51)\\d{6,10}",
		[
			8,
			9,
			10,
			11,
			12
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{2,8})",
				"$1 $2 $3",
				[
					"[14]|2[24-689]|3[02-689]|51[24-9]"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"510"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"21"
				]
			],
			[
				"(\\d{4})(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"51[13]"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3 $4",
				[
					"[235]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			0,
			0,
			0,
			0,
			0,
			0,
			0,
			0,
			[
				"(?:2(?:00\\d\\d|10)|(?:370[1-9]|51\\d0)\\d)\\d{7}|51(?:00\\d{5}|[24-9]0\\d{4,7})|(?:1[0-79]|2[24-689]|3[02-689]|4[0-4])0\\d{5,9}"
			]
		]
	],
	"888": [
		"888",
		0,
		"\\d{11}",
		[
			11
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{5})",
				"$1 $2 $3"
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			0,
			0,
			0,
			0,
			0,
			0,
			[
				"\\d{11}"
			]
		]
	],
	"979": [
		"979",
		0,
		"[1359]\\d{8}",
		[
			9
		],
		[
			[
				"(\\d)(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"[1359]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			0,
			0,
			0,
			[
				"[1359]\\d{8}"
			]
		]
	]
};
var world_metadata = {
	version: version,
	country_calling_codes: country_calling_codes,
	countries: countries,
	nonGeographic: nonGeographic
};

/**
 * We export all of the world countries in `index.ts`
 * The countries listed in this file equals to [world countries - europe countries]
 */ const REST_OF_WORLD = [
    {
        id: 'afghanistan_AF',
        flagPos: {
            left: -216,
            top: 0
        },
        code: '+93',
        iso: 'AF'
    },
    {
        id: 'algeria_DZ',
        flagPos: {
            left: -1080,
            top: -336
        },
        code: '+213',
        iso: 'DZ'
    },
    {
        id: 'american_samoa_AS',
        flagPos: {
            left: -972,
            top: 0
        },
        code: '+1-684',
        iso: 'AS'
    },
    {
        id: 'angola_AO',
        flagPos: {
            left: -756,
            top: 0
        },
        code: '+244',
        iso: 'AO'
    },
    {
        id: 'anguilla_AI',
        flagPos: {
            left: -432,
            top: 0
        },
        code: '+1-264',
        iso: 'AI'
    },
    {
        id: 'antigua_and_barbuda_AG',
        flagPos: {
            left: -324,
            top: 0
        },
        code: '+1-268',
        iso: 'AG'
    },
    {
        id: 'argentina_AR',
        flagPos: {
            left: -864,
            top: 0
        },
        code: '+54',
        iso: 'AR'
    },
    {
        id: 'aruba_AW',
        flagPos: {
            left: 0,
            top: -84
        },
        code: '+297',
        iso: 'AW'
    },
    {
        id: 'aland_islands_AX',
        flagPos: {
            left: -108,
            top: -84
        },
        code: '+358',
        iso: 'AX'
    },
    {
        id: 'australia_AU',
        flagPos: {
            left: -1188,
            top: 0
        },
        code: '+61',
        iso: 'AU'
    },
    {
        id: 'bahamas_BS',
        flagPos: {
            left: -540,
            top: -168
        },
        code: '+1-242',
        iso: 'BS'
    },
    {
        id: 'bahrain_BH',
        flagPos: {
            left: -972,
            top: -84
        },
        code: '+973',
        iso: 'BH'
    },
    {
        id: 'bangladesh_BD',
        flagPos: {
            left: -540,
            top: -84
        },
        code: '+880',
        iso: 'BD'
    },
    {
        id: 'barbados_BB',
        flagPos: {
            left: -432,
            top: -84
        },
        code: '+1-246',
        iso: 'BB'
    },
    {
        id: 'belize_BZ',
        flagPos: {
            left: -972,
            top: -168
        },
        code: '+501',
        iso: 'BZ'
    },
    {
        id: 'benin_BJ',
        flagPos: {
            left: -1188,
            top: -84
        },
        code: '+229',
        iso: 'BJ'
    },
    {
        id: 'st_barthelemy_BL',
        flagPos: {
            left: 0,
            top: -168
        },
        code: '+590',
        iso: 'BL'
    },
    {
        id: 'bermuda_BM',
        flagPos: {
            left: -108,
            top: -168
        },
        code: '+1-441',
        iso: 'BM'
    },
    {
        id: 'bhutan_BT',
        flagPos: {
            left: -756,
            top: -168
        },
        code: '+975',
        iso: 'BT'
    },
    {
        id: 'bolivia_BO',
        flagPos: {
            left: -324,
            top: -168
        },
        code: '+591',
        iso: 'BO'
    },
    {
        id: 'botswana_BW',
        flagPos: {
            left: -648,
            top: -168
        },
        code: '+267',
        iso: 'BW'
    },
    {
        id: 'brazil_BR',
        flagPos: {
            left: -432,
            top: -168
        },
        code: '+55',
        iso: 'BR'
    },
    {
        id: 'british_indian_ocean_territory_IO',
        flagPos: {
            left: 0,
            top: -672
        },
        code: '+246',
        iso: 'IO'
    },
    {
        id: 'british_virgin_islands_VG',
        flagPos: {
            left: -324,
            top: -1512
        },
        code: '+1-284',
        iso: 'VG'
    },
    {
        id: 'brunei_BN',
        flagPos: {
            left: -216,
            top: -168
        },
        code: '+673',
        iso: 'BN'
    },
    {
        id: 'burkina_faso_BF',
        flagPos: {
            left: -756,
            top: -84
        },
        code: '+226',
        iso: 'BF'
    },
    {
        id: 'burundi_BI',
        flagPos: {
            left: -1080,
            top: -84
        },
        code: '+257',
        iso: 'BI'
    },
    {
        id: 'cape_verde_CV',
        flagPos: {
            left: 0,
            top: -336
        },
        code: '+238',
        iso: 'CV'
    },
    {
        id: 'cambodia_KH',
        flagPos: {
            left: -1188,
            top: -672
        },
        code: '+855',
        iso: 'KH'
    },
    {
        id: 'cameroon_CM',
        flagPos: {
            left: -756,
            top: -252
        },
        code: '+237',
        iso: 'CM'
    },
    {
        id: 'canada_CA',
        flagPos: {
            left: 0,
            top: -252
        },
        code: '+1',
        iso: 'CA'
    },
    {
        id: 'cocos_islands_CC',
        flagPos: {
            left: -1080,
            top: -168
        },
        code: '+61',
        iso: 'CC'
    },
    {
        id: 'cayman_islands_KY',
        flagPos: {
            left: -540,
            top: -756
        },
        code: '+1-345',
        iso: 'KY'
    },
    {
        id: 'central_african_republic_CF',
        flagPos: {
            left: -108,
            top: -252
        },
        code: '+236',
        iso: 'CF'
    },
    {
        id: 'chad_TD',
        flagPos: {
            left: -648,
            top: -1344
        },
        code: '+235',
        iso: 'TD'
    },
    {
        id: 'chile_CL',
        flagPos: {
            left: -648,
            top: -252
        },
        code: '+56',
        iso: 'CL'
    },
    {
        id: 'china_CN',
        flagPos: {
            left: -864,
            top: -252
        },
        code: '+86',
        iso: 'CN'
    },
    {
        id: 'colombia_CO',
        flagPos: {
            left: -972,
            top: -252
        },
        code: '+57',
        iso: 'CO'
    },
    {
        id: 'ivory_coast_CI',
        flagPos: {
            left: -432,
            top: -252
        },
        code: '+225',
        iso: 'CI'
    },
    {
        id: 'comoros_KM',
        flagPos: {
            left: -108,
            top: -756
        },
        code: '+269',
        iso: 'KM'
    },
    {
        id: 'congo_CG',
        flagPos: {
            left: -216,
            top: -252
        },
        code: '+242',
        iso: 'CG'
    },
    {
        id: 'dr_congo_CD',
        flagPos: {
            left: -1188,
            top: -168
        },
        code: '+243',
        iso: 'CD'
    },
    {
        id: 'cook_islands_CK',
        flagPos: {
            left: -540,
            top: -252
        },
        code: '+682',
        iso: 'CK'
    },
    {
        id: 'costa_rica_CR',
        flagPos: {
            left: -1188,
            top: -252
        },
        code: '+506',
        iso: 'CR'
    },
    {
        id: 'cuba_CU',
        flagPos: {
            left: -1080,
            top: -252
        },
        code: '+53',
        iso: 'CU'
    },
    {
        id: 'curaçao_CW',
        flagPos: {
            left: -108,
            top: -336
        },
        code: '+599',
        iso: 'CW'
    },
    {
        id: 'christmas_island_CX',
        flagPos: {
            left: -216,
            top: -336
        },
        code: '+61',
        iso: 'CX'
    },
    {
        id: 'djibouti_DJ',
        flagPos: {
            left: -648,
            top: -336
        },
        code: '+253',
        iso: 'DJ'
    },
    {
        id: 'dominica_DM',
        flagPos: {
            left: -864,
            top: -336
        },
        code: '+1-767',
        iso: 'DM'
    },
    {
        id: 'dominican_republic_DO',
        flagPos: {
            left: -972,
            top: -336
        },
        code: '+1-809',
        iso: 'DO'
    },
    {
        id: 'ecuador_EC',
        flagPos: {
            left: -1188,
            top: -336
        },
        code: '+593',
        iso: 'EC'
    },
    {
        id: 'egypt_EG',
        flagPos: {
            left: -108,
            top: -420
        },
        code: '+20',
        iso: 'EG'
    },
    {
        id: 'el_salvador_SV',
        flagPos: {
            left: -324,
            top: -1344
        },
        code: '+503',
        iso: 'SV'
    },
    {
        id: 'equatorial_guinea_GQ',
        flagPos: {
            left: -864,
            top: -504
        },
        code: '+240',
        iso: 'GQ'
    },
    {
        id: 'eritrea_ER',
        flagPos: {
            left: -216,
            top: -420
        },
        code: '+291',
        iso: 'ER'
    },
    {
        id: 'eswatini_SZ',
        flagPos: {
            left: -540,
            top: -1344
        },
        code: '+268',
        iso: 'SZ'
    },
    {
        id: 'ethiopia_ET',
        flagPos: {
            left: -432,
            top: -420
        },
        code: '+251',
        iso: 'ET'
    },
    {
        id: 'fiji_FJ',
        flagPos: {
            left: -648,
            top: -420
        },
        code: '+679',
        iso: 'FJ'
    },
    {
        id: 'gabon_GA',
        flagPos: {
            left: -972,
            top: -420
        },
        code: '+241',
        iso: 'GA'
    },
    {
        id: 'gambia_GM',
        flagPos: {
            left: -540,
            top: -504
        },
        code: '+220',
        iso: 'GM'
    },
    {
        id: 'ghana_GH',
        flagPos: {
            left: -216,
            top: -504
        },
        code: '+233',
        iso: 'GH'
    },
    {
        id: 'gibraltar_GI',
        flagPos: {
            left: -324,
            top: -504
        },
        code: '+350',
        iso: 'GI'
    },
    {
        id: 'greenland_GL',
        flagPos: {
            left: -432,
            top: -504
        },
        code: '+299',
        iso: 'GL'
    },
    {
        id: 'grenada_GD',
        flagPos: {
            left: -1188,
            top: -420
        },
        code: '+1-473',
        iso: 'GD'
    },
    {
        id: 'french_guiana_GF',
        flagPos: {
            left: -108,
            top: -504
        },
        code: '+594',
        iso: 'GF'
    },
    {
        id: 'guam_GU',
        flagPos: {
            left: -1188,
            top: -504
        },
        code: '+1-671',
        iso: 'GU'
    },
    {
        id: 'guatemala_GT',
        flagPos: {
            left: -1080,
            top: -504
        },
        code: '+502',
        iso: 'GT'
    },
    {
        id: 'guinea_GN',
        flagPos: {
            left: -648,
            top: -504
        },
        code: '+224',
        iso: 'GN'
    },
    {
        id: 'guadeloupe_GP',
        flagPos: {
            left: -756,
            top: -504
        },
        code: '+590',
        iso: 'GP'
    },
    {
        id: 'guinea_bissau_GW',
        flagPos: {
            left: 0,
            top: -588
        },
        code: '+245',
        iso: 'GW'
    },
    {
        id: 'guyana_GY',
        flagPos: {
            left: -108,
            top: -588
        },
        code: '+592',
        iso: 'GY'
    },
    {
        id: 'haiti_HT',
        flagPos: {
            left: -540,
            top: -588
        },
        code: '+509',
        iso: 'HT'
    },
    {
        id: 'honduras_HN',
        flagPos: {
            left: -324,
            top: -588
        },
        code: '+504',
        iso: 'HN'
    },
    {
        id: 'hong_kong_HK',
        flagPos: {
            left: -216,
            top: -588
        },
        code: '+852',
        iso: 'HK'
    },
    {
        id: 'india_IN',
        flagPos: {
            left: -1188,
            top: -588
        },
        code: '+91',
        iso: 'IN'
    },
    {
        id: 'indonesia_ID',
        flagPos: {
            left: -756,
            top: -588
        },
        code: '+62',
        iso: 'ID'
    },
    {
        id: 'iran_IR',
        flagPos: {
            left: -216,
            top: -672
        },
        code: '+98',
        iso: 'IR'
    },
    {
        id: 'iraq_IQ',
        flagPos: {
            left: -108,
            top: -672
        },
        code: '+964',
        iso: 'IQ'
    },
    {
        id: 'israel_IL',
        flagPos: {
            left: -972,
            top: -588
        },
        code: '+972',
        iso: 'IL'
    },
    {
        id: 'isle_of_man_IM',
        flagPos: {
            left: -1080,
            top: -588
        },
        code: '+44',
        iso: 'IM'
    },
    {
        id: 'jersey_JE',
        flagPos: {
            left: -540,
            top: -672
        },
        code: '+44',
        iso: 'JE'
    },
    {
        id: 'jamaica_JM',
        flagPos: {
            left: -648,
            top: -672
        },
        code: '+1-876',
        iso: 'JM'
    },
    {
        id: 'japan_JP',
        flagPos: {
            left: -864,
            top: -672
        },
        code: '+81',
        iso: 'JP'
    },
    {
        id: 'jordan_JO',
        flagPos: {
            left: -756,
            top: -672
        },
        code: '+962',
        iso: 'JO'
    },
    {
        id: 'kenya_KE',
        flagPos: {
            left: -972,
            top: -672
        },
        code: '+254',
        iso: 'KE'
    },
    {
        id: 'kiribati_KI',
        flagPos: {
            left: 0,
            top: -756
        },
        code: '+686',
        iso: 'KI'
    },
    {
        id: 'kuwait_KW',
        flagPos: {
            left: -432,
            top: -756
        },
        code: '+965',
        iso: 'KW'
    },
    {
        id: 'kyrgyzstan_KG',
        flagPos: {
            left: -1080,
            top: -672
        },
        code: '+996',
        iso: 'KG'
    },
    {
        id: 'laos_LA',
        flagPos: {
            left: -756,
            top: -756
        },
        code: '+856',
        iso: 'LA'
    },
    {
        id: 'lebanon_LB',
        flagPos: {
            left: -864,
            top: -756
        },
        code: '+961',
        iso: 'LB'
    },
    {
        id: 'lesotho_LS',
        flagPos: {
            left: 0,
            top: -840
        },
        code: '+266',
        iso: 'LS'
    },
    {
        id: 'liberia_LR',
        flagPos: {
            left: -1188,
            top: -756
        },
        code: '+231',
        iso: 'LR'
    },
    {
        id: 'libya_LY',
        flagPos: {
            left: -432,
            top: -840
        },
        code: '+218',
        iso: 'LY'
    },
    {
        id: 'macau_MO',
        flagPos: {
            left: -432,
            top: -924
        },
        code: '+853',
        iso: 'MO'
    },
    {
        id: 'madagascar_MG',
        flagPos: {
            left: -1080,
            top: -840
        },
        code: '+261',
        iso: 'MG'
    },
    {
        id: 'malawi_MW',
        flagPos: {
            left: 0,
            top: -1008
        },
        code: '+265',
        iso: 'MW'
    },
    {
        id: 'malaysia_MY',
        flagPos: {
            left: -216,
            top: -1008
        },
        code: '+60',
        iso: 'MY'
    },
    {
        id: 'maldives_MV',
        flagPos: {
            left: -1188,
            top: -924
        },
        code: '+960',
        iso: 'MV'
    },
    {
        id: 'mali_ML',
        flagPos: {
            left: -108,
            top: -924
        },
        code: '+223',
        iso: 'ML'
    },
    {
        id: 'marshall_islands_MH',
        flagPos: {
            left: -1188,
            top: -840
        },
        code: '+692',
        iso: 'MH'
    },
    {
        id: 'martinique_MQ',
        flagPos: {
            left: -648,
            top: -924
        },
        code: '+596',
        iso: 'MQ'
    },
    {
        id: 'mauritania_MR',
        flagPos: {
            left: -756,
            top: -924
        },
        code: '+222',
        iso: 'MR'
    },
    {
        id: 'mauritius_MU',
        flagPos: {
            left: -1080,
            top: -924
        },
        code: '+230',
        iso: 'MU'
    },
    {
        id: 'mexico_MX',
        flagPos: {
            left: -108,
            top: -1008
        },
        code: '+52',
        iso: 'MX'
    },
    {
        id: 'micronesia_FM',
        flagPos: {
            left: -756,
            top: -420
        },
        code: '+691',
        iso: 'FM'
    },
    {
        id: 'mongolia_MN',
        flagPos: {
            left: -324,
            top: -924
        },
        code: '+976',
        iso: 'MN'
    },
    {
        id: 'montserrat_MS',
        flagPos: {
            left: -864,
            top: -924
        },
        code: '+1-664',
        iso: 'MS'
    },
    {
        id: 'morocco_MA',
        flagPos: {
            left: -540,
            top: -840
        },
        code: '+212',
        iso: 'MA'
    },
    {
        id: 'saint_martin_MF',
        flagPos: {
            left: -972,
            top: -840
        },
        code: '+590',
        iso: 'MF'
    },
    {
        id: 'mozambique_MZ',
        flagPos: {
            left: -324,
            top: -1008
        },
        code: '+258',
        iso: 'MZ'
    },
    {
        id: 'myanmar_MM',
        flagPos: {
            left: -216,
            top: -924
        },
        code: '+95',
        iso: 'MM'
    },
    {
        id: 'namibia_NA',
        flagPos: {
            left: -432,
            top: -1008
        },
        code: '+264',
        iso: 'NA'
    },
    {
        id: 'nauru_NR',
        flagPos: {
            left: 0,
            top: -1092
        },
        code: '+674',
        iso: 'NR'
    },
    {
        id: 'nepal_NP',
        flagPos: {
            left: -1188,
            top: -1008
        },
        code: '+977',
        iso: 'NP'
    },
    {
        id: 'new_caledonia_NC',
        flagPos: {
            left: -540,
            top: -1008
        },
        code: '+687',
        iso: 'NC'
    },
    {
        id: 'new_zealand_NZ',
        flagPos: {
            left: -216,
            top: -1092
        },
        code: '+64',
        iso: 'NZ'
    },
    {
        id: 'nicaragua_NI',
        flagPos: {
            left: -864,
            top: -1008
        },
        code: '+505',
        iso: 'NI'
    },
    {
        id: 'niger_NE',
        flagPos: {
            left: -648,
            top: -1008
        },
        code: '+227',
        iso: 'NE'
    },
    {
        id: 'nigeria_NG',
        flagPos: {
            left: -756,
            top: -1008
        },
        code: '+234',
        iso: 'NG'
    },
    {
        id: 'niue_NU',
        flagPos: {
            left: -108,
            top: -1092
        },
        code: '+683',
        iso: 'NU'
    },
    {
        id: 'north_korea_KP',
        flagPos: {
            left: -216,
            top: -756
        },
        code: '+850',
        iso: 'KP'
    },
    {
        id: 'northern_mariana_islands_MP',
        flagPos: {
            left: -540,
            top: -924
        },
        code: '+1-670',
        iso: 'MP'
    },
    {
        id: 'oman_OM',
        flagPos: {
            left: -324,
            top: -1092
        },
        code: '+968',
        iso: 'OM'
    },
    {
        id: 'pakistan_PK',
        flagPos: {
            left: -972,
            top: -1092
        },
        code: '+92',
        iso: 'PK'
    },
    {
        id: 'saint_pierre_miquelon_PM',
        flagPos: {
            left: -1188,
            top: -1092
        },
        code: '+508',
        iso: 'PM'
    },
    {
        id: 'palau_PW',
        flagPos: {
            left: -324,
            top: -1176
        },
        code: '+680',
        iso: 'PW'
    },
    {
        id: 'palestine_PS',
        flagPos: {
            left: -108,
            top: -1176
        },
        code: '+970',
        iso: 'PS'
    },
    {
        id: 'panama_PA',
        flagPos: {
            left: -432,
            top: -1092
        },
        code: '+507',
        iso: 'PA'
    },
    {
        id: 'papua_new_guinea_PG',
        flagPos: {
            left: -756,
            top: -1092
        },
        code: '+675',
        iso: 'PG'
    },
    {
        id: 'paraguay_PY',
        flagPos: {
            left: -432,
            top: -1176
        },
        code: '+595',
        iso: 'PY'
    },
    {
        id: 'peru_PE',
        flagPos: {
            left: -540,
            top: -1092
        },
        code: '+51',
        iso: 'PE'
    },
    {
        id: 'french_polynesia_PF',
        flagPos: {
            left: -648,
            top: -1092
        },
        code: '+689',
        iso: 'PF'
    },
    {
        id: 'philippines_PH',
        flagPos: {
            left: -864,
            top: -1092
        },
        code: '+63',
        iso: 'PH'
    },
    {
        id: 'puerto_rico_PR',
        flagPos: {
            left: 0,
            top: -1176
        },
        code: '+1-787',
        iso: 'PR'
    },
    {
        id: 'qatar_QA',
        flagPos: {
            left: -540,
            top: -1176
        },
        code: '+974',
        iso: 'QA'
    },
    {
        id: 'reunion_RE',
        flagPos: {
            left: -648,
            top: -1176
        },
        code: '+262',
        iso: 'RE'
    },
    {
        id: 'rwanda_RW',
        flagPos: {
            left: -1080,
            top: -1176
        },
        code: '+250',
        iso: 'RW'
    },
    {
        id: 'samoa_WS',
        flagPos: {
            left: -756,
            top: -1512
        },
        code: '+685',
        iso: 'WS'
    },
    {
        id: 'sao_tome_and_principe_ST',
        flagPos: {
            left: -216,
            top: -1344
        },
        code: '+239',
        iso: 'ST'
    },
    {
        id: 'saudi_arabia_SA',
        flagPos: {
            left: -1188,
            top: -1176
        },
        code: '+966',
        iso: 'SA'
    },
    {
        id: 'senegal_SN',
        flagPos: {
            left: -1080,
            top: -1260
        },
        code: '+221',
        iso: 'SN'
    },
    {
        id: 'seychelles_SC',
        flagPos: {
            left: -108,
            top: -1260
        },
        code: '+248',
        iso: 'SC'
    },
    {
        id: 'sierra_leone_SL',
        flagPos: {
            left: -864,
            top: -1260
        },
        code: '+232',
        iso: 'SL'
    },
    {
        id: 'singapore_SG',
        flagPos: {
            left: -432,
            top: -1260
        },
        code: '+65',
        iso: 'SG'
    },
    {
        id: 'saint_helena_SH',
        flagPos: {
            left: -540,
            top: -1260
        },
        code: '+290',
        iso: 'SH'
    },
    {
        id: 'solomon_islands_SB',
        flagPos: {
            left: 0,
            top: -1260
        },
        code: '+677',
        iso: 'SB'
    },
    {
        id: 'somalia_SO',
        flagPos: {
            left: -1188,
            top: -1260
        },
        code: '+252',
        iso: 'SO'
    },
    {
        id: 'south_africa_ZA',
        flagPos: {
            left: -1188,
            top: -1512
        },
        code: '+27',
        iso: 'ZA'
    },
    {
        id: 'south_korea_KR',
        flagPos: {
            left: -324,
            top: -756
        },
        code: '+82',
        iso: 'KR'
    },
    {
        id: 'south_sudan_SS',
        flagPos: {
            left: -108,
            top: -1344
        },
        code: '+211',
        iso: 'SS'
    },
    {
        id: 'sri_lanka_LK',
        flagPos: {
            left: -1080,
            top: -756
        },
        code: '+94',
        iso: 'LK'
    },
    {
        id: 'sudan_SD',
        flagPos: {
            left: -216,
            top: -1260
        },
        code: '+249',
        iso: 'SD'
    },
    {
        id: 'suriname_SR',
        flagPos: {
            left: 0,
            top: -1344
        },
        code: '+597',
        iso: 'SR'
    },
    {
        id: 'syria_SY',
        flagPos: {
            left: -432,
            top: -1344
        },
        code: '+963',
        iso: 'SY'
    },
    {
        id: 'taiwan_TW',
        flagPos: {
            left: -648,
            top: -1428
        },
        code: '+886',
        iso: 'TW'
    },
    {
        id: 'tajikistan_TJ',
        flagPos: {
            left: -972,
            top: -1344
        },
        code: '+992',
        iso: 'TJ'
    },
    {
        id: 'tokelau_TK',
        flagPos: {
            left: -1080,
            top: -1344
        },
        code: '+690',
        iso: 'TK'
    },
    {
        id: 'tanzania_TZ',
        flagPos: {
            left: -756,
            top: -1428
        },
        code: '+255',
        iso: 'TZ'
    },
    {
        id: 'thailand_TH',
        flagPos: {
            left: -864,
            top: -1344
        },
        code: '+66',
        iso: 'TH'
    },
    {
        id: 'timor_leste_TL',
        flagPos: {
            left: -1188,
            top: -1344
        },
        code: '+670',
        iso: 'TL'
    },
    {
        id: 'togo_TG',
        flagPos: {
            left: -756,
            top: -1344
        },
        code: '+228',
        iso: 'TG'
    },
    {
        id: 'tonga_TO',
        flagPos: {
            left: -216,
            top: -1428
        },
        code: '+676',
        iso: 'TO'
    },
    {
        id: 'trinidad_and_tobago_TT',
        flagPos: {
            left: -432,
            top: -1428
        },
        code: '+1-868',
        iso: 'TT'
    },
    {
        id: 'tunisia_TN',
        flagPos: {
            left: -108,
            top: -1428
        },
        code: '+216',
        iso: 'TN'
    },
    {
        id: 'turkmenistan_TM',
        flagPos: {
            left: 0,
            top: -1428
        },
        code: '+993',
        iso: 'TM'
    },
    {
        id: 'tuvalu_TV',
        flagPos: {
            left: -540,
            top: -1428
        },
        code: '+688',
        iso: 'TV'
    },
    {
        id: 'uganda_UG',
        flagPos: {
            left: -972,
            top: -1428
        },
        code: '+256',
        iso: 'UG'
    },
    {
        id: 'united_arab_emirates_AE',
        flagPos: {
            left: -108,
            top: 0
        },
        code: '+971',
        iso: 'AE'
    },
    {
        id: 'united_states_US',
        flagPos: {
            left: -1080,
            top: -1428
        },
        code: '+1',
        iso: 'US'
    },
    {
        id: 'uruguay_UY',
        flagPos: {
            left: -1188,
            top: -1428
        },
        code: '+598',
        iso: 'UY'
    },
    {
        id: 'uzbekistan_UZ',
        flagPos: {
            left: 0,
            top: -1512
        },
        code: '+998',
        iso: 'UZ'
    },
    {
        id: 'vanuatu_VU',
        flagPos: {
            left: -540,
            top: -1512
        },
        code: '+678',
        iso: 'VU'
    },
    {
        id: 'venezuela_VE',
        flagPos: {
            left: -216,
            top: -1512
        },
        code: '+58',
        iso: 'VE'
    },
    {
        id: 'vietnam_VN',
        flagPos: {
            left: -432,
            top: -1512
        },
        code: '+84',
        iso: 'VN'
    },
    {
        id: 'wallis_futuna_WF',
        flagPos: {
            left: -648,
            top: -1512
        },
        code: '+681',
        iso: 'WF'
    },
    {
        id: 'yemen_YE',
        flagPos: {
            left: -972,
            top: -1512
        },
        code: '+967',
        iso: 'YE'
    },
    {
        id: 'mayotte_YT',
        flagPos: {
            left: -1080,
            top: -1512
        },
        code: '+262',
        iso: 'YT'
    },
    {
        id: 'zambia_ZM',
        flagPos: {
            left: 0,
            top: -1596
        },
        code: '+260',
        iso: 'ZM'
    },
    {
        id: 'zimbabwe_ZW',
        flagPos: {
            left: -108,
            top: -1596
        },
        code: '+263',
        iso: 'ZW'
    }
];

const WORLD_COUNTRIES = {
    metadata: world_metadata,
    list: [
        ...EUROPE,
        ...REST_OF_WORLD
    ]
};

export { EUROPE_COUNTRIES, PhoneNumber, WORLD_COUNTRIES, usePhoneNumberValidation };
//# sourceMappingURL=index.mjs.map
